import * as z from 'zod';

export const PreparePaymentCollectionRequest = z.object({
  ecomOrderId: z.string().describe('Ecom order ID.').min(1).max(100),
  amount: z
    .object({
      amount: z.string().describe('Amount.').optional(),
      formattedAmount: z
        .string()
        .describe('Amount formatted with currency symbol.')
        .optional(),
    })
    .describe('Amount to collect'),
  options: z
    .object({
      paymentGatewayOrderId: z
        .string()
        .describe(
          'Optional parameter. When present, payment collection will be performed using given payment gateway order.\nExisting payment gateway order will be updated with a new amount.\nWhen parameter is absent, new payment gateway order will be created and used for payment collection.'
        )
        .optional()
        .nullable(),
      delayedCapture: z
        .boolean()
        .describe('Whether to delay capture of the payment.\nDefault: false')
        .optional(),
      delayedCaptureSettings: z
        .object({
          scheduledAction: z.enum(['VOID', 'CAPTURE']).optional(),
          delayDuration: z
            .object({
              count: z
                .number()
                .int()
                .describe(
                  'Amount of units. For example, 30 MINUTES, 1 HOURS, 7 DAYS, etc'
                )
                .min(1)
                .optional(),
              unit: z.enum(['MINUTES', 'HOURS', 'DAYS']).optional(),
            })
            .describe(
              'Delay duration before execution. Optional - if not set, providers default period will be used'
            )
            .optional(),
        })
        .describe('Delayed capture payment settings')
        .optional(),
    })
    .optional(),
});
export const PreparePaymentCollectionResponse = z.object({
  paymentGatewayOrderId: z
    .string()
    .describe('Payment gateway order id which is associated with given payment')
    .optional(),
});
export const GetPaymentCollectabilityStatusRequest = z.object({
  ecomOrderId: z.string().describe('Ecom order ID.').min(1).max(100),
});
export const GetPaymentCollectabilityStatusResponse = z.object({
  status: z
    .enum([
      'UNKNOWN',
      'COLLECTABLE',
      'NONCOLLECTABLE_ORDER_IS_CANCELLED',
      'NONCOLLECTABLE_ORDER_IS_PAID',
      'NONCOLLECTABLE_MISSING_PAYMENT_METHOD',
      'NONCOLLECTABLE_ORDER_IS_PENDING',
      'NONCOLLECTABLE_ORDER_IS_REJECTED',
      'NONCOLLECTABLE_ORDER_HAS_SUBSCRIPTIONS',
      'NONCOLLECTABLE_ORDER_HAS_CHARGEBACKS',
      'NONCOLLECTABLE_SUBSCRIPTION_CYCLE_ALREADY_PASSED',
      'NONCOLLECTABLE_ORDER_HAS_UNSUPPORTED_SUBSCRIPTION',
      'NONCOLLECTABLE_CUSTOM_AMOUNT_NOT_SUPPORTED',
    ])
    .describe('Payment collectability status')
    .optional(),
  amount: z
    .object({
      amount: z.string().describe('Amount.').optional(),
      formattedAmount: z
        .string()
        .describe('Amount formatted with currency symbol.')
        .optional(),
    })
    .describe('Collectable order amount')
    .optional(),
});
export const VoidAuthorizedPaymentsRequest = z.object({
  ecomOrderId: z.string().describe('Wix eCommerce order ID').min(1).max(100),
  paymentIds: z.array(z.string()).min(1).max(100),
});
export const VoidAuthorizedPaymentsResponse = z.object({
  orderTransactions: z
    .object({
      orderId: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      payments: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Payment ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the payment was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the payment was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                )
                .optional()
                .nullable(),
              amount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Payment amount.')
                .optional(),
              refundDisabled: z
                .boolean()
                .describe(
                  'Whether refunds for this payment are disabled.\n+ `true`: This payment is not refundable.\n+ `false`: This payment may be refunded. However, this ultimately depends on the payment provider.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                regularPaymentDetails: z.never().optional(),
                giftcardPaymentDetails: z.never().optional(),
              }),
              z.object({
                giftcardPaymentDetails: z.never().optional(),
                regularPaymentDetails: z
                  .intersection(
                    z.object({
                      paymentOrderId: z
                        .string()
                        .describe('Wix Payments order ID.')
                        .max(100)
                        .optional()
                        .nullable(),
                      gatewayTransactionId: z
                        .string()
                        .describe(
                          "Payment gateway's transaction ID.\nThis field is only returned when the value of `offline_payment` is `false`."
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      paymentMethod: z
                        .string()
                        .describe(
                          'Payment method.\nDeprecated. Use `paymentMethodName.buyerLanguageName` instead.\nNon-exhaustive list of supported values:\n+ `CreditCard`, `Alipay`, `AstropayCash`, `AstropayDBT`, `AstropayMBT`, `Bitcoin`, `BitPay`, `Cash`, `ConvenienceStore`, `EPay`, `Fake`, `Giropay`, `IDeal`, `InPerson`, `Klarna`, `MercadoPago`, `Netpay`, `NordeaSolo`, `Offline`, `PagSeguro`, `PayEasy`, `PayPal`, `Paysafecard`, `Paysafecash`, `PointOfSale`, `Poli`, `Privat24`, `Przelewy24`, `RapidTransfer`, `Sepa`, `Skrill`, `Sofort`, `Trustly`, `Neteller`, `Unionpay`, `UniPay`, `Yandex`'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      providerTransactionId: z
                        .string()
                        .describe(
                          "Transaction ID in the payment provider's system. For example, at PayPal, Square, Stripe, etc. Not returned for offline payments."
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      offlinePayment: z
                        .boolean()
                        .describe(
                          'Whether the payment was made offline. For example, when using cash or when marked as paid in the Business Manager.'
                        )
                        .optional(),
                      status: z
                        .enum([
                          'UNDEFINED',
                          'APPROVED',
                          'PENDING',
                          'PENDING_MERCHANT',
                          'CANCELED',
                          'DECLINED',
                          'REFUNDED',
                          'PARTIALLY_REFUNDED',
                          'AUTHORIZED',
                          'VOIDED',
                        ])
                        .describe('Payment status.')
                        .optional(),
                      savedPaymentMethod: z
                        .boolean()
                        .describe(
                          'Whether there is a payment agreement that allows for future charges.'
                        )
                        .optional(),
                      authorizationDetails: z
                        .object({
                          delayedCapture: z
                            .boolean()
                            .describe(
                              'Whether the authorized payment is of a delayed capture.'
                            )
                            .optional(),
                          authorizedDate: z
                            .date()
                            .describe(
                              'Date and time the payment was authorized in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                            )
                            .optional()
                            .nullable(),
                          captures: z
                            .array(
                              z.object({
                                _id: z
                                  .string()
                                  .describe('Capture ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                status: z
                                  .enum(['PENDING', 'SUCCEEDED', 'FAILED'])
                                  .describe('Status of this capture action')
                                  .optional(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Amount of this capture')
                                  .optional(),
                                _createdDate: z
                                  .date()
                                  .describe(
                                    'Date and time the capture was initiated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                                  )
                                  .optional()
                                  .nullable(),
                                failureDetails: z
                                  .object({
                                    failureCode: z.string().max(100).optional(),
                                  })
                                  .describe(
                                    'In case of status is FAILED may contain failure details'
                                  )
                                  .optional(),
                              })
                            )
                            .max(1)
                            .optional(),
                          void: z
                            .object({
                              status: z
                                .enum(['PENDING', 'SUCCEEDED', 'FAILED'])
                                .describe('Status of this void action')
                                .optional(),
                              voidedDate: z
                                .date()
                                .describe(
                                  'Date and time the void was initiated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                                )
                                .optional()
                                .nullable(),
                              failureDetails: z
                                .object({
                                  failureCode: z.string().max(100).optional(),
                                })
                                .describe(
                                  'In case of status is FAILED may contain failure details'
                                )
                                .optional(),
                              reason: z
                                .enum(['MANUAL', 'SCHEDULED'])
                                .describe('Reason of void action')
                                .optional(),
                            })
                            .describe('Void associated with payment')
                            .optional(),
                          scheduledAction: z
                            .object({
                              actionType: z
                                .enum(['VOID', 'CAPTURE'])
                                .describe('Type of the action.')
                                .optional(),
                              executionDate: z
                                .date()
                                .describe('The date and time of the action.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Scheduled action for this transaction')
                            .optional(),
                        })
                        .describe('Authorization details.')
                        .optional(),
                      chargebacks: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('Chargeback ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            _createdDate: z
                              .date()
                              .describe(
                                'Date and time the chargeback was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided.'
                              )
                              .optional()
                              .nullable(),
                            _updatedDate: z
                              .date()
                              .describe(
                                'Date and time the chargeback was updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided.'
                              )
                              .optional()
                              .nullable(),
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Amount.')
                              .optional(),
                            reversalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Reversal amount. Present only when status is REVERSED.'
                              )
                              .optional(),
                            status: z
                              .enum(['APPROVED', 'REVERSED'])
                              .describe('Status.\n\nDefault: `"APPROVED"`.')
                              .optional(),
                            externalId: z
                              .string()
                              .describe('External chargeback ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(6)
                        .optional(),
                      platformFee: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Platform fee amount associated with this payment.'
                        )
                        .optional(),
                      paymentMethodName: z
                        .object({
                          buyerLanguageName: z
                            .string()
                            .describe(
                              'Translated payment method name in buyer language.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                          siteLanguageName: z
                            .string()
                            .describe(
                              'Translated payment method name in site owner language.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                          userDefinedName: z
                            .intersection(
                              z.object({}),
                              z.xor([
                                z.object({
                                  predefined: z.never().optional(),
                                  custom: z.never().optional(),
                                }),
                                z.object({
                                  custom: z.never().optional(),
                                  predefined: z
                                    .enum(['CASH', 'BANK_TRANSFER', 'CHECK'])
                                    .describe('Predefined payment method.'),
                                }),
                                z.object({
                                  predefined: z.never().optional(),
                                  custom: z
                                    .string()
                                    .describe(
                                      'Custom payment method name provided by user.\n\nFor example, `"Wire transfer"`, `"Money order"`, `"Venmo"` or any other custom name.'
                                    )
                                    .min(1)
                                    .max(100),
                                }),
                              ])
                            )
                            .describe(
                              'User-provided payment method name.\n\nWhen set, takes precedence over `buyerLanguageName` and `siteLanguageName`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Payment method with buyer and optional owner translations.\nNon-exhaustive list of supported values:\n+ `CreditCard`, `Alipay`, `AstropayCash`, `AstropayDBT`, `AstropayMBT`, `Bitcoin`, `BitPay`, `Cash`, `ConvenienceStore`, `EPay`, `Fake`, `Giropay`, `IDeal`, `InPerson`, `Klarna`, `MercadoPago`, `Netpay`, `NordeaSolo`, `Offline`, `PagSeguro`, `PayEasy`, `PayPal`, `Paysafecard`, `Paysafecash`, `PointOfSale`, `Poli`, `Privat24`, `Przelewy24`, `RapidTransfer`, `Sepa`, `Skrill`, `Sofort`, `Trustly`, `Neteller`, `Unionpay`, `UniPay`, `Yandex`'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ creditCardDetails: z.never().optional() }),
                      z.object({
                        creditCardDetails: z
                          .object({
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Credit card details.'),
                      }),
                    ])
                  )
                  .describe('Regular payment details.'),
              }),
              z.object({
                regularPaymentDetails: z.never().optional(),
                giftcardPaymentDetails: z
                  .object({
                    giftCardPaymentId: z
                      .string()
                      .describe('Gift card payment ID.')
                      .min(1)
                      .max(100)
                      .optional(),
                    appId: z
                      .string()
                      .describe('ID of the app that created the gift card.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    voided: z
                      .boolean()
                      .describe('Whether the gift card is voided.')
                      .optional(),
                  })
                  .describe('Gift card payment details.'),
              }),
            ])
          )
        )
        .max(100)
        .optional(),
      refunds: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Refund ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            transactions: z
              .array(
                z.object({
                  paymentId: z
                    .string()
                    .describe('ID of the payment associated with this refund.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  amount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Refund amount.')
                    .optional(),
                  refundStatus: z
                    .enum([
                      'PENDING',
                      'SUCCEEDED',
                      'FAILED',
                      'SCHEDULED',
                      'STARTED',
                    ])
                    .describe('Refund status.')
                    .optional(),
                  refundStatusInfo: z
                    .object({
                      paymentGatewayReasonCode: z
                        .string()
                        .describe(
                          "Reason code for the refund's current status.\n\nLearn more about [reason codes](https://dev.wix.com/docs/rest/business-management/payments/service-plugins/payment-service-provider-service-plugin/reason-codes)."
                        )
                        .min(1)
                        .max(10)
                        .optional()
                        .nullable(),
                      description: z
                        .string()
                        .describe(
                          'Free text explanation of current refund status.'
                        )
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe('Optional details of current refund status.')
                    .optional(),
                  gatewayRefundId: z
                    .string()
                    .describe(
                      "Payment gateway's refund ID.\nThis field is only returned when the value of `external_refund` is `false`."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  providerRefundId: z
                    .string()
                    .describe(
                      "ID of the refund in the payment provider's system. For example, at PayPal, Square, Stripe, etc. Not returned for external refunds."
                    )
                    .optional()
                    .nullable(),
                  externalRefund: z
                    .boolean()
                    .describe(
                      "Whether refund was made externally and manually on the payment provider's side."
                    )
                    .optional(),
                })
              )
              .min(0)
              .max(50)
              .optional(),
            details: z
              .object({
                items: z
                  .array(
                    z.object({
                      lineItemId: z
                        .string()
                        .describe('Line item ID the refunded line item.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      quantity: z
                        .number()
                        .int()
                        .describe('Line item quantity refunded.')
                        .min(1)
                        .max(100000)
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(300)
                  .optional(),
                shippingIncluded: z
                  .boolean()
                  .describe('Whether the shipping fee was also refunded.')
                  .optional(),
                reason: z
                  .string()
                  .describe(
                    'Reason for the refund, provided by customer (optional).'
                  )
                  .max(200)
                  .optional()
                  .nullable(),
                lineItems: z
                  .array(
                    z.object({
                      lineItemId: z
                        .string()
                        .describe('Line item ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      quantity: z
                        .number()
                        .int()
                        .describe('Refund quantity.')
                        .min(1)
                        .max(100000)
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(300)
                  .optional(),
                additionalFees: z
                  .array(
                    z.object({
                      additionalFeeId: z
                        .string()
                        .describe('Additional fee ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Refund amount.')
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(100)
                  .optional(),
                shipping: z
                  .object({
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                  })
                  .describe('Shipping amount that was refunded.')
                  .optional(),
              })
              .describe('Refund business details.')
              .optional(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the refund was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided.'
              )
              .optional()
              .nullable(),
            summary: z
              .object({
                requestedRefund: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total amount requested for refund.')
                  .optional(),
                pendingRefund: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Pending refund amount - the portion of `requestedRefund` that is still pending.'
                  )
                  .optional(),
                refunded: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Refunded amount - the portion of `requestedRefund` that refunded successfully.'
                  )
                  .optional(),
                failedRefundAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Failed refund amount - the portion of `requestedRefund` that failed.'
                  )
                  .optional(),
                pending: z
                  .boolean()
                  .describe(
                    'Whether at least one refund transaction is still in `"PENDING"` status.'
                  )
                  .optional(),
                breakdown: z
                  .object({
                    lineItems: z
                      .array(
                        z.object({
                          lineItemId: z
                            .string()
                            .describe('ID of the refunded line item.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          totalRefundedAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Total refunded amount for the line item.'
                            )
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(300)
                      .optional(),
                  })
                  .describe(
                    'Breakdown of refunded items. Available only after refund is complete.'
                  )
                  .optional(),
              })
              .describe('Aggregated refund summary.')
              .optional(),
            requestingServiceAppId: z
              .string()
              .describe('ID of the app that initiated this refund.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(300)
        .optional(),
    })
    .describe("All order's transactions after the void was triggered")
    .optional(),
});
export const CaptureAuthorizedPaymentsRequest = z.object({
  ecomOrderId: z.string().describe('Wix eCommerce order ID').min(1).max(100),
  payments: z
    .array(
      z.object({
        paymentId: z
          .string()
          .describe('Payment ID')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        amount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Capture amount.\nIf not provided - full authorized amount will be captured.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100),
});
export const CaptureAuthorizedPaymentsResponse = z.object({
  orderTransactions: z
    .object({
      orderId: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      payments: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Payment ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the payment was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the payment was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                )
                .optional()
                .nullable(),
              amount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Payment amount.')
                .optional(),
              refundDisabled: z
                .boolean()
                .describe(
                  'Whether refunds for this payment are disabled.\n+ `true`: This payment is not refundable.\n+ `false`: This payment may be refunded. However, this ultimately depends on the payment provider.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                regularPaymentDetails: z.never().optional(),
                giftcardPaymentDetails: z.never().optional(),
              }),
              z.object({
                giftcardPaymentDetails: z.never().optional(),
                regularPaymentDetails: z
                  .intersection(
                    z.object({
                      paymentOrderId: z
                        .string()
                        .describe('Wix Payments order ID.')
                        .max(100)
                        .optional()
                        .nullable(),
                      gatewayTransactionId: z
                        .string()
                        .describe(
                          "Payment gateway's transaction ID.\nThis field is only returned when the value of `offline_payment` is `false`."
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      paymentMethod: z
                        .string()
                        .describe(
                          'Payment method.\nDeprecated. Use `paymentMethodName.buyerLanguageName` instead.\nNon-exhaustive list of supported values:\n+ `CreditCard`, `Alipay`, `AstropayCash`, `AstropayDBT`, `AstropayMBT`, `Bitcoin`, `BitPay`, `Cash`, `ConvenienceStore`, `EPay`, `Fake`, `Giropay`, `IDeal`, `InPerson`, `Klarna`, `MercadoPago`, `Netpay`, `NordeaSolo`, `Offline`, `PagSeguro`, `PayEasy`, `PayPal`, `Paysafecard`, `Paysafecash`, `PointOfSale`, `Poli`, `Privat24`, `Przelewy24`, `RapidTransfer`, `Sepa`, `Skrill`, `Sofort`, `Trustly`, `Neteller`, `Unionpay`, `UniPay`, `Yandex`'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      providerTransactionId: z
                        .string()
                        .describe(
                          "Transaction ID in the payment provider's system. For example, at PayPal, Square, Stripe, etc. Not returned for offline payments."
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      offlinePayment: z
                        .boolean()
                        .describe(
                          'Whether the payment was made offline. For example, when using cash or when marked as paid in the Business Manager.'
                        )
                        .optional(),
                      status: z
                        .enum([
                          'UNDEFINED',
                          'APPROVED',
                          'PENDING',
                          'PENDING_MERCHANT',
                          'CANCELED',
                          'DECLINED',
                          'REFUNDED',
                          'PARTIALLY_REFUNDED',
                          'AUTHORIZED',
                          'VOIDED',
                        ])
                        .describe('Payment status.')
                        .optional(),
                      savedPaymentMethod: z
                        .boolean()
                        .describe(
                          'Whether there is a payment agreement that allows for future charges.'
                        )
                        .optional(),
                      authorizationDetails: z
                        .object({
                          delayedCapture: z
                            .boolean()
                            .describe(
                              'Whether the authorized payment is of a delayed capture.'
                            )
                            .optional(),
                          authorizedDate: z
                            .date()
                            .describe(
                              'Date and time the payment was authorized in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                            )
                            .optional()
                            .nullable(),
                          captures: z
                            .array(
                              z.object({
                                _id: z
                                  .string()
                                  .describe('Capture ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                status: z
                                  .enum(['PENDING', 'SUCCEEDED', 'FAILED'])
                                  .describe('Status of this capture action')
                                  .optional(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Amount of this capture')
                                  .optional(),
                                _createdDate: z
                                  .date()
                                  .describe(
                                    'Date and time the capture was initiated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                                  )
                                  .optional()
                                  .nullable(),
                                failureDetails: z
                                  .object({
                                    failureCode: z.string().max(100).optional(),
                                  })
                                  .describe(
                                    'In case of status is FAILED may contain failure details'
                                  )
                                  .optional(),
                              })
                            )
                            .max(1)
                            .optional(),
                          void: z
                            .object({
                              status: z
                                .enum(['PENDING', 'SUCCEEDED', 'FAILED'])
                                .describe('Status of this void action')
                                .optional(),
                              voidedDate: z
                                .date()
                                .describe(
                                  'Date and time the void was initiated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                                )
                                .optional()
                                .nullable(),
                              failureDetails: z
                                .object({
                                  failureCode: z.string().max(100).optional(),
                                })
                                .describe(
                                  'In case of status is FAILED may contain failure details'
                                )
                                .optional(),
                              reason: z
                                .enum(['MANUAL', 'SCHEDULED'])
                                .describe('Reason of void action')
                                .optional(),
                            })
                            .describe('Void associated with payment')
                            .optional(),
                          scheduledAction: z
                            .object({
                              actionType: z
                                .enum(['VOID', 'CAPTURE'])
                                .describe('Type of the action.')
                                .optional(),
                              executionDate: z
                                .date()
                                .describe('The date and time of the action.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Scheduled action for this transaction')
                            .optional(),
                        })
                        .describe('Authorization details.')
                        .optional(),
                      chargebacks: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('Chargeback ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            _createdDate: z
                              .date()
                              .describe(
                                'Date and time the chargeback was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided.'
                              )
                              .optional()
                              .nullable(),
                            _updatedDate: z
                              .date()
                              .describe(
                                'Date and time the chargeback was updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided.'
                              )
                              .optional()
                              .nullable(),
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Amount.')
                              .optional(),
                            reversalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Reversal amount. Present only when status is REVERSED.'
                              )
                              .optional(),
                            status: z
                              .enum(['APPROVED', 'REVERSED'])
                              .describe('Status.\n\nDefault: `"APPROVED"`.')
                              .optional(),
                            externalId: z
                              .string()
                              .describe('External chargeback ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(6)
                        .optional(),
                      platformFee: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Platform fee amount associated with this payment.'
                        )
                        .optional(),
                      paymentMethodName: z
                        .object({
                          buyerLanguageName: z
                            .string()
                            .describe(
                              'Translated payment method name in buyer language.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                          siteLanguageName: z
                            .string()
                            .describe(
                              'Translated payment method name in site owner language.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                          userDefinedName: z
                            .intersection(
                              z.object({}),
                              z.xor([
                                z.object({
                                  predefined: z.never().optional(),
                                  custom: z.never().optional(),
                                }),
                                z.object({
                                  custom: z.never().optional(),
                                  predefined: z
                                    .enum(['CASH', 'BANK_TRANSFER', 'CHECK'])
                                    .describe('Predefined payment method.'),
                                }),
                                z.object({
                                  predefined: z.never().optional(),
                                  custom: z
                                    .string()
                                    .describe(
                                      'Custom payment method name provided by user.\n\nFor example, `"Wire transfer"`, `"Money order"`, `"Venmo"` or any other custom name.'
                                    )
                                    .min(1)
                                    .max(100),
                                }),
                              ])
                            )
                            .describe(
                              'User-provided payment method name.\n\nWhen set, takes precedence over `buyerLanguageName` and `siteLanguageName`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Payment method with buyer and optional owner translations.\nNon-exhaustive list of supported values:\n+ `CreditCard`, `Alipay`, `AstropayCash`, `AstropayDBT`, `AstropayMBT`, `Bitcoin`, `BitPay`, `Cash`, `ConvenienceStore`, `EPay`, `Fake`, `Giropay`, `IDeal`, `InPerson`, `Klarna`, `MercadoPago`, `Netpay`, `NordeaSolo`, `Offline`, `PagSeguro`, `PayEasy`, `PayPal`, `Paysafecard`, `Paysafecash`, `PointOfSale`, `Poli`, `Privat24`, `Przelewy24`, `RapidTransfer`, `Sepa`, `Skrill`, `Sofort`, `Trustly`, `Neteller`, `Unionpay`, `UniPay`, `Yandex`'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ creditCardDetails: z.never().optional() }),
                      z.object({
                        creditCardDetails: z
                          .object({
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Credit card details.'),
                      }),
                    ])
                  )
                  .describe('Regular payment details.'),
              }),
              z.object({
                regularPaymentDetails: z.never().optional(),
                giftcardPaymentDetails: z
                  .object({
                    giftCardPaymentId: z
                      .string()
                      .describe('Gift card payment ID.')
                      .min(1)
                      .max(100)
                      .optional(),
                    appId: z
                      .string()
                      .describe('ID of the app that created the gift card.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    voided: z
                      .boolean()
                      .describe('Whether the gift card is voided.')
                      .optional(),
                  })
                  .describe('Gift card payment details.'),
              }),
            ])
          )
        )
        .max(100)
        .optional(),
      refunds: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Refund ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            transactions: z
              .array(
                z.object({
                  paymentId: z
                    .string()
                    .describe('ID of the payment associated with this refund.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  amount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Refund amount.')
                    .optional(),
                  refundStatus: z
                    .enum([
                      'PENDING',
                      'SUCCEEDED',
                      'FAILED',
                      'SCHEDULED',
                      'STARTED',
                    ])
                    .describe('Refund status.')
                    .optional(),
                  refundStatusInfo: z
                    .object({
                      paymentGatewayReasonCode: z
                        .string()
                        .describe(
                          "Reason code for the refund's current status.\n\nLearn more about [reason codes](https://dev.wix.com/docs/rest/business-management/payments/service-plugins/payment-service-provider-service-plugin/reason-codes)."
                        )
                        .min(1)
                        .max(10)
                        .optional()
                        .nullable(),
                      description: z
                        .string()
                        .describe(
                          'Free text explanation of current refund status.'
                        )
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe('Optional details of current refund status.')
                    .optional(),
                  gatewayRefundId: z
                    .string()
                    .describe(
                      "Payment gateway's refund ID.\nThis field is only returned when the value of `external_refund` is `false`."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  providerRefundId: z
                    .string()
                    .describe(
                      "ID of the refund in the payment provider's system. For example, at PayPal, Square, Stripe, etc. Not returned for external refunds."
                    )
                    .optional()
                    .nullable(),
                  externalRefund: z
                    .boolean()
                    .describe(
                      "Whether refund was made externally and manually on the payment provider's side."
                    )
                    .optional(),
                })
              )
              .min(0)
              .max(50)
              .optional(),
            details: z
              .object({
                items: z
                  .array(
                    z.object({
                      lineItemId: z
                        .string()
                        .describe('Line item ID the refunded line item.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      quantity: z
                        .number()
                        .int()
                        .describe('Line item quantity refunded.')
                        .min(1)
                        .max(100000)
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(300)
                  .optional(),
                shippingIncluded: z
                  .boolean()
                  .describe('Whether the shipping fee was also refunded.')
                  .optional(),
                reason: z
                  .string()
                  .describe(
                    'Reason for the refund, provided by customer (optional).'
                  )
                  .max(200)
                  .optional()
                  .nullable(),
                lineItems: z
                  .array(
                    z.object({
                      lineItemId: z
                        .string()
                        .describe('Line item ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      quantity: z
                        .number()
                        .int()
                        .describe('Refund quantity.')
                        .min(1)
                        .max(100000)
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(300)
                  .optional(),
                additionalFees: z
                  .array(
                    z.object({
                      additionalFeeId: z
                        .string()
                        .describe('Additional fee ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Refund amount.')
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(100)
                  .optional(),
                shipping: z
                  .object({
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                  })
                  .describe('Shipping amount that was refunded.')
                  .optional(),
              })
              .describe('Refund business details.')
              .optional(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the refund was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. Defaults to current time when not provided.'
              )
              .optional()
              .nullable(),
            summary: z
              .object({
                requestedRefund: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total amount requested for refund.')
                  .optional(),
                pendingRefund: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Pending refund amount - the portion of `requestedRefund` that is still pending.'
                  )
                  .optional(),
                refunded: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Refunded amount - the portion of `requestedRefund` that refunded successfully.'
                  )
                  .optional(),
                failedRefundAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Failed refund amount - the portion of `requestedRefund` that failed.'
                  )
                  .optional(),
                pending: z
                  .boolean()
                  .describe(
                    'Whether at least one refund transaction is still in `"PENDING"` status.'
                  )
                  .optional(),
                breakdown: z
                  .object({
                    lineItems: z
                      .array(
                        z.object({
                          lineItemId: z
                            .string()
                            .describe('ID of the refunded line item.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          totalRefundedAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Total refunded amount for the line item.'
                            )
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(300)
                      .optional(),
                  })
                  .describe(
                    'Breakdown of refunded items. Available only after refund is complete.'
                  )
                  .optional(),
              })
              .describe('Aggregated refund summary.')
              .optional(),
            requestingServiceAppId: z
              .string()
              .describe('ID of the app that initiated this refund.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(300)
        .optional(),
    })
    .describe("All order's transactions after the capture was triggered")
    .optional(),
});
export const GetOrderRequest = z.object({
  _id: z.string().describe('ID of the order to retrieve.').min(1).max(100),
});
export const GetOrderResponse = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  number: z
    .string()
    .regex(/^\d+$/, 'Must be a valid UInt64 string')
    .describe(
      "Order number displayed in the site owner's dashboard (auto-generated)."
    )
    .optional(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  lineItems: z
    .array(
      z.object({
        _id: z.string().describe('Line item ID.').optional(),
        productName: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
              )
              .min(1)
              .max(200)
              .optional(),
            translated: z
              .string()
              .describe(
                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
              )
              .min(1)
              .max(400)
              .optional()
              .nullable(),
          })
          .describe(
            'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
          )
          .optional(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "References to the line item's origin catalog. Empty for custom line items."
          )
          .optional(),
        quantity: z
          .number()
          .int()
          .describe('Line item quantity.')
          .min(1)
          .max(100000)
          .optional(),
        totalDiscount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Total discount for this line item's entire quantity.")
          .optional(),
        descriptionLines: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                      )
                      .max(100)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe('Description line name.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z.never().optional(),
                }),
                z.object({
                  colorInfo: z.never().optional(),
                  plainText: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(600)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(600)
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line plain text value.'),
                }),
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(500)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      code: z
                        .string()
                        .describe('HEX or RGB color code for display.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line color value.'),
                }),
              ])
            )
          )
          .min(0)
          .max(20)
          .optional(),
        image: z.string().describe('Line item image.').optional(),
        physicalProperties: z
          .object({
            weight: z
              .number()
              .describe(
                'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
              )
              .optional()
              .nullable(),
            sku: z
              .string()
              .describe(
                'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
              )
              .max(40)
              .optional()
              .nullable(),
            shippable: z
              .boolean()
              .describe('Whether this line item is shippable.')
              .optional(),
          })
          .describe(
            'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
          )
          .optional(),
        itemType: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                preset: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                preset: z
                  .enum([
                    'UNRECOGNISED',
                    'PHYSICAL',
                    'DIGITAL',
                    'GIFT_CARD',
                    'SERVICE',
                  ])
                  .describe('Preset item type.'),
              }),
              z.object({
                preset: z.never().optional(),
                custom: z
                  .string()
                  .describe(
                    'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                  ),
              }),
            ])
          )
          .describe('Item type. Either a preset type or custom.')
          .optional(),
        fulfillerId: z
          .string()
          .describe(
            'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        refundQuantity: z
          .number()
          .int()
          .describe('Number of items that were refunded.')
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        restockQuantity: z
          .number()
          .int()
          .describe(
            'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        price: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Line item price after line item discounts for display purposes.'
          )
          .optional(),
        priceBeforeDiscounts: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
          )
          .optional(),
        totalPriceBeforeTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe('Total price after discounts, and before tax.')
          .optional(),
        totalPriceAfterTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe('Total price after all discounts and tax.')
          .optional(),
        paymentOption: z
          .enum([
            'FULL_PAYMENT_ONLINE',
            'FULL_PAYMENT_OFFLINE',
            'MEMBERSHIP',
            'DEPOSIT_ONLINE',
            'MEMBERSHIP_OFFLINE',
            'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
          ])
          .describe(
            'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
          )
          .optional(),
        taxDetails: z
          .object({
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Taxable amount of this line item.')
              .optional(),
            taxRate: z
              .string()
              .describe(
                'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
              )
              .optional(),
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The calculated tax, based on the `taxableAmount` and `taxRate`.'
              )
              .optional(),
          })
          .describe(
            'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
          )
          .optional(),
        taxInfo: z
          .object({
            taxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Calculated tax, based on `taxable_amount` and `tax_rate`.'
              )
              .optional(),
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Amount for which tax is calculated.')
              .optional(),
            taxRate: z
              .string()
              .describe('Tax rate %, as a decimal point.')
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxIncludedInPrice: z
              .boolean()
              .describe('Indicates whether the price already includes tax.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  jurisdiction: z
                    .string()
                    .describe(
                      'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                    )
                    .optional()
                    .nullable(),
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount of tax calculated for this line item.')
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'Type of jurisdiction that taxes were calculated for.'
                    )
                    .optional(),
                  nonTaxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Non-taxable amount of the line item price.')
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of the line item price.')
                    .optional(),
                })
              )
              .max(7)
              .optional(),
          })
          .describe(
            'Represents all the relevant tax details for a specific line item.'
          )
          .optional(),
        digitalFile: z
          .object({
            fileId: z
              .string()
              .describe('ID of the secure file in media.')
              .min(1)
              .max(100)
              .optional(),
            link: z
              .string()
              .describe(
                'Link will exist after the digital links have been generated on the order.'
              )
              .url()
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            expirationDate: z
              .date()
              .describe('Link expiration time and date.')
              .optional()
              .nullable(),
          })
          .describe(
            'Digital file identifier, relevant only for items with type DIGITAL.'
          )
          .optional(),
        subscriptionInfo: z
          .object({
            _id: z
              .string()
              .describe('Subscription ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            cycleNumber: z
              .number()
              .int()
              .describe(
                'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
              )
              .min(0)
              .optional(),
            subscriptionOptionTitle: z
              .string()
              .describe(
                'Subscription option title. For example, `"Monthly coffee Subscription"`.'
              )
              .min(0)
              .max(150)
              .optional(),
            subscriptionOptionDescription: z
              .string()
              .describe(
                'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
              )
              .max(500)
              .optional()
              .nullable(),
            subscriptionSettings: z
              .object({
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .describe('Frequency of recurring payment.')
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                  )
                  .min(1)
                  .max(3650)
                  .optional()
                  .nullable(),
                autoRenewal: z
                  .boolean()
                  .describe(
                    'Whether subscription is renewed automatically at the end of each period.'
                  )
                  .optional(),
                billingCycles: z
                  .number()
                  .int()
                  .describe(
                    'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                  )
                  .min(1)
                  .optional()
                  .nullable(),
                enableCustomerCancellation: z
                  .boolean()
                  .describe(
                    'Whether to allow the customer to cancel the subscription.'
                  )
                  .optional(),
                freeTrialPeriod: z
                  .object({
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe(
                        'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                      )
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                      )
                      .min(1)
                      .max(3650)
                      .optional(),
                  })
                  .describe(
                    'Period until first cycle starts. If None => no free trial'
                  )
                  .optional(),
              })
              .describe('Subscription detailed information.')
              .optional(),
            chargesDescription: z
              .string()
              .describe(
                'Description of the charges that will be applied for subscription.'
              )
              .max(1000)
              .optional()
              .nullable(),
          })
          .describe('Subscription info.')
          .optional(),
        priceDescription: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
              )
              .min(1)
              .max(100)
              .optional(),
            translated: z
              .string()
              .describe(
                "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
          )
          .optional(),
        depositAmount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
          )
          .optional(),
        locations: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Location id in the associated owner app.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              appId: z
                .string()
                .describe(
                  'Location owner app, if not provided then the site business info locations will be used.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe('Quantity for specific location.')
                .min(1)
                .max(100000)
                .optional(),
              name: z
                .string()
                .describe('Location name.')
                .max(500)
                .optional()
                .nullable(),
            })
          )
          .min(0)
          .max(5)
          .optional(),
        lineItemPrice: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Total price **after** catalog discounts and line item discounts.'
          )
          .optional(),
        taxableAddress: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ addressType: z.never().optional() }),
              z.object({
                addressType: z
                  .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                  .describe(
                    'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                  ),
              }),
            ])
          )
          .describe('Address used for tax calculation.')
          .optional(),
        priceUndetermined: z
          .boolean()
          .describe(
            'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .min(1)
                .max(36)
                .optional(),
              name: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe('Modifier group name.')
                .optional(),
              modifiers: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe('The quantity of this modifier.')
                      .min(1)
                      .max(100000)
                      .optional()
                      .nullable(),
                    label: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Primary display label for the modifier.')
                      .optional(),
                    details: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Additional details.')
                      .optional(),
                    price: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('The price of the modifier.')
                      .optional(),
                  })
                )
                .min(1)
                .max(10)
                .optional(),
            })
          )
          .max(10)
          .optional(),
      })
    )
    .min(1)
    .max(300)
    .optional(),
  buyerInfo: z
    .intersection(
      z.object({
        contactId: z
          .string()
          .describe(
            "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        email: z
          .string()
          .describe("Buyer's email address.")
          .email()
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          visitorId: z.never().optional(),
          memberId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          visitorId: z
            .string()
            .describe(
              "Visitor ID. Returned when the buyer isn't a logged-in site member."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          visitorId: z.never().optional(),
          memberId: z
            .string()
            .describe(
              'Member ID. Returned when the buyer is a logged-in site member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe('Buyer information.')
    .optional(),
  paymentStatus: z
    .enum([
      'UNSPECIFIED',
      'NOT_PAID',
      'PAID',
      'PARTIALLY_REFUNDED',
      'FULLY_REFUNDED',
      'PENDING',
      'PARTIALLY_PAID',
      'PENDING_MERCHANT',
      'CANCELED',
      'DECLINED',
    ])
    .describe('Order payment status.')
    .optional(),
  fulfillmentStatus: z
    .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
    .describe('Order fulfillment status.')
    .optional(),
  buyerLanguage: z
    .string()
    .describe(
      'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
    )
    .optional()
    .nullable(),
  weightUnit: z
    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
    .describe("Weight measurement unit - defaults to site's weight unit.")
    .optional(),
  currency: z
    .string()
    .describe(
      'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
    )
    .optional()
    .nullable(),
  currencyConversionDetails: z
    .object({
      originalCurrency: z
        .string()
        .describe(
          "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
        )
        .optional()
        .nullable(),
      conversionRate: z
        .string()
        .describe(
          'The rate used for converting the original currency to the currency used for payment.'
        )
        .optional()
        .nullable(),
    })
    .describe('Currency conversion details. For use with multi-currency sites.')
    .optional(),
  taxIncludedInPrices: z
    .boolean()
    .describe('Whether tax is included in line item prices.')
    .optional(),
  siteLanguage: z
    .string()
    .describe('Site language in which original values are shown.')
    .optional()
    .nullable(),
  priceSummary: z
    .object({
      subtotal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Subtotal of all line items, before discounts and before tax.'
        )
        .optional(),
      shipping: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total shipping price, before discounts and before tax.')
        .optional(),
      tax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax applied to the order.')
        .optional(),
      discount: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total discount amount applied to the order.')
        .optional(),
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe("Order's total price after discounts and tax.")
        .optional(),
      totalAdditionalFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total additional fees before tax.')
        .optional(),
    })
    .describe('Order price summary.')
    .optional(),
  billingInfo: z
    .object({
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      contactDetails: z
        .object({
          firstName: z
            .string()
            .describe('First name.')
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Last name.')
            .max(100)
            .optional()
            .nullable(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          company: z
            .string()
            .describe('Company name.')
            .max(1000)
            .optional()
            .nullable(),
          vatId: z
            .object({
              _id: z.string().describe("Customer's tax ID.").optional(),
              type: z
                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                .describe(
                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                )
                .optional(),
            })
            .describe(
              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
            )
            .optional(),
        })
        .describe('Contact details.')
        .optional(),
    })
    .describe('Billing address and contact details.')
    .optional(),
  shippingInfo: z
    .object({
      carrierId: z
        .string()
        .describe(
          'App Def Id of external provider which was a source of shipping info'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      code: z
        .string()
        .describe(
          'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      title: z
        .string()
        .describe(
          'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
        )
        .max(250)
        .optional(),
      logistics: z
        .intersection(
          z.object({
            deliveryTime: z
              .string()
              .describe(
                'Expected delivery time in free text. For example, `"3-5 business days"`.'
              )
              .max(500)
              .optional()
              .nullable(),
            instructions: z
              .string()
              .describe(
                'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
              )
              .max(1000)
              .optional()
              .nullable(),
            deliverByDate: z
              .date()
              .describe(
                'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            deliveryTimeSlot: z
              .object({
                from: z
                  .date()
                  .describe('Delivery slot starting time.')
                  .optional()
                  .nullable(),
                to: z
                  .date()
                  .describe('Delivery slot ending time.')
                  .optional()
                  .nullable(),
              })
              .describe('Expected delivery time slot with start and end times.')
              .optional(),
          }),
          z.xor([
            z.object({
              shippingDestination: z.never().optional(),
              pickupDetails: z.never().optional(),
            }),
            z.object({
              pickupDetails: z.never().optional(),
              shippingDestination: z
                .object({
                  address: z
                    .object({
                      streetAddress: z
                        .object({
                          name: z.string().optional(),
                          number: z.string().optional(),
                        })
                        .optional(),
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe('Address.')
                    .optional(),
                  contactDetails: z
                    .object({
                      firstName: z
                        .string()
                        .describe('First name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe('Last name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Phone number.')
                        .optional()
                        .nullable(),
                      company: z
                        .string()
                        .describe('Company name.')
                        .max(1000)
                        .optional()
                        .nullable(),
                      vatId: z
                        .object({
                          _id: z
                            .string()
                            .describe("Customer's tax ID.")
                            .optional(),
                          type: z
                            .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                            .describe(
                              'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                        )
                        .optional(),
                    })
                    .describe('Contact details.')
                    .optional(),
                })
                .describe(
                  "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                ),
            }),
            z.object({
              shippingDestination: z.never().optional(),
              pickupDetails: z
                .object({
                  address: z
                    .object({
                      streetAddress: z
                        .object({
                          name: z.string().optional(),
                          number: z.string().optional(),
                        })
                        .optional(),
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe('Pickup address.')
                    .optional(),
                  pickupMethod: z
                    .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                    .describe('Pickup method')
                    .optional(),
                })
                .describe(
                  'Pickup details for store pickup or pickup point orders.'
                ),
            }),
          ])
        )
        .describe('Shipping logistics.')
        .optional(),
      cost: z
        .object({
          price: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Shipping price for display purposes.')
            .optional(),
          totalPriceBeforeTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total price of shipping after discounts (when relevant), and before tax.'
            )
            .optional(),
          totalPriceAfterTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Shipping price after all discounts (if any exist), and after tax.'
            )
            .optional(),
          taxDetails: z
            .object({
              taxableAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Taxable amount of this line item.')
                .optional(),
              taxRate: z
                .string()
                .describe(
                  'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                )
                .optional(),
              totalTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                )
                .optional(),
            })
            .describe('Tax details.')
            .optional(),
          taxInfo: z
            .object({
              taxAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                )
                .optional(),
              taxableAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Amount for which tax is calculated.')
                .optional(),
              taxRate: z
                .string()
                .describe('Tax rate %, as a decimal point.')
                .optional()
                .nullable(),
              taxGroupId: z
                .string()
                .describe('Tax group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              taxIncludedInPrice: z
                .boolean()
                .describe('Indicates whether the price already includes tax.')
                .optional(),
              taxBreakdown: z
                .array(
                  z.object({
                    jurisdiction: z
                      .string()
                      .describe(
                        'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    rate: z
                      .string()
                      .describe(
                        'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                      )
                      .optional()
                      .nullable(),
                    taxAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Amount of tax calculated for this line item.')
                      .optional(),
                    taxType: z
                      .string()
                      .describe(
                        'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    taxName: z
                      .string()
                      .describe(
                        'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    jurisdictionType: z
                      .enum([
                        'UNDEFINED',
                        'COUNTRY',
                        'STATE',
                        'COUNTY',
                        'CITY',
                        'SPECIAL',
                      ])
                      .describe(
                        'Type of jurisdiction that taxes were calculated for.'
                      )
                      .optional(),
                    nonTaxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Non-taxable amount of the line item price.')
                      .optional(),
                    taxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Taxable amount of the line item price.')
                      .optional(),
                  })
                )
                .max(7)
                .optional(),
            })
            .describe('Represents all the relevant tax details for a shipping.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Shipping discount before tax.')
            .optional(),
        })
        .describe('Shipping costs.')
        .optional(),
      region: z
        .object({
          name: z
            .string()
            .describe(
              'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
            )
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Shipping region.')
        .optional(),
    })
    .describe('Shipping info and selected shipping option details.')
    .optional(),
  buyerNote: z
    .string()
    .describe(
      '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
    )
    .max(1000)
    .optional()
    .nullable(),
  status: z
    .enum(['INITIALIZED', 'APPROVED', 'CANCELED', 'PENDING', 'REJECTED'])
    .describe('Order status.')
    .optional(),
  archived: z
    .boolean()
    .describe('Whether order is archived.')
    .optional()
    .nullable(),
  taxSummary: z
    .object({
      totalTax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax.')
        .optional(),
    })
    .describe(
      'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
    )
    .optional(),
  taxInfo: z
    .object({
      totalTax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Calculated tax, added from line items.')
        .optional(),
      taxBreakdown: z
        .array(
          z.object({
            taxName: z
              .string()
              .describe(
                'The name of the tax against which this tax amount was calculated.'
              )
              .max(200)
              .optional(),
            taxType: z
              .string()
              .describe(
                "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
              )
              .max(200)
              .optional(),
            jurisdiction: z
              .string()
              .describe(
                'The name of the jurisdiction in which this tax detail applies.'
              )
              .max(200)
              .optional(),
            jurisdictionType: z
              .enum([
                'UNDEFINED',
                'COUNTRY',
                'STATE',
                'COUNTY',
                'CITY',
                'SPECIAL',
              ])
              .describe(
                'The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special).'
              )
              .optional(),
            rate: z
              .string()
              .describe('The rate at which this tax detail was calculated.')
              .optional(),
            aggregatedTaxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The sum of all the tax from line items that calculated by the tax identifiers.'
              )
              .optional(),
          })
        )
        .min(0)
        .max(50)
        .optional(),
      taxExempt: z
        .boolean()
        .describe(
          'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
    })
    .describe('Tax information.')
    .optional(),
  appliedDiscounts: z
    .array(
      z.intersection(
        z.object({
          discountType: z
            .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
            .optional(),
          lineItemIds: z.array(z.string()).min(0).max(300).optional(),
          _id: z
            .string()
            .describe('Discount ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          lineItemDiscounts: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Line item ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                totalDiscount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total discount amount for this line item.')
                  .optional(),
              })
            )
            .max(300)
            .optional(),
        }),
        z.xor([
          z.object({
            coupon: z.never().optional(),
            merchantDiscount: z.never().optional(),
            discountRule: z.never().optional(),
          }),
          z.object({
            merchantDiscount: z.never().optional(),
            discountRule: z.never().optional(),
            coupon: z
              .object({
                _id: z.string().describe('Coupon ID.').optional(),
                code: z.string().describe('Coupon code.').optional(),
                name: z.string().describe('Coupon name.').optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Coupon value.')
                  .optional(),
              })
              .describe('Coupon applied by the customer.'),
          }),
          z.object({
            coupon: z.never().optional(),
            discountRule: z.never().optional(),
            merchantDiscount: z
              .intersection(
                z.object({
                  amount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Discount amount.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    discountReason: z.never().optional(),
                    description: z.never().optional(),
                  }),
                  z.object({
                    description: z.never().optional(),
                    discountReason: z
                      .enum([
                        'UNSPECIFIED',
                        'EXCHANGED_ITEMS',
                        'BILLING_ADJUSTMENT',
                      ])
                      .describe('Predefined discount reason.'),
                  }),
                  z.object({
                    discountReason: z.never().optional(),
                    description: z
                      .string()
                      .describe('Custom discount description as free text.')
                      .max(200),
                  }),
                ])
              )
              .describe('Discount applied manually by the merchant.'),
          }),
          z.object({
            coupon: z.never().optional(),
            merchantDiscount: z.never().optional(),
            discountRule: z
              .object({
                _id: z
                  .string()
                  .describe('Discount rule ID')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Original discount rule name (in site's default language)."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                      )
                      .min(1)
                      .max(500)
                      .optional()
                      .nullable(),
                  })
                  .describe('Discount rule name')
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Discount value.')
                  .optional(),
              })
              .describe(
                'Automatic discount applied by the system based on configured discount rules.'
              ),
          }),
        ])
      )
    )
    .max(320)
    .optional(),
  activities: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Activity ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorEmail: z
            .string()
            .describe("Activity author's email.")
            .email()
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Activity creation date and time.')
            .optional()
            .nullable(),
          type: z
            .enum([
              'ORDER_REFUNDED',
              'ORDER_PLACED',
              'ORDER_PAID',
              'ORDER_FULFILLED',
              'ORDER_NOT_FULFILLED',
              'ORDER_CANCELED',
              'DOWNLOAD_LINK_SENT',
              'TRACKING_NUMBER_ADDED',
              'TRACKING_NUMBER_EDITED',
              'TRACKING_LINK_ADDED',
              'SHIPPING_CONFIRMATION_EMAIL_SENT',
              'INVOICE_ADDED',
              'INVOICE_REMOVED',
              'INVOICE_SENT',
              'FULFILLER_EMAIL_SENT',
              'SHIPPING_ADDRESS_EDITED',
              'EMAIL_EDITED',
              'PICKUP_READY_EMAIL_SENT',
              'CUSTOM_ACTIVITY',
              'MERCHANT_COMMENT',
              'ORDER_PARTIALLY_PAID',
              'DRAFT_ORDER_CHANGES_APPLIED',
              'SAVED_PAYMENT_METHOD',
              'AUTHORIZED_PAYMENT_CREATED',
              'AUTHORIZED_PAYMENT_CAPTURED',
              'AUTHORIZED_PAYMENT_VOIDED',
              'REFUND_INITIATED',
              'PAYMENT_REFUNDED',
              'PAYMENT_REFUND_FAILED',
              'REFUNDED_AS_STORE_CREDIT',
              'PAYMENT_PENDING',
              'PAYMENT_CANCELED',
              'PAYMENT_DECLINED',
              'ORDER_PENDING',
              'ORDER_REJECTED',
              'RECEIPT_CREATED',
              'RECEIPT_SENT',
              'CHARGEBACK_CREATED',
              'CHARGEBACK_REVERSED',
            ])
            .describe('Activity type.')
            .optional(),
          activityType: z
            .enum([
              'ORDER_PLACED',
              'ORDER_PAID',
              'ORDER_FULFILLED',
              'ORDER_NOT_FULFILLED',
              'ORDER_CANCELED',
              'DOWNLOAD_LINK_SENT',
              'TRACKING_NUMBER_ADDED',
              'TRACKING_NUMBER_EDITED',
              'TRACKING_LINK_ADDED',
              'SHIPPING_CONFIRMATION_EMAIL_SENT',
              'INVOICE_ADDED',
              'INVOICE_REMOVED',
              'INVOICE_SENT',
              'FULFILLER_EMAIL_SENT',
              'SHIPPING_ADDRESS_EDITED',
              'EMAIL_EDITED',
              'PICKUP_READY_EMAIL_SENT',
              'MERCHANT_COMMENT',
              'ORDER_PARTIALLY_PAID',
              'DRAFT_ORDER_CHANGES_APPLIED',
              'SAVED_PAYMENT_METHOD',
              'AUTHORIZED_PAYMENT_CREATED',
              'AUTHORIZED_PAYMENT_CAPTURED',
              'AUTHORIZED_PAYMENT_VOIDED',
              'REFUND_INITIATED',
              'PAYMENT_REFUNDED',
              'PAYMENT_REFUND_FAILED',
              'REFUNDED_AS_STORE_CREDIT',
              'PAYMENT_PENDING',
              'PAYMENT_CANCELED',
              'PAYMENT_DECLINED',
              'ORDER_PENDING',
              'ORDER_REJECTED',
              'RECEIPT_CREATED',
              'RECEIPT_SENT',
              'CHARGEBACK_CREATED',
              'CHARGEBACK_REVERSED',
              'ORDER_REFUNDED',
            ])
            .describe('Activity type.')
            .optional(),
        }),
        z.xor([
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
          }),
          z.object({
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            merchantComment: z
              .object({
                message: z
                  .string()
                  .describe('Merchant comment message.')
                  .min(1)
                  .max(5000)
                  .optional(),
              })
              .describe(
                'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            orderRefunded: z
              .object({
                manual: z
                  .boolean()
                  .describe(
                    'Whether order was refunded manually. For example, via payment provider or using cash.'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(200)
                  .optional(),
              })
              .describe(
                'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            draftOrderChangesApplied: z
              .object({
                draftOrderId: z.string().describe('Draft order id.').optional(),
                reason: z
                  .string()
                  .describe('Reason for edit, given by user (optional).')
                  .max(200)
                  .optional()
                  .nullable(),
                changes: z
                  .array(
                    z.intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                        }),
                        z.object({
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemChanged: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name after change.')
                              .optional(),
                            quantity: z
                              .object({
                                originalQuantity: z
                                  .number()
                                  .int()
                                  .describe('Item quantity before update.')
                                  .min(0)
                                  .max(1000000)
                                  .optional(),
                                newQuantity: z
                                  .number()
                                  .int()
                                  .describe('Item quantity after update.')
                                  .min(0)
                                  .max(1000000)
                                  .optional(),
                                diff: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Difference between original and new quantity. Absolute value.'
                                  )
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                                deltaType: z
                                  .enum([
                                    'QUANTITY_INCREASED',
                                    'QUANTITY_DECREASED',
                                  ])
                                  .describe(
                                    'Type of quantity change: increase or decrease.'
                                  )
                                  .optional(),
                              })
                              .describe('Item quantity change.')
                              .optional(),
                            price: z
                              .object({
                                originalPrice: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item price before update.')
                                  .optional(),
                                newPrice: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item price after update.')
                                  .optional(),
                              })
                              .describe('Item price change.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemAdded: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name.')
                              .optional(),
                            quantity: z
                              .number()
                              .int()
                              .describe('Added or removed item quantity.')
                              .min(1)
                              .max(1000000)
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name.')
                              .optional(),
                            quantity: z
                              .number()
                              .int()
                              .describe('Added or removed item quantity.')
                              .min(1)
                              .max(1000000)
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          discountAdded: z.object({
                            _id: z
                              .string()
                              .describe('Discount id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Discount name: coupon name / discount rule name / merchant discount description.'
                              )
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Discount amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          discountRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Discount id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Discount name: coupon name / discount rule name / merchant discount description.'
                              )
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Discount amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          additionalFeeAdded: z.object({
                            _id: z
                              .string()
                              .describe('Additional fee id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Additional fee name.')
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Additional fee amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          additionalFeeRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Additional fee id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Additional fee name.')
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Additional fee amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          totalPriceChanged: z.object({
                            originalTotal: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Order’s total price after discounts and tax. Before update'
                              )
                              .optional(),
                            newTotal: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Order’s total price after discounts and tax. After update'
                              )
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.object({
                            originalShippingInfo: z
                              .object({
                                total: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Order’s shipping price.')
                                  .optional(),
                                shippingTitle: z
                                  .string()
                                  .describe('Order’s shipping title.')
                                  .max(250)
                                  .optional(),
                              })
                              .describe(
                                'Order’s Shipping Information. Before update'
                              )
                              .optional(),
                            newShippingInfo: z
                              .object({
                                total: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Order’s shipping price.')
                                  .optional(),
                                shippingTitle: z
                                  .string()
                                  .describe('Order’s shipping title.')
                                  .max(250)
                                  .optional(),
                              })
                              .describe(
                                'Order’s Shipping Information. After update'
                              )
                              .optional(),
                          }),
                        }),
                      ])
                    )
                  )
                  .optional(),
              })
              .describe('Details of changes made by the Draft Orders API.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            savedPaymentMethod: z
              .object({
                name: z
                  .string()
                  .describe('Payment method name.')
                  .min(1)
                  .max(100)
                  .optional(),
                description: z
                  .string()
                  .describe('Payment method description.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of the payment method saved for the order.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentCreated: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment created.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentCaptured: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment captured.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentVoided: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment voided.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            refundInitiated: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                payments: z
                  .array(
                    z.intersection(
                      z.object({
                        paymentId: z
                          .string()
                          .describe('Payment ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        externalRefund: z
                          .boolean()
                          .describe(
                            "Whether refund was made externally and manually on the payment provider's side."
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          regular: z.never().optional(),
                          giftCard: z.never().optional(),
                          membership: z.never().optional(),
                        }),
                        z.object({
                          giftCard: z.never().optional(),
                          membership: z.never().optional(),
                          regular: z
                            .object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Refund amount.')
                                .optional(),
                              lastFourDigits: z
                                .string()
                                .describe(
                                  'The last 4 digits of the card number.'
                                )
                                .max(4)
                                .optional()
                                .nullable(),
                              brand: z
                                .string()
                                .describe("Card issuer's brand.")
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe('Regular payment refund.'),
                        }),
                        z.object({
                          regular: z.never().optional(),
                          membership: z.never().optional(),
                          giftCard: z
                            .object({
                              giftCardPaymentId: z
                                .string()
                                .describe('Gift card payment ID.')
                                .min(1)
                                .max(100)
                                .optional()
                                .nullable(),
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Refund amount.')
                                .optional(),
                            })
                            .describe('Gift card payment refund.'),
                        }),
                        z.object({
                          regular: z.never().optional(),
                          giftCard: z.never().optional(),
                          membership: z
                            .object({
                              membershipId: z
                                .string()
                                .describe('Membership ID.')
                                .min(1)
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe('Membership payment refund.'),
                        }),
                      ])
                    )
                  )
                  .min(1)
                  .max(50)
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(200)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentRefunded: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                payment: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe('Payment ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      externalRefund: z
                        .boolean()
                        .describe(
                          "Whether refund was made externally and manually on the payment provider's side."
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                      }),
                      z.object({
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                        regular: z
                          .object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Regular payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        membership: z.never().optional(),
                        giftCard: z
                          .object({
                            giftCardPaymentId: z
                              .string()
                              .describe('Gift card payment ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                          })
                          .describe('Gift card payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z
                          .object({
                            membershipId: z
                              .string()
                              .describe('Membership ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Membership payment refund.'),
                      }),
                    ])
                  )
                  .describe('Details about the refunded payment.')
                  .optional(),
              })
              .describe(
                'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentRefundFailed: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                payment: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe('Payment ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      externalRefund: z
                        .boolean()
                        .describe(
                          "Whether refund was made externally and manually on the payment provider's side."
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                      }),
                      z.object({
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                        regular: z
                          .object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Regular payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        membership: z.never().optional(),
                        giftCard: z
                          .object({
                            giftCardPaymentId: z
                              .string()
                              .describe('Gift card payment ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                          })
                          .describe('Gift card payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z
                          .object({
                            membershipId: z
                              .string()
                              .describe('Membership ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Membership payment refund.'),
                      }),
                    ])
                  )
                  .describe('Details about the failed payment refund.')
                  .optional(),
              })
              .describe(
                'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            refundedAsStoreCredit: z
              .object({
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of refund to store credit.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentPending: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a pending payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentCanceled: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a canceled payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentDeclined: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a declined payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            receiptCreated: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z.never().optional(),
                  }),
                  z.object({
                    externalReceipt: z.never().optional(),
                    wixReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by Wix.'),
                  }),
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .max(100)
                          .optional()
                          .nullable(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by an external system.'),
                  }),
                ])
              )
              .describe('Receipt was added for associated payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            receiptSent: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z.never().optional(),
                  }),
                  z.object({
                    externalReceipt: z.never().optional(),
                    wixReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by Wix.'),
                  }),
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .max(100)
                          .optional()
                          .nullable(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by an external system.'),
                  }),
                ])
              )
              .describe('Receipt sent to customer.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackReversed: z.never().optional(),
            chargebackCreated: z
              .object({
                paymentId: z
                  .string()
                  .describe('ID of the payment that received a chargeback.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargebackId: z
                  .string()
                  .describe('Chargeback ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback amount.')
                  .optional(),
                paymentDetails: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({ creditCardDetails: z.never().optional() }),
                      z.object({
                        creditCardDetails: z
                          .object({
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Credit card details.'),
                      }),
                    ])
                  )
                  .describe('Payment details.')
                  .optional(),
              })
              .describe('Order received a chargeback for one of its payments.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z
              .object({
                paymentId: z
                  .string()
                  .describe('ID of the payment involved with the chargeback.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargebackId: z
                  .string()
                  .describe('Chargeback ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback amount.')
                  .optional(),
                reversalAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback reversal amount.')
                  .optional(),
                paymentDetails: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({ creditCardDetails: z.never().optional() }),
                      z.object({
                        creditCardDetails: z
                          .object({
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Credit card details.'),
                      }),
                    ])
                  )
                  .describe('Payment details.')
                  .optional(),
              })
              .describe("Chargeback reversed for one of the order's payments."),
          }),
        ])
      )
    )
    .max(1000)
    .optional(),
  attributionSource: z
    .enum(['UNSPECIFIED', 'FACEBOOK_ADS'])
    .describe('Order attribution source.')
    .optional(),
  createdBy: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
          userId: z
            .string()
            .describe(
              'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
          memberId: z
            .string()
            .describe(
              'Member ID - when the order was created by a **logged in** site visitor.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          appId: z.never().optional(),
          visitorId: z
            .string()
            .describe(
              'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z
            .string()
            .describe(
              'App ID - when the order was created by an external application.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe("ID of the order's initiator.")
    .optional(),
  channelInfo: z
    .object({
      type: z
        .enum([
          'UNSPECIFIED',
          'WEB',
          'POS',
          'EBAY',
          'AMAZON',
          'OTHER_PLATFORM',
          'WIX_APP_STORE',
          'WIX_INVOICES',
          'BACKOFFICE_MERCHANT',
          'WISH',
          'CLASS_PASS',
          'GLOBAL_E',
          'FACEBOOK',
          'ETSY',
          'TIKTOK',
          'FAIRE_COM',
          'PAYPAL_AGENTIC_CHECKOUT',
          'STRIPE_AGENTIC_CHECKOUT',
        ])
        .describe(
          'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
        )
        .optional(),
      externalOrderId: z
        .string()
        .describe(
          'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
        )
        .max(100)
        .optional()
        .nullable(),
      externalOrderUrl: z
        .string()
        .describe(
          'URL to the order in the external system. Relevant when recording orders from external platforms.'
        )
        .max(300)
        .optional()
        .nullable(),
    })
    .describe('Information about the sales channel that submitted this order.')
    .optional(),
  seenByAHuman: z
    .boolean()
    .describe(
      'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
    )
    .optional()
    .nullable(),
  checkoutId: z
    .string()
    .describe('Checkout ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  customFields: z
    .array(
      z.object({
        value: z.any().describe('Custom field value.').optional().nullable(),
        title: z
          .string()
          .describe('Custom field title.')
          .min(1)
          .max(500)
          .optional(),
        translatedTitle: z
          .string()
          .describe('Translated custom field title.')
          .min(1)
          .max(500)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  balanceSummary: z
    .object({
      balance: z
        .object({
          amount: z
            .string()
            .describe(
              'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
            )
            .optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Current amount left to pay.')
        .optional(),
      paid: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
        )
        .optional(),
      refunded: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all successfully refunded payments.')
        .optional(),
      authorized: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all authorized payments.')
        .optional(),
      pendingRefund: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all pending refund transactions.')
        .optional(),
      pending: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all pending transactions.')
        .optional(),
      chargeback: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all transaction chargebacks.')
        .optional(),
      chargebackReversal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all chargeback reversals.')
        .optional(),
      platformFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
        )
        .optional(),
      totalMinusPlatformFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
        )
        .optional(),
    })
    .describe('Balance summary.')
    .optional(),
  additionalFees: z
    .array(
      z.object({
        code: z
          .string()
          .describe("Additional fee's unique code for future processing.")
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Name of additional fee.')
          .min(1)
          .max(50)
          .optional(),
        price: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price.")
          .optional(),
        taxDetails: z
          .object({
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Taxable amount of this line item.')
              .optional(),
            taxRate: z
              .string()
              .describe(
                'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
              )
              .optional(),
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The calculated tax, based on the `taxableAmount` and `taxRate`.'
              )
              .optional(),
          })
          .describe('Tax details.')
          .optional(),
        taxInfo: z
          .object({
            taxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Calculated tax, based on `taxable_amount` and `tax_rate`.'
              )
              .optional(),
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Amount for which tax is calculated.')
              .optional(),
            taxRate: z
              .string()
              .describe('Tax rate %, as a decimal point.')
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxIncludedInPrice: z
              .boolean()
              .describe('Indicates whether the price already includes tax.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  jurisdiction: z
                    .string()
                    .describe(
                      'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                    )
                    .optional()
                    .nullable(),
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount of tax calculated for this line item.')
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'Type of jurisdiction that taxes were calculated for.'
                    )
                    .optional(),
                  nonTaxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Non-taxable amount of the line item price.')
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of the line item price.')
                    .optional(),
                })
              )
              .max(7)
              .optional(),
          })
          .describe(
            'Represents all the relevant tax details for additional fee.'
          )
          .optional(),
        providerAppId: z
          .string()
          .describe("SPI implementer's `appId`.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        priceBeforeTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price before tax.")
          .optional(),
        priceAfterTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price after tax.")
          .optional(),
        _id: z
          .string()
          .describe("Additional fee's id.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        lineItemIds: z.array(z.string()).optional(),
        source: z
          .enum(['SERVICE_PLUGIN', 'ITEM', 'MANUAL', 'SHIPPING', 'PLATFORM'])
          .describe('Specifies the entity that added the additional fee.')
          .optional(),
      })
    )
    .max(100)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  purchaseFlowId: z
    .string()
    .describe(
      'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  recipientInfo: z
    .object({
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      contactDetails: z
        .object({
          firstName: z
            .string()
            .describe('First name.')
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Last name.')
            .max(100)
            .optional()
            .nullable(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          company: z
            .string()
            .describe('Company name.')
            .max(1000)
            .optional()
            .nullable(),
          vatId: z
            .object({
              _id: z.string().describe("Customer's tax ID.").optional(),
              type: z
                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                .describe(
                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                )
                .optional(),
            })
            .describe(
              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
            )
            .optional(),
        })
        .describe('Contact details.')
        .optional(),
    })
    .describe(
      "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
    )
    .optional(),
  purchasedDate: z
    .date()
    .describe(
      'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
    )
    .optional()
    .nullable(),
  businessLocation: z
    .object({
      _id: z
        .string()
        .describe(
          'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z.string().describe('Location name.').min(1).max(500).optional(),
    })
    .describe('Order location.')
    .optional(),
  payAfterFreeTrial: z
    .object({
      subtotal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Subtotal of all line items, before discounts and before tax.'
        )
        .optional(),
      shipping: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total shipping price, before discounts and before tax.')
        .optional(),
      tax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax applied to the order.')
        .optional(),
      discount: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total discount amount applied to the order.')
        .optional(),
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe("Order's total price after discounts and tax.")
        .optional(),
      totalAdditionalFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total additional fees before tax.')
        .optional(),
    })
    .describe(
      'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
    )
    .optional(),
  platformFeeSummary: z
    .object({
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total sum of all platform fees.')
        .optional(),
      totalPassOn: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total amount of platform fees with `PASS_ON` charge type.')
        .optional(),
      totalAbsorbed: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total amount of platform fees with `ABSORBED` charge type.')
        .optional(),
      fees: z
        .array(
          z.object({
            name: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Platform fee name.')
              .optional(),
            amount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Platform fee amount.')
              .optional(),
            lineItemId: z
              .string()
              .describe('ID of the line item the platform fee applies to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            chargeType: z
              .enum(['PASS_ON', 'ABSORBED'])
              .describe('Platform fee charge type.')
              .optional(),
            percentageRate: z
              .string()
              .describe(
                'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
              )
              .optional(),
          })
        )
        .max(300)
        .optional(),
    })
    .describe(
      'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
    )
    .optional(),
});
export const SearchOrdersRequest = z.object({
  search: z
    .object({
      filter: z
        .object({
          status: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          paymentStatus: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          fulfillmentStatus: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'shippingInfo.region.name': z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'shippingInfo.title': z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'channelInfo.type': z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          number: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'priceSummary.total.amount': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'balanceSummary.authorized.amount': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'balanceSummary.pendingRefund.amount': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'balanceSummary.pending.amount': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'billingInfo.contactDetails.firstName': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'billingInfo.contactDetails.lastName': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'buyerInfo.email': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'shippingInfo.logistics.deliveryTime': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'shippingInfo.logistics.deliveryTimeSlot.from': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'shippingInfo.logistics.deliveryTimeSlot.to': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          purchasedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessLocation._id': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessLocation.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'taxInfo.totalTax.amount': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'taxInfo.totalExempt.amount': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          archived: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          seenByAHuman: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _id: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          checkoutId: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'buyerInfo.memberId': z
            .object({
              $eq: z.any(),
              $in: z.array(z.any()),
              $ne: z.any(),
              $nin: z.array(z.any()),
            })
            .partial()
            .strict()
            .optional(),
          'buyerInfo.contactId': z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'channelInfo.externalOrderId': z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'createdBy.userId': z
            .object({
              $eq: z.any(),
              $in: z.array(z.any()),
              $ne: z.any(),
              $nin: z.array(z.any()),
            })
            .partial()
            .strict()
            .optional(),
          'lineItems.subscriptionInfo._id': z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'lineItems.catalogReference.catalogItemId': z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'lineItems.catalogReference.appId': z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'lineItems.locations._id': z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'lineItems.productName.original': z
            .object({
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'fulfillmentStatusesAggregate.statuses': z
            .object({ $hasAll: z.array(z.any()), $hasSome: z.array(z.any()) })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'status',
                'paymentStatus',
                'fulfillmentStatus',
                'shippingInfo.region.name',
                'shippingInfo.title',
                'channelInfo.type',
                '_createdDate',
                '_updatedDate',
                'number',
                'priceSummary.total.amount',
                'balanceSummary.authorized.amount',
                'balanceSummary.pendingRefund.amount',
                'balanceSummary.pending.amount',
                'billingInfo.contactDetails.firstName',
                'billingInfo.contactDetails.lastName',
                'buyerInfo.email',
                'shippingInfo.logistics.deliveryTime',
                'shippingInfo.logistics.deliveryTimeSlot.from',
                'shippingInfo.logistics.deliveryTimeSlot.to',
                'purchasedDate',
                'businessLocation._id',
                'businessLocation.name',
                'taxInfo.totalTax.amount',
                'taxInfo.totalExempt.amount',
                'lineItems.productName.original',
                'fulfillmentStatusesAggregate.statuses',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Search options.'),
});
export const SearchOrdersResponse = z.object({
  orders: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Order ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        number: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            "Order number displayed in the site owner's dashboard (auto-generated)."
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        lineItems: z
          .array(
            z.object({
              _id: z.string().describe('Line item ID.').optional(),
              productName: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
                )
                .optional(),
              catalogReference: z
                .object({
                  catalogItemId: z
                    .string()
                    .describe(
                      'ID of the item within the catalog it belongs to.'
                    )
                    .min(1)
                    .max(36)
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                    )
                    .min(1)
                    .optional(),
                  options: z
                    .record(z.string(), z.any())
                    .describe(
                      'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "References to the line item's origin catalog. Empty for custom line items."
                )
                .optional(),
              quantity: z
                .number()
                .int()
                .describe('Line item quantity.')
                .min(1)
                .max(100000)
                .optional(),
              totalDiscount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  "Total discount for this line item's entire quantity."
                )
                .optional(),
              descriptionLines: z
                .array(
                  z.intersection(
                    z.object({
                      name: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(100)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line name.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        plainText: z.never().optional(),
                        colorInfo: z.never().optional(),
                      }),
                      z.object({
                        colorInfo: z.never().optional(),
                        plainText: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                              )
                              .max(600)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                              )
                              .max(600)
                              .optional()
                              .nullable(),
                          })
                          .describe('Description line plain text value.'),
                      }),
                      z.object({
                        plainText: z.never().optional(),
                        colorInfo: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                              )
                              .max(500)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                              )
                              .max(500)
                              .optional()
                              .nullable(),
                            code: z
                              .string()
                              .describe('HEX or RGB color code for display.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Description line color value.'),
                      }),
                    ])
                  )
                )
                .min(0)
                .max(20)
                .optional(),
              image: z.string().describe('Line item image.').optional(),
              physicalProperties: z
                .object({
                  weight: z
                    .number()
                    .describe(
                      'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                    )
                    .optional()
                    .nullable(),
                  sku: z
                    .string()
                    .describe(
                      'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                    )
                    .max(40)
                    .optional()
                    .nullable(),
                  shippable: z
                    .boolean()
                    .describe('Whether this line item is shippable.')
                    .optional(),
                })
                .describe(
                  'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
                )
                .optional(),
              itemType: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      preset: z.never().optional(),
                      custom: z.never().optional(),
                    }),
                    z.object({
                      custom: z.never().optional(),
                      preset: z
                        .enum([
                          'UNRECOGNISED',
                          'PHYSICAL',
                          'DIGITAL',
                          'GIFT_CARD',
                          'SERVICE',
                        ])
                        .describe('Preset item type.'),
                    }),
                    z.object({
                      preset: z.never().optional(),
                      custom: z
                        .string()
                        .describe(
                          'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                        ),
                    }),
                  ])
                )
                .describe('Item type. Either a preset type or custom.')
                .optional(),
              fulfillerId: z
                .string()
                .describe(
                  'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              refundQuantity: z
                .number()
                .int()
                .describe('Number of items that were refunded.')
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              restockQuantity: z
                .number()
                .int()
                .describe(
                  'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
                )
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              price: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Line item price after line item discounts for display purposes.'
                )
                .optional(),
              priceBeforeDiscounts: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
                )
                .optional(),
              totalPriceBeforeTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Total price after discounts, and before tax.')
                .optional(),
              totalPriceAfterTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Total price after all discounts and tax.')
                .optional(),
              paymentOption: z
                .enum([
                  'FULL_PAYMENT_ONLINE',
                  'FULL_PAYMENT_OFFLINE',
                  'MEMBERSHIP',
                  'DEPOSIT_ONLINE',
                  'MEMBERSHIP_OFFLINE',
                  'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                ])
                .describe(
                  'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
                )
                .optional(),
              taxDetails: z
                .object({
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of this line item.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                    )
                    .optional(),
                  totalTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                    )
                    .optional(),
                })
                .describe(
                  'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
                )
                .optional(),
              taxInfo: z
                .object({
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                    )
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount for which tax is calculated.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax rate %, as a decimal point.')
                    .optional()
                    .nullable(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  taxIncludedInPrice: z
                    .boolean()
                    .describe(
                      'Indicates whether the price already includes tax.'
                    )
                    .optional(),
                  taxBreakdown: z
                    .array(
                      z.object({
                        jurisdiction: z
                          .string()
                          .describe(
                            'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        rate: z
                          .string()
                          .describe(
                            'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                          )
                          .optional()
                          .nullable(),
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Amount of tax calculated for this line item.'
                          )
                          .optional(),
                        taxType: z
                          .string()
                          .describe(
                            'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        taxName: z
                          .string()
                          .describe(
                            'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        jurisdictionType: z
                          .enum([
                            'UNDEFINED',
                            'COUNTRY',
                            'STATE',
                            'COUNTY',
                            'CITY',
                            'SPECIAL',
                          ])
                          .describe(
                            'Type of jurisdiction that taxes were calculated for.'
                          )
                          .optional(),
                        nonTaxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Non-taxable amount of the line item price.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of the line item price.')
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe(
                  'Represents all the relevant tax details for a specific line item.'
                )
                .optional(),
              digitalFile: z
                .object({
                  fileId: z
                    .string()
                    .describe('ID of the secure file in media.')
                    .min(1)
                    .max(100)
                    .optional(),
                  link: z
                    .string()
                    .describe(
                      'Link will exist after the digital links have been generated on the order.'
                    )
                    .url()
                    .min(1)
                    .max(2000)
                    .optional()
                    .nullable(),
                  expirationDate: z
                    .date()
                    .describe('Link expiration time and date.')
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Digital file identifier, relevant only for items with type DIGITAL.'
                )
                .optional(),
              subscriptionInfo: z
                .object({
                  _id: z
                    .string()
                    .describe('Subscription ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  cycleNumber: z
                    .number()
                    .int()
                    .describe(
                      'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
                    )
                    .min(0)
                    .optional(),
                  subscriptionOptionTitle: z
                    .string()
                    .describe(
                      'Subscription option title. For example, `"Monthly coffee Subscription"`.'
                    )
                    .min(0)
                    .max(150)
                    .optional(),
                  subscriptionOptionDescription: z
                    .string()
                    .describe(
                      'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  subscriptionSettings: z
                    .object({
                      frequency: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Frequency of recurring payment.')
                        .optional(),
                      interval: z
                        .number()
                        .int()
                        .describe(
                          'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                        )
                        .min(1)
                        .max(3650)
                        .optional()
                        .nullable(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period.'
                        )
                        .optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      enableCustomerCancellation: z
                        .boolean()
                        .describe(
                          'Whether to allow the customer to cancel the subscription.'
                        )
                        .optional(),
                      freeTrialPeriod: z
                        .object({
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .describe(
                              'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                            )
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                            )
                            .min(1)
                            .max(3650)
                            .optional(),
                        })
                        .describe(
                          'Period until first cycle starts. If None => no free trial'
                        )
                        .optional(),
                    })
                    .describe('Subscription detailed information.')
                    .optional(),
                  chargesDescription: z
                    .string()
                    .describe(
                      'Description of the charges that will be applied for subscription.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
                .describe('Subscription info.')
                .optional(),
              priceDescription: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
                )
                .optional(),
              depositAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
                )
                .optional(),
              locations: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Location id in the associated owner app.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    appId: z
                      .string()
                      .describe(
                        'Location owner app, if not provided then the site business info locations will be used.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe('Quantity for specific location.')
                      .min(1)
                      .max(100000)
                      .optional(),
                    name: z
                      .string()
                      .describe('Location name.')
                      .max(500)
                      .optional()
                      .nullable(),
                  })
                )
                .min(0)
                .max(5)
                .optional(),
              lineItemPrice: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Total price **after** catalog discounts and line item discounts.'
                )
                .optional(),
              taxableAddress: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({ addressType: z.never().optional() }),
                    z.object({
                      addressType: z
                        .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                        .describe(
                          'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                        ),
                    }),
                  ])
                )
                .describe('Address used for tax calculation.')
                .optional(),
              priceUndetermined: z
                .boolean()
                .describe(
                  'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
                )
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              modifierGroups: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier group ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Modifier group name.')
                      .optional(),
                    modifiers: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Modifier ID.')
                            .min(1)
                            .max(36)
                            .optional(),
                          quantity: z
                            .number()
                            .int()
                            .describe('The quantity of this modifier.')
                            .min(1)
                            .max(100000)
                            .optional()
                            .nullable(),
                          label: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                )
                                .min(1)
                                .max(200)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                )
                                .min(1)
                                .max(400)
                                .optional()
                                .nullable(),
                            })
                            .describe('Primary display label for the modifier.')
                            .optional(),
                          details: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                )
                                .min(1)
                                .max(200)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                )
                                .min(1)
                                .max(400)
                                .optional()
                                .nullable(),
                            })
                            .describe('Additional details.')
                            .optional(),
                          price: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('The price of the modifier.')
                            .optional(),
                        })
                      )
                      .min(1)
                      .max(10)
                      .optional(),
                  })
                )
                .max(10)
                .optional(),
            })
          )
          .min(1)
          .max(300)
          .optional(),
        buyerInfo: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              email: z
                .string()
                .describe("Buyer's email address.")
                .email()
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                visitorId: z.never().optional(),
                memberId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                visitorId: z
                  .string()
                  .describe(
                    "Visitor ID. Returned when the buyer isn't a logged-in site member."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                visitorId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'Member ID. Returned when the buyer is a logged-in site member.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe('Buyer information.')
          .optional(),
        paymentStatus: z
          .enum([
            'UNSPECIFIED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_REFUNDED',
            'FULLY_REFUNDED',
            'PENDING',
            'PARTIALLY_PAID',
            'PENDING_MERCHANT',
            'CANCELED',
            'DECLINED',
          ])
          .describe('Order payment status.')
          .optional(),
        fulfillmentStatus: z
          .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
          .describe('Order fulfillment status.')
          .optional(),
        buyerLanguage: z
          .string()
          .describe(
            'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
          )
          .optional()
          .nullable(),
        weightUnit: z
          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
          .describe("Weight measurement unit - defaults to site's weight unit.")
          .optional(),
        currency: z
          .string()
          .describe(
            'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
          )
          .optional()
          .nullable(),
        currencyConversionDetails: z
          .object({
            originalCurrency: z
              .string()
              .describe(
                "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
              )
              .optional()
              .nullable(),
            conversionRate: z
              .string()
              .describe(
                'The rate used for converting the original currency to the currency used for payment.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Currency conversion details. For use with multi-currency sites.'
          )
          .optional(),
        taxIncludedInPrices: z
          .boolean()
          .describe('Whether tax is included in line item prices.')
          .optional(),
        siteLanguage: z
          .string()
          .describe('Site language in which original values are shown.')
          .optional()
          .nullable(),
        priceSummary: z
          .object({
            subtotal: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Subtotal of all line items, before discounts and before tax.'
              )
              .optional(),
            shipping: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total shipping price, before discounts and before tax.'
              )
              .optional(),
            tax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total tax applied to the order.')
              .optional(),
            discount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total discount amount applied to the order.')
              .optional(),
            total: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Order's total price after discounts and tax.")
              .optional(),
            totalAdditionalFees: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total additional fees before tax.')
              .optional(),
          })
          .describe('Order price summary.')
          .optional(),
        billingInfo: z
          .object({
            address: z
              .object({
                streetAddress: z
                  .object({
                    name: z.string().optional(),
                    number: z.string().optional(),
                  })
                  .optional(),
                city: z.string().optional().nullable(),
                subdivision: z.string().optional().nullable(),
                country: z.string().optional().nullable(),
                postalCode: z.string().optional().nullable(),
                addressLine2: z.string().optional().nullable(),
              })
              .describe('Address.')
              .optional(),
            contactDetails: z
              .object({
                firstName: z
                  .string()
                  .describe('First name.')
                  .max(100)
                  .optional()
                  .nullable(),
                lastName: z
                  .string()
                  .describe('Last name.')
                  .max(100)
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Phone number.')
                  .optional()
                  .nullable(),
                company: z
                  .string()
                  .describe('Company name.')
                  .max(1000)
                  .optional()
                  .nullable(),
                vatId: z
                  .object({
                    _id: z.string().describe("Customer's tax ID.").optional(),
                    type: z
                      .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                      .describe(
                        'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                      )
                      .optional(),
                  })
                  .describe(
                    'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                  )
                  .optional(),
              })
              .describe('Contact details.')
              .optional(),
          })
          .describe('Billing address and contact details.')
          .optional(),
        shippingInfo: z
          .object({
            carrierId: z
              .string()
              .describe(
                'App Def Id of external provider which was a source of shipping info'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            code: z
              .string()
              .describe(
                'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            title: z
              .string()
              .describe(
                'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
              )
              .max(250)
              .optional(),
            logistics: z
              .intersection(
                z.object({
                  deliveryTime: z
                    .string()
                    .describe(
                      'Expected delivery time in free text. For example, `"3-5 business days"`.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  instructions: z
                    .string()
                    .describe(
                      'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                  deliverByDate: z
                    .date()
                    .describe(
                      'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                    )
                    .optional()
                    .nullable(),
                  deliveryTimeSlot: z
                    .object({
                      from: z
                        .date()
                        .describe('Delivery slot starting time.')
                        .optional()
                        .nullable(),
                      to: z
                        .date()
                        .describe('Delivery slot ending time.')
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Expected delivery time slot with start and end times.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    shippingDestination: z.never().optional(),
                    pickupDetails: z.never().optional(),
                  }),
                  z.object({
                    pickupDetails: z.never().optional(),
                    shippingDestination: z
                      .object({
                        address: z
                          .object({
                            streetAddress: z
                              .object({
                                name: z.string().optional(),
                                number: z.string().optional(),
                              })
                              .optional(),
                            city: z.string().optional().nullable(),
                            subdivision: z.string().optional().nullable(),
                            country: z.string().optional().nullable(),
                            postalCode: z.string().optional().nullable(),
                            addressLine2: z.string().optional().nullable(),
                          })
                          .describe('Address.')
                          .optional(),
                        contactDetails: z
                          .object({
                            firstName: z
                              .string()
                              .describe('First name.')
                              .max(100)
                              .optional()
                              .nullable(),
                            lastName: z
                              .string()
                              .describe('Last name.')
                              .max(100)
                              .optional()
                              .nullable(),
                            phone: z
                              .string()
                              .describe('Phone number.')
                              .optional()
                              .nullable(),
                            company: z
                              .string()
                              .describe('Company name.')
                              .max(1000)
                              .optional()
                              .nullable(),
                            vatId: z
                              .object({
                                _id: z
                                  .string()
                                  .describe("Customer's tax ID.")
                                  .optional(),
                                type: z
                                  .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                                  .describe(
                                    'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                              )
                              .optional(),
                          })
                          .describe('Contact details.')
                          .optional(),
                      })
                      .describe(
                        "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                      ),
                  }),
                  z.object({
                    shippingDestination: z.never().optional(),
                    pickupDetails: z
                      .object({
                        address: z
                          .object({
                            streetAddress: z
                              .object({
                                name: z.string().optional(),
                                number: z.string().optional(),
                              })
                              .optional(),
                            city: z.string().optional().nullable(),
                            subdivision: z.string().optional().nullable(),
                            country: z.string().optional().nullable(),
                            postalCode: z.string().optional().nullable(),
                            addressLine2: z.string().optional().nullable(),
                          })
                          .describe('Pickup address.')
                          .optional(),
                        pickupMethod: z
                          .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                          .describe('Pickup method')
                          .optional(),
                      })
                      .describe(
                        'Pickup details for store pickup or pickup point orders.'
                      ),
                  }),
                ])
              )
              .describe('Shipping logistics.')
              .optional(),
            cost: z
              .object({
                price: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Shipping price for display purposes.')
                  .optional(),
                totalPriceBeforeTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total price of shipping after discounts (when relevant), and before tax.'
                  )
                  .optional(),
                totalPriceAfterTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Shipping price after all discounts (if any exist), and after tax.'
                  )
                  .optional(),
                taxDetails: z
                  .object({
                    taxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Taxable amount of this line item.')
                      .optional(),
                    taxRate: z
                      .string()
                      .describe(
                        'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                      )
                      .optional(),
                    totalTax: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe(
                        'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                      )
                      .optional(),
                  })
                  .describe('Tax details.')
                  .optional(),
                taxInfo: z
                  .object({
                    taxAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe(
                        'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                      )
                      .optional(),
                    taxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Amount for which tax is calculated.')
                      .optional(),
                    taxRate: z
                      .string()
                      .describe('Tax rate %, as a decimal point.')
                      .optional()
                      .nullable(),
                    taxGroupId: z
                      .string()
                      .describe('Tax group ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    taxIncludedInPrice: z
                      .boolean()
                      .describe(
                        'Indicates whether the price already includes tax.'
                      )
                      .optional(),
                    taxBreakdown: z
                      .array(
                        z.object({
                          jurisdiction: z
                            .string()
                            .describe(
                              'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                          rate: z
                            .string()
                            .describe(
                              'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                            )
                            .optional()
                            .nullable(),
                          taxAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Amount of tax calculated for this line item.'
                            )
                            .optional(),
                          taxType: z
                            .string()
                            .describe(
                              'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                          taxName: z
                            .string()
                            .describe(
                              'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                          jurisdictionType: z
                            .enum([
                              'UNDEFINED',
                              'COUNTRY',
                              'STATE',
                              'COUNTY',
                              'CITY',
                              'SPECIAL',
                            ])
                            .describe(
                              'Type of jurisdiction that taxes were calculated for.'
                            )
                            .optional(),
                          nonTaxableAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Non-taxable amount of the line item price.'
                            )
                            .optional(),
                          taxableAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Taxable amount of the line item price.')
                            .optional(),
                        })
                      )
                      .max(7)
                      .optional(),
                  })
                  .describe(
                    'Represents all the relevant tax details for a shipping.'
                  )
                  .optional(),
                discount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Shipping discount before tax.')
                  .optional(),
              })
              .describe('Shipping costs.')
              .optional(),
            region: z
              .object({
                name: z
                  .string()
                  .describe(
                    'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Shipping region.')
              .optional(),
          })
          .describe('Shipping info and selected shipping option details.')
          .optional(),
        buyerNote: z
          .string()
          .describe(
            '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
          )
          .max(1000)
          .optional()
          .nullable(),
        status: z
          .enum(['INITIALIZED', 'APPROVED', 'CANCELED', 'PENDING', 'REJECTED'])
          .describe('Order status.')
          .optional(),
        archived: z
          .boolean()
          .describe('Whether order is archived.')
          .optional()
          .nullable(),
        taxSummary: z
          .object({
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total tax.')
              .optional(),
          })
          .describe(
            'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
          )
          .optional(),
        taxInfo: z
          .object({
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Calculated tax, added from line items.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated.'
                    )
                    .max(200)
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
                    )
                    .max(200)
                    .optional(),
                  jurisdiction: z
                    .string()
                    .describe(
                      'The name of the jurisdiction in which this tax detail applies.'
                    )
                    .max(200)
                    .optional(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special).'
                    )
                    .optional(),
                  rate: z
                    .string()
                    .describe(
                      'The rate at which this tax detail was calculated.'
                    )
                    .optional(),
                  aggregatedTaxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The sum of all the tax from line items that calculated by the tax identifiers.'
                    )
                    .optional(),
                })
              )
              .min(0)
              .max(50)
              .optional(),
            taxExempt: z
              .boolean()
              .describe(
                'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
          })
          .describe('Tax information.')
          .optional(),
        appliedDiscounts: z
          .array(
            z.intersection(
              z.object({
                discountType: z
                  .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
                  .optional(),
                lineItemIds: z.array(z.string()).min(0).max(300).optional(),
                _id: z
                  .string()
                  .describe('Discount ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                lineItemDiscounts: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Line item ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      totalDiscount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Total discount amount for this line item.')
                        .optional(),
                    })
                  )
                  .max(300)
                  .optional(),
              }),
              z.xor([
                z.object({
                  coupon: z.never().optional(),
                  merchantDiscount: z.never().optional(),
                  discountRule: z.never().optional(),
                }),
                z.object({
                  merchantDiscount: z.never().optional(),
                  discountRule: z.never().optional(),
                  coupon: z
                    .object({
                      _id: z.string().describe('Coupon ID.').optional(),
                      code: z.string().describe('Coupon code.').optional(),
                      name: z.string().describe('Coupon name.').optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Coupon value.')
                        .optional(),
                    })
                    .describe('Coupon applied by the customer.'),
                }),
                z.object({
                  coupon: z.never().optional(),
                  discountRule: z.never().optional(),
                  merchantDiscount: z
                    .intersection(
                      z.object({
                        amount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Discount amount.')
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          discountReason: z.never().optional(),
                          description: z.never().optional(),
                        }),
                        z.object({
                          description: z.never().optional(),
                          discountReason: z
                            .enum([
                              'UNSPECIFIED',
                              'EXCHANGED_ITEMS',
                              'BILLING_ADJUSTMENT',
                            ])
                            .describe('Predefined discount reason.'),
                        }),
                        z.object({
                          discountReason: z.never().optional(),
                          description: z
                            .string()
                            .describe(
                              'Custom discount description as free text.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe('Discount applied manually by the merchant.'),
                }),
                z.object({
                  coupon: z.never().optional(),
                  merchantDiscount: z.never().optional(),
                  discountRule: z
                    .object({
                      _id: z
                        .string()
                        .describe('Discount rule ID')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      name: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Original discount rule name (in site's default language)."
                            )
                            .min(1)
                            .max(256)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                            )
                            .min(1)
                            .max(500)
                            .optional()
                            .nullable(),
                        })
                        .describe('Discount rule name')
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Discount value.')
                        .optional(),
                    })
                    .describe(
                      'Automatic discount applied by the system based on configured discount rules.'
                    ),
                }),
              ])
            )
          )
          .max(320)
          .optional(),
        activities: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Activity ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                authorEmail: z
                  .string()
                  .describe("Activity author's email.")
                  .email()
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe('Activity creation date and time.')
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'ORDER_REFUNDED',
                    'ORDER_PLACED',
                    'ORDER_PAID',
                    'ORDER_FULFILLED',
                    'ORDER_NOT_FULFILLED',
                    'ORDER_CANCELED',
                    'DOWNLOAD_LINK_SENT',
                    'TRACKING_NUMBER_ADDED',
                    'TRACKING_NUMBER_EDITED',
                    'TRACKING_LINK_ADDED',
                    'SHIPPING_CONFIRMATION_EMAIL_SENT',
                    'INVOICE_ADDED',
                    'INVOICE_REMOVED',
                    'INVOICE_SENT',
                    'FULFILLER_EMAIL_SENT',
                    'SHIPPING_ADDRESS_EDITED',
                    'EMAIL_EDITED',
                    'PICKUP_READY_EMAIL_SENT',
                    'CUSTOM_ACTIVITY',
                    'MERCHANT_COMMENT',
                    'ORDER_PARTIALLY_PAID',
                    'DRAFT_ORDER_CHANGES_APPLIED',
                    'SAVED_PAYMENT_METHOD',
                    'AUTHORIZED_PAYMENT_CREATED',
                    'AUTHORIZED_PAYMENT_CAPTURED',
                    'AUTHORIZED_PAYMENT_VOIDED',
                    'REFUND_INITIATED',
                    'PAYMENT_REFUNDED',
                    'PAYMENT_REFUND_FAILED',
                    'REFUNDED_AS_STORE_CREDIT',
                    'PAYMENT_PENDING',
                    'PAYMENT_CANCELED',
                    'PAYMENT_DECLINED',
                    'ORDER_PENDING',
                    'ORDER_REJECTED',
                    'RECEIPT_CREATED',
                    'RECEIPT_SENT',
                    'CHARGEBACK_CREATED',
                    'CHARGEBACK_REVERSED',
                  ])
                  .describe('Activity type.')
                  .optional(),
                activityType: z
                  .enum([
                    'ORDER_PLACED',
                    'ORDER_PAID',
                    'ORDER_FULFILLED',
                    'ORDER_NOT_FULFILLED',
                    'ORDER_CANCELED',
                    'DOWNLOAD_LINK_SENT',
                    'TRACKING_NUMBER_ADDED',
                    'TRACKING_NUMBER_EDITED',
                    'TRACKING_LINK_ADDED',
                    'SHIPPING_CONFIRMATION_EMAIL_SENT',
                    'INVOICE_ADDED',
                    'INVOICE_REMOVED',
                    'INVOICE_SENT',
                    'FULFILLER_EMAIL_SENT',
                    'SHIPPING_ADDRESS_EDITED',
                    'EMAIL_EDITED',
                    'PICKUP_READY_EMAIL_SENT',
                    'MERCHANT_COMMENT',
                    'ORDER_PARTIALLY_PAID',
                    'DRAFT_ORDER_CHANGES_APPLIED',
                    'SAVED_PAYMENT_METHOD',
                    'AUTHORIZED_PAYMENT_CREATED',
                    'AUTHORIZED_PAYMENT_CAPTURED',
                    'AUTHORIZED_PAYMENT_VOIDED',
                    'REFUND_INITIATED',
                    'PAYMENT_REFUNDED',
                    'PAYMENT_REFUND_FAILED',
                    'REFUNDED_AS_STORE_CREDIT',
                    'PAYMENT_PENDING',
                    'PAYMENT_CANCELED',
                    'PAYMENT_DECLINED',
                    'ORDER_PENDING',
                    'ORDER_REJECTED',
                    'RECEIPT_CREATED',
                    'RECEIPT_SENT',
                    'CHARGEBACK_CREATED',
                    'CHARGEBACK_REVERSED',
                    'ORDER_REFUNDED',
                  ])
                  .describe('Activity type.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                }),
                z.object({
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  merchantComment: z
                    .object({
                      message: z
                        .string()
                        .describe('Merchant comment message.')
                        .min(1)
                        .max(5000)
                        .optional(),
                    })
                    .describe(
                      'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
                    ),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  orderRefunded: z
                    .object({
                      manual: z
                        .boolean()
                        .describe(
                          'Whether order was refunded manually. For example, via payment provider or using cash.'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Refund amount.')
                        .optional(),
                      reason: z
                        .string()
                        .describe('Reason for refund.')
                        .max(200)
                        .optional(),
                    })
                    .describe(
                      'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
                    ),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  draftOrderChangesApplied: z
                    .object({
                      draftOrderId: z
                        .string()
                        .describe('Draft order id.')
                        .optional(),
                      reason: z
                        .string()
                        .describe('Reason for edit, given by user (optional).')
                        .max(200)
                        .optional()
                        .nullable(),
                      changes: z
                        .array(
                          z.intersection(
                            z.object({}),
                            z.xor([
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemAdded: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountAdded: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                              }),
                              z.object({
                                lineItemAdded: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountAdded: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                                lineItemChanged: z.object({
                                  _id: z
                                    .string()
                                    .describe('Line item ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name after change.')
                                    .optional(),
                                  quantity: z
                                    .object({
                                      originalQuantity: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Item quantity before update.'
                                        )
                                        .min(0)
                                        .max(1000000)
                                        .optional(),
                                      newQuantity: z
                                        .number()
                                        .int()
                                        .describe('Item quantity after update.')
                                        .min(0)
                                        .max(1000000)
                                        .optional(),
                                      diff: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Difference between original and new quantity. Absolute value.'
                                        )
                                        .min(1)
                                        .max(1000000)
                                        .optional(),
                                      deltaType: z
                                        .enum([
                                          'QUANTITY_INCREASED',
                                          'QUANTITY_DECREASED',
                                        ])
                                        .describe(
                                          'Type of quantity change: increase or decrease.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Item quantity change.')
                                    .optional(),
                                  price: z
                                    .object({
                                      originalPrice: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Item price before update.')
                                        .optional(),
                                      newPrice: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Item price after update.')
                                        .optional(),
                                    })
                                    .describe('Item price change.')
                                    .optional(),
                                }),
                              }),
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountAdded: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                                lineItemAdded: z.object({
                                  _id: z
                                    .string()
                                    .describe('Line item ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  quantity: z
                                    .number()
                                    .int()
                                    .describe('Added or removed item quantity.')
                                    .min(1)
                                    .max(1000000)
                                    .optional(),
                                }),
                              }),
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemAdded: z.never().optional(),
                                discountAdded: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                                lineItemRemoved: z.object({
                                  _id: z
                                    .string()
                                    .describe('Line item ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  quantity: z
                                    .number()
                                    .int()
                                    .describe('Added or removed item quantity.')
                                    .min(1)
                                    .max(1000000)
                                    .optional(),
                                }),
                              }),
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemAdded: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                                discountAdded: z.object({
                                  _id: z
                                    .string()
                                    .describe('Discount id.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          'Value in site default language.'
                                        )
                                        .min(1)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe('Translated value.')
                                        .min(1)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Discount name: coupon name / discount rule name / merchant discount description.'
                                    )
                                    .optional(),
                                  affectedLineItems: z
                                    .array(
                                      z.object({
                                        _id: z
                                          .string()
                                          .describe('Order line item id')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                              )
                                              .min(1)
                                              .max(200)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe(
                                                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                              )
                                              .min(1)
                                              .max(400)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Item name.')
                                          .optional(),
                                        amount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                          )
                                          .optional(),
                                      })
                                    )
                                    .min(0)
                                    .optional(),
                                  totalAmount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Discount amount.')
                                    .optional(),
                                }),
                              }),
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemAdded: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountAdded: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                                discountRemoved: z.object({
                                  _id: z
                                    .string()
                                    .describe('Discount id.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          'Value in site default language.'
                                        )
                                        .min(1)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe('Translated value.')
                                        .min(1)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Discount name: coupon name / discount rule name / merchant discount description.'
                                    )
                                    .optional(),
                                  affectedLineItems: z
                                    .array(
                                      z.object({
                                        _id: z
                                          .string()
                                          .describe('Order line item id')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                              )
                                              .min(1)
                                              .max(200)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe(
                                                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                              )
                                              .min(1)
                                              .max(400)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Item name.')
                                          .optional(),
                                        amount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                          )
                                          .optional(),
                                      })
                                    )
                                    .min(0)
                                    .optional(),
                                  totalAmount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Discount amount.')
                                    .optional(),
                                }),
                              }),
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemAdded: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountAdded: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                                additionalFeeAdded: z.object({
                                  _id: z
                                    .string()
                                    .describe('Additional fee id.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          'Value in site default language.'
                                        )
                                        .min(1)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe('Translated value.')
                                        .min(1)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Additional fee name.')
                                    .optional(),
                                  affectedLineItems: z
                                    .array(
                                      z.object({
                                        _id: z
                                          .string()
                                          .describe('Order line item id')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                              )
                                              .min(1)
                                              .max(200)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe(
                                                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                              )
                                              .min(1)
                                              .max(400)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Item name.')
                                          .optional(),
                                        amount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                          )
                                          .optional(),
                                      })
                                    )
                                    .min(0)
                                    .optional(),
                                  totalAmount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Additional fee amount.')
                                    .optional(),
                                }),
                              }),
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemAdded: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountAdded: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                                additionalFeeRemoved: z.object({
                                  _id: z
                                    .string()
                                    .describe('Additional fee id.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          'Value in site default language.'
                                        )
                                        .min(1)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe('Translated value.')
                                        .min(1)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Additional fee name.')
                                    .optional(),
                                  affectedLineItems: z
                                    .array(
                                      z.object({
                                        _id: z
                                          .string()
                                          .describe('Order line item id')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        name: z
                                          .object({
                                            original: z
                                              .string()
                                              .describe(
                                                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                              )
                                              .min(1)
                                              .max(200)
                                              .optional(),
                                            translated: z
                                              .string()
                                              .describe(
                                                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                              )
                                              .min(1)
                                              .max(400)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Item name.')
                                          .optional(),
                                        amount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                          )
                                          .optional(),
                                      })
                                    )
                                    .min(0)
                                    .optional(),
                                  totalAmount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Additional fee amount.')
                                    .optional(),
                                }),
                              }),
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemAdded: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountAdded: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                shippingInformationChanged: z
                                  .never()
                                  .optional(),
                                totalPriceChanged: z.object({
                                  originalTotal: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Order’s total price after discounts and tax. Before update'
                                    )
                                    .optional(),
                                  newTotal: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Order’s total price after discounts and tax. After update'
                                    )
                                    .optional(),
                                }),
                              }),
                              z.object({
                                lineItemChanged: z.never().optional(),
                                lineItemAdded: z.never().optional(),
                                lineItemRemoved: z.never().optional(),
                                discountAdded: z.never().optional(),
                                discountRemoved: z.never().optional(),
                                additionalFeeAdded: z.never().optional(),
                                additionalFeeRemoved: z.never().optional(),
                                totalPriceChanged: z.never().optional(),
                                shippingInformationChanged: z.object({
                                  originalShippingInfo: z
                                    .object({
                                      total: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Order’s shipping price.')
                                        .optional(),
                                      shippingTitle: z
                                        .string()
                                        .describe('Order’s shipping title.')
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe(
                                      'Order’s Shipping Information. Before update'
                                    )
                                    .optional(),
                                  newShippingInfo: z
                                    .object({
                                      total: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Order’s shipping price.')
                                        .optional(),
                                      shippingTitle: z
                                        .string()
                                        .describe('Order’s shipping title.')
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe(
                                      'Order’s Shipping Information. After update'
                                    )
                                    .optional(),
                                }),
                              }),
                            ])
                          )
                        )
                        .optional(),
                    })
                    .describe(
                      'Details of changes made by the Draft Orders API.'
                    ),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  savedPaymentMethod: z
                    .object({
                      name: z
                        .string()
                        .describe('Payment method name.')
                        .min(1)
                        .max(100)
                        .optional(),
                      description: z
                        .string()
                        .describe('Payment method description.')
                        .min(1)
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Details of the payment method saved for the order.'
                    ),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  authorizedPaymentCreated: z
                    .object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                      lastFourDigits: z
                        .string()
                        .describe('The last 4 digits of the card number.')
                        .max(4)
                        .optional()
                        .nullable(),
                      brand: z
                        .string()
                        .describe("Card issuer's brand.")
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Details of an authorized payment created.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  authorizedPaymentCaptured: z
                    .object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                      lastFourDigits: z
                        .string()
                        .describe('The last 4 digits of the card number.')
                        .max(4)
                        .optional()
                        .nullable(),
                      brand: z
                        .string()
                        .describe("Card issuer's brand.")
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Details of an authorized payment captured.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  authorizedPaymentVoided: z
                    .object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                      lastFourDigits: z
                        .string()
                        .describe('The last 4 digits of the card number.')
                        .max(4)
                        .optional()
                        .nullable(),
                      brand: z
                        .string()
                        .describe("Card issuer's brand.")
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Details of an authorized payment voided.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  refundInitiated: z
                    .object({
                      refundId: z
                        .string()
                        .describe('Refund ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Refund amount.')
                        .optional(),
                      payments: z
                        .array(
                          z.intersection(
                            z.object({
                              paymentId: z
                                .string()
                                .describe('Payment ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              externalRefund: z
                                .boolean()
                                .describe(
                                  "Whether refund was made externally and manually on the payment provider's side."
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                regular: z.never().optional(),
                                giftCard: z.never().optional(),
                                membership: z.never().optional(),
                              }),
                              z.object({
                                giftCard: z.never().optional(),
                                membership: z.never().optional(),
                                regular: z
                                  .object({
                                    amount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Refund amount.')
                                      .optional(),
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Regular payment refund.'),
                              }),
                              z.object({
                                regular: z.never().optional(),
                                membership: z.never().optional(),
                                giftCard: z
                                  .object({
                                    giftCardPaymentId: z
                                      .string()
                                      .describe('Gift card payment ID.')
                                      .min(1)
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                    amount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Refund amount.')
                                      .optional(),
                                  })
                                  .describe('Gift card payment refund.'),
                              }),
                              z.object({
                                regular: z.never().optional(),
                                giftCard: z.never().optional(),
                                membership: z
                                  .object({
                                    membershipId: z
                                      .string()
                                      .describe('Membership ID.')
                                      .min(1)
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Membership payment refund.'),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                      reason: z
                        .string()
                        .describe('Reason for refund.')
                        .max(200)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                    ),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  paymentRefunded: z
                    .object({
                      refundId: z
                        .string()
                        .describe('Refund ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      payment: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe('Payment ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            externalRefund: z
                              .boolean()
                              .describe(
                                "Whether refund was made externally and manually on the payment provider's side."
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                            }),
                            z.object({
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                              regular: z
                                .object({
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Regular payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              membership: z.never().optional(),
                              giftCard: z
                                .object({
                                  giftCardPaymentId: z
                                    .string()
                                    .describe('Gift card payment ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                })
                                .describe('Gift card payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z
                                .object({
                                  membershipId: z
                                    .string()
                                    .describe('Membership ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Membership payment refund.'),
                            }),
                          ])
                        )
                        .describe('Details about the refunded payment.')
                        .optional(),
                    })
                    .describe(
                      'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                    ),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  paymentRefundFailed: z
                    .object({
                      refundId: z
                        .string()
                        .describe('Refund ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      payment: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe('Payment ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            externalRefund: z
                              .boolean()
                              .describe(
                                "Whether refund was made externally and manually on the payment provider's side."
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                            }),
                            z.object({
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                              regular: z
                                .object({
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Regular payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              membership: z.never().optional(),
                              giftCard: z
                                .object({
                                  giftCardPaymentId: z
                                    .string()
                                    .describe('Gift card payment ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                })
                                .describe('Gift card payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z
                                .object({
                                  membershipId: z
                                    .string()
                                    .describe('Membership ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Membership payment refund.'),
                            }),
                          ])
                        )
                        .describe('Details about the failed payment refund.')
                        .optional(),
                    })
                    .describe(
                      'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                    ),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  refundedAsStoreCredit: z
                    .object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Refund amount.')
                        .optional(),
                      reason: z
                        .string()
                        .describe('Reason for refund.')
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Details of refund to store credit.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  paymentPending: z
                    .intersection(
                      z.object({
                        paymentId: z
                          .string()
                          .describe(
                            'Payment ID of payment associated with this activity.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ regular: z.never().optional() }),
                        z.object({
                          regular: z
                            .intersection(
                              z.object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Payment amount.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  creditCardDetails: z.never().optional(),
                                }),
                                z.object({
                                  creditCardDetails: z
                                    .object({
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Credit card details.'),
                                }),
                              ])
                            )
                            .describe('Regular payment.'),
                        }),
                      ])
                    )
                    .describe('Details of a pending payment.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  paymentCanceled: z
                    .intersection(
                      z.object({
                        paymentId: z
                          .string()
                          .describe(
                            'Payment ID of payment associated with this activity.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ regular: z.never().optional() }),
                        z.object({
                          regular: z
                            .intersection(
                              z.object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Payment amount.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  creditCardDetails: z.never().optional(),
                                }),
                                z.object({
                                  creditCardDetails: z
                                    .object({
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Credit card details.'),
                                }),
                              ])
                            )
                            .describe('Regular payment.'),
                        }),
                      ])
                    )
                    .describe('Details of a canceled payment.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  paymentDeclined: z
                    .intersection(
                      z.object({
                        paymentId: z
                          .string()
                          .describe(
                            'Payment ID of payment associated with this activity.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ regular: z.never().optional() }),
                        z.object({
                          regular: z
                            .intersection(
                              z.object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Payment amount.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  creditCardDetails: z.never().optional(),
                                }),
                                z.object({
                                  creditCardDetails: z
                                    .object({
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Credit card details.'),
                                }),
                              ])
                            )
                            .describe('Regular payment.'),
                        }),
                      ])
                    )
                    .describe('Details of a declined payment.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  receiptCreated: z
                    .intersection(
                      z.object({
                        paymentId: z
                          .string()
                          .describe(
                            'Payment ID of payment associated with this activity.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          wixReceipt: z.never().optional(),
                          externalReceipt: z.never().optional(),
                        }),
                        z.object({
                          externalReceipt: z.never().optional(),
                          wixReceipt: z
                            .object({
                              receiptId: z
                                .string()
                                .describe('Receipt ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              displayNumber: z
                                .string()
                                .describe('Display number of receipt.')
                                .min(1)
                                .max(40)
                                .optional()
                                .nullable(),
                            })
                            .describe('Receipt created by Wix.'),
                        }),
                        z.object({
                          wixReceipt: z.never().optional(),
                          externalReceipt: z
                            .object({
                              receiptId: z
                                .string()
                                .describe('Receipt ID.')
                                .max(100)
                                .optional()
                                .nullable(),
                              displayNumber: z
                                .string()
                                .describe('Display number of receipt.')
                                .min(1)
                                .max(40)
                                .optional()
                                .nullable(),
                            })
                            .describe('Receipt created by an external system.'),
                        }),
                      ])
                    )
                    .describe('Receipt was added for associated payment.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  receiptSent: z
                    .intersection(
                      z.object({
                        paymentId: z
                          .string()
                          .describe(
                            'Payment ID of payment associated with this activity.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          wixReceipt: z.never().optional(),
                          externalReceipt: z.never().optional(),
                        }),
                        z.object({
                          externalReceipt: z.never().optional(),
                          wixReceipt: z
                            .object({
                              receiptId: z
                                .string()
                                .describe('Receipt ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              displayNumber: z
                                .string()
                                .describe('Display number of receipt.')
                                .min(1)
                                .max(40)
                                .optional()
                                .nullable(),
                            })
                            .describe('Receipt created by Wix.'),
                        }),
                        z.object({
                          wixReceipt: z.never().optional(),
                          externalReceipt: z
                            .object({
                              receiptId: z
                                .string()
                                .describe('Receipt ID.')
                                .max(100)
                                .optional()
                                .nullable(),
                              displayNumber: z
                                .string()
                                .describe('Display number of receipt.')
                                .min(1)
                                .max(40)
                                .optional()
                                .nullable(),
                            })
                            .describe('Receipt created by an external system.'),
                        }),
                      ])
                    )
                    .describe('Receipt sent to customer.'),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackReversed: z.never().optional(),
                  chargebackCreated: z
                    .object({
                      paymentId: z
                        .string()
                        .describe(
                          'ID of the payment that received a chargeback.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      chargebackId: z
                        .string()
                        .describe('Chargeback ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Chargeback amount.')
                        .optional(),
                      paymentDetails: z
                        .intersection(
                          z.object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Payment amount.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              creditCardDetails: z.never().optional(),
                            }),
                            z.object({
                              creditCardDetails: z
                                .object({
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Credit card details.'),
                            }),
                          ])
                        )
                        .describe('Payment details.')
                        .optional(),
                    })
                    .describe(
                      'Order received a chargeback for one of its payments.'
                    ),
                }),
                z.object({
                  merchantComment: z.never().optional(),
                  orderRefunded: z.never().optional(),
                  draftOrderChangesApplied: z.never().optional(),
                  savedPaymentMethod: z.never().optional(),
                  authorizedPaymentCreated: z.never().optional(),
                  authorizedPaymentCaptured: z.never().optional(),
                  authorizedPaymentVoided: z.never().optional(),
                  refundInitiated: z.never().optional(),
                  paymentRefunded: z.never().optional(),
                  paymentRefundFailed: z.never().optional(),
                  refundedAsStoreCredit: z.never().optional(),
                  paymentPending: z.never().optional(),
                  paymentCanceled: z.never().optional(),
                  paymentDeclined: z.never().optional(),
                  receiptCreated: z.never().optional(),
                  receiptSent: z.never().optional(),
                  chargebackCreated: z.never().optional(),
                  chargebackReversed: z
                    .object({
                      paymentId: z
                        .string()
                        .describe(
                          'ID of the payment involved with the chargeback.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      chargebackId: z
                        .string()
                        .describe('Chargeback ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Chargeback amount.')
                        .optional(),
                      reversalAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Chargeback reversal amount.')
                        .optional(),
                      paymentDetails: z
                        .intersection(
                          z.object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Payment amount.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              creditCardDetails: z.never().optional(),
                            }),
                            z.object({
                              creditCardDetails: z
                                .object({
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Credit card details.'),
                            }),
                          ])
                        )
                        .describe('Payment details.')
                        .optional(),
                    })
                    .describe(
                      "Chargeback reversed for one of the order's payments."
                    ),
                }),
              ])
            )
          )
          .max(1000)
          .optional(),
        attributionSource: z
          .enum(['UNSPECIFIED', 'FACEBOOK_ADS'])
          .describe('Order attribution source.')
          .optional(),
        createdBy: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                userId: z.never().optional(),
                memberId: z.never().optional(),
                visitorId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                visitorId: z.never().optional(),
                appId: z.never().optional(),
                userId: z
                  .string()
                  .describe(
                    'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                userId: z.never().optional(),
                visitorId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'Member ID - when the order was created by a **logged in** site visitor.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                userId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                visitorId: z
                  .string()
                  .describe(
                    'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                userId: z.never().optional(),
                memberId: z.never().optional(),
                visitorId: z.never().optional(),
                appId: z
                  .string()
                  .describe(
                    'App ID - when the order was created by an external application.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe("ID of the order's initiator.")
          .optional(),
        channelInfo: z
          .object({
            type: z
              .enum([
                'UNSPECIFIED',
                'WEB',
                'POS',
                'EBAY',
                'AMAZON',
                'OTHER_PLATFORM',
                'WIX_APP_STORE',
                'WIX_INVOICES',
                'BACKOFFICE_MERCHANT',
                'WISH',
                'CLASS_PASS',
                'GLOBAL_E',
                'FACEBOOK',
                'ETSY',
                'TIKTOK',
                'FAIRE_COM',
                'PAYPAL_AGENTIC_CHECKOUT',
                'STRIPE_AGENTIC_CHECKOUT',
              ])
              .describe(
                'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
              )
              .optional(),
            externalOrderId: z
              .string()
              .describe(
                'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
              )
              .max(100)
              .optional()
              .nullable(),
            externalOrderUrl: z
              .string()
              .describe(
                'URL to the order in the external system. Relevant when recording orders from external platforms.'
              )
              .max(300)
              .optional()
              .nullable(),
          })
          .describe(
            'Information about the sales channel that submitted this order.'
          )
          .optional(),
        seenByAHuman: z
          .boolean()
          .describe(
            'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
          )
          .optional()
          .nullable(),
        checkoutId: z
          .string()
          .describe('Checkout ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        customFields: z
          .array(
            z.object({
              value: z
                .any()
                .describe('Custom field value.')
                .optional()
                .nullable(),
              title: z
                .string()
                .describe('Custom field title.')
                .min(1)
                .max(500)
                .optional(),
              translatedTitle: z
                .string()
                .describe('Translated custom field title.')
                .min(1)
                .max(500)
                .optional()
                .nullable(),
            })
          )
          .optional(),
        balanceSummary: z
          .object({
            balance: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Current amount left to pay.')
              .optional(),
            paid: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
              )
              .optional(),
            refunded: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Sum of all successfully refunded payments.')
              .optional(),
            authorized: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Sum of all authorized payments.')
              .optional(),
            pendingRefund: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Sum of all pending refund transactions.')
              .optional(),
            pending: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Sum of all pending transactions.')
              .optional(),
            chargeback: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Sum of all transaction chargebacks.')
              .optional(),
            chargebackReversal: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Sum of all chargeback reversals.')
              .optional(),
            platformFees: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
              )
              .optional(),
            totalMinusPlatformFees: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
              )
              .optional(),
          })
          .describe('Balance summary.')
          .optional(),
        additionalFees: z
          .array(
            z.object({
              code: z
                .string()
                .describe("Additional fee's unique code for future processing.")
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Name of additional fee.')
                .min(1)
                .max(50)
                .optional(),
              price: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe("Additional fee's price.")
                .optional(),
              taxDetails: z
                .object({
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of this line item.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                    )
                    .optional(),
                  totalTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                    )
                    .optional(),
                })
                .describe('Tax details.')
                .optional(),
              taxInfo: z
                .object({
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                    )
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount for which tax is calculated.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax rate %, as a decimal point.')
                    .optional()
                    .nullable(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  taxIncludedInPrice: z
                    .boolean()
                    .describe(
                      'Indicates whether the price already includes tax.'
                    )
                    .optional(),
                  taxBreakdown: z
                    .array(
                      z.object({
                        jurisdiction: z
                          .string()
                          .describe(
                            'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        rate: z
                          .string()
                          .describe(
                            'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                          )
                          .optional()
                          .nullable(),
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Amount of tax calculated for this line item.'
                          )
                          .optional(),
                        taxType: z
                          .string()
                          .describe(
                            'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        taxName: z
                          .string()
                          .describe(
                            'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        jurisdictionType: z
                          .enum([
                            'UNDEFINED',
                            'COUNTRY',
                            'STATE',
                            'COUNTY',
                            'CITY',
                            'SPECIAL',
                          ])
                          .describe(
                            'Type of jurisdiction that taxes were calculated for.'
                          )
                          .optional(),
                        nonTaxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Non-taxable amount of the line item price.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of the line item price.')
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe(
                  'Represents all the relevant tax details for additional fee.'
                )
                .optional(),
              providerAppId: z
                .string()
                .describe("SPI implementer's `appId`.")
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              priceBeforeTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe("Additional fee's price before tax.")
                .optional(),
              priceAfterTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe("Additional fee's price after tax.")
                .optional(),
              _id: z
                .string()
                .describe("Additional fee's id.")
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              lineItemIds: z.array(z.string()).optional(),
              source: z
                .enum([
                  'SERVICE_PLUGIN',
                  'ITEM',
                  'MANUAL',
                  'SHIPPING',
                  'PLATFORM',
                ])
                .describe('Specifies the entity that added the additional fee.')
                .optional(),
            })
          )
          .max(100)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        purchaseFlowId: z
          .string()
          .describe(
            'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        recipientInfo: z
          .object({
            address: z
              .object({
                streetAddress: z
                  .object({
                    name: z.string().optional(),
                    number: z.string().optional(),
                  })
                  .optional(),
                city: z.string().optional().nullable(),
                subdivision: z.string().optional().nullable(),
                country: z.string().optional().nullable(),
                postalCode: z.string().optional().nullable(),
                addressLine2: z.string().optional().nullable(),
              })
              .describe('Address.')
              .optional(),
            contactDetails: z
              .object({
                firstName: z
                  .string()
                  .describe('First name.')
                  .max(100)
                  .optional()
                  .nullable(),
                lastName: z
                  .string()
                  .describe('Last name.')
                  .max(100)
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Phone number.')
                  .optional()
                  .nullable(),
                company: z
                  .string()
                  .describe('Company name.')
                  .max(1000)
                  .optional()
                  .nullable(),
                vatId: z
                  .object({
                    _id: z.string().describe("Customer's tax ID.").optional(),
                    type: z
                      .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                      .describe(
                        'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                      )
                      .optional(),
                  })
                  .describe(
                    'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                  )
                  .optional(),
              })
              .describe('Contact details.')
              .optional(),
          })
          .describe(
            "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
          )
          .optional(),
        purchasedDate: z
          .date()
          .describe(
            'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
          )
          .optional()
          .nullable(),
        businessLocation: z
          .object({
            _id: z
              .string()
              .describe(
                'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            name: z
              .string()
              .describe('Location name.')
              .min(1)
              .max(500)
              .optional(),
          })
          .describe('Order location.')
          .optional(),
        payAfterFreeTrial: z
          .object({
            subtotal: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Subtotal of all line items, before discounts and before tax.'
              )
              .optional(),
            shipping: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total shipping price, before discounts and before tax.'
              )
              .optional(),
            tax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total tax applied to the order.')
              .optional(),
            discount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total discount amount applied to the order.')
              .optional(),
            total: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Order's total price after discounts and tax.")
              .optional(),
            totalAdditionalFees: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total additional fees before tax.')
              .optional(),
          })
          .describe(
            'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
          )
          .optional(),
        platformFeeSummary: z
          .object({
            total: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total sum of all platform fees.')
              .optional(),
            totalPassOn: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total amount of platform fees with `PASS_ON` charge type.'
              )
              .optional(),
            totalAbsorbed: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total amount of platform fees with `ABSORBED` charge type.'
              )
              .optional(),
            fees: z
              .array(
                z.object({
                  name: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Platform fee name.')
                    .optional(),
                  amount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Platform fee amount.')
                    .optional(),
                  lineItemId: z
                    .string()
                    .describe(
                      'ID of the line item the platform fee applies to.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  chargeType: z
                    .enum(['PASS_ON', 'ABSORBED'])
                    .describe('Platform fee charge type.')
                    .optional(),
                  percentageRate: z
                    .string()
                    .describe(
                      'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
                    )
                    .optional(),
                })
              )
              .max(300)
              .optional(),
          })
          .describe(
            'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
          )
          .optional(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const CreateOrderRequest = z.object({
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      number: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          "Order number displayed in the site owner's dashboard (auto-generated)."
        )
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      lineItems: z
        .array(
          z.object({
            _id: z.string().describe('Line item ID.').optional(),
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
              ),
            catalogReference: z
              .object({
                catalogItemId: z
                  .string()
                  .describe('ID of the item within the catalog it belongs to.')
                  .min(1)
                  .max(36),
                appId: z
                  .string()
                  .describe(
                    'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                  )
                  .min(1),
                options: z
                  .record(z.string(), z.any())
                  .describe(
                    'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "References to the line item's origin catalog. Empty for custom line items."
              )
              .optional(),
            quantity: z
              .number()
              .int()
              .describe('Line item quantity.')
              .min(1)
              .max(100000),
            totalDiscount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Total discount for this line item's entire quantity.")
              .optional(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .min(0)
              .max(20)
              .optional(),
            image: z.string().describe('Line item image.').optional(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe(
                'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
              )
              .optional(),
            itemType: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    preset: z.never().optional(),
                    custom: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    preset: z
                      .enum([
                        'UNRECOGNISED',
                        'PHYSICAL',
                        'DIGITAL',
                        'GIFT_CARD',
                        'SERVICE',
                      ])
                      .describe('Preset item type.'),
                  }),
                  z.object({
                    preset: z.never().optional(),
                    custom: z
                      .string()
                      .describe(
                        'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                      ),
                  }),
                ])
              )
              .describe('Item type. Either a preset type or custom.'),
            fulfillerId: z
              .string()
              .describe(
                'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            refundQuantity: z
              .number()
              .int()
              .describe('Number of items that were refunded.')
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            restockQuantity: z
              .number()
              .int()
              .describe(
                'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
              )
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            price: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Line item price after line item discounts for display purposes.'
              ),
            priceBeforeDiscounts: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
              )
              .optional(),
            totalPriceBeforeTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total price after discounts, and before tax.')
              .optional(),
            totalPriceAfterTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total price after all discounts and tax.')
              .optional(),
            paymentOption: z
              .enum([
                'FULL_PAYMENT_ONLINE',
                'FULL_PAYMENT_OFFLINE',
                'MEMBERSHIP',
                'DEPOSIT_ONLINE',
                'MEMBERSHIP_OFFLINE',
                'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
              ])
              .optional(),
            taxDetails: z
              .object({
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Taxable amount of this line item.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                  )
                  .optional(),
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                  )
                  .optional(),
              })
              .describe(
                'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
              )
              .optional(),
            taxInfo: z
              .object({
                taxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                  )
                  .optional(),
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Amount for which tax is calculated.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe('Tax rate %, as a decimal point.')
                  .optional()
                  .nullable(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                taxIncludedInPrice: z
                  .boolean()
                  .describe('Indicates whether the price already includes tax.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      jurisdiction: z
                        .string()
                        .describe(
                          'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                        )
                        .optional()
                        .nullable(),
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Amount of tax calculated for this line item.'
                        )
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .optional(),
                      nonTaxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Non-taxable amount of the line item price.')
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of the line item price.')
                        .optional(),
                    })
                  )
                  .max(7)
                  .optional(),
              })
              .describe(
                'Represents all the relevant tax details for a specific line item.'
              )
              .optional(),
            digitalFile: z
              .object({
                fileId: z
                  .string()
                  .describe('ID of the secure file in media.')
                  .min(1)
                  .max(100)
                  .optional(),
                link: z
                  .string()
                  .describe(
                    'Link will exist after the digital links have been generated on the order.'
                  )
                  .url()
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                expirationDate: z
                  .date()
                  .describe('Link expiration time and date.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Digital file identifier, relevant only for items with type DIGITAL.'
              )
              .optional(),
            subscriptionInfo: z
              .object({
                _id: z
                  .string()
                  .describe('Subscription ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                cycleNumber: z
                  .number()
                  .int()
                  .describe(
                    'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
                  )
                  .min(0)
                  .optional(),
                subscriptionOptionTitle: z
                  .string()
                  .describe(
                    'Subscription option title. For example, `"Monthly coffee Subscription"`.'
                  )
                  .min(0)
                  .max(150)
                  .optional(),
                subscriptionOptionDescription: z
                  .string()
                  .describe(
                    'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                subscriptionSettings: z
                  .object({
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                      )
                      .min(1)
                      .max(3650)
                      .optional()
                      .nullable(),
                    autoRenewal: z
                      .boolean()
                      .describe(
                        'Whether subscription is renewed automatically at the end of each period.'
                      )
                      .optional(),
                    billingCycles: z
                      .number()
                      .int()
                      .describe(
                        'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    enableCustomerCancellation: z
                      .boolean()
                      .describe(
                        'Whether to allow the customer to cancel the subscription.'
                      )
                      .optional(),
                    freeTrialPeriod: z
                      .object({
                        frequency: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .optional(),
                        interval: z
                          .number()
                          .int()
                          .describe(
                            'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                          )
                          .min(1)
                          .max(3650)
                          .optional(),
                      })
                      .describe(
                        'Period until first cycle starts. If None => no free trial'
                      )
                      .optional(),
                  })
                  .describe('Subscription detailed information.')
                  .optional(),
                chargesDescription: z
                  .string()
                  .describe(
                    'Description of the charges that will be applied for subscription.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
              .describe('Subscription info.')
              .optional(),
            priceDescription: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                  )
                  .min(1)
                  .max(100)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
              )
              .optional(),
            depositAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
              )
              .optional(),
            locations: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Location id in the associated owner app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'Location owner app, if not provided then the site business info locations will be used.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe('Quantity for specific location.')
                    .min(1)
                    .max(100000)
                    .optional(),
                  name: z
                    .string()
                    .describe('Location name.')
                    .max(500)
                    .optional()
                    .nullable(),
                })
              )
              .min(0)
              .max(5)
              .optional(),
            lineItemPrice: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total price **after** catalog discounts and line item discounts.'
              )
              .optional(),
            taxableAddress: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ addressType: z.never().optional() }),
                  z.object({
                    addressType: z
                      .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                      .describe(
                        'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                      ),
                  }),
                ])
              )
              .describe('Address used for tax calculation.')
              .optional(),
            priceUndetermined: z
              .boolean()
              .describe(
                'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            modifierGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Modifier group ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  name: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Modifier group name.')
                    .optional(),
                  modifiers: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Modifier ID.')
                          .min(1)
                          .max(36)
                          .optional(),
                        quantity: z
                          .number()
                          .int()
                          .describe('The quantity of this modifier.')
                          .min(1)
                          .max(100000)
                          .optional()
                          .nullable(),
                        label: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Primary display label for the modifier.')
                          .optional(),
                        details: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Additional details.')
                          .optional(),
                        price: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('The price of the modifier.')
                          .optional(),
                      })
                    )
                    .min(1)
                    .max(10)
                    .optional(),
                })
              )
              .max(10)
              .optional(),
          })
        )
        .min(1)
        .max(300),
      buyerInfo: z
        .intersection(
          z.object({
            contactId: z
              .string()
              .describe(
                "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            email: z
              .string()
              .describe("Buyer's email address.")
              .email()
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              visitorId: z.never().optional(),
              memberId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              visitorId: z
                .string()
                .describe(
                  "Visitor ID. Returned when the buyer isn't a logged-in site member."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              visitorId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID. Returned when the buyer is a logged-in site member.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe('Buyer information.')
        .optional(),
      paymentStatus: z
        .enum([
          'UNSPECIFIED',
          'NOT_PAID',
          'PAID',
          'PARTIALLY_REFUNDED',
          'FULLY_REFUNDED',
          'PENDING',
          'PARTIALLY_PAID',
          'PENDING_MERCHANT',
          'CANCELED',
          'DECLINED',
        ])
        .optional(),
      fulfillmentStatus: z
        .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
        .optional(),
      buyerLanguage: z
        .string()
        .describe(
          'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
        )
        .optional()
        .nullable(),
      weightUnit: z.enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB']).optional(),
      currency: z
        .string()
        .describe(
          'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
        )
        .optional()
        .nullable(),
      currencyConversionDetails: z
        .object({
          originalCurrency: z
            .string()
            .describe(
              "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
            ),
          conversionRate: z
            .string()
            .describe(
              'The rate used for converting the original currency to the currency used for payment.'
            ),
        })
        .describe(
          'Currency conversion details. For use with multi-currency sites.'
        )
        .optional(),
      taxIncludedInPrices: z
        .boolean()
        .describe('Whether tax is included in line item prices.')
        .optional(),
      siteLanguage: z
        .string()
        .describe('Site language in which original values are shown.')
        .optional()
        .nullable(),
      priceSummary: z
        .object({
          subtotal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Subtotal of all line items, before discounts and before tax.'
            )
            .optional(),
          shipping: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total shipping price, before discounts and before tax.')
            .optional(),
          tax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax applied to the order.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total discount amount applied to the order.')
            .optional(),
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe("Order's total price after discounts and tax.")
            .optional(),
          totalAdditionalFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total additional fees before tax.')
            .optional(),
        })
        .describe('Order price summary.'),
      billingInfo: z
        .object({
          address: z
            .object({
              streetAddress: z
                .object({
                  name: z.string().optional(),
                  number: z.string().optional(),
                })
                .optional(),
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Address.')
            .optional(),
          contactDetails: z
            .object({
              firstName: z
                .string()
                .describe('First name.')
                .max(100)
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe('Last name.')
                .max(100)
                .optional()
                .nullable(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              company: z
                .string()
                .describe('Company name.')
                .max(1000)
                .optional()
                .nullable(),
              vatId: z
                .object({
                  _id: z.string().describe("Customer's tax ID.").optional(),
                  type: z.enum(['UNSPECIFIED', 'CPF', 'CNPJ']).optional(),
                })
                .describe(
                  'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                )
                .optional(),
            })
            .describe('Contact details.')
            .optional(),
        })
        .describe('Billing address and contact details.')
        .optional(),
      shippingInfo: z
        .object({
          carrierId: z
            .string()
            .describe(
              'App Def Id of external provider which was a source of shipping info'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          code: z
            .string()
            .describe(
              'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          title: z
            .string()
            .describe(
              'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
            )
            .max(250)
            .optional(),
          logistics: z
            .intersection(
              z.object({
                deliveryTime: z
                  .string()
                  .describe(
                    'Expected delivery time in free text. For example, `"3-5 business days"`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                instructions: z
                  .string()
                  .describe(
                    'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                deliverByDate: z
                  .date()
                  .describe(
                    'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                  )
                  .optional()
                  .nullable(),
                deliveryTimeSlot: z
                  .object({
                    from: z
                      .date()
                      .describe('Delivery slot starting time.')
                      .optional()
                      .nullable(),
                    to: z
                      .date()
                      .describe('Delivery slot ending time.')
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Expected delivery time slot with start and end times.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  shippingDestination: z.never().optional(),
                  pickupDetails: z.never().optional(),
                }),
                z.object({
                  pickupDetails: z.never().optional(),
                  shippingDestination: z
                    .object({
                      address: z
                        .object({
                          streetAddress: z
                            .object({
                              name: z.string().optional(),
                              number: z.string().optional(),
                            })
                            .optional(),
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Address.')
                        .optional(),
                      contactDetails: z
                        .object({
                          firstName: z
                            .string()
                            .describe('First name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          lastName: z
                            .string()
                            .describe('Last name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          phone: z
                            .string()
                            .describe('Phone number.')
                            .optional()
                            .nullable(),
                          company: z
                            .string()
                            .describe('Company name.')
                            .max(1000)
                            .optional()
                            .nullable(),
                          vatId: z
                            .object({
                              _id: z
                                .string()
                                .describe("Customer's tax ID.")
                                .optional(),
                              type: z
                                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                                .optional(),
                            })
                            .describe(
                              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                            )
                            .optional(),
                        })
                        .describe('Contact details.')
                        .optional(),
                    })
                    .describe(
                      "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                    ),
                }),
                z.object({
                  shippingDestination: z.never().optional(),
                  pickupDetails: z
                    .object({
                      address: z
                        .object({
                          streetAddress: z
                            .object({
                              name: z.string().optional(),
                              number: z.string().optional(),
                            })
                            .optional(),
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Pickup address.')
                        .optional(),
                      pickupMethod: z
                        .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                        .describe('Pickup method')
                        .optional(),
                    })
                    .describe(
                      'Pickup details for store pickup or pickup point orders.'
                    ),
                }),
              ])
            )
            .describe('Shipping logistics.')
            .optional(),
          cost: z
            .object({
              price: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Shipping price for display purposes.')
                .optional(),
              totalPriceBeforeTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Total price of shipping after discounts (when relevant), and before tax.'
                )
                .optional(),
              totalPriceAfterTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Shipping price after all discounts (if any exist), and after tax.'
                )
                .optional(),
              taxDetails: z
                .object({
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of this line item.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                    )
                    .optional(),
                  totalTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                    )
                    .optional(),
                })
                .describe('Tax details.')
                .optional(),
              taxInfo: z
                .object({
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                    )
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount for which tax is calculated.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax rate %, as a decimal point.')
                    .optional()
                    .nullable(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  taxIncludedInPrice: z
                    .boolean()
                    .describe(
                      'Indicates whether the price already includes tax.'
                    )
                    .optional(),
                  taxBreakdown: z
                    .array(
                      z.object({
                        jurisdiction: z
                          .string()
                          .describe(
                            'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        rate: z
                          .string()
                          .describe(
                            'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                          )
                          .optional()
                          .nullable(),
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Amount of tax calculated for this line item.'
                          )
                          .optional(),
                        taxType: z
                          .string()
                          .describe(
                            'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        taxName: z
                          .string()
                          .describe(
                            'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        jurisdictionType: z
                          .enum([
                            'UNDEFINED',
                            'COUNTRY',
                            'STATE',
                            'COUNTY',
                            'CITY',
                            'SPECIAL',
                          ])
                          .optional(),
                        nonTaxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Non-taxable amount of the line item price.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of the line item price.')
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe(
                  'Represents all the relevant tax details for a shipping.'
                )
                .optional(),
              discount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Shipping discount before tax.')
                .optional(),
            })
            .describe('Shipping costs.')
            .optional(),
          region: z
            .object({
              name: z
                .string()
                .describe(
                  'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Shipping region.')
            .optional(),
        })
        .describe('Shipping info and selected shipping option details.')
        .optional(),
      buyerNote: z
        .string()
        .describe(
          '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
        )
        .max(1000)
        .optional()
        .nullable(),
      status: z
        .enum(['INITIALIZED', 'APPROVED', 'CANCELED', 'PENDING', 'REJECTED'])
        .optional(),
      archived: z
        .boolean()
        .describe('Whether order is archived.')
        .optional()
        .nullable(),
      taxSummary: z
        .object({
          totalTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax.')
            .optional(),
        })
        .describe(
          'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
        )
        .optional(),
      taxInfo: z
        .object({
          totalTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Calculated tax, added from line items.')
            .optional(),
          taxBreakdown: z
            .array(
              z.object({
                taxName: z
                  .string()
                  .describe(
                    'The name of the tax against which this tax amount was calculated.'
                  )
                  .max(200)
                  .optional(),
                taxType: z
                  .string()
                  .describe(
                    "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
                  )
                  .max(200)
                  .optional(),
                jurisdiction: z
                  .string()
                  .describe(
                    'The name of the jurisdiction in which this tax detail applies.'
                  )
                  .max(200)
                  .optional(),
                jurisdictionType: z
                  .enum([
                    'UNDEFINED',
                    'COUNTRY',
                    'STATE',
                    'COUNTY',
                    'CITY',
                    'SPECIAL',
                  ])
                  .optional(),
                rate: z
                  .string()
                  .describe('The rate at which this tax detail was calculated.')
                  .optional(),
                aggregatedTaxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The sum of all the tax from line items that calculated by the tax identifiers.'
                  )
                  .optional(),
              })
            )
            .min(0)
            .max(50)
            .optional(),
          taxExempt: z
            .boolean()
            .describe(
              'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
        })
        .describe('Tax information.')
        .optional(),
      appliedDiscounts: z
        .array(
          z.intersection(
            z.object({
              discountType: z
                .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
                .optional(),
              lineItemIds: z.array(z.string()).min(0).max(300).optional(),
              _id: z
                .string()
                .describe('Discount ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              lineItemDiscounts: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Line item ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    totalDiscount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Total discount amount for this line item.')
                      .optional(),
                  })
                )
                .max(300)
                .optional(),
            }),
            z.xor([
              z.object({
                coupon: z.never().optional(),
                merchantDiscount: z.never().optional(),
                discountRule: z.never().optional(),
              }),
              z.object({
                merchantDiscount: z.never().optional(),
                discountRule: z.never().optional(),
                coupon: z
                  .object({
                    _id: z.string().describe('Coupon ID.').optional(),
                    code: z.string().describe('Coupon code.').optional(),
                    name: z.string().describe('Coupon name.').optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Coupon value.')
                      .optional(),
                  })
                  .describe('Coupon applied by the customer.'),
              }),
              z.object({
                coupon: z.never().optional(),
                discountRule: z.never().optional(),
                merchantDiscount: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Discount amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        discountReason: z.never().optional(),
                        description: z.never().optional(),
                      }),
                      z.object({
                        description: z.never().optional(),
                        discountReason: z
                          .enum([
                            'UNSPECIFIED',
                            'EXCHANGED_ITEMS',
                            'BILLING_ADJUSTMENT',
                          ])
                          .describe('Predefined discount reason.'),
                      }),
                      z.object({
                        discountReason: z.never().optional(),
                        description: z
                          .string()
                          .describe('Custom discount description as free text.')
                          .max(200),
                      }),
                    ])
                  )
                  .describe('Discount applied manually by the merchant.'),
              }),
              z.object({
                coupon: z.never().optional(),
                merchantDiscount: z.never().optional(),
                discountRule: z
                  .object({
                    _id: z
                      .string()
                      .describe('Discount rule ID')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Original discount rule name (in site's default language)."
                          )
                          .min(1)
                          .max(256)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                          )
                          .min(1)
                          .max(500)
                          .optional()
                          .nullable(),
                      })
                      .describe('Discount rule name')
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Discount value.')
                      .optional(),
                  })
                  .describe(
                    'Automatic discount applied by the system based on configured discount rules.'
                  ),
              }),
            ])
          )
        )
        .max(320)
        .optional(),
      activities: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Activity ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              authorEmail: z
                .string()
                .describe("Activity author's email.")
                .email()
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Activity creation date and time.')
                .optional()
                .nullable(),
              type: z
                .enum([
                  'ORDER_REFUNDED',
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'CUSTOM_ACTIVITY',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                ])
                .optional(),
              activityType: z
                .enum([
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                  'ORDER_REFUNDED',
                ])
                .optional(),
            }),
            z.xor([
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
              }),
              z.object({
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                merchantComment: z
                  .object({
                    message: z
                      .string()
                      .describe('Merchant comment message.')
                      .min(1)
                      .max(5000)
                      .optional(),
                  })
                  .describe(
                    'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                orderRefunded: z
                  .object({
                    manual: z
                      .boolean()
                      .describe(
                        'Whether order was refunded manually. For example, via payment provider or using cash.'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                draftOrderChangesApplied: z
                  .object({
                    draftOrderId: z
                      .string()
                      .describe('Draft order id.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for edit, given by user (optional).')
                      .max(200)
                      .optional()
                      .nullable(),
                    changes: z
                      .array(
                        z.intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                            }),
                            z.object({
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemChanged: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name after change.')
                                  .optional(),
                                quantity: z
                                  .object({
                                    originalQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity before update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    newQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity after update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    diff: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Difference between original and new quantity. Absolute value.'
                                      )
                                      .min(1)
                                      .max(1000000)
                                      .optional(),
                                    deltaType: z
                                      .enum([
                                        'QUANTITY_INCREASED',
                                        'QUANTITY_DECREASED',
                                      ])
                                      .describe(
                                        'Type of quantity change: increase or decrease.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item quantity change.')
                                  .optional(),
                                price: z
                                  .object({
                                    originalPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price before update.')
                                      .optional(),
                                    newPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price after update.')
                                      .optional(),
                                  })
                                  .describe('Item price change.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              totalPriceChanged: z.object({
                                originalTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. Before update'
                                  )
                                  .optional(),
                                newTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.object({
                                originalShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. Before update'
                                  )
                                  .optional(),
                                newShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                          ])
                        )
                      )
                      .optional(),
                  })
                  .describe('Details of changes made by the Draft Orders API.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                savedPaymentMethod: z
                  .object({
                    name: z
                      .string()
                      .describe('Payment method name.')
                      .min(1)
                      .max(100)
                      .optional(),
                    description: z
                      .string()
                      .describe('Payment method description.')
                      .min(1)
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of the payment method saved for the order.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment created.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCaptured: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment captured.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentVoided: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment voided.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundInitiated: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    payments: z
                      .array(
                        z.intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe('Payment ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            externalRefund: z
                              .boolean()
                              .describe(
                                "Whether refund was made externally and manually on the payment provider's side."
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                            }),
                            z.object({
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                              regular: z
                                .object({
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Regular payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              membership: z.never().optional(),
                              giftCard: z
                                .object({
                                  giftCardPaymentId: z
                                    .string()
                                    .describe('Gift card payment ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                })
                                .describe('Gift card payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z
                                .object({
                                  membershipId: z
                                    .string()
                                    .describe('Membership ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Membership payment refund.'),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(50)
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefunded: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the refunded payment.')
                      .optional(),
                  })
                  .describe(
                    'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefundFailed: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the failed payment refund.')
                      .optional(),
                  })
                  .describe(
                    'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundedAsStoreCredit: z
                  .object({
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of refund to store credit.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentPending: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a pending payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentCanceled: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a canceled payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentDeclined: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a declined payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptCreated: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt was added for associated payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptSent: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt sent to customer.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackReversed: z.never().optional(),
                chargebackCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe('ID of the payment that received a chargeback.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    'Order received a chargeback for one of its payments.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'ID of the payment involved with the chargeback.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    reversalAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback reversal amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    "Chargeback reversed for one of the order's payments."
                  ),
              }),
            ])
          )
        )
        .max(1000)
        .optional(),
      attributionSource: z.enum(['UNSPECIFIED', 'FACEBOOK_ADS']).optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID - when the order was created by a **logged in** site visitor.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              appId: z.never().optional(),
              visitorId: z
                .string()
                .describe(
                  'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID - when the order was created by an external application.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe("ID of the order's initiator.")
        .optional(),
      channelInfo: z
        .object({
          type: z
            .enum([
              'UNSPECIFIED',
              'WEB',
              'POS',
              'EBAY',
              'AMAZON',
              'OTHER_PLATFORM',
              'WIX_APP_STORE',
              'WIX_INVOICES',
              'BACKOFFICE_MERCHANT',
              'WISH',
              'CLASS_PASS',
              'GLOBAL_E',
              'FACEBOOK',
              'ETSY',
              'TIKTOK',
              'FAIRE_COM',
              'PAYPAL_AGENTIC_CHECKOUT',
              'STRIPE_AGENTIC_CHECKOUT',
            ])
            .optional(),
          externalOrderId: z
            .string()
            .describe(
              'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
            )
            .max(100)
            .optional()
            .nullable(),
          externalOrderUrl: z
            .string()
            .describe(
              'URL to the order in the external system. Relevant when recording orders from external platforms.'
            )
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Information about the sales channel that submitted this order.'
        ),
      seenByAHuman: z
        .boolean()
        .describe(
          'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
        )
        .optional()
        .nullable(),
      checkoutId: z
        .string()
        .describe('Checkout ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      customFields: z
        .array(
          z.object({
            value: z
              .any()
              .describe('Custom field value.')
              .optional()
              .nullable(),
            title: z
              .string()
              .describe('Custom field title.')
              .min(1)
              .max(500)
              .optional(),
            translatedTitle: z
              .string()
              .describe('Translated custom field title.')
              .min(1)
              .max(500)
              .optional()
              .nullable(),
          })
        )
        .optional(),
      balanceSummary: z
        .object({
          balance: z
            .object({
              amount: z
                .string()
                .describe(
                  'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
                )
                .optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Current amount left to pay.')
            .optional(),
          paid: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
            )
            .optional(),
          refunded: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all successfully refunded payments.')
            .optional(),
          authorized: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all authorized payments.')
            .optional(),
          pendingRefund: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all pending refund transactions.')
            .optional(),
          pending: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all pending transactions.')
            .optional(),
          chargeback: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all transaction chargebacks.')
            .optional(),
          chargebackReversal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all chargeback reversals.')
            .optional(),
          platformFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
            )
            .optional(),
          totalMinusPlatformFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
            )
            .optional(),
        })
        .describe('Balance summary.')
        .optional(),
      additionalFees: z
        .array(
          z.object({
            code: z
              .string()
              .describe("Additional fee's unique code for future processing.")
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of additional fee.')
              .min(1)
              .max(50)
              .optional(),
            price: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price.")
              .optional(),
            taxDetails: z
              .object({
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Taxable amount of this line item.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                  )
                  .optional(),
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                  )
                  .optional(),
              })
              .describe('Tax details.')
              .optional(),
            taxInfo: z
              .object({
                taxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                  )
                  .optional(),
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Amount for which tax is calculated.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe('Tax rate %, as a decimal point.')
                  .optional()
                  .nullable(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                taxIncludedInPrice: z
                  .boolean()
                  .describe('Indicates whether the price already includes tax.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      jurisdiction: z
                        .string()
                        .describe(
                          'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                        )
                        .optional()
                        .nullable(),
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Amount of tax calculated for this line item.'
                        )
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .optional(),
                      nonTaxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Non-taxable amount of the line item price.')
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of the line item price.')
                        .optional(),
                    })
                  )
                  .max(7)
                  .optional(),
              })
              .describe(
                'Represents all the relevant tax details for additional fee.'
              )
              .optional(),
            providerAppId: z
              .string()
              .describe("SPI implementer's `appId`.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            priceBeforeTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price before tax.")
              .optional(),
            priceAfterTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price after tax.")
              .optional(),
            _id: z
              .string()
              .describe("Additional fee's id.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            lineItemIds: z.array(z.string()).optional(),
            source: z
              .enum([
                'SERVICE_PLUGIN',
                'ITEM',
                'MANUAL',
                'SHIPPING',
                'PLATFORM',
              ])
              .optional(),
          })
        )
        .max(100)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      purchaseFlowId: z
        .string()
        .describe(
          'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      recipientInfo: z
        .object({
          address: z
            .object({
              streetAddress: z
                .object({
                  name: z.string().optional(),
                  number: z.string().optional(),
                })
                .optional(),
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Address.')
            .optional(),
          contactDetails: z
            .object({
              firstName: z
                .string()
                .describe('First name.')
                .max(100)
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe('Last name.')
                .max(100)
                .optional()
                .nullable(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              company: z
                .string()
                .describe('Company name.')
                .max(1000)
                .optional()
                .nullable(),
              vatId: z
                .object({
                  _id: z.string().describe("Customer's tax ID.").optional(),
                  type: z.enum(['UNSPECIFIED', 'CPF', 'CNPJ']).optional(),
                })
                .describe(
                  'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                )
                .optional(),
            })
            .describe('Contact details.')
            .optional(),
        })
        .describe(
          "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
        )
        .optional(),
      purchasedDate: z
        .date()
        .describe(
          'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
        )
        .optional()
        .nullable(),
      businessLocation: z
        .object({
          _id: z
            .string()
            .describe(
              'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Location name.')
            .min(1)
            .max(500)
            .optional(),
        })
        .describe('Order location.')
        .optional(),
      payAfterFreeTrial: z
        .object({
          subtotal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Subtotal of all line items, before discounts and before tax.'
            )
            .optional(),
          shipping: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total shipping price, before discounts and before tax.')
            .optional(),
          tax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax applied to the order.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total discount amount applied to the order.')
            .optional(),
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe("Order's total price after discounts and tax.")
            .optional(),
          totalAdditionalFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total additional fees before tax.')
            .optional(),
        })
        .describe(
          'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
        )
        .optional(),
      platformFeeSummary: z
        .object({
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total sum of all platform fees.')
            .optional(),
          totalPassOn: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total amount of platform fees with `PASS_ON` charge type.'
            )
            .optional(),
          totalAbsorbed: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total amount of platform fees with `ABSORBED` charge type.'
            )
            .optional(),
          fees: z
            .array(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                      )
                      .min(1)
                      .max(400)
                      .optional()
                      .nullable(),
                  })
                  .describe('Platform fee name.')
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Platform fee amount.')
                  .optional(),
                lineItemId: z
                  .string()
                  .describe('ID of the line item the platform fee applies to.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargeType: z.enum(['PASS_ON', 'ABSORBED']).optional(),
                percentageRate: z
                  .string()
                  .describe(
                    'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
                  )
                  .optional(),
              })
            )
            .max(300)
            .optional(),
        })
        .describe(
          'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
        )
        .optional(),
    })
    .describe('Order info.'),
  options: z
    .object({
      settings: z
        .object({
          orderApprovalStrategy: z
            .enum(['DEFAULT', 'PAYMENT_RECEIVED', 'PAYMENT_METHOD_SAVED'])
            .optional(),
          notifications: z
            .object({
              sendNotificationToBuyer: z
                .boolean()
                .describe(
                  'Whether to send notification to the buyer.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
              sendNotificationsToBusiness: z
                .boolean()
                .describe(
                  'Whether to send notifications to the business.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
            })
            .describe('Notification settings to be applied on order creation')
            .optional(),
        })
        .describe('Determine order lifecycle')
        .optional(),
    })
    .optional(),
});
export const CreateOrderResponse = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  number: z
    .string()
    .regex(/^\d+$/, 'Must be a valid UInt64 string')
    .describe(
      "Order number displayed in the site owner's dashboard (auto-generated)."
    )
    .optional(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  lineItems: z
    .array(
      z.object({
        _id: z.string().describe('Line item ID.').optional(),
        productName: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
              )
              .min(1)
              .max(200)
              .optional(),
            translated: z
              .string()
              .describe(
                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
              )
              .min(1)
              .max(400)
              .optional()
              .nullable(),
          })
          .describe(
            'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
          )
          .optional(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "References to the line item's origin catalog. Empty for custom line items."
          )
          .optional(),
        quantity: z
          .number()
          .int()
          .describe('Line item quantity.')
          .min(1)
          .max(100000)
          .optional(),
        totalDiscount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Total discount for this line item's entire quantity.")
          .optional(),
        descriptionLines: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                      )
                      .max(100)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe('Description line name.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z.never().optional(),
                }),
                z.object({
                  colorInfo: z.never().optional(),
                  plainText: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(600)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(600)
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line plain text value.'),
                }),
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(500)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      code: z
                        .string()
                        .describe('HEX or RGB color code for display.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line color value.'),
                }),
              ])
            )
          )
          .min(0)
          .max(20)
          .optional(),
        image: z.string().describe('Line item image.').optional(),
        physicalProperties: z
          .object({
            weight: z
              .number()
              .describe(
                'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
              )
              .optional()
              .nullable(),
            sku: z
              .string()
              .describe(
                'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
              )
              .max(40)
              .optional()
              .nullable(),
            shippable: z
              .boolean()
              .describe('Whether this line item is shippable.')
              .optional(),
          })
          .describe(
            'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
          )
          .optional(),
        itemType: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                preset: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                preset: z
                  .enum([
                    'UNRECOGNISED',
                    'PHYSICAL',
                    'DIGITAL',
                    'GIFT_CARD',
                    'SERVICE',
                  ])
                  .describe('Preset item type.'),
              }),
              z.object({
                preset: z.never().optional(),
                custom: z
                  .string()
                  .describe(
                    'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                  ),
              }),
            ])
          )
          .describe('Item type. Either a preset type or custom.')
          .optional(),
        fulfillerId: z
          .string()
          .describe(
            'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        refundQuantity: z
          .number()
          .int()
          .describe('Number of items that were refunded.')
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        restockQuantity: z
          .number()
          .int()
          .describe(
            'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        price: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Line item price after line item discounts for display purposes.'
          )
          .optional(),
        priceBeforeDiscounts: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
          )
          .optional(),
        totalPriceBeforeTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe('Total price after discounts, and before tax.')
          .optional(),
        totalPriceAfterTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe('Total price after all discounts and tax.')
          .optional(),
        paymentOption: z
          .enum([
            'FULL_PAYMENT_ONLINE',
            'FULL_PAYMENT_OFFLINE',
            'MEMBERSHIP',
            'DEPOSIT_ONLINE',
            'MEMBERSHIP_OFFLINE',
            'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
          ])
          .describe(
            'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
          )
          .optional(),
        taxDetails: z
          .object({
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Taxable amount of this line item.')
              .optional(),
            taxRate: z
              .string()
              .describe(
                'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
              )
              .optional(),
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The calculated tax, based on the `taxableAmount` and `taxRate`.'
              )
              .optional(),
          })
          .describe(
            'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
          )
          .optional(),
        taxInfo: z
          .object({
            taxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Calculated tax, based on `taxable_amount` and `tax_rate`.'
              )
              .optional(),
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Amount for which tax is calculated.')
              .optional(),
            taxRate: z
              .string()
              .describe('Tax rate %, as a decimal point.')
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxIncludedInPrice: z
              .boolean()
              .describe('Indicates whether the price already includes tax.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  jurisdiction: z
                    .string()
                    .describe(
                      'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                    )
                    .optional()
                    .nullable(),
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount of tax calculated for this line item.')
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'Type of jurisdiction that taxes were calculated for.'
                    )
                    .optional(),
                  nonTaxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Non-taxable amount of the line item price.')
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of the line item price.')
                    .optional(),
                })
              )
              .max(7)
              .optional(),
          })
          .describe(
            'Represents all the relevant tax details for a specific line item.'
          )
          .optional(),
        digitalFile: z
          .object({
            fileId: z
              .string()
              .describe('ID of the secure file in media.')
              .min(1)
              .max(100)
              .optional(),
            link: z
              .string()
              .describe(
                'Link will exist after the digital links have been generated on the order.'
              )
              .url()
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            expirationDate: z
              .date()
              .describe('Link expiration time and date.')
              .optional()
              .nullable(),
          })
          .describe(
            'Digital file identifier, relevant only for items with type DIGITAL.'
          )
          .optional(),
        subscriptionInfo: z
          .object({
            _id: z
              .string()
              .describe('Subscription ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            cycleNumber: z
              .number()
              .int()
              .describe(
                'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
              )
              .min(0)
              .optional(),
            subscriptionOptionTitle: z
              .string()
              .describe(
                'Subscription option title. For example, `"Monthly coffee Subscription"`.'
              )
              .min(0)
              .max(150)
              .optional(),
            subscriptionOptionDescription: z
              .string()
              .describe(
                'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
              )
              .max(500)
              .optional()
              .nullable(),
            subscriptionSettings: z
              .object({
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .describe('Frequency of recurring payment.')
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                  )
                  .min(1)
                  .max(3650)
                  .optional()
                  .nullable(),
                autoRenewal: z
                  .boolean()
                  .describe(
                    'Whether subscription is renewed automatically at the end of each period.'
                  )
                  .optional(),
                billingCycles: z
                  .number()
                  .int()
                  .describe(
                    'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                  )
                  .min(1)
                  .optional()
                  .nullable(),
                enableCustomerCancellation: z
                  .boolean()
                  .describe(
                    'Whether to allow the customer to cancel the subscription.'
                  )
                  .optional(),
                freeTrialPeriod: z
                  .object({
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe(
                        'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                      )
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                      )
                      .min(1)
                      .max(3650)
                      .optional(),
                  })
                  .describe(
                    'Period until first cycle starts. If None => no free trial'
                  )
                  .optional(),
              })
              .describe('Subscription detailed information.')
              .optional(),
            chargesDescription: z
              .string()
              .describe(
                'Description of the charges that will be applied for subscription.'
              )
              .max(1000)
              .optional()
              .nullable(),
          })
          .describe('Subscription info.')
          .optional(),
        priceDescription: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
              )
              .min(1)
              .max(100)
              .optional(),
            translated: z
              .string()
              .describe(
                "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
          )
          .optional(),
        depositAmount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
          )
          .optional(),
        locations: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Location id in the associated owner app.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              appId: z
                .string()
                .describe(
                  'Location owner app, if not provided then the site business info locations will be used.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe('Quantity for specific location.')
                .min(1)
                .max(100000)
                .optional(),
              name: z
                .string()
                .describe('Location name.')
                .max(500)
                .optional()
                .nullable(),
            })
          )
          .min(0)
          .max(5)
          .optional(),
        lineItemPrice: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Total price **after** catalog discounts and line item discounts.'
          )
          .optional(),
        taxableAddress: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ addressType: z.never().optional() }),
              z.object({
                addressType: z
                  .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                  .describe(
                    'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                  ),
              }),
            ])
          )
          .describe('Address used for tax calculation.')
          .optional(),
        priceUndetermined: z
          .boolean()
          .describe(
            'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .min(1)
                .max(36)
                .optional(),
              name: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe('Modifier group name.')
                .optional(),
              modifiers: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe('The quantity of this modifier.')
                      .min(1)
                      .max(100000)
                      .optional()
                      .nullable(),
                    label: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Primary display label for the modifier.')
                      .optional(),
                    details: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Additional details.')
                      .optional(),
                    price: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('The price of the modifier.')
                      .optional(),
                  })
                )
                .min(1)
                .max(10)
                .optional(),
            })
          )
          .max(10)
          .optional(),
      })
    )
    .min(1)
    .max(300)
    .optional(),
  buyerInfo: z
    .intersection(
      z.object({
        contactId: z
          .string()
          .describe(
            "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        email: z
          .string()
          .describe("Buyer's email address.")
          .email()
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          visitorId: z.never().optional(),
          memberId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          visitorId: z
            .string()
            .describe(
              "Visitor ID. Returned when the buyer isn't a logged-in site member."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          visitorId: z.never().optional(),
          memberId: z
            .string()
            .describe(
              'Member ID. Returned when the buyer is a logged-in site member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe('Buyer information.')
    .optional(),
  paymentStatus: z
    .enum([
      'UNSPECIFIED',
      'NOT_PAID',
      'PAID',
      'PARTIALLY_REFUNDED',
      'FULLY_REFUNDED',
      'PENDING',
      'PARTIALLY_PAID',
      'PENDING_MERCHANT',
      'CANCELED',
      'DECLINED',
    ])
    .describe('Order payment status.')
    .optional(),
  fulfillmentStatus: z
    .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
    .describe('Order fulfillment status.')
    .optional(),
  buyerLanguage: z
    .string()
    .describe(
      'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
    )
    .optional()
    .nullable(),
  weightUnit: z
    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
    .describe("Weight measurement unit - defaults to site's weight unit.")
    .optional(),
  currency: z
    .string()
    .describe(
      'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
    )
    .optional()
    .nullable(),
  currencyConversionDetails: z
    .object({
      originalCurrency: z
        .string()
        .describe(
          "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
        )
        .optional()
        .nullable(),
      conversionRate: z
        .string()
        .describe(
          'The rate used for converting the original currency to the currency used for payment.'
        )
        .optional()
        .nullable(),
    })
    .describe('Currency conversion details. For use with multi-currency sites.')
    .optional(),
  taxIncludedInPrices: z
    .boolean()
    .describe('Whether tax is included in line item prices.')
    .optional(),
  siteLanguage: z
    .string()
    .describe('Site language in which original values are shown.')
    .optional()
    .nullable(),
  priceSummary: z
    .object({
      subtotal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Subtotal of all line items, before discounts and before tax.'
        )
        .optional(),
      shipping: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total shipping price, before discounts and before tax.')
        .optional(),
      tax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax applied to the order.')
        .optional(),
      discount: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total discount amount applied to the order.')
        .optional(),
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe("Order's total price after discounts and tax.")
        .optional(),
      totalAdditionalFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total additional fees before tax.')
        .optional(),
    })
    .describe('Order price summary.')
    .optional(),
  billingInfo: z
    .object({
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      contactDetails: z
        .object({
          firstName: z
            .string()
            .describe('First name.')
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Last name.')
            .max(100)
            .optional()
            .nullable(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          company: z
            .string()
            .describe('Company name.')
            .max(1000)
            .optional()
            .nullable(),
          vatId: z
            .object({
              _id: z.string().describe("Customer's tax ID.").optional(),
              type: z
                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                .describe(
                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                )
                .optional(),
            })
            .describe(
              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
            )
            .optional(),
        })
        .describe('Contact details.')
        .optional(),
    })
    .describe('Billing address and contact details.')
    .optional(),
  shippingInfo: z
    .object({
      carrierId: z
        .string()
        .describe(
          'App Def Id of external provider which was a source of shipping info'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      code: z
        .string()
        .describe(
          'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      title: z
        .string()
        .describe(
          'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
        )
        .max(250)
        .optional(),
      logistics: z
        .intersection(
          z.object({
            deliveryTime: z
              .string()
              .describe(
                'Expected delivery time in free text. For example, `"3-5 business days"`.'
              )
              .max(500)
              .optional()
              .nullable(),
            instructions: z
              .string()
              .describe(
                'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
              )
              .max(1000)
              .optional()
              .nullable(),
            deliverByDate: z
              .date()
              .describe(
                'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            deliveryTimeSlot: z
              .object({
                from: z
                  .date()
                  .describe('Delivery slot starting time.')
                  .optional()
                  .nullable(),
                to: z
                  .date()
                  .describe('Delivery slot ending time.')
                  .optional()
                  .nullable(),
              })
              .describe('Expected delivery time slot with start and end times.')
              .optional(),
          }),
          z.xor([
            z.object({
              shippingDestination: z.never().optional(),
              pickupDetails: z.never().optional(),
            }),
            z.object({
              pickupDetails: z.never().optional(),
              shippingDestination: z
                .object({
                  address: z
                    .object({
                      streetAddress: z
                        .object({
                          name: z.string().optional(),
                          number: z.string().optional(),
                        })
                        .optional(),
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe('Address.')
                    .optional(),
                  contactDetails: z
                    .object({
                      firstName: z
                        .string()
                        .describe('First name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe('Last name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Phone number.')
                        .optional()
                        .nullable(),
                      company: z
                        .string()
                        .describe('Company name.')
                        .max(1000)
                        .optional()
                        .nullable(),
                      vatId: z
                        .object({
                          _id: z
                            .string()
                            .describe("Customer's tax ID.")
                            .optional(),
                          type: z
                            .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                            .describe(
                              'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                        )
                        .optional(),
                    })
                    .describe('Contact details.')
                    .optional(),
                })
                .describe(
                  "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                ),
            }),
            z.object({
              shippingDestination: z.never().optional(),
              pickupDetails: z
                .object({
                  address: z
                    .object({
                      streetAddress: z
                        .object({
                          name: z.string().optional(),
                          number: z.string().optional(),
                        })
                        .optional(),
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe('Pickup address.')
                    .optional(),
                  pickupMethod: z
                    .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                    .describe('Pickup method')
                    .optional(),
                })
                .describe(
                  'Pickup details for store pickup or pickup point orders.'
                ),
            }),
          ])
        )
        .describe('Shipping logistics.')
        .optional(),
      cost: z
        .object({
          price: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Shipping price for display purposes.')
            .optional(),
          totalPriceBeforeTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total price of shipping after discounts (when relevant), and before tax.'
            )
            .optional(),
          totalPriceAfterTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Shipping price after all discounts (if any exist), and after tax.'
            )
            .optional(),
          taxDetails: z
            .object({
              taxableAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Taxable amount of this line item.')
                .optional(),
              taxRate: z
                .string()
                .describe(
                  'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                )
                .optional(),
              totalTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                )
                .optional(),
            })
            .describe('Tax details.')
            .optional(),
          taxInfo: z
            .object({
              taxAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                )
                .optional(),
              taxableAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Amount for which tax is calculated.')
                .optional(),
              taxRate: z
                .string()
                .describe('Tax rate %, as a decimal point.')
                .optional()
                .nullable(),
              taxGroupId: z
                .string()
                .describe('Tax group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              taxIncludedInPrice: z
                .boolean()
                .describe('Indicates whether the price already includes tax.')
                .optional(),
              taxBreakdown: z
                .array(
                  z.object({
                    jurisdiction: z
                      .string()
                      .describe(
                        'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    rate: z
                      .string()
                      .describe(
                        'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                      )
                      .optional()
                      .nullable(),
                    taxAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Amount of tax calculated for this line item.')
                      .optional(),
                    taxType: z
                      .string()
                      .describe(
                        'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    taxName: z
                      .string()
                      .describe(
                        'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    jurisdictionType: z
                      .enum([
                        'UNDEFINED',
                        'COUNTRY',
                        'STATE',
                        'COUNTY',
                        'CITY',
                        'SPECIAL',
                      ])
                      .describe(
                        'Type of jurisdiction that taxes were calculated for.'
                      )
                      .optional(),
                    nonTaxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Non-taxable amount of the line item price.')
                      .optional(),
                    taxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Taxable amount of the line item price.')
                      .optional(),
                  })
                )
                .max(7)
                .optional(),
            })
            .describe('Represents all the relevant tax details for a shipping.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Shipping discount before tax.')
            .optional(),
        })
        .describe('Shipping costs.')
        .optional(),
      region: z
        .object({
          name: z
            .string()
            .describe(
              'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
            )
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Shipping region.')
        .optional(),
    })
    .describe('Shipping info and selected shipping option details.')
    .optional(),
  buyerNote: z
    .string()
    .describe(
      '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
    )
    .max(1000)
    .optional()
    .nullable(),
  status: z
    .enum(['INITIALIZED', 'APPROVED', 'CANCELED', 'PENDING', 'REJECTED'])
    .describe('Order status.')
    .optional(),
  archived: z
    .boolean()
    .describe('Whether order is archived.')
    .optional()
    .nullable(),
  taxSummary: z
    .object({
      totalTax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax.')
        .optional(),
    })
    .describe(
      'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
    )
    .optional(),
  taxInfo: z
    .object({
      totalTax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Calculated tax, added from line items.')
        .optional(),
      taxBreakdown: z
        .array(
          z.object({
            taxName: z
              .string()
              .describe(
                'The name of the tax against which this tax amount was calculated.'
              )
              .max(200)
              .optional(),
            taxType: z
              .string()
              .describe(
                "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
              )
              .max(200)
              .optional(),
            jurisdiction: z
              .string()
              .describe(
                'The name of the jurisdiction in which this tax detail applies.'
              )
              .max(200)
              .optional(),
            jurisdictionType: z
              .enum([
                'UNDEFINED',
                'COUNTRY',
                'STATE',
                'COUNTY',
                'CITY',
                'SPECIAL',
              ])
              .describe(
                'The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special).'
              )
              .optional(),
            rate: z
              .string()
              .describe('The rate at which this tax detail was calculated.')
              .optional(),
            aggregatedTaxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The sum of all the tax from line items that calculated by the tax identifiers.'
              )
              .optional(),
          })
        )
        .min(0)
        .max(50)
        .optional(),
      taxExempt: z
        .boolean()
        .describe(
          'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
    })
    .describe('Tax information.')
    .optional(),
  appliedDiscounts: z
    .array(
      z.intersection(
        z.object({
          discountType: z
            .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
            .optional(),
          lineItemIds: z.array(z.string()).min(0).max(300).optional(),
          _id: z
            .string()
            .describe('Discount ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          lineItemDiscounts: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Line item ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                totalDiscount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total discount amount for this line item.')
                  .optional(),
              })
            )
            .max(300)
            .optional(),
        }),
        z.xor([
          z.object({
            coupon: z.never().optional(),
            merchantDiscount: z.never().optional(),
            discountRule: z.never().optional(),
          }),
          z.object({
            merchantDiscount: z.never().optional(),
            discountRule: z.never().optional(),
            coupon: z
              .object({
                _id: z.string().describe('Coupon ID.').optional(),
                code: z.string().describe('Coupon code.').optional(),
                name: z.string().describe('Coupon name.').optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Coupon value.')
                  .optional(),
              })
              .describe('Coupon applied by the customer.'),
          }),
          z.object({
            coupon: z.never().optional(),
            discountRule: z.never().optional(),
            merchantDiscount: z
              .intersection(
                z.object({
                  amount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Discount amount.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    discountReason: z.never().optional(),
                    description: z.never().optional(),
                  }),
                  z.object({
                    description: z.never().optional(),
                    discountReason: z
                      .enum([
                        'UNSPECIFIED',
                        'EXCHANGED_ITEMS',
                        'BILLING_ADJUSTMENT',
                      ])
                      .describe('Predefined discount reason.'),
                  }),
                  z.object({
                    discountReason: z.never().optional(),
                    description: z
                      .string()
                      .describe('Custom discount description as free text.')
                      .max(200),
                  }),
                ])
              )
              .describe('Discount applied manually by the merchant.'),
          }),
          z.object({
            coupon: z.never().optional(),
            merchantDiscount: z.never().optional(),
            discountRule: z
              .object({
                _id: z
                  .string()
                  .describe('Discount rule ID')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Original discount rule name (in site's default language)."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                      )
                      .min(1)
                      .max(500)
                      .optional()
                      .nullable(),
                  })
                  .describe('Discount rule name')
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Discount value.')
                  .optional(),
              })
              .describe(
                'Automatic discount applied by the system based on configured discount rules.'
              ),
          }),
        ])
      )
    )
    .max(320)
    .optional(),
  activities: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Activity ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorEmail: z
            .string()
            .describe("Activity author's email.")
            .email()
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Activity creation date and time.')
            .optional()
            .nullable(),
          type: z
            .enum([
              'ORDER_REFUNDED',
              'ORDER_PLACED',
              'ORDER_PAID',
              'ORDER_FULFILLED',
              'ORDER_NOT_FULFILLED',
              'ORDER_CANCELED',
              'DOWNLOAD_LINK_SENT',
              'TRACKING_NUMBER_ADDED',
              'TRACKING_NUMBER_EDITED',
              'TRACKING_LINK_ADDED',
              'SHIPPING_CONFIRMATION_EMAIL_SENT',
              'INVOICE_ADDED',
              'INVOICE_REMOVED',
              'INVOICE_SENT',
              'FULFILLER_EMAIL_SENT',
              'SHIPPING_ADDRESS_EDITED',
              'EMAIL_EDITED',
              'PICKUP_READY_EMAIL_SENT',
              'CUSTOM_ACTIVITY',
              'MERCHANT_COMMENT',
              'ORDER_PARTIALLY_PAID',
              'DRAFT_ORDER_CHANGES_APPLIED',
              'SAVED_PAYMENT_METHOD',
              'AUTHORIZED_PAYMENT_CREATED',
              'AUTHORIZED_PAYMENT_CAPTURED',
              'AUTHORIZED_PAYMENT_VOIDED',
              'REFUND_INITIATED',
              'PAYMENT_REFUNDED',
              'PAYMENT_REFUND_FAILED',
              'REFUNDED_AS_STORE_CREDIT',
              'PAYMENT_PENDING',
              'PAYMENT_CANCELED',
              'PAYMENT_DECLINED',
              'ORDER_PENDING',
              'ORDER_REJECTED',
              'RECEIPT_CREATED',
              'RECEIPT_SENT',
              'CHARGEBACK_CREATED',
              'CHARGEBACK_REVERSED',
            ])
            .describe('Activity type.')
            .optional(),
          activityType: z
            .enum([
              'ORDER_PLACED',
              'ORDER_PAID',
              'ORDER_FULFILLED',
              'ORDER_NOT_FULFILLED',
              'ORDER_CANCELED',
              'DOWNLOAD_LINK_SENT',
              'TRACKING_NUMBER_ADDED',
              'TRACKING_NUMBER_EDITED',
              'TRACKING_LINK_ADDED',
              'SHIPPING_CONFIRMATION_EMAIL_SENT',
              'INVOICE_ADDED',
              'INVOICE_REMOVED',
              'INVOICE_SENT',
              'FULFILLER_EMAIL_SENT',
              'SHIPPING_ADDRESS_EDITED',
              'EMAIL_EDITED',
              'PICKUP_READY_EMAIL_SENT',
              'MERCHANT_COMMENT',
              'ORDER_PARTIALLY_PAID',
              'DRAFT_ORDER_CHANGES_APPLIED',
              'SAVED_PAYMENT_METHOD',
              'AUTHORIZED_PAYMENT_CREATED',
              'AUTHORIZED_PAYMENT_CAPTURED',
              'AUTHORIZED_PAYMENT_VOIDED',
              'REFUND_INITIATED',
              'PAYMENT_REFUNDED',
              'PAYMENT_REFUND_FAILED',
              'REFUNDED_AS_STORE_CREDIT',
              'PAYMENT_PENDING',
              'PAYMENT_CANCELED',
              'PAYMENT_DECLINED',
              'ORDER_PENDING',
              'ORDER_REJECTED',
              'RECEIPT_CREATED',
              'RECEIPT_SENT',
              'CHARGEBACK_CREATED',
              'CHARGEBACK_REVERSED',
              'ORDER_REFUNDED',
            ])
            .describe('Activity type.')
            .optional(),
        }),
        z.xor([
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
          }),
          z.object({
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            merchantComment: z
              .object({
                message: z
                  .string()
                  .describe('Merchant comment message.')
                  .min(1)
                  .max(5000)
                  .optional(),
              })
              .describe(
                'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            orderRefunded: z
              .object({
                manual: z
                  .boolean()
                  .describe(
                    'Whether order was refunded manually. For example, via payment provider or using cash.'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(200)
                  .optional(),
              })
              .describe(
                'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            draftOrderChangesApplied: z
              .object({
                draftOrderId: z.string().describe('Draft order id.').optional(),
                reason: z
                  .string()
                  .describe('Reason for edit, given by user (optional).')
                  .max(200)
                  .optional()
                  .nullable(),
                changes: z
                  .array(
                    z.intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                        }),
                        z.object({
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemChanged: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name after change.')
                              .optional(),
                            quantity: z
                              .object({
                                originalQuantity: z
                                  .number()
                                  .int()
                                  .describe('Item quantity before update.')
                                  .min(0)
                                  .max(1000000)
                                  .optional(),
                                newQuantity: z
                                  .number()
                                  .int()
                                  .describe('Item quantity after update.')
                                  .min(0)
                                  .max(1000000)
                                  .optional(),
                                diff: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Difference between original and new quantity. Absolute value.'
                                  )
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                                deltaType: z
                                  .enum([
                                    'QUANTITY_INCREASED',
                                    'QUANTITY_DECREASED',
                                  ])
                                  .describe(
                                    'Type of quantity change: increase or decrease.'
                                  )
                                  .optional(),
                              })
                              .describe('Item quantity change.')
                              .optional(),
                            price: z
                              .object({
                                originalPrice: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item price before update.')
                                  .optional(),
                                newPrice: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item price after update.')
                                  .optional(),
                              })
                              .describe('Item price change.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemAdded: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name.')
                              .optional(),
                            quantity: z
                              .number()
                              .int()
                              .describe('Added or removed item quantity.')
                              .min(1)
                              .max(1000000)
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name.')
                              .optional(),
                            quantity: z
                              .number()
                              .int()
                              .describe('Added or removed item quantity.')
                              .min(1)
                              .max(1000000)
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          discountAdded: z.object({
                            _id: z
                              .string()
                              .describe('Discount id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Discount name: coupon name / discount rule name / merchant discount description.'
                              )
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Discount amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          discountRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Discount id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Discount name: coupon name / discount rule name / merchant discount description.'
                              )
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Discount amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          additionalFeeAdded: z.object({
                            _id: z
                              .string()
                              .describe('Additional fee id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Additional fee name.')
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Additional fee amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          additionalFeeRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Additional fee id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Additional fee name.')
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Additional fee amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          totalPriceChanged: z.object({
                            originalTotal: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Order’s total price after discounts and tax. Before update'
                              )
                              .optional(),
                            newTotal: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Order’s total price after discounts and tax. After update'
                              )
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.object({
                            originalShippingInfo: z
                              .object({
                                total: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Order’s shipping price.')
                                  .optional(),
                                shippingTitle: z
                                  .string()
                                  .describe('Order’s shipping title.')
                                  .max(250)
                                  .optional(),
                              })
                              .describe(
                                'Order’s Shipping Information. Before update'
                              )
                              .optional(),
                            newShippingInfo: z
                              .object({
                                total: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Order’s shipping price.')
                                  .optional(),
                                shippingTitle: z
                                  .string()
                                  .describe('Order’s shipping title.')
                                  .max(250)
                                  .optional(),
                              })
                              .describe(
                                'Order’s Shipping Information. After update'
                              )
                              .optional(),
                          }),
                        }),
                      ])
                    )
                  )
                  .optional(),
              })
              .describe('Details of changes made by the Draft Orders API.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            savedPaymentMethod: z
              .object({
                name: z
                  .string()
                  .describe('Payment method name.')
                  .min(1)
                  .max(100)
                  .optional(),
                description: z
                  .string()
                  .describe('Payment method description.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of the payment method saved for the order.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentCreated: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment created.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentCaptured: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment captured.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentVoided: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment voided.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            refundInitiated: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                payments: z
                  .array(
                    z.intersection(
                      z.object({
                        paymentId: z
                          .string()
                          .describe('Payment ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        externalRefund: z
                          .boolean()
                          .describe(
                            "Whether refund was made externally and manually on the payment provider's side."
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          regular: z.never().optional(),
                          giftCard: z.never().optional(),
                          membership: z.never().optional(),
                        }),
                        z.object({
                          giftCard: z.never().optional(),
                          membership: z.never().optional(),
                          regular: z
                            .object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Refund amount.')
                                .optional(),
                              lastFourDigits: z
                                .string()
                                .describe(
                                  'The last 4 digits of the card number.'
                                )
                                .max(4)
                                .optional()
                                .nullable(),
                              brand: z
                                .string()
                                .describe("Card issuer's brand.")
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe('Regular payment refund.'),
                        }),
                        z.object({
                          regular: z.never().optional(),
                          membership: z.never().optional(),
                          giftCard: z
                            .object({
                              giftCardPaymentId: z
                                .string()
                                .describe('Gift card payment ID.')
                                .min(1)
                                .max(100)
                                .optional()
                                .nullable(),
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Refund amount.')
                                .optional(),
                            })
                            .describe('Gift card payment refund.'),
                        }),
                        z.object({
                          regular: z.never().optional(),
                          giftCard: z.never().optional(),
                          membership: z
                            .object({
                              membershipId: z
                                .string()
                                .describe('Membership ID.')
                                .min(1)
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe('Membership payment refund.'),
                        }),
                      ])
                    )
                  )
                  .min(1)
                  .max(50)
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(200)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentRefunded: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                payment: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe('Payment ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      externalRefund: z
                        .boolean()
                        .describe(
                          "Whether refund was made externally and manually on the payment provider's side."
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                      }),
                      z.object({
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                        regular: z
                          .object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Regular payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        membership: z.never().optional(),
                        giftCard: z
                          .object({
                            giftCardPaymentId: z
                              .string()
                              .describe('Gift card payment ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                          })
                          .describe('Gift card payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z
                          .object({
                            membershipId: z
                              .string()
                              .describe('Membership ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Membership payment refund.'),
                      }),
                    ])
                  )
                  .describe('Details about the refunded payment.')
                  .optional(),
              })
              .describe(
                'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentRefundFailed: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                payment: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe('Payment ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      externalRefund: z
                        .boolean()
                        .describe(
                          "Whether refund was made externally and manually on the payment provider's side."
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                      }),
                      z.object({
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                        regular: z
                          .object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Regular payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        membership: z.never().optional(),
                        giftCard: z
                          .object({
                            giftCardPaymentId: z
                              .string()
                              .describe('Gift card payment ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                          })
                          .describe('Gift card payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z
                          .object({
                            membershipId: z
                              .string()
                              .describe('Membership ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Membership payment refund.'),
                      }),
                    ])
                  )
                  .describe('Details about the failed payment refund.')
                  .optional(),
              })
              .describe(
                'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            refundedAsStoreCredit: z
              .object({
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of refund to store credit.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentPending: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a pending payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentCanceled: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a canceled payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentDeclined: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a declined payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            receiptCreated: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z.never().optional(),
                  }),
                  z.object({
                    externalReceipt: z.never().optional(),
                    wixReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by Wix.'),
                  }),
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .max(100)
                          .optional()
                          .nullable(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by an external system.'),
                  }),
                ])
              )
              .describe('Receipt was added for associated payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            receiptSent: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z.never().optional(),
                  }),
                  z.object({
                    externalReceipt: z.never().optional(),
                    wixReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by Wix.'),
                  }),
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .max(100)
                          .optional()
                          .nullable(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by an external system.'),
                  }),
                ])
              )
              .describe('Receipt sent to customer.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackReversed: z.never().optional(),
            chargebackCreated: z
              .object({
                paymentId: z
                  .string()
                  .describe('ID of the payment that received a chargeback.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargebackId: z
                  .string()
                  .describe('Chargeback ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback amount.')
                  .optional(),
                paymentDetails: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({ creditCardDetails: z.never().optional() }),
                      z.object({
                        creditCardDetails: z
                          .object({
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Credit card details.'),
                      }),
                    ])
                  )
                  .describe('Payment details.')
                  .optional(),
              })
              .describe('Order received a chargeback for one of its payments.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z
              .object({
                paymentId: z
                  .string()
                  .describe('ID of the payment involved with the chargeback.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargebackId: z
                  .string()
                  .describe('Chargeback ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback amount.')
                  .optional(),
                reversalAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback reversal amount.')
                  .optional(),
                paymentDetails: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({ creditCardDetails: z.never().optional() }),
                      z.object({
                        creditCardDetails: z
                          .object({
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Credit card details.'),
                      }),
                    ])
                  )
                  .describe('Payment details.')
                  .optional(),
              })
              .describe("Chargeback reversed for one of the order's payments."),
          }),
        ])
      )
    )
    .max(1000)
    .optional(),
  attributionSource: z
    .enum(['UNSPECIFIED', 'FACEBOOK_ADS'])
    .describe('Order attribution source.')
    .optional(),
  createdBy: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
          userId: z
            .string()
            .describe(
              'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
          memberId: z
            .string()
            .describe(
              'Member ID - when the order was created by a **logged in** site visitor.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          appId: z.never().optional(),
          visitorId: z
            .string()
            .describe(
              'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z
            .string()
            .describe(
              'App ID - when the order was created by an external application.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe("ID of the order's initiator.")
    .optional(),
  channelInfo: z
    .object({
      type: z
        .enum([
          'UNSPECIFIED',
          'WEB',
          'POS',
          'EBAY',
          'AMAZON',
          'OTHER_PLATFORM',
          'WIX_APP_STORE',
          'WIX_INVOICES',
          'BACKOFFICE_MERCHANT',
          'WISH',
          'CLASS_PASS',
          'GLOBAL_E',
          'FACEBOOK',
          'ETSY',
          'TIKTOK',
          'FAIRE_COM',
          'PAYPAL_AGENTIC_CHECKOUT',
          'STRIPE_AGENTIC_CHECKOUT',
        ])
        .describe(
          'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
        )
        .optional(),
      externalOrderId: z
        .string()
        .describe(
          'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
        )
        .max(100)
        .optional()
        .nullable(),
      externalOrderUrl: z
        .string()
        .describe(
          'URL to the order in the external system. Relevant when recording orders from external platforms.'
        )
        .max(300)
        .optional()
        .nullable(),
    })
    .describe('Information about the sales channel that submitted this order.')
    .optional(),
  seenByAHuman: z
    .boolean()
    .describe(
      'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
    )
    .optional()
    .nullable(),
  checkoutId: z
    .string()
    .describe('Checkout ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  customFields: z
    .array(
      z.object({
        value: z.any().describe('Custom field value.').optional().nullable(),
        title: z
          .string()
          .describe('Custom field title.')
          .min(1)
          .max(500)
          .optional(),
        translatedTitle: z
          .string()
          .describe('Translated custom field title.')
          .min(1)
          .max(500)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  balanceSummary: z
    .object({
      balance: z
        .object({
          amount: z
            .string()
            .describe(
              'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
            )
            .optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Current amount left to pay.')
        .optional(),
      paid: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
        )
        .optional(),
      refunded: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all successfully refunded payments.')
        .optional(),
      authorized: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all authorized payments.')
        .optional(),
      pendingRefund: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all pending refund transactions.')
        .optional(),
      pending: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all pending transactions.')
        .optional(),
      chargeback: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all transaction chargebacks.')
        .optional(),
      chargebackReversal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all chargeback reversals.')
        .optional(),
      platformFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
        )
        .optional(),
      totalMinusPlatformFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
        )
        .optional(),
    })
    .describe('Balance summary.')
    .optional(),
  additionalFees: z
    .array(
      z.object({
        code: z
          .string()
          .describe("Additional fee's unique code for future processing.")
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Name of additional fee.')
          .min(1)
          .max(50)
          .optional(),
        price: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price.")
          .optional(),
        taxDetails: z
          .object({
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Taxable amount of this line item.')
              .optional(),
            taxRate: z
              .string()
              .describe(
                'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
              )
              .optional(),
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The calculated tax, based on the `taxableAmount` and `taxRate`.'
              )
              .optional(),
          })
          .describe('Tax details.')
          .optional(),
        taxInfo: z
          .object({
            taxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Calculated tax, based on `taxable_amount` and `tax_rate`.'
              )
              .optional(),
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Amount for which tax is calculated.')
              .optional(),
            taxRate: z
              .string()
              .describe('Tax rate %, as a decimal point.')
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxIncludedInPrice: z
              .boolean()
              .describe('Indicates whether the price already includes tax.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  jurisdiction: z
                    .string()
                    .describe(
                      'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                    )
                    .optional()
                    .nullable(),
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount of tax calculated for this line item.')
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'Type of jurisdiction that taxes were calculated for.'
                    )
                    .optional(),
                  nonTaxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Non-taxable amount of the line item price.')
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of the line item price.')
                    .optional(),
                })
              )
              .max(7)
              .optional(),
          })
          .describe(
            'Represents all the relevant tax details for additional fee.'
          )
          .optional(),
        providerAppId: z
          .string()
          .describe("SPI implementer's `appId`.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        priceBeforeTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price before tax.")
          .optional(),
        priceAfterTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price after tax.")
          .optional(),
        _id: z
          .string()
          .describe("Additional fee's id.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        lineItemIds: z.array(z.string()).optional(),
        source: z
          .enum(['SERVICE_PLUGIN', 'ITEM', 'MANUAL', 'SHIPPING', 'PLATFORM'])
          .describe('Specifies the entity that added the additional fee.')
          .optional(),
      })
    )
    .max(100)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  purchaseFlowId: z
    .string()
    .describe(
      'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  recipientInfo: z
    .object({
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      contactDetails: z
        .object({
          firstName: z
            .string()
            .describe('First name.')
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Last name.')
            .max(100)
            .optional()
            .nullable(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          company: z
            .string()
            .describe('Company name.')
            .max(1000)
            .optional()
            .nullable(),
          vatId: z
            .object({
              _id: z.string().describe("Customer's tax ID.").optional(),
              type: z
                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                .describe(
                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                )
                .optional(),
            })
            .describe(
              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
            )
            .optional(),
        })
        .describe('Contact details.')
        .optional(),
    })
    .describe(
      "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
    )
    .optional(),
  purchasedDate: z
    .date()
    .describe(
      'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
    )
    .optional()
    .nullable(),
  businessLocation: z
    .object({
      _id: z
        .string()
        .describe(
          'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z.string().describe('Location name.').min(1).max(500).optional(),
    })
    .describe('Order location.')
    .optional(),
  payAfterFreeTrial: z
    .object({
      subtotal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Subtotal of all line items, before discounts and before tax.'
        )
        .optional(),
      shipping: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total shipping price, before discounts and before tax.')
        .optional(),
      tax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax applied to the order.')
        .optional(),
      discount: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total discount amount applied to the order.')
        .optional(),
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe("Order's total price after discounts and tax.")
        .optional(),
      totalAdditionalFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total additional fees before tax.')
        .optional(),
    })
    .describe(
      'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
    )
    .optional(),
  platformFeeSummary: z
    .object({
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total sum of all platform fees.')
        .optional(),
      totalPassOn: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total amount of platform fees with `PASS_ON` charge type.')
        .optional(),
      totalAbsorbed: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total amount of platform fees with `ABSORBED` charge type.')
        .optional(),
      fees: z
        .array(
          z.object({
            name: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Platform fee name.')
              .optional(),
            amount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Platform fee amount.')
              .optional(),
            lineItemId: z
              .string()
              .describe('ID of the line item the platform fee applies to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            chargeType: z
              .enum(['PASS_ON', 'ABSORBED'])
              .describe('Platform fee charge type.')
              .optional(),
            percentageRate: z
              .string()
              .describe(
                'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
              )
              .optional(),
          })
        )
        .max(300)
        .optional(),
    })
    .describe(
      'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
    )
    .optional(),
});
export const UpdateOrderRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      number: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          "Order number displayed in the site owner's dashboard (auto-generated)."
        )
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      lineItems: z
        .array(
          z.object({
            _id: z.string().describe('Line item ID.').optional(),
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
              )
              .optional(),
            catalogReference: z
              .object({
                catalogItemId: z
                  .string()
                  .describe('ID of the item within the catalog it belongs to.')
                  .min(1)
                  .max(36)
                  .optional(),
                appId: z
                  .string()
                  .describe(
                    'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                  )
                  .min(1)
                  .optional(),
                options: z
                  .record(z.string(), z.any())
                  .describe(
                    'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "References to the line item's origin catalog. Empty for custom line items."
              )
              .optional(),
            quantity: z
              .number()
              .int()
              .describe('Line item quantity.')
              .min(1)
              .max(100000)
              .optional(),
            totalDiscount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Total discount for this line item's entire quantity.")
              .optional(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .min(0)
              .max(20)
              .optional(),
            image: z.string().describe('Line item image.').optional(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe(
                'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
              )
              .optional(),
            itemType: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    preset: z.never().optional(),
                    custom: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    preset: z
                      .enum([
                        'UNRECOGNISED',
                        'PHYSICAL',
                        'DIGITAL',
                        'GIFT_CARD',
                        'SERVICE',
                      ])
                      .describe('Preset item type.'),
                  }),
                  z.object({
                    preset: z.never().optional(),
                    custom: z
                      .string()
                      .describe(
                        'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                      ),
                  }),
                ])
              )
              .describe('Item type. Either a preset type or custom.')
              .optional(),
            fulfillerId: z
              .string()
              .describe(
                'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            refundQuantity: z
              .number()
              .int()
              .describe('Number of items that were refunded.')
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            restockQuantity: z
              .number()
              .int()
              .describe(
                'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
              )
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            price: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Line item price after line item discounts for display purposes.'
              )
              .optional(),
            priceBeforeDiscounts: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
              )
              .optional(),
            totalPriceBeforeTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total price after discounts, and before tax.')
              .optional(),
            totalPriceAfterTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total price after all discounts and tax.')
              .optional(),
            paymentOption: z
              .enum([
                'FULL_PAYMENT_ONLINE',
                'FULL_PAYMENT_OFFLINE',
                'MEMBERSHIP',
                'DEPOSIT_ONLINE',
                'MEMBERSHIP_OFFLINE',
                'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
              ])
              .optional(),
            taxDetails: z
              .object({
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Taxable amount of this line item.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                  )
                  .optional(),
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                  )
                  .optional(),
              })
              .describe(
                'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
              )
              .optional(),
            taxInfo: z
              .object({
                taxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                  )
                  .optional(),
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Amount for which tax is calculated.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe('Tax rate %, as a decimal point.')
                  .optional()
                  .nullable(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                taxIncludedInPrice: z
                  .boolean()
                  .describe('Indicates whether the price already includes tax.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      jurisdiction: z
                        .string()
                        .describe(
                          'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                        )
                        .optional()
                        .nullable(),
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Amount of tax calculated for this line item.'
                        )
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .optional(),
                      nonTaxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Non-taxable amount of the line item price.')
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of the line item price.')
                        .optional(),
                    })
                  )
                  .max(7)
                  .optional(),
              })
              .describe(
                'Represents all the relevant tax details for a specific line item.'
              )
              .optional(),
            digitalFile: z
              .object({
                fileId: z
                  .string()
                  .describe('ID of the secure file in media.')
                  .min(1)
                  .max(100)
                  .optional(),
                link: z
                  .string()
                  .describe(
                    'Link will exist after the digital links have been generated on the order.'
                  )
                  .url()
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                expirationDate: z
                  .date()
                  .describe('Link expiration time and date.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Digital file identifier, relevant only for items with type DIGITAL.'
              )
              .optional(),
            subscriptionInfo: z
              .object({
                _id: z
                  .string()
                  .describe('Subscription ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                cycleNumber: z
                  .number()
                  .int()
                  .describe(
                    'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
                  )
                  .min(0)
                  .optional(),
                subscriptionOptionTitle: z
                  .string()
                  .describe(
                    'Subscription option title. For example, `"Monthly coffee Subscription"`.'
                  )
                  .min(0)
                  .max(150)
                  .optional(),
                subscriptionOptionDescription: z
                  .string()
                  .describe(
                    'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                subscriptionSettings: z
                  .object({
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                      )
                      .min(1)
                      .max(3650)
                      .optional()
                      .nullable(),
                    autoRenewal: z
                      .boolean()
                      .describe(
                        'Whether subscription is renewed automatically at the end of each period.'
                      )
                      .optional(),
                    billingCycles: z
                      .number()
                      .int()
                      .describe(
                        'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    enableCustomerCancellation: z
                      .boolean()
                      .describe(
                        'Whether to allow the customer to cancel the subscription.'
                      )
                      .optional(),
                    freeTrialPeriod: z
                      .object({
                        frequency: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .optional(),
                        interval: z
                          .number()
                          .int()
                          .describe(
                            'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                          )
                          .min(1)
                          .max(3650)
                          .optional(),
                      })
                      .describe(
                        'Period until first cycle starts. If None => no free trial'
                      )
                      .optional(),
                  })
                  .describe('Subscription detailed information.')
                  .optional(),
                chargesDescription: z
                  .string()
                  .describe(
                    'Description of the charges that will be applied for subscription.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
              .describe('Subscription info.')
              .optional(),
            priceDescription: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                  )
                  .min(1)
                  .max(100)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
              )
              .optional(),
            depositAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
              )
              .optional(),
            locations: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Location id in the associated owner app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'Location owner app, if not provided then the site business info locations will be used.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe('Quantity for specific location.')
                    .min(1)
                    .max(100000)
                    .optional(),
                  name: z
                    .string()
                    .describe('Location name.')
                    .max(500)
                    .optional()
                    .nullable(),
                })
              )
              .min(0)
              .max(5)
              .optional(),
            lineItemPrice: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total price **after** catalog discounts and line item discounts.'
              )
              .optional(),
            taxableAddress: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ addressType: z.never().optional() }),
                  z.object({
                    addressType: z
                      .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                      .describe(
                        'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                      ),
                  }),
                ])
              )
              .describe('Address used for tax calculation.')
              .optional(),
            priceUndetermined: z
              .boolean()
              .describe(
                'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            modifierGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Modifier group ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  name: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Modifier group name.')
                    .optional(),
                  modifiers: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Modifier ID.')
                          .min(1)
                          .max(36)
                          .optional(),
                        quantity: z
                          .number()
                          .int()
                          .describe('The quantity of this modifier.')
                          .min(1)
                          .max(100000)
                          .optional()
                          .nullable(),
                        label: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Primary display label for the modifier.')
                          .optional(),
                        details: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Additional details.')
                          .optional(),
                        price: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('The price of the modifier.')
                          .optional(),
                      })
                    )
                    .min(1)
                    .max(10)
                    .optional(),
                })
              )
              .max(10)
              .optional(),
          })
        )
        .min(1)
        .max(300)
        .optional(),
      buyerInfo: z
        .intersection(
          z.object({
            contactId: z
              .string()
              .describe(
                "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            email: z
              .string()
              .describe("Buyer's email address.")
              .email()
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              visitorId: z.never().optional(),
              memberId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              visitorId: z
                .string()
                .describe(
                  "Visitor ID. Returned when the buyer isn't a logged-in site member."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              visitorId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID. Returned when the buyer is a logged-in site member.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe('Buyer information.')
        .optional(),
      paymentStatus: z
        .enum([
          'UNSPECIFIED',
          'NOT_PAID',
          'PAID',
          'PARTIALLY_REFUNDED',
          'FULLY_REFUNDED',
          'PENDING',
          'PARTIALLY_PAID',
          'PENDING_MERCHANT',
          'CANCELED',
          'DECLINED',
        ])
        .optional(),
      fulfillmentStatus: z
        .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
        .optional(),
      buyerLanguage: z
        .string()
        .describe(
          'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
        )
        .optional()
        .nullable(),
      weightUnit: z.enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB']).optional(),
      currency: z
        .string()
        .describe(
          'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
        )
        .optional()
        .nullable(),
      currencyConversionDetails: z
        .object({
          originalCurrency: z
            .string()
            .describe(
              "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
            )
            .optional()
            .nullable(),
          conversionRate: z
            .string()
            .describe(
              'The rate used for converting the original currency to the currency used for payment.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Currency conversion details. For use with multi-currency sites.'
        )
        .optional(),
      taxIncludedInPrices: z
        .boolean()
        .describe('Whether tax is included in line item prices.')
        .optional(),
      siteLanguage: z
        .string()
        .describe('Site language in which original values are shown.')
        .optional()
        .nullable(),
      priceSummary: z
        .object({
          subtotal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Subtotal of all line items, before discounts and before tax.'
            )
            .optional(),
          shipping: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total shipping price, before discounts and before tax.')
            .optional(),
          tax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax applied to the order.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total discount amount applied to the order.')
            .optional(),
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe("Order's total price after discounts and tax.")
            .optional(),
          totalAdditionalFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total additional fees before tax.')
            .optional(),
        })
        .describe('Order price summary.')
        .optional(),
      billingInfo: z
        .object({
          address: z
            .object({
              streetAddress: z
                .object({
                  name: z.string().optional(),
                  number: z.string().optional(),
                })
                .optional(),
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Address.')
            .optional(),
          contactDetails: z
            .object({
              firstName: z
                .string()
                .describe('First name.')
                .max(100)
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe('Last name.')
                .max(100)
                .optional()
                .nullable(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              company: z
                .string()
                .describe('Company name.')
                .max(1000)
                .optional()
                .nullable(),
              vatId: z
                .object({
                  _id: z.string().describe("Customer's tax ID.").optional(),
                  type: z.enum(['UNSPECIFIED', 'CPF', 'CNPJ']).optional(),
                })
                .describe(
                  'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                )
                .optional(),
            })
            .describe('Contact details.')
            .optional(),
        })
        .describe('Billing address and contact details.')
        .optional(),
      shippingInfo: z
        .object({
          carrierId: z
            .string()
            .describe(
              'App Def Id of external provider which was a source of shipping info'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          code: z
            .string()
            .describe(
              'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          title: z
            .string()
            .describe(
              'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
            )
            .max(250)
            .optional(),
          logistics: z
            .intersection(
              z.object({
                deliveryTime: z
                  .string()
                  .describe(
                    'Expected delivery time in free text. For example, `"3-5 business days"`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                instructions: z
                  .string()
                  .describe(
                    'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                deliverByDate: z
                  .date()
                  .describe(
                    'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                  )
                  .optional()
                  .nullable(),
                deliveryTimeSlot: z
                  .object({
                    from: z
                      .date()
                      .describe('Delivery slot starting time.')
                      .optional()
                      .nullable(),
                    to: z
                      .date()
                      .describe('Delivery slot ending time.')
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Expected delivery time slot with start and end times.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  shippingDestination: z.never().optional(),
                  pickupDetails: z.never().optional(),
                }),
                z.object({
                  pickupDetails: z.never().optional(),
                  shippingDestination: z
                    .object({
                      address: z
                        .object({
                          streetAddress: z
                            .object({
                              name: z.string().optional(),
                              number: z.string().optional(),
                            })
                            .optional(),
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Address.')
                        .optional(),
                      contactDetails: z
                        .object({
                          firstName: z
                            .string()
                            .describe('First name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          lastName: z
                            .string()
                            .describe('Last name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          phone: z
                            .string()
                            .describe('Phone number.')
                            .optional()
                            .nullable(),
                          company: z
                            .string()
                            .describe('Company name.')
                            .max(1000)
                            .optional()
                            .nullable(),
                          vatId: z
                            .object({
                              _id: z
                                .string()
                                .describe("Customer's tax ID.")
                                .optional(),
                              type: z
                                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                                .optional(),
                            })
                            .describe(
                              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                            )
                            .optional(),
                        })
                        .describe('Contact details.')
                        .optional(),
                    })
                    .describe(
                      "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                    ),
                }),
                z.object({
                  shippingDestination: z.never().optional(),
                  pickupDetails: z
                    .object({
                      address: z
                        .object({
                          streetAddress: z
                            .object({
                              name: z.string().optional(),
                              number: z.string().optional(),
                            })
                            .optional(),
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Pickup address.')
                        .optional(),
                      pickupMethod: z
                        .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                        .describe('Pickup method')
                        .optional(),
                    })
                    .describe(
                      'Pickup details for store pickup or pickup point orders.'
                    ),
                }),
              ])
            )
            .describe('Shipping logistics.')
            .optional(),
          cost: z
            .object({
              price: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Shipping price for display purposes.')
                .optional(),
              totalPriceBeforeTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Total price of shipping after discounts (when relevant), and before tax.'
                )
                .optional(),
              totalPriceAfterTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Shipping price after all discounts (if any exist), and after tax.'
                )
                .optional(),
              taxDetails: z
                .object({
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of this line item.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                    )
                    .optional(),
                  totalTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                    )
                    .optional(),
                })
                .describe('Tax details.')
                .optional(),
              taxInfo: z
                .object({
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                    )
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount for which tax is calculated.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax rate %, as a decimal point.')
                    .optional()
                    .nullable(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  taxIncludedInPrice: z
                    .boolean()
                    .describe(
                      'Indicates whether the price already includes tax.'
                    )
                    .optional(),
                  taxBreakdown: z
                    .array(
                      z.object({
                        jurisdiction: z
                          .string()
                          .describe(
                            'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        rate: z
                          .string()
                          .describe(
                            'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                          )
                          .optional()
                          .nullable(),
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Amount of tax calculated for this line item.'
                          )
                          .optional(),
                        taxType: z
                          .string()
                          .describe(
                            'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        taxName: z
                          .string()
                          .describe(
                            'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        jurisdictionType: z
                          .enum([
                            'UNDEFINED',
                            'COUNTRY',
                            'STATE',
                            'COUNTY',
                            'CITY',
                            'SPECIAL',
                          ])
                          .optional(),
                        nonTaxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Non-taxable amount of the line item price.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of the line item price.')
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe(
                  'Represents all the relevant tax details for a shipping.'
                )
                .optional(),
              discount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Shipping discount before tax.')
                .optional(),
            })
            .describe('Shipping costs.')
            .optional(),
          region: z
            .object({
              name: z
                .string()
                .describe(
                  'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Shipping region.')
            .optional(),
        })
        .describe('Shipping info and selected shipping option details.')
        .optional(),
      buyerNote: z
        .string()
        .describe(
          '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
        )
        .max(1000)
        .optional()
        .nullable(),
      status: z
        .enum(['INITIALIZED', 'APPROVED', 'CANCELED', 'PENDING', 'REJECTED'])
        .optional(),
      archived: z
        .boolean()
        .describe('Whether order is archived.')
        .optional()
        .nullable(),
      taxSummary: z
        .object({
          totalTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax.')
            .optional(),
        })
        .describe(
          'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
        )
        .optional(),
      taxInfo: z
        .object({
          totalTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Calculated tax, added from line items.')
            .optional(),
          taxBreakdown: z
            .array(
              z.object({
                taxName: z
                  .string()
                  .describe(
                    'The name of the tax against which this tax amount was calculated.'
                  )
                  .max(200)
                  .optional(),
                taxType: z
                  .string()
                  .describe(
                    "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
                  )
                  .max(200)
                  .optional(),
                jurisdiction: z
                  .string()
                  .describe(
                    'The name of the jurisdiction in which this tax detail applies.'
                  )
                  .max(200)
                  .optional(),
                jurisdictionType: z
                  .enum([
                    'UNDEFINED',
                    'COUNTRY',
                    'STATE',
                    'COUNTY',
                    'CITY',
                    'SPECIAL',
                  ])
                  .optional(),
                rate: z
                  .string()
                  .describe('The rate at which this tax detail was calculated.')
                  .optional(),
                aggregatedTaxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The sum of all the tax from line items that calculated by the tax identifiers.'
                  )
                  .optional(),
              })
            )
            .min(0)
            .max(50)
            .optional(),
          taxExempt: z
            .boolean()
            .describe(
              'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
        })
        .describe('Tax information.')
        .optional(),
      appliedDiscounts: z
        .array(
          z.intersection(
            z.object({
              discountType: z
                .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
                .optional(),
              lineItemIds: z.array(z.string()).min(0).max(300).optional(),
              _id: z
                .string()
                .describe('Discount ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              lineItemDiscounts: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Line item ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    totalDiscount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Total discount amount for this line item.')
                      .optional(),
                  })
                )
                .max(300)
                .optional(),
            }),
            z.xor([
              z.object({
                coupon: z.never().optional(),
                merchantDiscount: z.never().optional(),
                discountRule: z.never().optional(),
              }),
              z.object({
                merchantDiscount: z.never().optional(),
                discountRule: z.never().optional(),
                coupon: z
                  .object({
                    _id: z.string().describe('Coupon ID.').optional(),
                    code: z.string().describe('Coupon code.').optional(),
                    name: z.string().describe('Coupon name.').optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Coupon value.')
                      .optional(),
                  })
                  .describe('Coupon applied by the customer.'),
              }),
              z.object({
                coupon: z.never().optional(),
                discountRule: z.never().optional(),
                merchantDiscount: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Discount amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        discountReason: z.never().optional(),
                        description: z.never().optional(),
                      }),
                      z.object({
                        description: z.never().optional(),
                        discountReason: z
                          .enum([
                            'UNSPECIFIED',
                            'EXCHANGED_ITEMS',
                            'BILLING_ADJUSTMENT',
                          ])
                          .describe('Predefined discount reason.'),
                      }),
                      z.object({
                        discountReason: z.never().optional(),
                        description: z
                          .string()
                          .describe('Custom discount description as free text.')
                          .max(200),
                      }),
                    ])
                  )
                  .describe('Discount applied manually by the merchant.'),
              }),
              z.object({
                coupon: z.never().optional(),
                merchantDiscount: z.never().optional(),
                discountRule: z
                  .object({
                    _id: z
                      .string()
                      .describe('Discount rule ID')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Original discount rule name (in site's default language)."
                          )
                          .min(1)
                          .max(256)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                          )
                          .min(1)
                          .max(500)
                          .optional()
                          .nullable(),
                      })
                      .describe('Discount rule name')
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Discount value.')
                      .optional(),
                  })
                  .describe(
                    'Automatic discount applied by the system based on configured discount rules.'
                  ),
              }),
            ])
          )
        )
        .max(320)
        .optional(),
      activities: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Activity ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              authorEmail: z
                .string()
                .describe("Activity author's email.")
                .email()
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Activity creation date and time.')
                .optional()
                .nullable(),
              type: z
                .enum([
                  'ORDER_REFUNDED',
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'CUSTOM_ACTIVITY',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                ])
                .optional(),
              activityType: z
                .enum([
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                  'ORDER_REFUNDED',
                ])
                .optional(),
            }),
            z.xor([
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
              }),
              z.object({
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                merchantComment: z
                  .object({
                    message: z
                      .string()
                      .describe('Merchant comment message.')
                      .min(1)
                      .max(5000)
                      .optional(),
                  })
                  .describe(
                    'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                orderRefunded: z
                  .object({
                    manual: z
                      .boolean()
                      .describe(
                        'Whether order was refunded manually. For example, via payment provider or using cash.'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                draftOrderChangesApplied: z
                  .object({
                    draftOrderId: z
                      .string()
                      .describe('Draft order id.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for edit, given by user (optional).')
                      .max(200)
                      .optional()
                      .nullable(),
                    changes: z
                      .array(
                        z.intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                            }),
                            z.object({
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemChanged: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name after change.')
                                  .optional(),
                                quantity: z
                                  .object({
                                    originalQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity before update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    newQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity after update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    diff: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Difference between original and new quantity. Absolute value.'
                                      )
                                      .min(1)
                                      .max(1000000)
                                      .optional(),
                                    deltaType: z
                                      .enum([
                                        'QUANTITY_INCREASED',
                                        'QUANTITY_DECREASED',
                                      ])
                                      .describe(
                                        'Type of quantity change: increase or decrease.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item quantity change.')
                                  .optional(),
                                price: z
                                  .object({
                                    originalPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price before update.')
                                      .optional(),
                                    newPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price after update.')
                                      .optional(),
                                  })
                                  .describe('Item price change.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        ),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        ),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        ),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        ),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              totalPriceChanged: z.object({
                                originalTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. Before update'
                                  )
                                  .optional(),
                                newTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.object({
                                originalShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. Before update'
                                  )
                                  .optional(),
                                newShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                          ])
                        )
                      )
                      .optional(),
                  })
                  .describe('Details of changes made by the Draft Orders API.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                savedPaymentMethod: z
                  .object({
                    name: z
                      .string()
                      .describe('Payment method name.')
                      .min(1)
                      .max(100)
                      .optional(),
                    description: z
                      .string()
                      .describe('Payment method description.')
                      .min(1)
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of the payment method saved for the order.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment created.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCaptured: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment captured.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentVoided: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment voided.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundInitiated: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    payments: z
                      .array(
                        z.intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe('Payment ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            externalRefund: z
                              .boolean()
                              .describe(
                                "Whether refund was made externally and manually on the payment provider's side."
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                            }),
                            z.object({
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                              regular: z
                                .object({
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Regular payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              membership: z.never().optional(),
                              giftCard: z
                                .object({
                                  giftCardPaymentId: z
                                    .string()
                                    .describe('Gift card payment ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                })
                                .describe('Gift card payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z
                                .object({
                                  membershipId: z
                                    .string()
                                    .describe('Membership ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Membership payment refund.'),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(50)
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefunded: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the refunded payment.')
                      .optional(),
                  })
                  .describe(
                    'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefundFailed: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the failed payment refund.')
                      .optional(),
                  })
                  .describe(
                    'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundedAsStoreCredit: z
                  .object({
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of refund to store credit.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentPending: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a pending payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentCanceled: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a canceled payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentDeclined: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a declined payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptCreated: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt was added for associated payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptSent: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt sent to customer.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackReversed: z.never().optional(),
                chargebackCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe('ID of the payment that received a chargeback.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    'Order received a chargeback for one of its payments.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'ID of the payment involved with the chargeback.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    reversalAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback reversal amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    "Chargeback reversed for one of the order's payments."
                  ),
              }),
            ])
          )
        )
        .max(1000)
        .optional(),
      attributionSource: z.enum(['UNSPECIFIED', 'FACEBOOK_ADS']).optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID - when the order was created by a **logged in** site visitor.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              appId: z.never().optional(),
              visitorId: z
                .string()
                .describe(
                  'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID - when the order was created by an external application.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe("ID of the order's initiator.")
        .optional(),
      channelInfo: z
        .object({
          type: z
            .enum([
              'UNSPECIFIED',
              'WEB',
              'POS',
              'EBAY',
              'AMAZON',
              'OTHER_PLATFORM',
              'WIX_APP_STORE',
              'WIX_INVOICES',
              'BACKOFFICE_MERCHANT',
              'WISH',
              'CLASS_PASS',
              'GLOBAL_E',
              'FACEBOOK',
              'ETSY',
              'TIKTOK',
              'FAIRE_COM',
              'PAYPAL_AGENTIC_CHECKOUT',
              'STRIPE_AGENTIC_CHECKOUT',
            ])
            .optional(),
          externalOrderId: z
            .string()
            .describe(
              'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
            )
            .max(100)
            .optional()
            .nullable(),
          externalOrderUrl: z
            .string()
            .describe(
              'URL to the order in the external system. Relevant when recording orders from external platforms.'
            )
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Information about the sales channel that submitted this order.'
        )
        .optional(),
      seenByAHuman: z
        .boolean()
        .describe(
          'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
        )
        .optional()
        .nullable(),
      checkoutId: z
        .string()
        .describe('Checkout ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      customFields: z
        .array(
          z.object({
            value: z
              .any()
              .describe('Custom field value.')
              .optional()
              .nullable(),
            title: z
              .string()
              .describe('Custom field title.')
              .min(1)
              .max(500)
              .optional(),
            translatedTitle: z
              .string()
              .describe('Translated custom field title.')
              .min(1)
              .max(500)
              .optional()
              .nullable(),
          })
        )
        .optional(),
      balanceSummary: z
        .object({
          balance: z
            .object({
              amount: z
                .string()
                .describe(
                  'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
                )
                .optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Current amount left to pay.')
            .optional(),
          paid: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
            )
            .optional(),
          refunded: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all successfully refunded payments.')
            .optional(),
          authorized: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all authorized payments.')
            .optional(),
          pendingRefund: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all pending refund transactions.')
            .optional(),
          pending: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all pending transactions.')
            .optional(),
          chargeback: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all transaction chargebacks.')
            .optional(),
          chargebackReversal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all chargeback reversals.')
            .optional(),
          platformFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
            )
            .optional(),
          totalMinusPlatformFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
            )
            .optional(),
        })
        .describe('Balance summary.')
        .optional(),
      additionalFees: z
        .array(
          z.object({
            code: z
              .string()
              .describe("Additional fee's unique code for future processing.")
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of additional fee.')
              .min(1)
              .max(50)
              .optional(),
            price: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price.")
              .optional(),
            taxDetails: z
              .object({
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Taxable amount of this line item.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                  )
                  .optional(),
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                  )
                  .optional(),
              })
              .describe('Tax details.')
              .optional(),
            taxInfo: z
              .object({
                taxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                  )
                  .optional(),
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Amount for which tax is calculated.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe('Tax rate %, as a decimal point.')
                  .optional()
                  .nullable(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                taxIncludedInPrice: z
                  .boolean()
                  .describe('Indicates whether the price already includes tax.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      jurisdiction: z
                        .string()
                        .describe(
                          'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                        )
                        .optional()
                        .nullable(),
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Amount of tax calculated for this line item.'
                        )
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .optional(),
                      nonTaxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Non-taxable amount of the line item price.')
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of the line item price.')
                        .optional(),
                    })
                  )
                  .max(7)
                  .optional(),
              })
              .describe(
                'Represents all the relevant tax details for additional fee.'
              )
              .optional(),
            providerAppId: z
              .string()
              .describe("SPI implementer's `appId`.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            priceBeforeTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price before tax.")
              .optional(),
            priceAfterTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price after tax.")
              .optional(),
            _id: z
              .string()
              .describe("Additional fee's id.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            lineItemIds: z.array(z.string()).optional(),
            source: z
              .enum([
                'SERVICE_PLUGIN',
                'ITEM',
                'MANUAL',
                'SHIPPING',
                'PLATFORM',
              ])
              .optional(),
          })
        )
        .max(100)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      purchaseFlowId: z
        .string()
        .describe(
          'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      recipientInfo: z
        .object({
          address: z
            .object({
              streetAddress: z
                .object({
                  name: z.string().optional(),
                  number: z.string().optional(),
                })
                .optional(),
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Address.')
            .optional(),
          contactDetails: z
            .object({
              firstName: z
                .string()
                .describe('First name.')
                .max(100)
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe('Last name.')
                .max(100)
                .optional()
                .nullable(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              company: z
                .string()
                .describe('Company name.')
                .max(1000)
                .optional()
                .nullable(),
              vatId: z
                .object({
                  _id: z.string().describe("Customer's tax ID.").optional(),
                  type: z.enum(['UNSPECIFIED', 'CPF', 'CNPJ']).optional(),
                })
                .describe(
                  'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                )
                .optional(),
            })
            .describe('Contact details.')
            .optional(),
        })
        .describe(
          "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
        )
        .optional(),
      purchasedDate: z
        .date()
        .describe(
          'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
        )
        .optional()
        .nullable(),
      businessLocation: z
        .object({
          _id: z
            .string()
            .describe(
              'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Location name.')
            .min(1)
            .max(500)
            .optional(),
        })
        .describe('Order location.')
        .optional(),
      payAfterFreeTrial: z
        .object({
          subtotal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Subtotal of all line items, before discounts and before tax.'
            )
            .optional(),
          shipping: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total shipping price, before discounts and before tax.')
            .optional(),
          tax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax applied to the order.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total discount amount applied to the order.')
            .optional(),
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe("Order's total price after discounts and tax.")
            .optional(),
          totalAdditionalFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total additional fees before tax.')
            .optional(),
        })
        .describe(
          'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
        )
        .optional(),
      platformFeeSummary: z
        .object({
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total sum of all platform fees.')
            .optional(),
          totalPassOn: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total amount of platform fees with `PASS_ON` charge type.'
            )
            .optional(),
          totalAbsorbed: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total amount of platform fees with `ABSORBED` charge type.'
            )
            .optional(),
          fees: z
            .array(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                      )
                      .min(1)
                      .max(400)
                      .optional()
                      .nullable(),
                  })
                  .describe('Platform fee name.')
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Platform fee amount.')
                  .optional(),
                lineItemId: z
                  .string()
                  .describe('ID of the line item the platform fee applies to.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargeType: z.enum(['PASS_ON', 'ABSORBED']).optional(),
                percentageRate: z
                  .string()
                  .describe(
                    'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
                  )
                  .optional(),
              })
            )
            .max(300)
            .optional(),
        })
        .describe(
          'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
        )
        .optional(),
    })
    .describe('Order to be updated.'),
});
export const UpdateOrderResponse = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  number: z
    .string()
    .regex(/^\d+$/, 'Must be a valid UInt64 string')
    .describe(
      "Order number displayed in the site owner's dashboard (auto-generated)."
    )
    .optional(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  lineItems: z
    .array(
      z.object({
        _id: z.string().describe('Line item ID.').optional(),
        productName: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
              )
              .min(1)
              .max(200)
              .optional(),
            translated: z
              .string()
              .describe(
                "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
              )
              .min(1)
              .max(400)
              .optional()
              .nullable(),
          })
          .describe(
            'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
          )
          .optional(),
        catalogReference: z
          .object({
            catalogItemId: z
              .string()
              .describe('ID of the item within the catalog it belongs to.')
              .min(1)
              .max(36)
              .optional(),
            appId: z
              .string()
              .describe(
                'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
              )
              .min(1)
              .optional(),
            options: z
              .record(z.string(), z.any())
              .describe(
                'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "References to the line item's origin catalog. Empty for custom line items."
          )
          .optional(),
        quantity: z
          .number()
          .int()
          .describe('Line item quantity.')
          .min(1)
          .max(100000)
          .optional(),
        totalDiscount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Total discount for this line item's entire quantity.")
          .optional(),
        descriptionLines: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                      )
                      .max(100)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe('Description line name.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z.never().optional(),
                }),
                z.object({
                  colorInfo: z.never().optional(),
                  plainText: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(600)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(600)
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line plain text value.'),
                }),
                z.object({
                  plainText: z.never().optional(),
                  colorInfo: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .max(500)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      code: z
                        .string()
                        .describe('HEX or RGB color code for display.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Description line color value.'),
                }),
              ])
            )
          )
          .min(0)
          .max(20)
          .optional(),
        image: z.string().describe('Line item image.').optional(),
        physicalProperties: z
          .object({
            weight: z
              .number()
              .describe(
                'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
              )
              .optional()
              .nullable(),
            sku: z
              .string()
              .describe(
                'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
              )
              .max(40)
              .optional()
              .nullable(),
            shippable: z
              .boolean()
              .describe('Whether this line item is shippable.')
              .optional(),
          })
          .describe(
            'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
          )
          .optional(),
        itemType: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                preset: z.never().optional(),
                custom: z.never().optional(),
              }),
              z.object({
                custom: z.never().optional(),
                preset: z
                  .enum([
                    'UNRECOGNISED',
                    'PHYSICAL',
                    'DIGITAL',
                    'GIFT_CARD',
                    'SERVICE',
                  ])
                  .describe('Preset item type.'),
              }),
              z.object({
                preset: z.never().optional(),
                custom: z
                  .string()
                  .describe(
                    'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                  ),
              }),
            ])
          )
          .describe('Item type. Either a preset type or custom.')
          .optional(),
        fulfillerId: z
          .string()
          .describe(
            'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        refundQuantity: z
          .number()
          .int()
          .describe('Number of items that were refunded.')
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        restockQuantity: z
          .number()
          .int()
          .describe(
            'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        price: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Line item price after line item discounts for display purposes.'
          )
          .optional(),
        priceBeforeDiscounts: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
          )
          .optional(),
        totalPriceBeforeTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe('Total price after discounts, and before tax.')
          .optional(),
        totalPriceAfterTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe('Total price after all discounts and tax.')
          .optional(),
        paymentOption: z
          .enum([
            'FULL_PAYMENT_ONLINE',
            'FULL_PAYMENT_OFFLINE',
            'MEMBERSHIP',
            'DEPOSIT_ONLINE',
            'MEMBERSHIP_OFFLINE',
            'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
          ])
          .describe(
            'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
          )
          .optional(),
        taxDetails: z
          .object({
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Taxable amount of this line item.')
              .optional(),
            taxRate: z
              .string()
              .describe(
                'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
              )
              .optional(),
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The calculated tax, based on the `taxableAmount` and `taxRate`.'
              )
              .optional(),
          })
          .describe(
            'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
          )
          .optional(),
        taxInfo: z
          .object({
            taxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Calculated tax, based on `taxable_amount` and `tax_rate`.'
              )
              .optional(),
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Amount for which tax is calculated.')
              .optional(),
            taxRate: z
              .string()
              .describe('Tax rate %, as a decimal point.')
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxIncludedInPrice: z
              .boolean()
              .describe('Indicates whether the price already includes tax.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  jurisdiction: z
                    .string()
                    .describe(
                      'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                    )
                    .optional()
                    .nullable(),
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount of tax calculated for this line item.')
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'Type of jurisdiction that taxes were calculated for.'
                    )
                    .optional(),
                  nonTaxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Non-taxable amount of the line item price.')
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of the line item price.')
                    .optional(),
                })
              )
              .max(7)
              .optional(),
          })
          .describe(
            'Represents all the relevant tax details for a specific line item.'
          )
          .optional(),
        digitalFile: z
          .object({
            fileId: z
              .string()
              .describe('ID of the secure file in media.')
              .min(1)
              .max(100)
              .optional(),
            link: z
              .string()
              .describe(
                'Link will exist after the digital links have been generated on the order.'
              )
              .url()
              .min(1)
              .max(2000)
              .optional()
              .nullable(),
            expirationDate: z
              .date()
              .describe('Link expiration time and date.')
              .optional()
              .nullable(),
          })
          .describe(
            'Digital file identifier, relevant only for items with type DIGITAL.'
          )
          .optional(),
        subscriptionInfo: z
          .object({
            _id: z
              .string()
              .describe('Subscription ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            cycleNumber: z
              .number()
              .int()
              .describe(
                'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
              )
              .min(0)
              .optional(),
            subscriptionOptionTitle: z
              .string()
              .describe(
                'Subscription option title. For example, `"Monthly coffee Subscription"`.'
              )
              .min(0)
              .max(150)
              .optional(),
            subscriptionOptionDescription: z
              .string()
              .describe(
                'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
              )
              .max(500)
              .optional()
              .nullable(),
            subscriptionSettings: z
              .object({
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .describe('Frequency of recurring payment.')
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                  )
                  .min(1)
                  .max(3650)
                  .optional()
                  .nullable(),
                autoRenewal: z
                  .boolean()
                  .describe(
                    'Whether subscription is renewed automatically at the end of each period.'
                  )
                  .optional(),
                billingCycles: z
                  .number()
                  .int()
                  .describe(
                    'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                  )
                  .min(1)
                  .optional()
                  .nullable(),
                enableCustomerCancellation: z
                  .boolean()
                  .describe(
                    'Whether to allow the customer to cancel the subscription.'
                  )
                  .optional(),
                freeTrialPeriod: z
                  .object({
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe(
                        'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                      )
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                      )
                      .min(1)
                      .max(3650)
                      .optional(),
                  })
                  .describe(
                    'Period until first cycle starts. If None => no free trial'
                  )
                  .optional(),
              })
              .describe('Subscription detailed information.')
              .optional(),
            chargesDescription: z
              .string()
              .describe(
                'Description of the charges that will be applied for subscription.'
              )
              .max(1000)
              .optional()
              .nullable(),
          })
          .describe('Subscription info.')
          .optional(),
        priceDescription: z
          .object({
            original: z
              .string()
              .describe(
                "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
              )
              .min(1)
              .max(100)
              .optional(),
            translated: z
              .string()
              .describe(
                "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
          })
          .describe(
            'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
          )
          .optional(),
        depositAmount: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
          )
          .optional(),
        locations: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Location id in the associated owner app.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              appId: z
                .string()
                .describe(
                  'Location owner app, if not provided then the site business info locations will be used.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe('Quantity for specific location.')
                .min(1)
                .max(100000)
                .optional(),
              name: z
                .string()
                .describe('Location name.')
                .max(500)
                .optional()
                .nullable(),
            })
          )
          .min(0)
          .max(5)
          .optional(),
        lineItemPrice: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe(
            'Total price **after** catalog discounts and line item discounts.'
          )
          .optional(),
        taxableAddress: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ addressType: z.never().optional() }),
              z.object({
                addressType: z
                  .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                  .describe(
                    'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                  ),
              }),
            ])
          )
          .describe('Address used for tax calculation.')
          .optional(),
        priceUndetermined: z
          .boolean()
          .describe(
            'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        modifierGroups: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Modifier group ID.')
                .min(1)
                .max(36)
                .optional(),
              name: z
                .object({
                  original: z
                    .string()
                    .describe(
                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                    )
                    .min(1)
                    .max(200)
                    .optional(),
                  translated: z
                    .string()
                    .describe(
                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                    )
                    .min(1)
                    .max(400)
                    .optional()
                    .nullable(),
                })
                .describe('Modifier group name.')
                .optional(),
              modifiers: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Modifier ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe('The quantity of this modifier.')
                      .min(1)
                      .max(100000)
                      .optional()
                      .nullable(),
                    label: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Primary display label for the modifier.')
                      .optional(),
                    details: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                          )
                          .min(1)
                          .max(200)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                          )
                          .min(1)
                          .max(400)
                          .optional()
                          .nullable(),
                      })
                      .describe('Additional details.')
                      .optional(),
                    price: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('The price of the modifier.')
                      .optional(),
                  })
                )
                .min(1)
                .max(10)
                .optional(),
            })
          )
          .max(10)
          .optional(),
      })
    )
    .min(1)
    .max(300)
    .optional(),
  buyerInfo: z
    .intersection(
      z.object({
        contactId: z
          .string()
          .describe(
            "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        email: z
          .string()
          .describe("Buyer's email address.")
          .email()
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          visitorId: z.never().optional(),
          memberId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          visitorId: z
            .string()
            .describe(
              "Visitor ID. Returned when the buyer isn't a logged-in site member."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          visitorId: z.never().optional(),
          memberId: z
            .string()
            .describe(
              'Member ID. Returned when the buyer is a logged-in site member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe('Buyer information.')
    .optional(),
  paymentStatus: z
    .enum([
      'UNSPECIFIED',
      'NOT_PAID',
      'PAID',
      'PARTIALLY_REFUNDED',
      'FULLY_REFUNDED',
      'PENDING',
      'PARTIALLY_PAID',
      'PENDING_MERCHANT',
      'CANCELED',
      'DECLINED',
    ])
    .describe('Order payment status.')
    .optional(),
  fulfillmentStatus: z
    .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
    .describe('Order fulfillment status.')
    .optional(),
  buyerLanguage: z
    .string()
    .describe(
      'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
    )
    .optional()
    .nullable(),
  weightUnit: z
    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
    .describe("Weight measurement unit - defaults to site's weight unit.")
    .optional(),
  currency: z
    .string()
    .describe(
      'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
    )
    .optional()
    .nullable(),
  currencyConversionDetails: z
    .object({
      originalCurrency: z
        .string()
        .describe(
          "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
        )
        .optional()
        .nullable(),
      conversionRate: z
        .string()
        .describe(
          'The rate used for converting the original currency to the currency used for payment.'
        )
        .optional()
        .nullable(),
    })
    .describe('Currency conversion details. For use with multi-currency sites.')
    .optional(),
  taxIncludedInPrices: z
    .boolean()
    .describe('Whether tax is included in line item prices.')
    .optional(),
  siteLanguage: z
    .string()
    .describe('Site language in which original values are shown.')
    .optional()
    .nullable(),
  priceSummary: z
    .object({
      subtotal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Subtotal of all line items, before discounts and before tax.'
        )
        .optional(),
      shipping: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total shipping price, before discounts and before tax.')
        .optional(),
      tax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax applied to the order.')
        .optional(),
      discount: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total discount amount applied to the order.')
        .optional(),
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe("Order's total price after discounts and tax.")
        .optional(),
      totalAdditionalFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total additional fees before tax.')
        .optional(),
    })
    .describe('Order price summary.')
    .optional(),
  billingInfo: z
    .object({
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      contactDetails: z
        .object({
          firstName: z
            .string()
            .describe('First name.')
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Last name.')
            .max(100)
            .optional()
            .nullable(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          company: z
            .string()
            .describe('Company name.')
            .max(1000)
            .optional()
            .nullable(),
          vatId: z
            .object({
              _id: z.string().describe("Customer's tax ID.").optional(),
              type: z
                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                .describe(
                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                )
                .optional(),
            })
            .describe(
              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
            )
            .optional(),
        })
        .describe('Contact details.')
        .optional(),
    })
    .describe('Billing address and contact details.')
    .optional(),
  shippingInfo: z
    .object({
      carrierId: z
        .string()
        .describe(
          'App Def Id of external provider which was a source of shipping info'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      code: z
        .string()
        .describe(
          'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      title: z
        .string()
        .describe(
          'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
        )
        .max(250)
        .optional(),
      logistics: z
        .intersection(
          z.object({
            deliveryTime: z
              .string()
              .describe(
                'Expected delivery time in free text. For example, `"3-5 business days"`.'
              )
              .max(500)
              .optional()
              .nullable(),
            instructions: z
              .string()
              .describe(
                'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
              )
              .max(1000)
              .optional()
              .nullable(),
            deliverByDate: z
              .date()
              .describe(
                'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            deliveryTimeSlot: z
              .object({
                from: z
                  .date()
                  .describe('Delivery slot starting time.')
                  .optional()
                  .nullable(),
                to: z
                  .date()
                  .describe('Delivery slot ending time.')
                  .optional()
                  .nullable(),
              })
              .describe('Expected delivery time slot with start and end times.')
              .optional(),
          }),
          z.xor([
            z.object({
              shippingDestination: z.never().optional(),
              pickupDetails: z.never().optional(),
            }),
            z.object({
              pickupDetails: z.never().optional(),
              shippingDestination: z
                .object({
                  address: z
                    .object({
                      streetAddress: z
                        .object({
                          name: z.string().optional(),
                          number: z.string().optional(),
                        })
                        .optional(),
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe('Address.')
                    .optional(),
                  contactDetails: z
                    .object({
                      firstName: z
                        .string()
                        .describe('First name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe('Last name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Phone number.')
                        .optional()
                        .nullable(),
                      company: z
                        .string()
                        .describe('Company name.')
                        .max(1000)
                        .optional()
                        .nullable(),
                      vatId: z
                        .object({
                          _id: z
                            .string()
                            .describe("Customer's tax ID.")
                            .optional(),
                          type: z
                            .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                            .describe(
                              'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                        )
                        .optional(),
                    })
                    .describe('Contact details.')
                    .optional(),
                })
                .describe(
                  "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                ),
            }),
            z.object({
              shippingDestination: z.never().optional(),
              pickupDetails: z
                .object({
                  address: z
                    .object({
                      streetAddress: z
                        .object({
                          name: z.string().optional(),
                          number: z.string().optional(),
                        })
                        .optional(),
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe('Pickup address.')
                    .optional(),
                  pickupMethod: z
                    .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                    .describe('Pickup method')
                    .optional(),
                })
                .describe(
                  'Pickup details for store pickup or pickup point orders.'
                ),
            }),
          ])
        )
        .describe('Shipping logistics.')
        .optional(),
      cost: z
        .object({
          price: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Shipping price for display purposes.')
            .optional(),
          totalPriceBeforeTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total price of shipping after discounts (when relevant), and before tax.'
            )
            .optional(),
          totalPriceAfterTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Shipping price after all discounts (if any exist), and after tax.'
            )
            .optional(),
          taxDetails: z
            .object({
              taxableAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Taxable amount of this line item.')
                .optional(),
              taxRate: z
                .string()
                .describe(
                  'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                )
                .optional(),
              totalTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                )
                .optional(),
            })
            .describe('Tax details.')
            .optional(),
          taxInfo: z
            .object({
              taxAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                )
                .optional(),
              taxableAmount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Amount for which tax is calculated.')
                .optional(),
              taxRate: z
                .string()
                .describe('Tax rate %, as a decimal point.')
                .optional()
                .nullable(),
              taxGroupId: z
                .string()
                .describe('Tax group ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              taxIncludedInPrice: z
                .boolean()
                .describe('Indicates whether the price already includes tax.')
                .optional(),
              taxBreakdown: z
                .array(
                  z.object({
                    jurisdiction: z
                      .string()
                      .describe(
                        'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    rate: z
                      .string()
                      .describe(
                        'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                      )
                      .optional()
                      .nullable(),
                    taxAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Amount of tax calculated for this line item.')
                      .optional(),
                    taxType: z
                      .string()
                      .describe(
                        'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    taxName: z
                      .string()
                      .describe(
                        'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                      )
                      .max(200)
                      .optional()
                      .nullable(),
                    jurisdictionType: z
                      .enum([
                        'UNDEFINED',
                        'COUNTRY',
                        'STATE',
                        'COUNTY',
                        'CITY',
                        'SPECIAL',
                      ])
                      .describe(
                        'Type of jurisdiction that taxes were calculated for.'
                      )
                      .optional(),
                    nonTaxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Non-taxable amount of the line item price.')
                      .optional(),
                    taxableAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Taxable amount of the line item price.')
                      .optional(),
                  })
                )
                .max(7)
                .optional(),
            })
            .describe('Represents all the relevant tax details for a shipping.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Shipping discount before tax.')
            .optional(),
        })
        .describe('Shipping costs.')
        .optional(),
      region: z
        .object({
          name: z
            .string()
            .describe(
              'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
            )
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Shipping region.')
        .optional(),
    })
    .describe('Shipping info and selected shipping option details.')
    .optional(),
  buyerNote: z
    .string()
    .describe(
      '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
    )
    .max(1000)
    .optional()
    .nullable(),
  status: z
    .enum(['INITIALIZED', 'APPROVED', 'CANCELED', 'PENDING', 'REJECTED'])
    .describe('Order status.')
    .optional(),
  archived: z
    .boolean()
    .describe('Whether order is archived.')
    .optional()
    .nullable(),
  taxSummary: z
    .object({
      totalTax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax.')
        .optional(),
    })
    .describe(
      'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
    )
    .optional(),
  taxInfo: z
    .object({
      totalTax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Calculated tax, added from line items.')
        .optional(),
      taxBreakdown: z
        .array(
          z.object({
            taxName: z
              .string()
              .describe(
                'The name of the tax against which this tax amount was calculated.'
              )
              .max(200)
              .optional(),
            taxType: z
              .string()
              .describe(
                "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
              )
              .max(200)
              .optional(),
            jurisdiction: z
              .string()
              .describe(
                'The name of the jurisdiction in which this tax detail applies.'
              )
              .max(200)
              .optional(),
            jurisdictionType: z
              .enum([
                'UNDEFINED',
                'COUNTRY',
                'STATE',
                'COUNTY',
                'CITY',
                'SPECIAL',
              ])
              .describe(
                'The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special).'
              )
              .optional(),
            rate: z
              .string()
              .describe('The rate at which this tax detail was calculated.')
              .optional(),
            aggregatedTaxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The sum of all the tax from line items that calculated by the tax identifiers.'
              )
              .optional(),
          })
        )
        .min(0)
        .max(50)
        .optional(),
      taxExempt: z
        .boolean()
        .describe(
          'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
    })
    .describe('Tax information.')
    .optional(),
  appliedDiscounts: z
    .array(
      z.intersection(
        z.object({
          discountType: z
            .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
            .optional(),
          lineItemIds: z.array(z.string()).min(0).max(300).optional(),
          _id: z
            .string()
            .describe('Discount ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          lineItemDiscounts: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Line item ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                totalDiscount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total discount amount for this line item.')
                  .optional(),
              })
            )
            .max(300)
            .optional(),
        }),
        z.xor([
          z.object({
            coupon: z.never().optional(),
            merchantDiscount: z.never().optional(),
            discountRule: z.never().optional(),
          }),
          z.object({
            merchantDiscount: z.never().optional(),
            discountRule: z.never().optional(),
            coupon: z
              .object({
                _id: z.string().describe('Coupon ID.').optional(),
                code: z.string().describe('Coupon code.').optional(),
                name: z.string().describe('Coupon name.').optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Coupon value.')
                  .optional(),
              })
              .describe('Coupon applied by the customer.'),
          }),
          z.object({
            coupon: z.never().optional(),
            discountRule: z.never().optional(),
            merchantDiscount: z
              .intersection(
                z.object({
                  amount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Discount amount.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    discountReason: z.never().optional(),
                    description: z.never().optional(),
                  }),
                  z.object({
                    description: z.never().optional(),
                    discountReason: z
                      .enum([
                        'UNSPECIFIED',
                        'EXCHANGED_ITEMS',
                        'BILLING_ADJUSTMENT',
                      ])
                      .describe('Predefined discount reason.'),
                  }),
                  z.object({
                    discountReason: z.never().optional(),
                    description: z
                      .string()
                      .describe('Custom discount description as free text.')
                      .max(200),
                  }),
                ])
              )
              .describe('Discount applied manually by the merchant.'),
          }),
          z.object({
            coupon: z.never().optional(),
            merchantDiscount: z.never().optional(),
            discountRule: z
              .object({
                _id: z
                  .string()
                  .describe('Discount rule ID')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "Original discount rule name (in site's default language)."
                      )
                      .min(1)
                      .max(256)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                      )
                      .min(1)
                      .max(500)
                      .optional()
                      .nullable(),
                  })
                  .describe('Discount rule name')
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Discount value.')
                  .optional(),
              })
              .describe(
                'Automatic discount applied by the system based on configured discount rules.'
              ),
          }),
        ])
      )
    )
    .max(320)
    .optional(),
  activities: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Activity ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorEmail: z
            .string()
            .describe("Activity author's email.")
            .email()
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Activity creation date and time.')
            .optional()
            .nullable(),
          type: z
            .enum([
              'ORDER_REFUNDED',
              'ORDER_PLACED',
              'ORDER_PAID',
              'ORDER_FULFILLED',
              'ORDER_NOT_FULFILLED',
              'ORDER_CANCELED',
              'DOWNLOAD_LINK_SENT',
              'TRACKING_NUMBER_ADDED',
              'TRACKING_NUMBER_EDITED',
              'TRACKING_LINK_ADDED',
              'SHIPPING_CONFIRMATION_EMAIL_SENT',
              'INVOICE_ADDED',
              'INVOICE_REMOVED',
              'INVOICE_SENT',
              'FULFILLER_EMAIL_SENT',
              'SHIPPING_ADDRESS_EDITED',
              'EMAIL_EDITED',
              'PICKUP_READY_EMAIL_SENT',
              'CUSTOM_ACTIVITY',
              'MERCHANT_COMMENT',
              'ORDER_PARTIALLY_PAID',
              'DRAFT_ORDER_CHANGES_APPLIED',
              'SAVED_PAYMENT_METHOD',
              'AUTHORIZED_PAYMENT_CREATED',
              'AUTHORIZED_PAYMENT_CAPTURED',
              'AUTHORIZED_PAYMENT_VOIDED',
              'REFUND_INITIATED',
              'PAYMENT_REFUNDED',
              'PAYMENT_REFUND_FAILED',
              'REFUNDED_AS_STORE_CREDIT',
              'PAYMENT_PENDING',
              'PAYMENT_CANCELED',
              'PAYMENT_DECLINED',
              'ORDER_PENDING',
              'ORDER_REJECTED',
              'RECEIPT_CREATED',
              'RECEIPT_SENT',
              'CHARGEBACK_CREATED',
              'CHARGEBACK_REVERSED',
            ])
            .describe('Activity type.')
            .optional(),
          activityType: z
            .enum([
              'ORDER_PLACED',
              'ORDER_PAID',
              'ORDER_FULFILLED',
              'ORDER_NOT_FULFILLED',
              'ORDER_CANCELED',
              'DOWNLOAD_LINK_SENT',
              'TRACKING_NUMBER_ADDED',
              'TRACKING_NUMBER_EDITED',
              'TRACKING_LINK_ADDED',
              'SHIPPING_CONFIRMATION_EMAIL_SENT',
              'INVOICE_ADDED',
              'INVOICE_REMOVED',
              'INVOICE_SENT',
              'FULFILLER_EMAIL_SENT',
              'SHIPPING_ADDRESS_EDITED',
              'EMAIL_EDITED',
              'PICKUP_READY_EMAIL_SENT',
              'MERCHANT_COMMENT',
              'ORDER_PARTIALLY_PAID',
              'DRAFT_ORDER_CHANGES_APPLIED',
              'SAVED_PAYMENT_METHOD',
              'AUTHORIZED_PAYMENT_CREATED',
              'AUTHORIZED_PAYMENT_CAPTURED',
              'AUTHORIZED_PAYMENT_VOIDED',
              'REFUND_INITIATED',
              'PAYMENT_REFUNDED',
              'PAYMENT_REFUND_FAILED',
              'REFUNDED_AS_STORE_CREDIT',
              'PAYMENT_PENDING',
              'PAYMENT_CANCELED',
              'PAYMENT_DECLINED',
              'ORDER_PENDING',
              'ORDER_REJECTED',
              'RECEIPT_CREATED',
              'RECEIPT_SENT',
              'CHARGEBACK_CREATED',
              'CHARGEBACK_REVERSED',
              'ORDER_REFUNDED',
            ])
            .describe('Activity type.')
            .optional(),
        }),
        z.xor([
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
          }),
          z.object({
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            merchantComment: z
              .object({
                message: z
                  .string()
                  .describe('Merchant comment message.')
                  .min(1)
                  .max(5000)
                  .optional(),
              })
              .describe(
                'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            orderRefunded: z
              .object({
                manual: z
                  .boolean()
                  .describe(
                    'Whether order was refunded manually. For example, via payment provider or using cash.'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(200)
                  .optional(),
              })
              .describe(
                'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            draftOrderChangesApplied: z
              .object({
                draftOrderId: z.string().describe('Draft order id.').optional(),
                reason: z
                  .string()
                  .describe('Reason for edit, given by user (optional).')
                  .max(200)
                  .optional()
                  .nullable(),
                changes: z
                  .array(
                    z.intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                        }),
                        z.object({
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemChanged: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name after change.')
                              .optional(),
                            quantity: z
                              .object({
                                originalQuantity: z
                                  .number()
                                  .int()
                                  .describe('Item quantity before update.')
                                  .min(0)
                                  .max(1000000)
                                  .optional(),
                                newQuantity: z
                                  .number()
                                  .int()
                                  .describe('Item quantity after update.')
                                  .min(0)
                                  .max(1000000)
                                  .optional(),
                                diff: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Difference between original and new quantity. Absolute value.'
                                  )
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                                deltaType: z
                                  .enum([
                                    'QUANTITY_INCREASED',
                                    'QUANTITY_DECREASED',
                                  ])
                                  .describe(
                                    'Type of quantity change: increase or decrease.'
                                  )
                                  .optional(),
                              })
                              .describe('Item quantity change.')
                              .optional(),
                            price: z
                              .object({
                                originalPrice: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item price before update.')
                                  .optional(),
                                newPrice: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item price after update.')
                                  .optional(),
                              })
                              .describe('Item price change.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemAdded: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name.')
                              .optional(),
                            quantity: z
                              .number()
                              .int()
                              .describe('Added or removed item quantity.')
                              .min(1)
                              .max(1000000)
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          lineItemRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Line item ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                  )
                                  .min(1)
                                  .max(200)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                  )
                                  .min(1)
                                  .max(400)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item name.')
                              .optional(),
                            quantity: z
                              .number()
                              .int()
                              .describe('Added or removed item quantity.')
                              .min(1)
                              .max(1000000)
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          discountAdded: z.object({
                            _id: z
                              .string()
                              .describe('Discount id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Discount name: coupon name / discount rule name / merchant discount description.'
                              )
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Discount amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          discountRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Discount id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Discount name: coupon name / discount rule name / merchant discount description.'
                              )
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Discount amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          additionalFeeAdded: z.object({
                            _id: z
                              .string()
                              .describe('Additional fee id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Additional fee name.')
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Additional fee amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          additionalFeeRemoved: z.object({
                            _id: z
                              .string()
                              .describe('Additional fee id.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .object({
                                original: z
                                  .string()
                                  .describe('Value in site default language.')
                                  .min(1)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe('Translated value.')
                                  .min(1)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Additional fee name.')
                              .optional(),
                            affectedLineItems: z
                              .array(
                                z.object({
                                  _id: z
                                    .string()
                                    .describe('Order line item id')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  name: z
                                    .object({
                                      original: z
                                        .string()
                                        .describe(
                                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                        )
                                        .min(1)
                                        .max(200)
                                        .optional(),
                                      translated: z
                                        .string()
                                        .describe(
                                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                        )
                                        .min(1)
                                        .max(400)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item name.')
                                    .optional(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                    )
                                    .optional(),
                                })
                              )
                              .min(0)
                              .optional(),
                            totalAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Additional fee amount.')
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          shippingInformationChanged: z.never().optional(),
                          totalPriceChanged: z.object({
                            originalTotal: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Order’s total price after discounts and tax. Before update'
                              )
                              .optional(),
                            newTotal: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Order’s total price after discounts and tax. After update'
                              )
                              .optional(),
                          }),
                        }),
                        z.object({
                          lineItemChanged: z.never().optional(),
                          lineItemAdded: z.never().optional(),
                          lineItemRemoved: z.never().optional(),
                          discountAdded: z.never().optional(),
                          discountRemoved: z.never().optional(),
                          additionalFeeAdded: z.never().optional(),
                          additionalFeeRemoved: z.never().optional(),
                          totalPriceChanged: z.never().optional(),
                          shippingInformationChanged: z.object({
                            originalShippingInfo: z
                              .object({
                                total: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Order’s shipping price.')
                                  .optional(),
                                shippingTitle: z
                                  .string()
                                  .describe('Order’s shipping title.')
                                  .max(250)
                                  .optional(),
                              })
                              .describe(
                                'Order’s Shipping Information. Before update'
                              )
                              .optional(),
                            newShippingInfo: z
                              .object({
                                total: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Order’s shipping price.')
                                  .optional(),
                                shippingTitle: z
                                  .string()
                                  .describe('Order’s shipping title.')
                                  .max(250)
                                  .optional(),
                              })
                              .describe(
                                'Order’s Shipping Information. After update'
                              )
                              .optional(),
                          }),
                        }),
                      ])
                    )
                  )
                  .optional(),
              })
              .describe('Details of changes made by the Draft Orders API.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            savedPaymentMethod: z
              .object({
                name: z
                  .string()
                  .describe('Payment method name.')
                  .min(1)
                  .max(100)
                  .optional(),
                description: z
                  .string()
                  .describe('Payment method description.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of the payment method saved for the order.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentCreated: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment created.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentCaptured: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment captured.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            authorizedPaymentVoided: z
              .object({
                paymentId: z
                  .string()
                  .describe(
                    'Payment ID of payment associated with this activity.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Payment amount.')
                  .optional(),
                lastFourDigits: z
                  .string()
                  .describe('The last 4 digits of the card number.')
                  .max(4)
                  .optional()
                  .nullable(),
                brand: z
                  .string()
                  .describe("Card issuer's brand.")
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of an authorized payment voided.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            refundInitiated: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                payments: z
                  .array(
                    z.intersection(
                      z.object({
                        paymentId: z
                          .string()
                          .describe('Payment ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        externalRefund: z
                          .boolean()
                          .describe(
                            "Whether refund was made externally and manually on the payment provider's side."
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          regular: z.never().optional(),
                          giftCard: z.never().optional(),
                          membership: z.never().optional(),
                        }),
                        z.object({
                          giftCard: z.never().optional(),
                          membership: z.never().optional(),
                          regular: z
                            .object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Refund amount.')
                                .optional(),
                              lastFourDigits: z
                                .string()
                                .describe(
                                  'The last 4 digits of the card number.'
                                )
                                .max(4)
                                .optional()
                                .nullable(),
                              brand: z
                                .string()
                                .describe("Card issuer's brand.")
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe('Regular payment refund.'),
                        }),
                        z.object({
                          regular: z.never().optional(),
                          membership: z.never().optional(),
                          giftCard: z
                            .object({
                              giftCardPaymentId: z
                                .string()
                                .describe('Gift card payment ID.')
                                .min(1)
                                .max(100)
                                .optional()
                                .nullable(),
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Refund amount.')
                                .optional(),
                            })
                            .describe('Gift card payment refund.'),
                        }),
                        z.object({
                          regular: z.never().optional(),
                          giftCard: z.never().optional(),
                          membership: z
                            .object({
                              membershipId: z
                                .string()
                                .describe('Membership ID.')
                                .min(1)
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe('Membership payment refund.'),
                        }),
                      ])
                    )
                  )
                  .min(1)
                  .max(50)
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(200)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentRefunded: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                payment: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe('Payment ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      externalRefund: z
                        .boolean()
                        .describe(
                          "Whether refund was made externally and manually on the payment provider's side."
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                      }),
                      z.object({
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                        regular: z
                          .object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Regular payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        membership: z.never().optional(),
                        giftCard: z
                          .object({
                            giftCardPaymentId: z
                              .string()
                              .describe('Gift card payment ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                          })
                          .describe('Gift card payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z
                          .object({
                            membershipId: z
                              .string()
                              .describe('Membership ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Membership payment refund.'),
                      }),
                    ])
                  )
                  .describe('Details about the refunded payment.')
                  .optional(),
              })
              .describe(
                'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentRefundFailed: z
              .object({
                refundId: z
                  .string()
                  .describe('Refund ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                payment: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe('Payment ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      externalRefund: z
                        .boolean()
                        .describe(
                          "Whether refund was made externally and manually on the payment provider's side."
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                      }),
                      z.object({
                        giftCard: z.never().optional(),
                        membership: z.never().optional(),
                        regular: z
                          .object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Regular payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        membership: z.never().optional(),
                        giftCard: z
                          .object({
                            giftCardPaymentId: z
                              .string()
                              .describe('Gift card payment ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Refund amount.')
                              .optional(),
                          })
                          .describe('Gift card payment refund.'),
                      }),
                      z.object({
                        regular: z.never().optional(),
                        giftCard: z.never().optional(),
                        membership: z
                          .object({
                            membershipId: z
                              .string()
                              .describe('Membership ID.')
                              .min(1)
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Membership payment refund.'),
                      }),
                    ])
                  )
                  .describe('Details about the failed payment refund.')
                  .optional(),
              })
              .describe(
                'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
              ),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            refundedAsStoreCredit: z
              .object({
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Refund amount.')
                  .optional(),
                reason: z
                  .string()
                  .describe('Reason for refund.')
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Details of refund to store credit.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentPending: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a pending payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentCanceled: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a canceled payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            paymentDeclined: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ regular: z.never().optional() }),
                  z.object({
                    regular: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Regular payment.'),
                  }),
                ])
              )
              .describe('Details of a declined payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            receiptCreated: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z.never().optional(),
                  }),
                  z.object({
                    externalReceipt: z.never().optional(),
                    wixReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by Wix.'),
                  }),
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .max(100)
                          .optional()
                          .nullable(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by an external system.'),
                  }),
                ])
              )
              .describe('Receipt was added for associated payment.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z.never().optional(),
            receiptSent: z
              .intersection(
                z.object({
                  paymentId: z
                    .string()
                    .describe(
                      'Payment ID of payment associated with this activity.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z.never().optional(),
                  }),
                  z.object({
                    externalReceipt: z.never().optional(),
                    wixReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by Wix.'),
                  }),
                  z.object({
                    wixReceipt: z.never().optional(),
                    externalReceipt: z
                      .object({
                        receiptId: z
                          .string()
                          .describe('Receipt ID.')
                          .max(100)
                          .optional()
                          .nullable(),
                        displayNumber: z
                          .string()
                          .describe('Display number of receipt.')
                          .min(1)
                          .max(40)
                          .optional()
                          .nullable(),
                      })
                      .describe('Receipt created by an external system.'),
                  }),
                ])
              )
              .describe('Receipt sent to customer.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackReversed: z.never().optional(),
            chargebackCreated: z
              .object({
                paymentId: z
                  .string()
                  .describe('ID of the payment that received a chargeback.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargebackId: z
                  .string()
                  .describe('Chargeback ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback amount.')
                  .optional(),
                paymentDetails: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({ creditCardDetails: z.never().optional() }),
                      z.object({
                        creditCardDetails: z
                          .object({
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Credit card details.'),
                      }),
                    ])
                  )
                  .describe('Payment details.')
                  .optional(),
              })
              .describe('Order received a chargeback for one of its payments.'),
          }),
          z.object({
            merchantComment: z.never().optional(),
            orderRefunded: z.never().optional(),
            draftOrderChangesApplied: z.never().optional(),
            savedPaymentMethod: z.never().optional(),
            authorizedPaymentCreated: z.never().optional(),
            authorizedPaymentCaptured: z.never().optional(),
            authorizedPaymentVoided: z.never().optional(),
            refundInitiated: z.never().optional(),
            paymentRefunded: z.never().optional(),
            paymentRefundFailed: z.never().optional(),
            refundedAsStoreCredit: z.never().optional(),
            paymentPending: z.never().optional(),
            paymentCanceled: z.never().optional(),
            paymentDeclined: z.never().optional(),
            receiptCreated: z.never().optional(),
            receiptSent: z.never().optional(),
            chargebackCreated: z.never().optional(),
            chargebackReversed: z
              .object({
                paymentId: z
                  .string()
                  .describe('ID of the payment involved with the chargeback.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargebackId: z
                  .string()
                  .describe('Chargeback ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback amount.')
                  .optional(),
                reversalAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Chargeback reversal amount.')
                  .optional(),
                paymentDetails: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Payment amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({ creditCardDetails: z.never().optional() }),
                      z.object({
                        creditCardDetails: z
                          .object({
                            lastFourDigits: z
                              .string()
                              .describe('The last 4 digits of the card number.')
                              .max(4)
                              .optional()
                              .nullable(),
                            brand: z
                              .string()
                              .describe("Card issuer's brand.")
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe('Credit card details.'),
                      }),
                    ])
                  )
                  .describe('Payment details.')
                  .optional(),
              })
              .describe("Chargeback reversed for one of the order's payments."),
          }),
        ])
      )
    )
    .max(1000)
    .optional(),
  attributionSource: z
    .enum(['UNSPECIFIED', 'FACEBOOK_ADS'])
    .describe('Order attribution source.')
    .optional(),
  createdBy: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
          userId: z
            .string()
            .describe(
              'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z.never().optional(),
          memberId: z
            .string()
            .describe(
              'Member ID - when the order was created by a **logged in** site visitor.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          appId: z.never().optional(),
          visitorId: z
            .string()
            .describe(
              'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          userId: z.never().optional(),
          memberId: z.never().optional(),
          visitorId: z.never().optional(),
          appId: z
            .string()
            .describe(
              'App ID - when the order was created by an external application.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe("ID of the order's initiator.")
    .optional(),
  channelInfo: z
    .object({
      type: z
        .enum([
          'UNSPECIFIED',
          'WEB',
          'POS',
          'EBAY',
          'AMAZON',
          'OTHER_PLATFORM',
          'WIX_APP_STORE',
          'WIX_INVOICES',
          'BACKOFFICE_MERCHANT',
          'WISH',
          'CLASS_PASS',
          'GLOBAL_E',
          'FACEBOOK',
          'ETSY',
          'TIKTOK',
          'FAIRE_COM',
          'PAYPAL_AGENTIC_CHECKOUT',
          'STRIPE_AGENTIC_CHECKOUT',
        ])
        .describe(
          'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
        )
        .optional(),
      externalOrderId: z
        .string()
        .describe(
          'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
        )
        .max(100)
        .optional()
        .nullable(),
      externalOrderUrl: z
        .string()
        .describe(
          'URL to the order in the external system. Relevant when recording orders from external platforms.'
        )
        .max(300)
        .optional()
        .nullable(),
    })
    .describe('Information about the sales channel that submitted this order.')
    .optional(),
  seenByAHuman: z
    .boolean()
    .describe(
      'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
    )
    .optional()
    .nullable(),
  checkoutId: z
    .string()
    .describe('Checkout ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  customFields: z
    .array(
      z.object({
        value: z.any().describe('Custom field value.').optional().nullable(),
        title: z
          .string()
          .describe('Custom field title.')
          .min(1)
          .max(500)
          .optional(),
        translatedTitle: z
          .string()
          .describe('Translated custom field title.')
          .min(1)
          .max(500)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  balanceSummary: z
    .object({
      balance: z
        .object({
          amount: z
            .string()
            .describe(
              'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
            )
            .optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Current amount left to pay.')
        .optional(),
      paid: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
        )
        .optional(),
      refunded: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all successfully refunded payments.')
        .optional(),
      authorized: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all authorized payments.')
        .optional(),
      pendingRefund: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all pending refund transactions.')
        .optional(),
      pending: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all pending transactions.')
        .optional(),
      chargeback: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all transaction chargebacks.')
        .optional(),
      chargebackReversal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Sum of all chargeback reversals.')
        .optional(),
      platformFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
        )
        .optional(),
      totalMinusPlatformFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
        )
        .optional(),
    })
    .describe('Balance summary.')
    .optional(),
  additionalFees: z
    .array(
      z.object({
        code: z
          .string()
          .describe("Additional fee's unique code for future processing.")
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Name of additional fee.')
          .min(1)
          .max(50)
          .optional(),
        price: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price.")
          .optional(),
        taxDetails: z
          .object({
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Taxable amount of this line item.')
              .optional(),
            taxRate: z
              .string()
              .describe(
                'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
              )
              .optional(),
            totalTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'The calculated tax, based on the `taxableAmount` and `taxRate`.'
              )
              .optional(),
          })
          .describe('Tax details.')
          .optional(),
        taxInfo: z
          .object({
            taxAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Calculated tax, based on `taxable_amount` and `tax_rate`.'
              )
              .optional(),
            taxableAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Amount for which tax is calculated.')
              .optional(),
            taxRate: z
              .string()
              .describe('Tax rate %, as a decimal point.')
              .optional()
              .nullable(),
            taxGroupId: z
              .string()
              .describe('Tax group ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            taxIncludedInPrice: z
              .boolean()
              .describe('Indicates whether the price already includes tax.')
              .optional(),
            taxBreakdown: z
              .array(
                z.object({
                  jurisdiction: z
                    .string()
                    .describe(
                      'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                    )
                    .optional()
                    .nullable(),
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount of tax calculated for this line item.')
                    .optional(),
                  taxType: z
                    .string()
                    .describe(
                      'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  taxName: z
                    .string()
                    .describe(
                      'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                    )
                    .max(200)
                    .optional()
                    .nullable(),
                  jurisdictionType: z
                    .enum([
                      'UNDEFINED',
                      'COUNTRY',
                      'STATE',
                      'COUNTY',
                      'CITY',
                      'SPECIAL',
                    ])
                    .describe(
                      'Type of jurisdiction that taxes were calculated for.'
                    )
                    .optional(),
                  nonTaxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Non-taxable amount of the line item price.')
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of the line item price.')
                    .optional(),
                })
              )
              .max(7)
              .optional(),
          })
          .describe(
            'Represents all the relevant tax details for additional fee.'
          )
          .optional(),
        providerAppId: z
          .string()
          .describe("SPI implementer's `appId`.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        priceBeforeTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price before tax.")
          .optional(),
        priceAfterTax: z
          .object({
            amount: z.string().describe('Amount.').optional(),
            formattedAmount: z
              .string()
              .describe('Amount formatted with currency symbol.')
              .optional(),
          })
          .describe("Additional fee's price after tax.")
          .optional(),
        _id: z
          .string()
          .describe("Additional fee's id.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        lineItemIds: z.array(z.string()).optional(),
        source: z
          .enum(['SERVICE_PLUGIN', 'ITEM', 'MANUAL', 'SHIPPING', 'PLATFORM'])
          .describe('Specifies the entity that added the additional fee.')
          .optional(),
      })
    )
    .max(100)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  purchaseFlowId: z
    .string()
    .describe(
      'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  recipientInfo: z
    .object({
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      contactDetails: z
        .object({
          firstName: z
            .string()
            .describe('First name.')
            .max(100)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Last name.')
            .max(100)
            .optional()
            .nullable(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          company: z
            .string()
            .describe('Company name.')
            .max(1000)
            .optional()
            .nullable(),
          vatId: z
            .object({
              _id: z.string().describe("Customer's tax ID.").optional(),
              type: z
                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                .describe(
                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                )
                .optional(),
            })
            .describe(
              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
            )
            .optional(),
        })
        .describe('Contact details.')
        .optional(),
    })
    .describe(
      "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
    )
    .optional(),
  purchasedDate: z
    .date()
    .describe(
      'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
    )
    .optional()
    .nullable(),
  businessLocation: z
    .object({
      _id: z
        .string()
        .describe(
          'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z.string().describe('Location name.').min(1).max(500).optional(),
    })
    .describe('Order location.')
    .optional(),
  payAfterFreeTrial: z
    .object({
      subtotal: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe(
          'Subtotal of all line items, before discounts and before tax.'
        )
        .optional(),
      shipping: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total shipping price, before discounts and before tax.')
        .optional(),
      tax: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total tax applied to the order.')
        .optional(),
      discount: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total discount amount applied to the order.')
        .optional(),
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe("Order's total price after discounts and tax.")
        .optional(),
      totalAdditionalFees: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total additional fees before tax.')
        .optional(),
    })
    .describe(
      'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
    )
    .optional(),
  platformFeeSummary: z
    .object({
      total: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total sum of all platform fees.')
        .optional(),
      totalPassOn: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total amount of platform fees with `PASS_ON` charge type.')
        .optional(),
      totalAbsorbed: z
        .object({
          amount: z.string().describe('Amount.').optional(),
          formattedAmount: z
            .string()
            .describe('Amount formatted with currency symbol.')
            .optional(),
        })
        .describe('Total amount of platform fees with `ABSORBED` charge type.')
        .optional(),
      fees: z
        .array(
          z.object({
            name: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe('Platform fee name.')
              .optional(),
            amount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Platform fee amount.')
              .optional(),
            lineItemId: z
              .string()
              .describe('ID of the line item the platform fee applies to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            chargeType: z
              .enum(['PASS_ON', 'ABSORBED'])
              .describe('Platform fee charge type.')
              .optional(),
            percentageRate: z
              .string()
              .describe(
                'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
              )
              .optional(),
          })
        )
        .max(300)
        .optional(),
    })
    .describe(
      'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
    )
    .optional(),
});
export const BulkUpdateOrdersRequest = z.object({
  orders: z
    .array(
      z.object({
        order: z
          .object({
            _id: z
              .string()
              .describe('Order ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            number: z
              .string()
              .regex(/^\d+$/, 'Must be a valid UInt64 string')
              .describe(
                "Order number displayed in the site owner's dashboard (auto-generated)."
              )
              .optional(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            lineItems: z
              .array(
                z.object({
                  _id: z.string().describe('Line item ID.').optional(),
                  productName: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
                    )
                    .optional(),
                  catalogReference: z
                    .object({
                      catalogItemId: z
                        .string()
                        .describe(
                          'ID of the item within the catalog it belongs to.'
                        )
                        .min(1)
                        .max(36)
                        .optional(),
                      appId: z
                        .string()
                        .describe(
                          'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                        )
                        .min(1)
                        .optional(),
                      options: z
                        .record(z.string(), z.any())
                        .describe(
                          'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "References to the line item's origin catalog. Empty for custom line items."
                    )
                    .optional(),
                  quantity: z
                    .number()
                    .int()
                    .describe('Line item quantity.')
                    .min(1)
                    .max(100000)
                    .optional(),
                  totalDiscount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      "Total discount for this line item's entire quantity."
                    )
                    .optional(),
                  descriptionLines: z
                    .array(
                      z.intersection(
                        z.object({
                          name: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                )
                                .max(100)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                            })
                            .describe('Description line name.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            plainText: z.never().optional(),
                            colorInfo: z.never().optional(),
                          }),
                          z.object({
                            colorInfo: z.never().optional(),
                            plainText: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                  )
                                  .max(600)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                                  )
                                  .max(600)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Description line plain text value.'),
                          }),
                          z.object({
                            plainText: z.never().optional(),
                            colorInfo: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                  )
                                  .max(500)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                                  )
                                  .max(500)
                                  .optional()
                                  .nullable(),
                                code: z
                                  .string()
                                  .describe(
                                    'HEX or RGB color code for display.'
                                  )
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Description line color value.'),
                          }),
                        ])
                      )
                    )
                    .min(0)
                    .max(20)
                    .optional(),
                  image: z.string().describe('Line item image.').optional(),
                  physicalProperties: z
                    .object({
                      weight: z
                        .number()
                        .describe(
                          'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                        )
                        .optional()
                        .nullable(),
                      sku: z
                        .string()
                        .describe(
                          'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                        )
                        .max(40)
                        .optional()
                        .nullable(),
                      shippable: z
                        .boolean()
                        .describe('Whether this line item is shippable.')
                        .optional(),
                    })
                    .describe(
                      'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
                    )
                    .optional(),
                  itemType: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          preset: z.never().optional(),
                          custom: z.never().optional(),
                        }),
                        z.object({
                          custom: z.never().optional(),
                          preset: z
                            .enum([
                              'UNRECOGNISED',
                              'PHYSICAL',
                              'DIGITAL',
                              'GIFT_CARD',
                              'SERVICE',
                            ])
                            .describe('Preset item type.'),
                        }),
                        z.object({
                          preset: z.never().optional(),
                          custom: z
                            .string()
                            .describe(
                              'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                            ),
                        }),
                      ])
                    )
                    .describe('Item type. Either a preset type or custom.')
                    .optional(),
                  fulfillerId: z
                    .string()
                    .describe(
                      'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  refundQuantity: z
                    .number()
                    .int()
                    .describe('Number of items that were refunded.')
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  restockQuantity: z
                    .number()
                    .int()
                    .describe(
                      'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
                    )
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  price: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Line item price after line item discounts for display purposes.'
                    )
                    .optional(),
                  priceBeforeDiscounts: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
                    )
                    .optional(),
                  totalPriceBeforeTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Total price after discounts, and before tax.')
                    .optional(),
                  totalPriceAfterTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Total price after all discounts and tax.')
                    .optional(),
                  paymentOption: z
                    .enum([
                      'FULL_PAYMENT_ONLINE',
                      'FULL_PAYMENT_OFFLINE',
                      'MEMBERSHIP',
                      'DEPOSIT_ONLINE',
                      'MEMBERSHIP_OFFLINE',
                      'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                    ])
                    .optional(),
                  taxDetails: z
                    .object({
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of this line item.')
                        .optional(),
                      taxRate: z
                        .string()
                        .describe(
                          'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                        )
                        .optional(),
                      totalTax: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
                    )
                    .optional(),
                  taxInfo: z
                    .object({
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                        )
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Amount for which tax is calculated.')
                        .optional(),
                      taxRate: z
                        .string()
                        .describe('Tax rate %, as a decimal point.')
                        .optional()
                        .nullable(),
                      taxGroupId: z
                        .string()
                        .describe('Tax group ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      taxIncludedInPrice: z
                        .boolean()
                        .describe(
                          'Indicates whether the price already includes tax.'
                        )
                        .optional(),
                      taxBreakdown: z
                        .array(
                          z.object({
                            jurisdiction: z
                              .string()
                              .describe(
                                'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            rate: z
                              .string()
                              .describe(
                                'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                              )
                              .optional()
                              .nullable(),
                            taxAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Amount of tax calculated for this line item.'
                              )
                              .optional(),
                            taxType: z
                              .string()
                              .describe(
                                'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            taxName: z
                              .string()
                              .describe(
                                'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            jurisdictionType: z
                              .enum([
                                'UNDEFINED',
                                'COUNTRY',
                                'STATE',
                                'COUNTY',
                                'CITY',
                                'SPECIAL',
                              ])
                              .optional(),
                            nonTaxableAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Non-taxable amount of the line item price.'
                              )
                              .optional(),
                            taxableAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Taxable amount of the line item price.'
                              )
                              .optional(),
                          })
                        )
                        .max(7)
                        .optional(),
                    })
                    .describe(
                      'Represents all the relevant tax details for a specific line item.'
                    )
                    .optional(),
                  digitalFile: z
                    .object({
                      fileId: z
                        .string()
                        .describe('ID of the secure file in media.')
                        .min(1)
                        .max(100)
                        .optional(),
                      link: z
                        .string()
                        .describe(
                          'Link will exist after the digital links have been generated on the order.'
                        )
                        .url()
                        .min(1)
                        .max(2000)
                        .optional()
                        .nullable(),
                      expirationDate: z
                        .date()
                        .describe('Link expiration time and date.')
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Digital file identifier, relevant only for items with type DIGITAL.'
                    )
                    .optional(),
                  subscriptionInfo: z
                    .object({
                      _id: z
                        .string()
                        .describe('Subscription ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      cycleNumber: z
                        .number()
                        .int()
                        .describe(
                          'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
                        )
                        .min(0)
                        .optional(),
                      subscriptionOptionTitle: z
                        .string()
                        .describe(
                          'Subscription option title. For example, `"Monthly coffee Subscription"`.'
                        )
                        .min(0)
                        .max(150)
                        .optional(),
                      subscriptionOptionDescription: z
                        .string()
                        .describe(
                          'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      subscriptionSettings: z
                        .object({
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                            )
                            .min(1)
                            .max(3650)
                            .optional()
                            .nullable(),
                          autoRenewal: z
                            .boolean()
                            .describe(
                              'Whether subscription is renewed automatically at the end of each period.'
                            )
                            .optional(),
                          billingCycles: z
                            .number()
                            .int()
                            .describe(
                              'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                            )
                            .min(1)
                            .optional()
                            .nullable(),
                          enableCustomerCancellation: z
                            .boolean()
                            .describe(
                              'Whether to allow the customer to cancel the subscription.'
                            )
                            .optional(),
                          freeTrialPeriod: z
                            .object({
                              frequency: z
                                .enum([
                                  'UNDEFINED',
                                  'DAY',
                                  'WEEK',
                                  'MONTH',
                                  'YEAR',
                                ])
                                .optional(),
                              interval: z
                                .number()
                                .int()
                                .describe(
                                  'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                                )
                                .min(1)
                                .max(3650)
                                .optional(),
                            })
                            .describe(
                              'Period until first cycle starts. If None => no free trial'
                            )
                            .optional(),
                        })
                        .describe('Subscription detailed information.')
                        .optional(),
                      chargesDescription: z
                        .string()
                        .describe(
                          'Description of the charges that will be applied for subscription.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe('Subscription info.')
                    .optional(),
                  priceDescription: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
                    )
                    .optional(),
                  depositAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
                    )
                    .optional(),
                  locations: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Location id in the associated owner app.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        appId: z
                          .string()
                          .describe(
                            'Location owner app, if not provided then the site business info locations will be used.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        quantity: z
                          .number()
                          .int()
                          .describe('Quantity for specific location.')
                          .min(1)
                          .max(100000)
                          .optional(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .max(500)
                          .optional()
                          .nullable(),
                      })
                    )
                    .min(0)
                    .max(5)
                    .optional(),
                  lineItemPrice: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Total price **after** catalog discounts and line item discounts.'
                    )
                    .optional(),
                  taxableAddress: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ addressType: z.never().optional() }),
                        z.object({
                          addressType: z
                            .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                            .describe(
                              'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                            ),
                        }),
                      ])
                    )
                    .describe('Address used for tax calculation.')
                    .optional(),
                  priceUndetermined: z
                    .boolean()
                    .describe(
                      'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
                    )
                    .optional(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  modifierGroups: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Modifier group ID.')
                          .min(1)
                          .max(36)
                          .optional(),
                        name: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Modifier group name.')
                          .optional(),
                        modifiers: z
                          .array(
                            z.object({
                              _id: z
                                .string()
                                .describe('Modifier ID.')
                                .min(1)
                                .max(36)
                                .optional(),
                              quantity: z
                                .number()
                                .int()
                                .describe('The quantity of this modifier.')
                                .min(1)
                                .max(100000)
                                .optional()
                                .nullable(),
                              label: z
                                .object({
                                  original: z
                                    .string()
                                    .describe(
                                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                    )
                                    .min(1)
                                    .max(200)
                                    .optional(),
                                  translated: z
                                    .string()
                                    .describe(
                                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                    )
                                    .min(1)
                                    .max(400)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Primary display label for the modifier.'
                                )
                                .optional(),
                              details: z
                                .object({
                                  original: z
                                    .string()
                                    .describe(
                                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                    )
                                    .min(1)
                                    .max(200)
                                    .optional(),
                                  translated: z
                                    .string()
                                    .describe(
                                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                    )
                                    .min(1)
                                    .max(400)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Additional details.')
                                .optional(),
                              price: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('The price of the modifier.')
                                .optional(),
                            })
                          )
                          .min(1)
                          .max(10)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(1)
              .max(300)
              .optional(),
            buyerInfo: z
              .intersection(
                z.object({
                  contactId: z
                    .string()
                    .describe(
                      "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe("Buyer's email address.")
                    .email()
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    visitorId: z.never().optional(),
                    memberId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    visitorId: z
                      .string()
                      .describe(
                        "Visitor ID. Returned when the buyer isn't a logged-in site member."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    visitorId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe(
                        'Member ID. Returned when the buyer is a logged-in site member.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe('Buyer information.')
              .optional(),
            paymentStatus: z
              .enum([
                'UNSPECIFIED',
                'NOT_PAID',
                'PAID',
                'PARTIALLY_REFUNDED',
                'FULLY_REFUNDED',
                'PENDING',
                'PARTIALLY_PAID',
                'PENDING_MERCHANT',
                'CANCELED',
                'DECLINED',
              ])
              .optional(),
            fulfillmentStatus: z
              .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
              .optional(),
            buyerLanguage: z
              .string()
              .describe(
                'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
              )
              .optional()
              .nullable(),
            weightUnit: z
              .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
              )
              .optional()
              .nullable(),
            currencyConversionDetails: z
              .object({
                originalCurrency: z
                  .string()
                  .describe(
                    "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
                  )
                  .optional()
                  .nullable(),
                conversionRate: z
                  .string()
                  .describe(
                    'The rate used for converting the original currency to the currency used for payment.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Currency conversion details. For use with multi-currency sites.'
              )
              .optional(),
            taxIncludedInPrices: z
              .boolean()
              .describe('Whether tax is included in line item prices.')
              .optional(),
            siteLanguage: z
              .string()
              .describe('Site language in which original values are shown.')
              .optional()
              .nullable(),
            priceSummary: z
              .object({
                subtotal: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Subtotal of all line items, before discounts and before tax.'
                  )
                  .optional(),
                shipping: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total shipping price, before discounts and before tax.'
                  )
                  .optional(),
                tax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total tax applied to the order.')
                  .optional(),
                discount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total discount amount applied to the order.')
                  .optional(),
                total: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe("Order's total price after discounts and tax.")
                  .optional(),
                totalAdditionalFees: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total additional fees before tax.')
                  .optional(),
              })
              .describe('Order price summary.')
              .optional(),
            billingInfo: z
              .object({
                address: z
                  .object({
                    streetAddress: z
                      .object({
                        name: z.string().optional(),
                        number: z.string().optional(),
                      })
                      .optional(),
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Address.')
                  .optional(),
                contactDetails: z
                  .object({
                    firstName: z
                      .string()
                      .describe('First name.')
                      .max(100)
                      .optional()
                      .nullable(),
                    lastName: z
                      .string()
                      .describe('Last name.')
                      .max(100)
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .optional()
                      .nullable(),
                    company: z
                      .string()
                      .describe('Company name.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    vatId: z
                      .object({
                        _id: z
                          .string()
                          .describe("Customer's tax ID.")
                          .optional(),
                        type: z.enum(['UNSPECIFIED', 'CPF', 'CNPJ']).optional(),
                      })
                      .describe(
                        'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                      )
                      .optional(),
                  })
                  .describe('Contact details.')
                  .optional(),
              })
              .describe('Billing address and contact details.')
              .optional(),
            shippingInfo: z
              .object({
                carrierId: z
                  .string()
                  .describe(
                    'App Def Id of external provider which was a source of shipping info'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                code: z
                  .string()
                  .describe(
                    'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
                  )
                  .max(250)
                  .optional(),
                logistics: z
                  .intersection(
                    z.object({
                      deliveryTime: z
                        .string()
                        .describe(
                          'Expected delivery time in free text. For example, `"3-5 business days"`.'
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      instructions: z
                        .string()
                        .describe(
                          'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                      deliverByDate: z
                        .date()
                        .describe(
                          'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                        )
                        .optional()
                        .nullable(),
                      deliveryTimeSlot: z
                        .object({
                          from: z
                            .date()
                            .describe('Delivery slot starting time.')
                            .optional()
                            .nullable(),
                          to: z
                            .date()
                            .describe('Delivery slot ending time.')
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Expected delivery time slot with start and end times.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        shippingDestination: z.never().optional(),
                        pickupDetails: z.never().optional(),
                      }),
                      z.object({
                        pickupDetails: z.never().optional(),
                        shippingDestination: z
                          .object({
                            address: z
                              .object({
                                streetAddress: z
                                  .object({
                                    name: z.string().optional(),
                                    number: z.string().optional(),
                                  })
                                  .optional(),
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine2: z.string().optional().nullable(),
                              })
                              .describe('Address.')
                              .optional(),
                            contactDetails: z
                              .object({
                                firstName: z
                                  .string()
                                  .describe('First name.')
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                lastName: z
                                  .string()
                                  .describe('Last name.')
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                phone: z
                                  .string()
                                  .describe('Phone number.')
                                  .optional()
                                  .nullable(),
                                company: z
                                  .string()
                                  .describe('Company name.')
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                vatId: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe("Customer's tax ID.")
                                      .optional(),
                                    type: z
                                      .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                                      .optional(),
                                  })
                                  .describe(
                                    'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                                  )
                                  .optional(),
                              })
                              .describe('Contact details.')
                              .optional(),
                          })
                          .describe(
                            "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                          ),
                      }),
                      z.object({
                        shippingDestination: z.never().optional(),
                        pickupDetails: z
                          .object({
                            address: z
                              .object({
                                streetAddress: z
                                  .object({
                                    name: z.string().optional(),
                                    number: z.string().optional(),
                                  })
                                  .optional(),
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine2: z.string().optional().nullable(),
                              })
                              .describe('Pickup address.')
                              .optional(),
                            pickupMethod: z
                              .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                              .describe('Pickup method')
                              .optional(),
                          })
                          .describe(
                            'Pickup details for store pickup or pickup point orders.'
                          ),
                      }),
                    ])
                  )
                  .describe('Shipping logistics.')
                  .optional(),
                cost: z
                  .object({
                    price: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Shipping price for display purposes.')
                      .optional(),
                    totalPriceBeforeTax: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe(
                        'Total price of shipping after discounts (when relevant), and before tax.'
                      )
                      .optional(),
                    totalPriceAfterTax: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe(
                        'Shipping price after all discounts (if any exist), and after tax.'
                      )
                      .optional(),
                    taxDetails: z
                      .object({
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of this line item.')
                          .optional(),
                        taxRate: z
                          .string()
                          .describe(
                            'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                          )
                          .optional(),
                        totalTax: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                          )
                          .optional(),
                      })
                      .describe('Tax details.')
                      .optional(),
                    taxInfo: z
                      .object({
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Amount for which tax is calculated.')
                          .optional(),
                        taxRate: z
                          .string()
                          .describe('Tax rate %, as a decimal point.')
                          .optional()
                          .nullable(),
                        taxGroupId: z
                          .string()
                          .describe('Tax group ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        taxIncludedInPrice: z
                          .boolean()
                          .describe(
                            'Indicates whether the price already includes tax.'
                          )
                          .optional(),
                        taxBreakdown: z
                          .array(
                            z.object({
                              jurisdiction: z
                                .string()
                                .describe(
                                  'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                              rate: z
                                .string()
                                .describe(
                                  'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                                )
                                .optional()
                                .nullable(),
                              taxAmount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Amount of tax calculated for this line item.'
                                )
                                .optional(),
                              taxType: z
                                .string()
                                .describe(
                                  'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                              taxName: z
                                .string()
                                .describe(
                                  'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                              jurisdictionType: z
                                .enum([
                                  'UNDEFINED',
                                  'COUNTRY',
                                  'STATE',
                                  'COUNTY',
                                  'CITY',
                                  'SPECIAL',
                                ])
                                .optional(),
                              nonTaxableAmount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Non-taxable amount of the line item price.'
                                )
                                .optional(),
                              taxableAmount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Taxable amount of the line item price.'
                                )
                                .optional(),
                            })
                          )
                          .max(7)
                          .optional(),
                      })
                      .describe(
                        'Represents all the relevant tax details for a shipping.'
                      )
                      .optional(),
                    discount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Shipping discount before tax.')
                      .optional(),
                  })
                  .describe('Shipping costs.')
                  .optional(),
                region: z
                  .object({
                    name: z
                      .string()
                      .describe(
                        'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Shipping region.')
                  .optional(),
              })
              .describe('Shipping info and selected shipping option details.')
              .optional(),
            buyerNote: z
              .string()
              .describe(
                '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
              )
              .max(1000)
              .optional()
              .nullable(),
            status: z
              .enum([
                'INITIALIZED',
                'APPROVED',
                'CANCELED',
                'PENDING',
                'REJECTED',
              ])
              .optional(),
            archived: z
              .boolean()
              .describe('Whether order is archived.')
              .optional()
              .nullable(),
            taxSummary: z
              .object({
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total tax.')
                  .optional(),
              })
              .describe(
                'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
              )
              .optional(),
            taxInfo: z
              .object({
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Calculated tax, added from line items.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated.'
                        )
                        .max(200)
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
                        )
                        .max(200)
                        .optional(),
                      jurisdiction: z
                        .string()
                        .describe(
                          'The name of the jurisdiction in which this tax detail applies.'
                        )
                        .max(200)
                        .optional(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .optional(),
                      rate: z
                        .string()
                        .describe(
                          'The rate at which this tax detail was calculated.'
                        )
                        .optional(),
                      aggregatedTaxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'The sum of all the tax from line items that calculated by the tax identifiers.'
                        )
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(50)
                  .optional(),
                taxExempt: z
                  .boolean()
                  .describe(
                    'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Tax information.')
              .optional(),
            appliedDiscounts: z
              .array(
                z.intersection(
                  z.object({
                    discountType: z
                      .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
                      .optional(),
                    lineItemIds: z.array(z.string()).min(0).max(300).optional(),
                    _id: z
                      .string()
                      .describe('Discount ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    lineItemDiscounts: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Line item ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          totalDiscount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Total discount amount for this line item.'
                            )
                            .optional(),
                        })
                      )
                      .max(300)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      coupon: z.never().optional(),
                      merchantDiscount: z.never().optional(),
                      discountRule: z.never().optional(),
                    }),
                    z.object({
                      merchantDiscount: z.never().optional(),
                      discountRule: z.never().optional(),
                      coupon: z
                        .object({
                          _id: z.string().describe('Coupon ID.').optional(),
                          code: z.string().describe('Coupon code.').optional(),
                          name: z.string().describe('Coupon name.').optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Coupon value.')
                            .optional(),
                        })
                        .describe('Coupon applied by the customer.'),
                    }),
                    z.object({
                      coupon: z.never().optional(),
                      discountRule: z.never().optional(),
                      merchantDiscount: z
                        .intersection(
                          z.object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Discount amount.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              discountReason: z.never().optional(),
                              description: z.never().optional(),
                            }),
                            z.object({
                              description: z.never().optional(),
                              discountReason: z
                                .enum([
                                  'UNSPECIFIED',
                                  'EXCHANGED_ITEMS',
                                  'BILLING_ADJUSTMENT',
                                ])
                                .describe('Predefined discount reason.'),
                            }),
                            z.object({
                              discountReason: z.never().optional(),
                              description: z
                                .string()
                                .describe(
                                  'Custom discount description as free text.'
                                )
                                .max(200),
                            }),
                          ])
                        )
                        .describe('Discount applied manually by the merchant.'),
                    }),
                    z.object({
                      coupon: z.never().optional(),
                      merchantDiscount: z.never().optional(),
                      discountRule: z
                        .object({
                          _id: z
                            .string()
                            .describe('Discount rule ID')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          name: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Original discount rule name (in site's default language)."
                                )
                                .min(1)
                                .max(256)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                                )
                                .min(1)
                                .max(500)
                                .optional()
                                .nullable(),
                            })
                            .describe('Discount rule name')
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Discount value.')
                            .optional(),
                        })
                        .describe(
                          'Automatic discount applied by the system based on configured discount rules.'
                        ),
                    }),
                  ])
                )
              )
              .max(320)
              .optional(),
            activities: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Activity ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    authorEmail: z
                      .string()
                      .describe("Activity author's email.")
                      .email()
                      .optional()
                      .nullable(),
                    _createdDate: z
                      .date()
                      .describe('Activity creation date and time.')
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'ORDER_REFUNDED',
                        'ORDER_PLACED',
                        'ORDER_PAID',
                        'ORDER_FULFILLED',
                        'ORDER_NOT_FULFILLED',
                        'ORDER_CANCELED',
                        'DOWNLOAD_LINK_SENT',
                        'TRACKING_NUMBER_ADDED',
                        'TRACKING_NUMBER_EDITED',
                        'TRACKING_LINK_ADDED',
                        'SHIPPING_CONFIRMATION_EMAIL_SENT',
                        'INVOICE_ADDED',
                        'INVOICE_REMOVED',
                        'INVOICE_SENT',
                        'FULFILLER_EMAIL_SENT',
                        'SHIPPING_ADDRESS_EDITED',
                        'EMAIL_EDITED',
                        'PICKUP_READY_EMAIL_SENT',
                        'CUSTOM_ACTIVITY',
                        'MERCHANT_COMMENT',
                        'ORDER_PARTIALLY_PAID',
                        'DRAFT_ORDER_CHANGES_APPLIED',
                        'SAVED_PAYMENT_METHOD',
                        'AUTHORIZED_PAYMENT_CREATED',
                        'AUTHORIZED_PAYMENT_CAPTURED',
                        'AUTHORIZED_PAYMENT_VOIDED',
                        'REFUND_INITIATED',
                        'PAYMENT_REFUNDED',
                        'PAYMENT_REFUND_FAILED',
                        'REFUNDED_AS_STORE_CREDIT',
                        'PAYMENT_PENDING',
                        'PAYMENT_CANCELED',
                        'PAYMENT_DECLINED',
                        'ORDER_PENDING',
                        'ORDER_REJECTED',
                        'RECEIPT_CREATED',
                        'RECEIPT_SENT',
                        'CHARGEBACK_CREATED',
                        'CHARGEBACK_REVERSED',
                      ])
                      .optional(),
                    activityType: z
                      .enum([
                        'ORDER_PLACED',
                        'ORDER_PAID',
                        'ORDER_FULFILLED',
                        'ORDER_NOT_FULFILLED',
                        'ORDER_CANCELED',
                        'DOWNLOAD_LINK_SENT',
                        'TRACKING_NUMBER_ADDED',
                        'TRACKING_NUMBER_EDITED',
                        'TRACKING_LINK_ADDED',
                        'SHIPPING_CONFIRMATION_EMAIL_SENT',
                        'INVOICE_ADDED',
                        'INVOICE_REMOVED',
                        'INVOICE_SENT',
                        'FULFILLER_EMAIL_SENT',
                        'SHIPPING_ADDRESS_EDITED',
                        'EMAIL_EDITED',
                        'PICKUP_READY_EMAIL_SENT',
                        'MERCHANT_COMMENT',
                        'ORDER_PARTIALLY_PAID',
                        'DRAFT_ORDER_CHANGES_APPLIED',
                        'SAVED_PAYMENT_METHOD',
                        'AUTHORIZED_PAYMENT_CREATED',
                        'AUTHORIZED_PAYMENT_CAPTURED',
                        'AUTHORIZED_PAYMENT_VOIDED',
                        'REFUND_INITIATED',
                        'PAYMENT_REFUNDED',
                        'PAYMENT_REFUND_FAILED',
                        'REFUNDED_AS_STORE_CREDIT',
                        'PAYMENT_PENDING',
                        'PAYMENT_CANCELED',
                        'PAYMENT_DECLINED',
                        'ORDER_PENDING',
                        'ORDER_REJECTED',
                        'RECEIPT_CREATED',
                        'RECEIPT_SENT',
                        'CHARGEBACK_CREATED',
                        'CHARGEBACK_REVERSED',
                        'ORDER_REFUNDED',
                      ])
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                    }),
                    z.object({
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      merchantComment: z
                        .object({
                          message: z
                            .string()
                            .describe('Merchant comment message.')
                            .min(1)
                            .max(5000)
                            .optional(),
                        })
                        .describe(
                          'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      orderRefunded: z
                        .object({
                          manual: z
                            .boolean()
                            .describe(
                              'Whether order was refunded manually. For example, via payment provider or using cash.'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Refund amount.')
                            .optional(),
                          reason: z
                            .string()
                            .describe('Reason for refund.')
                            .max(200)
                            .optional(),
                        })
                        .describe(
                          'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      draftOrderChangesApplied: z
                        .object({
                          draftOrderId: z
                            .string()
                            .describe('Draft order id.')
                            .optional(),
                          reason: z
                            .string()
                            .describe(
                              'Reason for edit, given by user (optional).'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                          changes: z
                            .array(
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                  }),
                                  z.object({
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    lineItemChanged: z.object({
                                      _id: z
                                        .string()
                                        .describe('Line item ID.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name after change.')
                                        .optional(),
                                      quantity: z
                                        .object({
                                          originalQuantity: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Item quantity before update.'
                                            )
                                            .min(0)
                                            .max(1000000)
                                            .optional(),
                                          newQuantity: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Item quantity after update.'
                                            )
                                            .min(0)
                                            .max(1000000)
                                            .optional(),
                                          diff: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Difference between original and new quantity. Absolute value.'
                                            )
                                            .min(1)
                                            .max(1000000)
                                            .optional(),
                                          deltaType: z
                                            .enum([
                                              'QUANTITY_INCREASED',
                                              'QUANTITY_DECREASED',
                                            ])
                                            .describe(
                                              'Type of quantity change: increase or decrease.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Item quantity change.')
                                        .optional(),
                                      price: z
                                        .object({
                                          originalPrice: z
                                            .object({
                                              amount: z
                                                .string()
                                                .describe('Amount.')
                                                .optional(),
                                              formattedAmount: z
                                                .string()
                                                .describe(
                                                  'Amount formatted with currency symbol.'
                                                )
                                                .optional(),
                                            })
                                            .describe(
                                              'Item price before update.'
                                            )
                                            .optional(),
                                          newPrice: z
                                            .object({
                                              amount: z
                                                .string()
                                                .describe('Amount.')
                                                .optional(),
                                              formattedAmount: z
                                                .string()
                                                .describe(
                                                  'Amount formatted with currency symbol.'
                                                )
                                                .optional(),
                                            })
                                            .describe(
                                              'Item price after update.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Item price change.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    lineItemAdded: z.object({
                                      _id: z
                                        .string()
                                        .describe('Line item ID.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      quantity: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Added or removed item quantity.'
                                        )
                                        .min(1)
                                        .max(1000000)
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    lineItemRemoved: z.object({
                                      _id: z
                                        .string()
                                        .describe('Line item ID.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      quantity: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Added or removed item quantity.'
                                        )
                                        .min(1)
                                        .max(1000000)
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    discountAdded: z.object({
                                      _id: z
                                        .string()
                                        .describe('Discount id.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              'Value in site default language.'
                                            )
                                            .min(1)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe('Translated value.')
                                            .min(1)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Discount name: coupon name / discount rule name / merchant discount description.'
                                        )
                                        .optional(),
                                      affectedLineItems: z
                                        .array(
                                          z.object({
                                            _id: z
                                              .string()
                                              .describe('Order line item id')
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                            name: z
                                              .object({
                                                original: z
                                                  .string()
                                                  .describe(
                                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                                  )
                                                  .min(1)
                                                  .max(200)
                                                  .optional(),
                                                translated: z
                                                  .string()
                                                  .describe(
                                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                                  )
                                                  .min(1)
                                                  .max(400)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe('Item name.')
                                              .optional(),
                                            amount: z
                                              .object({
                                                amount: z
                                                  .string()
                                                  .describe('Amount.')
                                                  .optional(),
                                                formattedAmount: z
                                                  .string()
                                                  .describe(
                                                    'Amount formatted with currency symbol.'
                                                  )
                                                  .optional(),
                                              })
                                              .describe(
                                                'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .min(0)
                                        .optional(),
                                      totalAmount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Discount amount.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    discountRemoved: z.object({
                                      _id: z
                                        .string()
                                        .describe('Discount id.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              'Value in site default language.'
                                            )
                                            .min(1)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe('Translated value.')
                                            .min(1)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Discount name: coupon name / discount rule name / merchant discount description.'
                                        )
                                        .optional(),
                                      affectedLineItems: z
                                        .array(
                                          z.object({
                                            _id: z
                                              .string()
                                              .describe('Order line item id')
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                            name: z
                                              .object({
                                                original: z
                                                  .string()
                                                  .describe(
                                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                                  )
                                                  .min(1)
                                                  .max(200)
                                                  .optional(),
                                                translated: z
                                                  .string()
                                                  .describe(
                                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                                  )
                                                  .min(1)
                                                  .max(400)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe('Item name.')
                                              .optional(),
                                            amount: z
                                              .object({
                                                amount: z
                                                  .string()
                                                  .describe('Amount.')
                                                  .optional(),
                                                formattedAmount: z
                                                  .string()
                                                  .describe(
                                                    'Amount formatted with currency symbol.'
                                                  )
                                                  .optional(),
                                              })
                                              .describe(
                                                'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .min(0)
                                        .optional(),
                                      totalAmount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Discount amount.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    additionalFeeAdded: z.object({
                                      _id: z
                                        .string()
                                        .describe('Additional fee id.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              'Value in site default language.'
                                            )
                                            .min(1)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe('Translated value.')
                                            .min(1)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Additional fee name.')
                                        .optional(),
                                      affectedLineItems: z
                                        .array(
                                          z.object({
                                            _id: z
                                              .string()
                                              .describe('Order line item id')
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                            name: z
                                              .object({
                                                original: z
                                                  .string()
                                                  .describe(
                                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                                  )
                                                  .min(1)
                                                  .max(200)
                                                  .optional(),
                                                translated: z
                                                  .string()
                                                  .describe(
                                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                                  )
                                                  .min(1)
                                                  .max(400)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe('Item name.')
                                              .optional(),
                                            amount: z
                                              .object({
                                                amount: z
                                                  .string()
                                                  .describe('Amount.')
                                                  .optional(),
                                                formattedAmount: z
                                                  .string()
                                                  .describe(
                                                    'Amount formatted with currency symbol.'
                                                  )
                                                  .optional(),
                                              })
                                              .describe(
                                                'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .min(0)
                                        .optional(),
                                      totalAmount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Additional fee amount.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    additionalFeeRemoved: z.object({
                                      _id: z
                                        .string()
                                        .describe('Additional fee id.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              'Value in site default language.'
                                            )
                                            .min(1)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe('Translated value.')
                                            .min(1)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Additional fee name.')
                                        .optional(),
                                      affectedLineItems: z
                                        .array(
                                          z.object({
                                            _id: z
                                              .string()
                                              .describe('Order line item id')
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                            name: z
                                              .object({
                                                original: z
                                                  .string()
                                                  .describe(
                                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                                  )
                                                  .min(1)
                                                  .max(200)
                                                  .optional(),
                                                translated: z
                                                  .string()
                                                  .describe(
                                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                                  )
                                                  .min(1)
                                                  .max(400)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe('Item name.')
                                              .optional(),
                                            amount: z
                                              .object({
                                                amount: z
                                                  .string()
                                                  .describe('Amount.')
                                                  .optional(),
                                                formattedAmount: z
                                                  .string()
                                                  .describe(
                                                    'Amount formatted with currency symbol.'
                                                  )
                                                  .optional(),
                                              })
                                              .describe(
                                                'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .min(0)
                                        .optional(),
                                      totalAmount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Additional fee amount.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    totalPriceChanged: z.object({
                                      originalTotal: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Order’s total price after discounts and tax. Before update'
                                        )
                                        .optional(),
                                      newTotal: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Order’s total price after discounts and tax. After update'
                                        )
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z.object({
                                      originalShippingInfo: z
                                        .object({
                                          total: z
                                            .object({
                                              amount: z
                                                .string()
                                                .describe('Amount.')
                                                .optional(),
                                              formattedAmount: z
                                                .string()
                                                .describe(
                                                  'Amount formatted with currency symbol.'
                                                )
                                                .optional(),
                                            })
                                            .describe('Order’s shipping price.')
                                            .optional(),
                                          shippingTitle: z
                                            .string()
                                            .describe('Order’s shipping title.')
                                            .max(250)
                                            .optional(),
                                        })
                                        .describe(
                                          'Order’s Shipping Information. Before update'
                                        )
                                        .optional(),
                                      newShippingInfo: z
                                        .object({
                                          total: z
                                            .object({
                                              amount: z
                                                .string()
                                                .describe('Amount.')
                                                .optional(),
                                              formattedAmount: z
                                                .string()
                                                .describe(
                                                  'Amount formatted with currency symbol.'
                                                )
                                                .optional(),
                                            })
                                            .describe('Order’s shipping price.')
                                            .optional(),
                                          shippingTitle: z
                                            .string()
                                            .describe('Order’s shipping title.')
                                            .max(250)
                                            .optional(),
                                        })
                                        .describe(
                                          'Order’s Shipping Information. After update'
                                        )
                                        .optional(),
                                    }),
                                  }),
                                ])
                              )
                            )
                            .optional(),
                        })
                        .describe(
                          'Details of changes made by the Draft Orders API.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      savedPaymentMethod: z
                        .object({
                          name: z
                            .string()
                            .describe('Payment method name.')
                            .min(1)
                            .max(100)
                            .optional(),
                          description: z
                            .string()
                            .describe('Payment method description.')
                            .min(1)
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Details of the payment method saved for the order.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      authorizedPaymentCreated: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'Payment ID of payment associated with this activity.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                          lastFourDigits: z
                            .string()
                            .describe('The last 4 digits of the card number.')
                            .max(4)
                            .optional()
                            .nullable(),
                          brand: z
                            .string()
                            .describe("Card issuer's brand.")
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe('Details of an authorized payment created.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      authorizedPaymentCaptured: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'Payment ID of payment associated with this activity.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                          lastFourDigits: z
                            .string()
                            .describe('The last 4 digits of the card number.')
                            .max(4)
                            .optional()
                            .nullable(),
                          brand: z
                            .string()
                            .describe("Card issuer's brand.")
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe('Details of an authorized payment captured.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      authorizedPaymentVoided: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'Payment ID of payment associated with this activity.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                          lastFourDigits: z
                            .string()
                            .describe('The last 4 digits of the card number.')
                            .max(4)
                            .optional()
                            .nullable(),
                          brand: z
                            .string()
                            .describe("Card issuer's brand.")
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe('Details of an authorized payment voided.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      refundInitiated: z
                        .object({
                          refundId: z
                            .string()
                            .describe('Refund ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Refund amount.')
                            .optional(),
                          payments: z
                            .array(
                              z.intersection(
                                z.object({
                                  paymentId: z
                                    .string()
                                    .describe('Payment ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  externalRefund: z
                                    .boolean()
                                    .describe(
                                      "Whether refund was made externally and manually on the payment provider's side."
                                    )
                                    .optional(),
                                }),
                                z.xor([
                                  z.object({
                                    regular: z.never().optional(),
                                    giftCard: z.never().optional(),
                                    membership: z.never().optional(),
                                  }),
                                  z.object({
                                    giftCard: z.never().optional(),
                                    membership: z.never().optional(),
                                    regular: z
                                      .object({
                                        amount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Refund amount.')
                                          .optional(),
                                        lastFourDigits: z
                                          .string()
                                          .describe(
                                            'The last 4 digits of the card number.'
                                          )
                                          .max(4)
                                          .optional()
                                          .nullable(),
                                        brand: z
                                          .string()
                                          .describe("Card issuer's brand.")
                                          .max(100)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Regular payment refund.'),
                                  }),
                                  z.object({
                                    regular: z.never().optional(),
                                    membership: z.never().optional(),
                                    giftCard: z
                                      .object({
                                        giftCardPaymentId: z
                                          .string()
                                          .describe('Gift card payment ID.')
                                          .min(1)
                                          .max(100)
                                          .optional()
                                          .nullable(),
                                        amount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Refund amount.')
                                          .optional(),
                                      })
                                      .describe('Gift card payment refund.'),
                                  }),
                                  z.object({
                                    regular: z.never().optional(),
                                    giftCard: z.never().optional(),
                                    membership: z
                                      .object({
                                        membershipId: z
                                          .string()
                                          .describe('Membership ID.')
                                          .min(1)
                                          .max(100)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Membership payment refund.'),
                                  }),
                                ])
                              )
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                          reason: z
                            .string()
                            .describe('Reason for refund.')
                            .max(200)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentRefunded: z
                        .object({
                          refundId: z
                            .string()
                            .describe('Refund ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          payment: z
                            .intersection(
                              z.object({
                                paymentId: z
                                  .string()
                                  .describe('Payment ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                externalRefund: z
                                  .boolean()
                                  .describe(
                                    "Whether refund was made externally and manually on the payment provider's side."
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  regular: z.never().optional(),
                                  giftCard: z.never().optional(),
                                  membership: z.never().optional(),
                                }),
                                z.object({
                                  giftCard: z.never().optional(),
                                  membership: z.never().optional(),
                                  regular: z
                                    .object({
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Refund amount.')
                                        .optional(),
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Regular payment refund.'),
                                }),
                                z.object({
                                  regular: z.never().optional(),
                                  membership: z.never().optional(),
                                  giftCard: z
                                    .object({
                                      giftCardPaymentId: z
                                        .string()
                                        .describe('Gift card payment ID.')
                                        .min(1)
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Refund amount.')
                                        .optional(),
                                    })
                                    .describe('Gift card payment refund.'),
                                }),
                                z.object({
                                  regular: z.never().optional(),
                                  giftCard: z.never().optional(),
                                  membership: z
                                    .object({
                                      membershipId: z
                                        .string()
                                        .describe('Membership ID.')
                                        .min(1)
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Membership payment refund.'),
                                }),
                              ])
                            )
                            .describe('Details about the refunded payment.')
                            .optional(),
                        })
                        .describe(
                          'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentRefundFailed: z
                        .object({
                          refundId: z
                            .string()
                            .describe('Refund ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          payment: z
                            .intersection(
                              z.object({
                                paymentId: z
                                  .string()
                                  .describe('Payment ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                externalRefund: z
                                  .boolean()
                                  .describe(
                                    "Whether refund was made externally and manually on the payment provider's side."
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  regular: z.never().optional(),
                                  giftCard: z.never().optional(),
                                  membership: z.never().optional(),
                                }),
                                z.object({
                                  giftCard: z.never().optional(),
                                  membership: z.never().optional(),
                                  regular: z
                                    .object({
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Refund amount.')
                                        .optional(),
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Regular payment refund.'),
                                }),
                                z.object({
                                  regular: z.never().optional(),
                                  membership: z.never().optional(),
                                  giftCard: z
                                    .object({
                                      giftCardPaymentId: z
                                        .string()
                                        .describe('Gift card payment ID.')
                                        .min(1)
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Refund amount.')
                                        .optional(),
                                    })
                                    .describe('Gift card payment refund.'),
                                }),
                                z.object({
                                  regular: z.never().optional(),
                                  giftCard: z.never().optional(),
                                  membership: z
                                    .object({
                                      membershipId: z
                                        .string()
                                        .describe('Membership ID.')
                                        .min(1)
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Membership payment refund.'),
                                }),
                              ])
                            )
                            .describe(
                              'Details about the failed payment refund.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      refundedAsStoreCredit: z
                        .object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Refund amount.')
                            .optional(),
                          reason: z
                            .string()
                            .describe('Reason for refund.')
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe('Details of refund to store credit.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentPending: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({ regular: z.never().optional() }),
                            z.object({
                              regular: z
                                .intersection(
                                  z.object({
                                    amount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Payment amount.')
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      creditCardDetails: z.never().optional(),
                                    }),
                                    z.object({
                                      creditCardDetails: z
                                        .object({
                                          lastFourDigits: z
                                            .string()
                                            .describe(
                                              'The last 4 digits of the card number.'
                                            )
                                            .max(4)
                                            .optional()
                                            .nullable(),
                                          brand: z
                                            .string()
                                            .describe("Card issuer's brand.")
                                            .max(100)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Credit card details.'),
                                    }),
                                  ])
                                )
                                .describe('Regular payment.'),
                            }),
                          ])
                        )
                        .describe('Details of a pending payment.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentCanceled: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({ regular: z.never().optional() }),
                            z.object({
                              regular: z
                                .intersection(
                                  z.object({
                                    amount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Payment amount.')
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      creditCardDetails: z.never().optional(),
                                    }),
                                    z.object({
                                      creditCardDetails: z
                                        .object({
                                          lastFourDigits: z
                                            .string()
                                            .describe(
                                              'The last 4 digits of the card number.'
                                            )
                                            .max(4)
                                            .optional()
                                            .nullable(),
                                          brand: z
                                            .string()
                                            .describe("Card issuer's brand.")
                                            .max(100)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Credit card details.'),
                                    }),
                                  ])
                                )
                                .describe('Regular payment.'),
                            }),
                          ])
                        )
                        .describe('Details of a canceled payment.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentDeclined: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({ regular: z.never().optional() }),
                            z.object({
                              regular: z
                                .intersection(
                                  z.object({
                                    amount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Payment amount.')
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      creditCardDetails: z.never().optional(),
                                    }),
                                    z.object({
                                      creditCardDetails: z
                                        .object({
                                          lastFourDigits: z
                                            .string()
                                            .describe(
                                              'The last 4 digits of the card number.'
                                            )
                                            .max(4)
                                            .optional()
                                            .nullable(),
                                          brand: z
                                            .string()
                                            .describe("Card issuer's brand.")
                                            .max(100)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Credit card details.'),
                                    }),
                                  ])
                                )
                                .describe('Regular payment.'),
                            }),
                          ])
                        )
                        .describe('Details of a declined payment.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      receiptCreated: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              wixReceipt: z.never().optional(),
                              externalReceipt: z.never().optional(),
                            }),
                            z.object({
                              externalReceipt: z.never().optional(),
                              wixReceipt: z
                                .object({
                                  receiptId: z
                                    .string()
                                    .describe('Receipt ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  displayNumber: z
                                    .string()
                                    .describe('Display number of receipt.')
                                    .min(1)
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Receipt created by Wix.'),
                            }),
                            z.object({
                              wixReceipt: z.never().optional(),
                              externalReceipt: z
                                .object({
                                  receiptId: z
                                    .string()
                                    .describe('Receipt ID.')
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  displayNumber: z
                                    .string()
                                    .describe('Display number of receipt.')
                                    .min(1)
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Receipt created by an external system.'
                                ),
                            }),
                          ])
                        )
                        .describe('Receipt was added for associated payment.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      receiptSent: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              wixReceipt: z.never().optional(),
                              externalReceipt: z.never().optional(),
                            }),
                            z.object({
                              externalReceipt: z.never().optional(),
                              wixReceipt: z
                                .object({
                                  receiptId: z
                                    .string()
                                    .describe('Receipt ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  displayNumber: z
                                    .string()
                                    .describe('Display number of receipt.')
                                    .min(1)
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Receipt created by Wix.'),
                            }),
                            z.object({
                              wixReceipt: z.never().optional(),
                              externalReceipt: z
                                .object({
                                  receiptId: z
                                    .string()
                                    .describe('Receipt ID.')
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  displayNumber: z
                                    .string()
                                    .describe('Display number of receipt.')
                                    .min(1)
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Receipt created by an external system.'
                                ),
                            }),
                          ])
                        )
                        .describe('Receipt sent to customer.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      chargebackCreated: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'ID of the payment that received a chargeback.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          chargebackId: z
                            .string()
                            .describe('Chargeback ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Chargeback amount.')
                            .optional(),
                          paymentDetails: z
                            .intersection(
                              z.object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Payment amount.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  creditCardDetails: z.never().optional(),
                                }),
                                z.object({
                                  creditCardDetails: z
                                    .object({
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Credit card details.'),
                                }),
                              ])
                            )
                            .describe('Payment details.')
                            .optional(),
                        })
                        .describe(
                          'Order received a chargeback for one of its payments.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'ID of the payment involved with the chargeback.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          chargebackId: z
                            .string()
                            .describe('Chargeback ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Chargeback amount.')
                            .optional(),
                          reversalAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Chargeback reversal amount.')
                            .optional(),
                          paymentDetails: z
                            .intersection(
                              z.object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Payment amount.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  creditCardDetails: z.never().optional(),
                                }),
                                z.object({
                                  creditCardDetails: z
                                    .object({
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Credit card details.'),
                                }),
                              ])
                            )
                            .describe('Payment details.')
                            .optional(),
                        })
                        .describe(
                          "Chargeback reversed for one of the order's payments."
                        ),
                    }),
                  ])
                )
              )
              .max(1000)
              .optional(),
            attributionSource: z
              .enum(['UNSPECIFIED', 'FACEBOOK_ADS'])
              .optional(),
            createdBy: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    userId: z.never().optional(),
                    memberId: z.never().optional(),
                    visitorId: z.never().optional(),
                    appId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    visitorId: z.never().optional(),
                    appId: z.never().optional(),
                    userId: z
                      .string()
                      .describe(
                        'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    userId: z.never().optional(),
                    visitorId: z.never().optional(),
                    appId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe(
                        'Member ID - when the order was created by a **logged in** site visitor.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    userId: z.never().optional(),
                    memberId: z.never().optional(),
                    appId: z.never().optional(),
                    visitorId: z
                      .string()
                      .describe(
                        'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    userId: z.never().optional(),
                    memberId: z.never().optional(),
                    visitorId: z.never().optional(),
                    appId: z
                      .string()
                      .describe(
                        'App ID - when the order was created by an external application.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe("ID of the order's initiator.")
              .optional(),
            channelInfo: z
              .object({
                type: z
                  .enum([
                    'UNSPECIFIED',
                    'WEB',
                    'POS',
                    'EBAY',
                    'AMAZON',
                    'OTHER_PLATFORM',
                    'WIX_APP_STORE',
                    'WIX_INVOICES',
                    'BACKOFFICE_MERCHANT',
                    'WISH',
                    'CLASS_PASS',
                    'GLOBAL_E',
                    'FACEBOOK',
                    'ETSY',
                    'TIKTOK',
                    'FAIRE_COM',
                    'PAYPAL_AGENTIC_CHECKOUT',
                    'STRIPE_AGENTIC_CHECKOUT',
                  ])
                  .optional(),
                externalOrderId: z
                  .string()
                  .describe(
                    'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                externalOrderUrl: z
                  .string()
                  .describe(
                    'URL to the order in the external system. Relevant when recording orders from external platforms.'
                  )
                  .max(300)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Information about the sales channel that submitted this order.'
              )
              .optional(),
            seenByAHuman: z
              .boolean()
              .describe(
                'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
              )
              .optional()
              .nullable(),
            checkoutId: z
              .string()
              .describe('Checkout ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            customFields: z
              .array(
                z.object({
                  value: z
                    .any()
                    .describe('Custom field value.')
                    .optional()
                    .nullable(),
                  title: z
                    .string()
                    .describe('Custom field title.')
                    .min(1)
                    .max(500)
                    .optional(),
                  translatedTitle: z
                    .string()
                    .describe('Translated custom field title.')
                    .min(1)
                    .max(500)
                    .optional()
                    .nullable(),
                })
              )
              .optional(),
            balanceSummary: z
              .object({
                balance: z
                  .object({
                    amount: z
                      .string()
                      .describe(
                        'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
                      )
                      .optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Current amount left to pay.')
                  .optional(),
                paid: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
                  )
                  .optional(),
                refunded: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all successfully refunded payments.')
                  .optional(),
                authorized: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all authorized payments.')
                  .optional(),
                pendingRefund: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all pending refund transactions.')
                  .optional(),
                pending: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all pending transactions.')
                  .optional(),
                chargeback: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all transaction chargebacks.')
                  .optional(),
                chargebackReversal: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all chargeback reversals.')
                  .optional(),
                platformFees: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
                  )
                  .optional(),
                totalMinusPlatformFees: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
                  )
                  .optional(),
              })
              .describe('Balance summary.')
              .optional(),
            additionalFees: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe(
                      "Additional fee's unique code for future processing."
                    )
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Name of additional fee.')
                    .min(1)
                    .max(50)
                    .optional(),
                  price: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe("Additional fee's price.")
                    .optional(),
                  taxDetails: z
                    .object({
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of this line item.')
                        .optional(),
                      taxRate: z
                        .string()
                        .describe(
                          'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                        )
                        .optional(),
                      totalTax: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                        )
                        .optional(),
                    })
                    .describe('Tax details.')
                    .optional(),
                  taxInfo: z
                    .object({
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                        )
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Amount for which tax is calculated.')
                        .optional(),
                      taxRate: z
                        .string()
                        .describe('Tax rate %, as a decimal point.')
                        .optional()
                        .nullable(),
                      taxGroupId: z
                        .string()
                        .describe('Tax group ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      taxIncludedInPrice: z
                        .boolean()
                        .describe(
                          'Indicates whether the price already includes tax.'
                        )
                        .optional(),
                      taxBreakdown: z
                        .array(
                          z.object({
                            jurisdiction: z
                              .string()
                              .describe(
                                'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            rate: z
                              .string()
                              .describe(
                                'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                              )
                              .optional()
                              .nullable(),
                            taxAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Amount of tax calculated for this line item.'
                              )
                              .optional(),
                            taxType: z
                              .string()
                              .describe(
                                'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            taxName: z
                              .string()
                              .describe(
                                'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            jurisdictionType: z
                              .enum([
                                'UNDEFINED',
                                'COUNTRY',
                                'STATE',
                                'COUNTY',
                                'CITY',
                                'SPECIAL',
                              ])
                              .optional(),
                            nonTaxableAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Non-taxable amount of the line item price.'
                              )
                              .optional(),
                            taxableAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Taxable amount of the line item price.'
                              )
                              .optional(),
                          })
                        )
                        .max(7)
                        .optional(),
                    })
                    .describe(
                      'Represents all the relevant tax details for additional fee.'
                    )
                    .optional(),
                  providerAppId: z
                    .string()
                    .describe("SPI implementer's `appId`.")
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  priceBeforeTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe("Additional fee's price before tax.")
                    .optional(),
                  priceAfterTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe("Additional fee's price after tax.")
                    .optional(),
                  _id: z
                    .string()
                    .describe("Additional fee's id.")
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  lineItemIds: z.array(z.string()).optional(),
                  source: z
                    .enum([
                      'SERVICE_PLUGIN',
                      'ITEM',
                      'MANUAL',
                      'SHIPPING',
                      'PLATFORM',
                    ])
                    .optional(),
                })
              )
              .max(100)
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            purchaseFlowId: z
              .string()
              .describe(
                'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            recipientInfo: z
              .object({
                address: z
                  .object({
                    streetAddress: z
                      .object({
                        name: z.string().optional(),
                        number: z.string().optional(),
                      })
                      .optional(),
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Address.')
                  .optional(),
                contactDetails: z
                  .object({
                    firstName: z
                      .string()
                      .describe('First name.')
                      .max(100)
                      .optional()
                      .nullable(),
                    lastName: z
                      .string()
                      .describe('Last name.')
                      .max(100)
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .optional()
                      .nullable(),
                    company: z
                      .string()
                      .describe('Company name.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    vatId: z
                      .object({
                        _id: z
                          .string()
                          .describe("Customer's tax ID.")
                          .optional(),
                        type: z.enum(['UNSPECIFIED', 'CPF', 'CNPJ']).optional(),
                      })
                      .describe(
                        'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                      )
                      .optional(),
                  })
                  .describe('Contact details.')
                  .optional(),
              })
              .describe(
                "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
              )
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
              )
              .optional(),
            purchasedDate: z
              .date()
              .describe(
                'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
              )
              .optional()
              .nullable(),
            businessLocation: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Location name.')
                  .min(1)
                  .max(500)
                  .optional(),
              })
              .describe('Order location.')
              .optional(),
            payAfterFreeTrial: z
              .object({
                subtotal: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Subtotal of all line items, before discounts and before tax.'
                  )
                  .optional(),
                shipping: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total shipping price, before discounts and before tax.'
                  )
                  .optional(),
                tax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total tax applied to the order.')
                  .optional(),
                discount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total discount amount applied to the order.')
                  .optional(),
                total: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe("Order's total price after discounts and tax.")
                  .optional(),
                totalAdditionalFees: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total additional fees before tax.')
                  .optional(),
              })
              .describe(
                'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
              )
              .optional(),
            platformFeeSummary: z
              .object({
                total: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total sum of all platform fees.')
                  .optional(),
                totalPassOn: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total amount of platform fees with `PASS_ON` charge type.'
                  )
                  .optional(),
                totalAbsorbed: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total amount of platform fees with `ABSORBED` charge type.'
                  )
                  .optional(),
                fees: z
                  .array(
                    z.object({
                      name: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                            )
                            .min(1)
                            .max(200)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                            )
                            .min(1)
                            .max(400)
                            .optional()
                            .nullable(),
                        })
                        .describe('Platform fee name.')
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Platform fee amount.')
                        .optional(),
                      lineItemId: z
                        .string()
                        .describe(
                          'ID of the line item the platform fee applies to.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      chargeType: z.enum(['PASS_ON', 'ABSORBED']).optional(),
                      percentageRate: z
                        .string()
                        .describe(
                          'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
                        )
                        .optional(),
                    })
                  )
                  .max(300)
                  .optional(),
              })
              .describe(
                'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
              )
              .optional(),
          })
          .describe('Order to be updated.'),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full order entities.\n\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateOrdersResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Order ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            number: z
              .string()
              .regex(/^\d+$/, 'Must be a valid UInt64 string')
              .describe(
                "Order number displayed in the site owner's dashboard (auto-generated)."
              )
              .optional(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            lineItems: z
              .array(
                z.object({
                  _id: z.string().describe('Line item ID.').optional(),
                  productName: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
                    )
                    .optional(),
                  catalogReference: z
                    .object({
                      catalogItemId: z
                        .string()
                        .describe(
                          'ID of the item within the catalog it belongs to.'
                        )
                        .min(1)
                        .max(36)
                        .optional(),
                      appId: z
                        .string()
                        .describe(
                          'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                        )
                        .min(1)
                        .optional(),
                      options: z
                        .record(z.string(), z.any())
                        .describe(
                          'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "References to the line item's origin catalog. Empty for custom line items."
                    )
                    .optional(),
                  quantity: z
                    .number()
                    .int()
                    .describe('Line item quantity.')
                    .min(1)
                    .max(100000)
                    .optional(),
                  totalDiscount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      "Total discount for this line item's entire quantity."
                    )
                    .optional(),
                  descriptionLines: z
                    .array(
                      z.intersection(
                        z.object({
                          name: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                )
                                .max(100)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                            })
                            .describe('Description line name.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            plainText: z.never().optional(),
                            colorInfo: z.never().optional(),
                          }),
                          z.object({
                            colorInfo: z.never().optional(),
                            plainText: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                  )
                                  .max(600)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                                  )
                                  .max(600)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Description line plain text value.'),
                          }),
                          z.object({
                            plainText: z.never().optional(),
                            colorInfo: z
                              .object({
                                original: z
                                  .string()
                                  .describe(
                                    "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                                  )
                                  .max(500)
                                  .optional(),
                                translated: z
                                  .string()
                                  .describe(
                                    "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                                  )
                                  .max(500)
                                  .optional()
                                  .nullable(),
                                code: z
                                  .string()
                                  .describe(
                                    'HEX or RGB color code for display.'
                                  )
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Description line color value.'),
                          }),
                        ])
                      )
                    )
                    .min(0)
                    .max(20)
                    .optional(),
                  image: z.string().describe('Line item image.').optional(),
                  physicalProperties: z
                    .object({
                      weight: z
                        .number()
                        .describe(
                          'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                        )
                        .optional()
                        .nullable(),
                      sku: z
                        .string()
                        .describe(
                          'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                        )
                        .max(40)
                        .optional()
                        .nullable(),
                      shippable: z
                        .boolean()
                        .describe('Whether this line item is shippable.')
                        .optional(),
                    })
                    .describe(
                      'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
                    )
                    .optional(),
                  itemType: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          preset: z.never().optional(),
                          custom: z.never().optional(),
                        }),
                        z.object({
                          custom: z.never().optional(),
                          preset: z
                            .enum([
                              'UNRECOGNISED',
                              'PHYSICAL',
                              'DIGITAL',
                              'GIFT_CARD',
                              'SERVICE',
                            ])
                            .describe('Preset item type.'),
                        }),
                        z.object({
                          preset: z.never().optional(),
                          custom: z
                            .string()
                            .describe(
                              'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                            ),
                        }),
                      ])
                    )
                    .describe('Item type. Either a preset type or custom.')
                    .optional(),
                  fulfillerId: z
                    .string()
                    .describe(
                      'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  refundQuantity: z
                    .number()
                    .int()
                    .describe('Number of items that were refunded.')
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  restockQuantity: z
                    .number()
                    .int()
                    .describe(
                      'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
                    )
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  price: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Line item price after line item discounts for display purposes.'
                    )
                    .optional(),
                  priceBeforeDiscounts: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
                    )
                    .optional(),
                  totalPriceBeforeTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Total price after discounts, and before tax.')
                    .optional(),
                  totalPriceAfterTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Total price after all discounts and tax.')
                    .optional(),
                  paymentOption: z
                    .enum([
                      'FULL_PAYMENT_ONLINE',
                      'FULL_PAYMENT_OFFLINE',
                      'MEMBERSHIP',
                      'DEPOSIT_ONLINE',
                      'MEMBERSHIP_OFFLINE',
                      'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
                    ])
                    .describe(
                      'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
                    )
                    .optional(),
                  taxDetails: z
                    .object({
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of this line item.')
                        .optional(),
                      taxRate: z
                        .string()
                        .describe(
                          'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                        )
                        .optional(),
                      totalTax: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
                    )
                    .optional(),
                  taxInfo: z
                    .object({
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                        )
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Amount for which tax is calculated.')
                        .optional(),
                      taxRate: z
                        .string()
                        .describe('Tax rate %, as a decimal point.')
                        .optional()
                        .nullable(),
                      taxGroupId: z
                        .string()
                        .describe('Tax group ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      taxIncludedInPrice: z
                        .boolean()
                        .describe(
                          'Indicates whether the price already includes tax.'
                        )
                        .optional(),
                      taxBreakdown: z
                        .array(
                          z.object({
                            jurisdiction: z
                              .string()
                              .describe(
                                'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            rate: z
                              .string()
                              .describe(
                                'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                              )
                              .optional()
                              .nullable(),
                            taxAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Amount of tax calculated for this line item.'
                              )
                              .optional(),
                            taxType: z
                              .string()
                              .describe(
                                'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            taxName: z
                              .string()
                              .describe(
                                'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            jurisdictionType: z
                              .enum([
                                'UNDEFINED',
                                'COUNTRY',
                                'STATE',
                                'COUNTY',
                                'CITY',
                                'SPECIAL',
                              ])
                              .describe(
                                'Type of jurisdiction that taxes were calculated for.'
                              )
                              .optional(),
                            nonTaxableAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Non-taxable amount of the line item price.'
                              )
                              .optional(),
                            taxableAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Taxable amount of the line item price.'
                              )
                              .optional(),
                          })
                        )
                        .max(7)
                        .optional(),
                    })
                    .describe(
                      'Represents all the relevant tax details for a specific line item.'
                    )
                    .optional(),
                  digitalFile: z
                    .object({
                      fileId: z
                        .string()
                        .describe('ID of the secure file in media.')
                        .min(1)
                        .max(100)
                        .optional(),
                      link: z
                        .string()
                        .describe(
                          'Link will exist after the digital links have been generated on the order.'
                        )
                        .url()
                        .min(1)
                        .max(2000)
                        .optional()
                        .nullable(),
                      expirationDate: z
                        .date()
                        .describe('Link expiration time and date.')
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Digital file identifier, relevant only for items with type DIGITAL.'
                    )
                    .optional(),
                  subscriptionInfo: z
                    .object({
                      _id: z
                        .string()
                        .describe('Subscription ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      cycleNumber: z
                        .number()
                        .int()
                        .describe(
                          'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
                        )
                        .min(0)
                        .optional(),
                      subscriptionOptionTitle: z
                        .string()
                        .describe(
                          'Subscription option title. For example, `"Monthly coffee Subscription"`.'
                        )
                        .min(0)
                        .max(150)
                        .optional(),
                      subscriptionOptionDescription: z
                        .string()
                        .describe(
                          'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      subscriptionSettings: z
                        .object({
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .describe('Frequency of recurring payment.')
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                            )
                            .min(1)
                            .max(3650)
                            .optional()
                            .nullable(),
                          autoRenewal: z
                            .boolean()
                            .describe(
                              'Whether subscription is renewed automatically at the end of each period.'
                            )
                            .optional(),
                          billingCycles: z
                            .number()
                            .int()
                            .describe(
                              'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                            )
                            .min(1)
                            .optional()
                            .nullable(),
                          enableCustomerCancellation: z
                            .boolean()
                            .describe(
                              'Whether to allow the customer to cancel the subscription.'
                            )
                            .optional(),
                          freeTrialPeriod: z
                            .object({
                              frequency: z
                                .enum([
                                  'UNDEFINED',
                                  'DAY',
                                  'WEEK',
                                  'MONTH',
                                  'YEAR',
                                ])
                                .describe(
                                  'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                                )
                                .optional(),
                              interval: z
                                .number()
                                .int()
                                .describe(
                                  'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                                )
                                .min(1)
                                .max(3650)
                                .optional(),
                            })
                            .describe(
                              'Period until first cycle starts. If None => no free trial'
                            )
                            .optional(),
                        })
                        .describe('Subscription detailed information.')
                        .optional(),
                      chargesDescription: z
                        .string()
                        .describe(
                          'Description of the charges that will be applied for subscription.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe('Subscription info.')
                    .optional(),
                  priceDescription: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
                    )
                    .optional(),
                  depositAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
                    )
                    .optional(),
                  locations: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Location id in the associated owner app.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        appId: z
                          .string()
                          .describe(
                            'Location owner app, if not provided then the site business info locations will be used.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        quantity: z
                          .number()
                          .int()
                          .describe('Quantity for specific location.')
                          .min(1)
                          .max(100000)
                          .optional(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .max(500)
                          .optional()
                          .nullable(),
                      })
                    )
                    .min(0)
                    .max(5)
                    .optional(),
                  lineItemPrice: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Total price **after** catalog discounts and line item discounts.'
                    )
                    .optional(),
                  taxableAddress: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({ addressType: z.never().optional() }),
                        z.object({
                          addressType: z
                            .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                            .describe(
                              'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                            ),
                        }),
                      ])
                    )
                    .describe('Address used for tax calculation.')
                    .optional(),
                  priceUndetermined: z
                    .boolean()
                    .describe(
                      'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
                    )
                    .optional(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  modifierGroups: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Modifier group ID.')
                          .min(1)
                          .max(36)
                          .optional(),
                        name: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Modifier group name.')
                          .optional(),
                        modifiers: z
                          .array(
                            z.object({
                              _id: z
                                .string()
                                .describe('Modifier ID.')
                                .min(1)
                                .max(36)
                                .optional(),
                              quantity: z
                                .number()
                                .int()
                                .describe('The quantity of this modifier.')
                                .min(1)
                                .max(100000)
                                .optional()
                                .nullable(),
                              label: z
                                .object({
                                  original: z
                                    .string()
                                    .describe(
                                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                    )
                                    .min(1)
                                    .max(200)
                                    .optional(),
                                  translated: z
                                    .string()
                                    .describe(
                                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                    )
                                    .min(1)
                                    .max(400)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Primary display label for the modifier.'
                                )
                                .optional(),
                              details: z
                                .object({
                                  original: z
                                    .string()
                                    .describe(
                                      "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                    )
                                    .min(1)
                                    .max(200)
                                    .optional(),
                                  translated: z
                                    .string()
                                    .describe(
                                      "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                    )
                                    .min(1)
                                    .max(400)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Additional details.')
                                .optional(),
                              price: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('The price of the modifier.')
                                .optional(),
                            })
                          )
                          .min(1)
                          .max(10)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(1)
              .max(300)
              .optional(),
            buyerInfo: z
              .intersection(
                z.object({
                  contactId: z
                    .string()
                    .describe(
                      "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  email: z
                    .string()
                    .describe("Buyer's email address.")
                    .email()
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    visitorId: z.never().optional(),
                    memberId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    visitorId: z
                      .string()
                      .describe(
                        "Visitor ID. Returned when the buyer isn't a logged-in site member."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    visitorId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe(
                        'Member ID. Returned when the buyer is a logged-in site member.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe('Buyer information.')
              .optional(),
            paymentStatus: z
              .enum([
                'UNSPECIFIED',
                'NOT_PAID',
                'PAID',
                'PARTIALLY_REFUNDED',
                'FULLY_REFUNDED',
                'PENDING',
                'PARTIALLY_PAID',
                'PENDING_MERCHANT',
                'CANCELED',
                'DECLINED',
              ])
              .describe('Order payment status.')
              .optional(),
            fulfillmentStatus: z
              .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
              .describe('Order fulfillment status.')
              .optional(),
            buyerLanguage: z
              .string()
              .describe(
                'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
              )
              .optional()
              .nullable(),
            weightUnit: z
              .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
              .describe(
                "Weight measurement unit - defaults to site's weight unit."
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
              )
              .optional()
              .nullable(),
            currencyConversionDetails: z
              .object({
                originalCurrency: z
                  .string()
                  .describe(
                    "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
                  )
                  .optional()
                  .nullable(),
                conversionRate: z
                  .string()
                  .describe(
                    'The rate used for converting the original currency to the currency used for payment.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Currency conversion details. For use with multi-currency sites.'
              )
              .optional(),
            taxIncludedInPrices: z
              .boolean()
              .describe('Whether tax is included in line item prices.')
              .optional(),
            siteLanguage: z
              .string()
              .describe('Site language in which original values are shown.')
              .optional()
              .nullable(),
            priceSummary: z
              .object({
                subtotal: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Subtotal of all line items, before discounts and before tax.'
                  )
                  .optional(),
                shipping: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total shipping price, before discounts and before tax.'
                  )
                  .optional(),
                tax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total tax applied to the order.')
                  .optional(),
                discount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total discount amount applied to the order.')
                  .optional(),
                total: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe("Order's total price after discounts and tax.")
                  .optional(),
                totalAdditionalFees: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total additional fees before tax.')
                  .optional(),
              })
              .describe('Order price summary.')
              .optional(),
            billingInfo: z
              .object({
                address: z
                  .object({
                    streetAddress: z
                      .object({
                        name: z.string().optional(),
                        number: z.string().optional(),
                      })
                      .optional(),
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Address.')
                  .optional(),
                contactDetails: z
                  .object({
                    firstName: z
                      .string()
                      .describe('First name.')
                      .max(100)
                      .optional()
                      .nullable(),
                    lastName: z
                      .string()
                      .describe('Last name.')
                      .max(100)
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .optional()
                      .nullable(),
                    company: z
                      .string()
                      .describe('Company name.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    vatId: z
                      .object({
                        _id: z
                          .string()
                          .describe("Customer's tax ID.")
                          .optional(),
                        type: z
                          .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                          .describe(
                            'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                          )
                          .optional(),
                      })
                      .describe(
                        'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                      )
                      .optional(),
                  })
                  .describe('Contact details.')
                  .optional(),
              })
              .describe('Billing address and contact details.')
              .optional(),
            shippingInfo: z
              .object({
                carrierId: z
                  .string()
                  .describe(
                    'App Def Id of external provider which was a source of shipping info'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                code: z
                  .string()
                  .describe(
                    'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
                  )
                  .max(250)
                  .optional(),
                logistics: z
                  .intersection(
                    z.object({
                      deliveryTime: z
                        .string()
                        .describe(
                          'Expected delivery time in free text. For example, `"3-5 business days"`.'
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      instructions: z
                        .string()
                        .describe(
                          'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                      deliverByDate: z
                        .date()
                        .describe(
                          'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                        )
                        .optional()
                        .nullable(),
                      deliveryTimeSlot: z
                        .object({
                          from: z
                            .date()
                            .describe('Delivery slot starting time.')
                            .optional()
                            .nullable(),
                          to: z
                            .date()
                            .describe('Delivery slot ending time.')
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Expected delivery time slot with start and end times.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        shippingDestination: z.never().optional(),
                        pickupDetails: z.never().optional(),
                      }),
                      z.object({
                        pickupDetails: z.never().optional(),
                        shippingDestination: z
                          .object({
                            address: z
                              .object({
                                streetAddress: z
                                  .object({
                                    name: z.string().optional(),
                                    number: z.string().optional(),
                                  })
                                  .optional(),
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine2: z.string().optional().nullable(),
                              })
                              .describe('Address.')
                              .optional(),
                            contactDetails: z
                              .object({
                                firstName: z
                                  .string()
                                  .describe('First name.')
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                lastName: z
                                  .string()
                                  .describe('Last name.')
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                phone: z
                                  .string()
                                  .describe('Phone number.')
                                  .optional()
                                  .nullable(),
                                company: z
                                  .string()
                                  .describe('Company name.')
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                vatId: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe("Customer's tax ID.")
                                      .optional(),
                                    type: z
                                      .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                                      .describe(
                                        'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                                  )
                                  .optional(),
                              })
                              .describe('Contact details.')
                              .optional(),
                          })
                          .describe(
                            "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                          ),
                      }),
                      z.object({
                        shippingDestination: z.never().optional(),
                        pickupDetails: z
                          .object({
                            address: z
                              .object({
                                streetAddress: z
                                  .object({
                                    name: z.string().optional(),
                                    number: z.string().optional(),
                                  })
                                  .optional(),
                                city: z.string().optional().nullable(),
                                subdivision: z.string().optional().nullable(),
                                country: z.string().optional().nullable(),
                                postalCode: z.string().optional().nullable(),
                                addressLine2: z.string().optional().nullable(),
                              })
                              .describe('Pickup address.')
                              .optional(),
                            pickupMethod: z
                              .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                              .describe('Pickup method')
                              .optional(),
                          })
                          .describe(
                            'Pickup details for store pickup or pickup point orders.'
                          ),
                      }),
                    ])
                  )
                  .describe('Shipping logistics.')
                  .optional(),
                cost: z
                  .object({
                    price: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Shipping price for display purposes.')
                      .optional(),
                    totalPriceBeforeTax: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe(
                        'Total price of shipping after discounts (when relevant), and before tax.'
                      )
                      .optional(),
                    totalPriceAfterTax: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe(
                        'Shipping price after all discounts (if any exist), and after tax.'
                      )
                      .optional(),
                    taxDetails: z
                      .object({
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of this line item.')
                          .optional(),
                        taxRate: z
                          .string()
                          .describe(
                            'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                          )
                          .optional(),
                        totalTax: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                          )
                          .optional(),
                      })
                      .describe('Tax details.')
                      .optional(),
                    taxInfo: z
                      .object({
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Amount for which tax is calculated.')
                          .optional(),
                        taxRate: z
                          .string()
                          .describe('Tax rate %, as a decimal point.')
                          .optional()
                          .nullable(),
                        taxGroupId: z
                          .string()
                          .describe('Tax group ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        taxIncludedInPrice: z
                          .boolean()
                          .describe(
                            'Indicates whether the price already includes tax.'
                          )
                          .optional(),
                        taxBreakdown: z
                          .array(
                            z.object({
                              jurisdiction: z
                                .string()
                                .describe(
                                  'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                              rate: z
                                .string()
                                .describe(
                                  'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                                )
                                .optional()
                                .nullable(),
                              taxAmount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Amount of tax calculated for this line item.'
                                )
                                .optional(),
                              taxType: z
                                .string()
                                .describe(
                                  'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                              taxName: z
                                .string()
                                .describe(
                                  'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                                )
                                .max(200)
                                .optional()
                                .nullable(),
                              jurisdictionType: z
                                .enum([
                                  'UNDEFINED',
                                  'COUNTRY',
                                  'STATE',
                                  'COUNTY',
                                  'CITY',
                                  'SPECIAL',
                                ])
                                .describe(
                                  'Type of jurisdiction that taxes were calculated for.'
                                )
                                .optional(),
                              nonTaxableAmount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Non-taxable amount of the line item price.'
                                )
                                .optional(),
                              taxableAmount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Taxable amount of the line item price.'
                                )
                                .optional(),
                            })
                          )
                          .max(7)
                          .optional(),
                      })
                      .describe(
                        'Represents all the relevant tax details for a shipping.'
                      )
                      .optional(),
                    discount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Shipping discount before tax.')
                      .optional(),
                  })
                  .describe('Shipping costs.')
                  .optional(),
                region: z
                  .object({
                    name: z
                      .string()
                      .describe(
                        'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Shipping region.')
                  .optional(),
              })
              .describe('Shipping info and selected shipping option details.')
              .optional(),
            buyerNote: z
              .string()
              .describe(
                '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
              )
              .max(1000)
              .optional()
              .nullable(),
            status: z
              .enum([
                'INITIALIZED',
                'APPROVED',
                'CANCELED',
                'PENDING',
                'REJECTED',
              ])
              .describe('Order status.')
              .optional(),
            archived: z
              .boolean()
              .describe('Whether order is archived.')
              .optional()
              .nullable(),
            taxSummary: z
              .object({
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total tax.')
                  .optional(),
              })
              .describe(
                'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
              )
              .optional(),
            taxInfo: z
              .object({
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Calculated tax, added from line items.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated.'
                        )
                        .max(200)
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
                        )
                        .max(200)
                        .optional(),
                      jurisdiction: z
                        .string()
                        .describe(
                          'The name of the jurisdiction in which this tax detail applies.'
                        )
                        .max(200)
                        .optional(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .describe(
                          'The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special).'
                        )
                        .optional(),
                      rate: z
                        .string()
                        .describe(
                          'The rate at which this tax detail was calculated.'
                        )
                        .optional(),
                      aggregatedTaxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'The sum of all the tax from line items that calculated by the tax identifiers.'
                        )
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(50)
                  .optional(),
                taxExempt: z
                  .boolean()
                  .describe(
                    'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Tax information.')
              .optional(),
            appliedDiscounts: z
              .array(
                z.intersection(
                  z.object({
                    discountType: z
                      .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
                      .optional(),
                    lineItemIds: z.array(z.string()).min(0).max(300).optional(),
                    _id: z
                      .string()
                      .describe('Discount ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    lineItemDiscounts: z
                      .array(
                        z.object({
                          _id: z
                            .string()
                            .describe('Line item ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          totalDiscount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Total discount amount for this line item.'
                            )
                            .optional(),
                        })
                      )
                      .max(300)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      coupon: z.never().optional(),
                      merchantDiscount: z.never().optional(),
                      discountRule: z.never().optional(),
                    }),
                    z.object({
                      merchantDiscount: z.never().optional(),
                      discountRule: z.never().optional(),
                      coupon: z
                        .object({
                          _id: z.string().describe('Coupon ID.').optional(),
                          code: z.string().describe('Coupon code.').optional(),
                          name: z.string().describe('Coupon name.').optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Coupon value.')
                            .optional(),
                        })
                        .describe('Coupon applied by the customer.'),
                    }),
                    z.object({
                      coupon: z.never().optional(),
                      discountRule: z.never().optional(),
                      merchantDiscount: z
                        .intersection(
                          z.object({
                            amount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe('Discount amount.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              discountReason: z.never().optional(),
                              description: z.never().optional(),
                            }),
                            z.object({
                              description: z.never().optional(),
                              discountReason: z
                                .enum([
                                  'UNSPECIFIED',
                                  'EXCHANGED_ITEMS',
                                  'BILLING_ADJUSTMENT',
                                ])
                                .describe('Predefined discount reason.'),
                            }),
                            z.object({
                              discountReason: z.never().optional(),
                              description: z
                                .string()
                                .describe(
                                  'Custom discount description as free text.'
                                )
                                .max(200),
                            }),
                          ])
                        )
                        .describe('Discount applied manually by the merchant.'),
                    }),
                    z.object({
                      coupon: z.never().optional(),
                      merchantDiscount: z.never().optional(),
                      discountRule: z
                        .object({
                          _id: z
                            .string()
                            .describe('Discount rule ID')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          name: z
                            .object({
                              original: z
                                .string()
                                .describe(
                                  "Original discount rule name (in site's default language)."
                                )
                                .min(1)
                                .max(256)
                                .optional(),
                              translated: z
                                .string()
                                .describe(
                                  'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                                )
                                .min(1)
                                .max(500)
                                .optional()
                                .nullable(),
                            })
                            .describe('Discount rule name')
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Discount value.')
                            .optional(),
                        })
                        .describe(
                          'Automatic discount applied by the system based on configured discount rules.'
                        ),
                    }),
                  ])
                )
              )
              .max(320)
              .optional(),
            activities: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Activity ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    authorEmail: z
                      .string()
                      .describe("Activity author's email.")
                      .email()
                      .optional()
                      .nullable(),
                    _createdDate: z
                      .date()
                      .describe('Activity creation date and time.')
                      .optional()
                      .nullable(),
                    type: z
                      .enum([
                        'ORDER_REFUNDED',
                        'ORDER_PLACED',
                        'ORDER_PAID',
                        'ORDER_FULFILLED',
                        'ORDER_NOT_FULFILLED',
                        'ORDER_CANCELED',
                        'DOWNLOAD_LINK_SENT',
                        'TRACKING_NUMBER_ADDED',
                        'TRACKING_NUMBER_EDITED',
                        'TRACKING_LINK_ADDED',
                        'SHIPPING_CONFIRMATION_EMAIL_SENT',
                        'INVOICE_ADDED',
                        'INVOICE_REMOVED',
                        'INVOICE_SENT',
                        'FULFILLER_EMAIL_SENT',
                        'SHIPPING_ADDRESS_EDITED',
                        'EMAIL_EDITED',
                        'PICKUP_READY_EMAIL_SENT',
                        'CUSTOM_ACTIVITY',
                        'MERCHANT_COMMENT',
                        'ORDER_PARTIALLY_PAID',
                        'DRAFT_ORDER_CHANGES_APPLIED',
                        'SAVED_PAYMENT_METHOD',
                        'AUTHORIZED_PAYMENT_CREATED',
                        'AUTHORIZED_PAYMENT_CAPTURED',
                        'AUTHORIZED_PAYMENT_VOIDED',
                        'REFUND_INITIATED',
                        'PAYMENT_REFUNDED',
                        'PAYMENT_REFUND_FAILED',
                        'REFUNDED_AS_STORE_CREDIT',
                        'PAYMENT_PENDING',
                        'PAYMENT_CANCELED',
                        'PAYMENT_DECLINED',
                        'ORDER_PENDING',
                        'ORDER_REJECTED',
                        'RECEIPT_CREATED',
                        'RECEIPT_SENT',
                        'CHARGEBACK_CREATED',
                        'CHARGEBACK_REVERSED',
                      ])
                      .describe('Activity type.')
                      .optional(),
                    activityType: z
                      .enum([
                        'ORDER_PLACED',
                        'ORDER_PAID',
                        'ORDER_FULFILLED',
                        'ORDER_NOT_FULFILLED',
                        'ORDER_CANCELED',
                        'DOWNLOAD_LINK_SENT',
                        'TRACKING_NUMBER_ADDED',
                        'TRACKING_NUMBER_EDITED',
                        'TRACKING_LINK_ADDED',
                        'SHIPPING_CONFIRMATION_EMAIL_SENT',
                        'INVOICE_ADDED',
                        'INVOICE_REMOVED',
                        'INVOICE_SENT',
                        'FULFILLER_EMAIL_SENT',
                        'SHIPPING_ADDRESS_EDITED',
                        'EMAIL_EDITED',
                        'PICKUP_READY_EMAIL_SENT',
                        'MERCHANT_COMMENT',
                        'ORDER_PARTIALLY_PAID',
                        'DRAFT_ORDER_CHANGES_APPLIED',
                        'SAVED_PAYMENT_METHOD',
                        'AUTHORIZED_PAYMENT_CREATED',
                        'AUTHORIZED_PAYMENT_CAPTURED',
                        'AUTHORIZED_PAYMENT_VOIDED',
                        'REFUND_INITIATED',
                        'PAYMENT_REFUNDED',
                        'PAYMENT_REFUND_FAILED',
                        'REFUNDED_AS_STORE_CREDIT',
                        'PAYMENT_PENDING',
                        'PAYMENT_CANCELED',
                        'PAYMENT_DECLINED',
                        'ORDER_PENDING',
                        'ORDER_REJECTED',
                        'RECEIPT_CREATED',
                        'RECEIPT_SENT',
                        'CHARGEBACK_CREATED',
                        'CHARGEBACK_REVERSED',
                        'ORDER_REFUNDED',
                      ])
                      .describe('Activity type.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                    }),
                    z.object({
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      merchantComment: z
                        .object({
                          message: z
                            .string()
                            .describe('Merchant comment message.')
                            .min(1)
                            .max(5000)
                            .optional(),
                        })
                        .describe(
                          'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      orderRefunded: z
                        .object({
                          manual: z
                            .boolean()
                            .describe(
                              'Whether order was refunded manually. For example, via payment provider or using cash.'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Refund amount.')
                            .optional(),
                          reason: z
                            .string()
                            .describe('Reason for refund.')
                            .max(200)
                            .optional(),
                        })
                        .describe(
                          'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      draftOrderChangesApplied: z
                        .object({
                          draftOrderId: z
                            .string()
                            .describe('Draft order id.')
                            .optional(),
                          reason: z
                            .string()
                            .describe(
                              'Reason for edit, given by user (optional).'
                            )
                            .max(200)
                            .optional()
                            .nullable(),
                          changes: z
                            .array(
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                  }),
                                  z.object({
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    lineItemChanged: z.object({
                                      _id: z
                                        .string()
                                        .describe('Line item ID.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name after change.')
                                        .optional(),
                                      quantity: z
                                        .object({
                                          originalQuantity: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Item quantity before update.'
                                            )
                                            .min(0)
                                            .max(1000000)
                                            .optional(),
                                          newQuantity: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Item quantity after update.'
                                            )
                                            .min(0)
                                            .max(1000000)
                                            .optional(),
                                          diff: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Difference between original and new quantity. Absolute value.'
                                            )
                                            .min(1)
                                            .max(1000000)
                                            .optional(),
                                          deltaType: z
                                            .enum([
                                              'QUANTITY_INCREASED',
                                              'QUANTITY_DECREASED',
                                            ])
                                            .describe(
                                              'Type of quantity change: increase or decrease.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Item quantity change.')
                                        .optional(),
                                      price: z
                                        .object({
                                          originalPrice: z
                                            .object({
                                              amount: z
                                                .string()
                                                .describe('Amount.')
                                                .optional(),
                                              formattedAmount: z
                                                .string()
                                                .describe(
                                                  'Amount formatted with currency symbol.'
                                                )
                                                .optional(),
                                            })
                                            .describe(
                                              'Item price before update.'
                                            )
                                            .optional(),
                                          newPrice: z
                                            .object({
                                              amount: z
                                                .string()
                                                .describe('Amount.')
                                                .optional(),
                                              formattedAmount: z
                                                .string()
                                                .describe(
                                                  'Amount formatted with currency symbol.'
                                                )
                                                .optional(),
                                            })
                                            .describe(
                                              'Item price after update.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Item price change.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    lineItemAdded: z.object({
                                      _id: z
                                        .string()
                                        .describe('Line item ID.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      quantity: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Added or removed item quantity.'
                                        )
                                        .min(1)
                                        .max(1000000)
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    lineItemRemoved: z.object({
                                      _id: z
                                        .string()
                                        .describe('Line item ID.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      quantity: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Added or removed item quantity.'
                                        )
                                        .min(1)
                                        .max(1000000)
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    discountAdded: z.object({
                                      _id: z
                                        .string()
                                        .describe('Discount id.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              'Value in site default language.'
                                            )
                                            .min(1)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe('Translated value.')
                                            .min(1)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Discount name: coupon name / discount rule name / merchant discount description.'
                                        )
                                        .optional(),
                                      affectedLineItems: z
                                        .array(
                                          z.object({
                                            _id: z
                                              .string()
                                              .describe('Order line item id')
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                            name: z
                                              .object({
                                                original: z
                                                  .string()
                                                  .describe(
                                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                                  )
                                                  .min(1)
                                                  .max(200)
                                                  .optional(),
                                                translated: z
                                                  .string()
                                                  .describe(
                                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                                  )
                                                  .min(1)
                                                  .max(400)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe('Item name.')
                                              .optional(),
                                            amount: z
                                              .object({
                                                amount: z
                                                  .string()
                                                  .describe('Amount.')
                                                  .optional(),
                                                formattedAmount: z
                                                  .string()
                                                  .describe(
                                                    'Amount formatted with currency symbol.'
                                                  )
                                                  .optional(),
                                              })
                                              .describe(
                                                'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .min(0)
                                        .optional(),
                                      totalAmount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Discount amount.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    discountRemoved: z.object({
                                      _id: z
                                        .string()
                                        .describe('Discount id.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              'Value in site default language.'
                                            )
                                            .min(1)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe('Translated value.')
                                            .min(1)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Discount name: coupon name / discount rule name / merchant discount description.'
                                        )
                                        .optional(),
                                      affectedLineItems: z
                                        .array(
                                          z.object({
                                            _id: z
                                              .string()
                                              .describe('Order line item id')
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                            name: z
                                              .object({
                                                original: z
                                                  .string()
                                                  .describe(
                                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                                  )
                                                  .min(1)
                                                  .max(200)
                                                  .optional(),
                                                translated: z
                                                  .string()
                                                  .describe(
                                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                                  )
                                                  .min(1)
                                                  .max(400)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe('Item name.')
                                              .optional(),
                                            amount: z
                                              .object({
                                                amount: z
                                                  .string()
                                                  .describe('Amount.')
                                                  .optional(),
                                                formattedAmount: z
                                                  .string()
                                                  .describe(
                                                    'Amount formatted with currency symbol.'
                                                  )
                                                  .optional(),
                                              })
                                              .describe(
                                                'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .min(0)
                                        .optional(),
                                      totalAmount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Discount amount.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    additionalFeeAdded: z.object({
                                      _id: z
                                        .string()
                                        .describe('Additional fee id.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              'Value in site default language.'
                                            )
                                            .min(1)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe('Translated value.')
                                            .min(1)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Additional fee name.')
                                        .optional(),
                                      affectedLineItems: z
                                        .array(
                                          z.object({
                                            _id: z
                                              .string()
                                              .describe('Order line item id')
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                            name: z
                                              .object({
                                                original: z
                                                  .string()
                                                  .describe(
                                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                                  )
                                                  .min(1)
                                                  .max(200)
                                                  .optional(),
                                                translated: z
                                                  .string()
                                                  .describe(
                                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                                  )
                                                  .min(1)
                                                  .max(400)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe('Item name.')
                                              .optional(),
                                            amount: z
                                              .object({
                                                amount: z
                                                  .string()
                                                  .describe('Amount.')
                                                  .optional(),
                                                formattedAmount: z
                                                  .string()
                                                  .describe(
                                                    'Amount formatted with currency symbol.'
                                                  )
                                                  .optional(),
                                              })
                                              .describe(
                                                'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .min(0)
                                        .optional(),
                                      totalAmount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Additional fee amount.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    additionalFeeRemoved: z.object({
                                      _id: z
                                        .string()
                                        .describe('Additional fee id.')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              'Value in site default language.'
                                            )
                                            .min(1)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe('Translated value.')
                                            .min(1)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Additional fee name.')
                                        .optional(),
                                      affectedLineItems: z
                                        .array(
                                          z.object({
                                            _id: z
                                              .string()
                                              .describe('Order line item id')
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                            name: z
                                              .object({
                                                original: z
                                                  .string()
                                                  .describe(
                                                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                                  )
                                                  .min(1)
                                                  .max(200)
                                                  .optional(),
                                                translated: z
                                                  .string()
                                                  .describe(
                                                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                                  )
                                                  .min(1)
                                                  .max(400)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe('Item name.')
                                              .optional(),
                                            amount: z
                                              .object({
                                                amount: z
                                                  .string()
                                                  .describe('Amount.')
                                                  .optional(),
                                                formattedAmount: z
                                                  .string()
                                                  .describe(
                                                    'Amount formatted with currency symbol.'
                                                  )
                                                  .optional(),
                                              })
                                              .describe(
                                                'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .min(0)
                                        .optional(),
                                      totalAmount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Additional fee amount.')
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    shippingInformationChanged: z
                                      .never()
                                      .optional(),
                                    totalPriceChanged: z.object({
                                      originalTotal: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Order’s total price after discounts and tax. Before update'
                                        )
                                        .optional(),
                                      newTotal: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Order’s total price after discounts and tax. After update'
                                        )
                                        .optional(),
                                    }),
                                  }),
                                  z.object({
                                    lineItemChanged: z.never().optional(),
                                    lineItemAdded: z.never().optional(),
                                    lineItemRemoved: z.never().optional(),
                                    discountAdded: z.never().optional(),
                                    discountRemoved: z.never().optional(),
                                    additionalFeeAdded: z.never().optional(),
                                    additionalFeeRemoved: z.never().optional(),
                                    totalPriceChanged: z.never().optional(),
                                    shippingInformationChanged: z.object({
                                      originalShippingInfo: z
                                        .object({
                                          total: z
                                            .object({
                                              amount: z
                                                .string()
                                                .describe('Amount.')
                                                .optional(),
                                              formattedAmount: z
                                                .string()
                                                .describe(
                                                  'Amount formatted with currency symbol.'
                                                )
                                                .optional(),
                                            })
                                            .describe('Order’s shipping price.')
                                            .optional(),
                                          shippingTitle: z
                                            .string()
                                            .describe('Order’s shipping title.')
                                            .max(250)
                                            .optional(),
                                        })
                                        .describe(
                                          'Order’s Shipping Information. Before update'
                                        )
                                        .optional(),
                                      newShippingInfo: z
                                        .object({
                                          total: z
                                            .object({
                                              amount: z
                                                .string()
                                                .describe('Amount.')
                                                .optional(),
                                              formattedAmount: z
                                                .string()
                                                .describe(
                                                  'Amount formatted with currency symbol.'
                                                )
                                                .optional(),
                                            })
                                            .describe('Order’s shipping price.')
                                            .optional(),
                                          shippingTitle: z
                                            .string()
                                            .describe('Order’s shipping title.')
                                            .max(250)
                                            .optional(),
                                        })
                                        .describe(
                                          'Order’s Shipping Information. After update'
                                        )
                                        .optional(),
                                    }),
                                  }),
                                ])
                              )
                            )
                            .optional(),
                        })
                        .describe(
                          'Details of changes made by the Draft Orders API.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      savedPaymentMethod: z
                        .object({
                          name: z
                            .string()
                            .describe('Payment method name.')
                            .min(1)
                            .max(100)
                            .optional(),
                          description: z
                            .string()
                            .describe('Payment method description.')
                            .min(1)
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Details of the payment method saved for the order.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      authorizedPaymentCreated: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'Payment ID of payment associated with this activity.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                          lastFourDigits: z
                            .string()
                            .describe('The last 4 digits of the card number.')
                            .max(4)
                            .optional()
                            .nullable(),
                          brand: z
                            .string()
                            .describe("Card issuer's brand.")
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe('Details of an authorized payment created.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      authorizedPaymentCaptured: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'Payment ID of payment associated with this activity.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                          lastFourDigits: z
                            .string()
                            .describe('The last 4 digits of the card number.')
                            .max(4)
                            .optional()
                            .nullable(),
                          brand: z
                            .string()
                            .describe("Card issuer's brand.")
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe('Details of an authorized payment captured.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      authorizedPaymentVoided: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'Payment ID of payment associated with this activity.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                          lastFourDigits: z
                            .string()
                            .describe('The last 4 digits of the card number.')
                            .max(4)
                            .optional()
                            .nullable(),
                          brand: z
                            .string()
                            .describe("Card issuer's brand.")
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe('Details of an authorized payment voided.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      refundInitiated: z
                        .object({
                          refundId: z
                            .string()
                            .describe('Refund ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Refund amount.')
                            .optional(),
                          payments: z
                            .array(
                              z.intersection(
                                z.object({
                                  paymentId: z
                                    .string()
                                    .describe('Payment ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  externalRefund: z
                                    .boolean()
                                    .describe(
                                      "Whether refund was made externally and manually on the payment provider's side."
                                    )
                                    .optional(),
                                }),
                                z.xor([
                                  z.object({
                                    regular: z.never().optional(),
                                    giftCard: z.never().optional(),
                                    membership: z.never().optional(),
                                  }),
                                  z.object({
                                    giftCard: z.never().optional(),
                                    membership: z.never().optional(),
                                    regular: z
                                      .object({
                                        amount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Refund amount.')
                                          .optional(),
                                        lastFourDigits: z
                                          .string()
                                          .describe(
                                            'The last 4 digits of the card number.'
                                          )
                                          .max(4)
                                          .optional()
                                          .nullable(),
                                        brand: z
                                          .string()
                                          .describe("Card issuer's brand.")
                                          .max(100)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Regular payment refund.'),
                                  }),
                                  z.object({
                                    regular: z.never().optional(),
                                    membership: z.never().optional(),
                                    giftCard: z
                                      .object({
                                        giftCardPaymentId: z
                                          .string()
                                          .describe('Gift card payment ID.')
                                          .min(1)
                                          .max(100)
                                          .optional()
                                          .nullable(),
                                        amount: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe('Amount.')
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Amount formatted with currency symbol.'
                                              )
                                              .optional(),
                                          })
                                          .describe('Refund amount.')
                                          .optional(),
                                      })
                                      .describe('Gift card payment refund.'),
                                  }),
                                  z.object({
                                    regular: z.never().optional(),
                                    giftCard: z.never().optional(),
                                    membership: z
                                      .object({
                                        membershipId: z
                                          .string()
                                          .describe('Membership ID.')
                                          .min(1)
                                          .max(100)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Membership payment refund.'),
                                  }),
                                ])
                              )
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                          reason: z
                            .string()
                            .describe('Reason for refund.')
                            .max(200)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentRefunded: z
                        .object({
                          refundId: z
                            .string()
                            .describe('Refund ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          payment: z
                            .intersection(
                              z.object({
                                paymentId: z
                                  .string()
                                  .describe('Payment ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                externalRefund: z
                                  .boolean()
                                  .describe(
                                    "Whether refund was made externally and manually on the payment provider's side."
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  regular: z.never().optional(),
                                  giftCard: z.never().optional(),
                                  membership: z.never().optional(),
                                }),
                                z.object({
                                  giftCard: z.never().optional(),
                                  membership: z.never().optional(),
                                  regular: z
                                    .object({
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Refund amount.')
                                        .optional(),
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Regular payment refund.'),
                                }),
                                z.object({
                                  regular: z.never().optional(),
                                  membership: z.never().optional(),
                                  giftCard: z
                                    .object({
                                      giftCardPaymentId: z
                                        .string()
                                        .describe('Gift card payment ID.')
                                        .min(1)
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Refund amount.')
                                        .optional(),
                                    })
                                    .describe('Gift card payment refund.'),
                                }),
                                z.object({
                                  regular: z.never().optional(),
                                  giftCard: z.never().optional(),
                                  membership: z
                                    .object({
                                      membershipId: z
                                        .string()
                                        .describe('Membership ID.')
                                        .min(1)
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Membership payment refund.'),
                                }),
                              ])
                            )
                            .describe('Details about the refunded payment.')
                            .optional(),
                        })
                        .describe(
                          'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentRefundFailed: z
                        .object({
                          refundId: z
                            .string()
                            .describe('Refund ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          payment: z
                            .intersection(
                              z.object({
                                paymentId: z
                                  .string()
                                  .describe('Payment ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                externalRefund: z
                                  .boolean()
                                  .describe(
                                    "Whether refund was made externally and manually on the payment provider's side."
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  regular: z.never().optional(),
                                  giftCard: z.never().optional(),
                                  membership: z.never().optional(),
                                }),
                                z.object({
                                  giftCard: z.never().optional(),
                                  membership: z.never().optional(),
                                  regular: z
                                    .object({
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Refund amount.')
                                        .optional(),
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Regular payment refund.'),
                                }),
                                z.object({
                                  regular: z.never().optional(),
                                  membership: z.never().optional(),
                                  giftCard: z
                                    .object({
                                      giftCardPaymentId: z
                                        .string()
                                        .describe('Gift card payment ID.')
                                        .min(1)
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Refund amount.')
                                        .optional(),
                                    })
                                    .describe('Gift card payment refund.'),
                                }),
                                z.object({
                                  regular: z.never().optional(),
                                  giftCard: z.never().optional(),
                                  membership: z
                                    .object({
                                      membershipId: z
                                        .string()
                                        .describe('Membership ID.')
                                        .min(1)
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Membership payment refund.'),
                                }),
                              ])
                            )
                            .describe(
                              'Details about the failed payment refund.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      refundedAsStoreCredit: z
                        .object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Refund amount.')
                            .optional(),
                          reason: z
                            .string()
                            .describe('Reason for refund.')
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe('Details of refund to store credit.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentPending: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({ regular: z.never().optional() }),
                            z.object({
                              regular: z
                                .intersection(
                                  z.object({
                                    amount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Payment amount.')
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      creditCardDetails: z.never().optional(),
                                    }),
                                    z.object({
                                      creditCardDetails: z
                                        .object({
                                          lastFourDigits: z
                                            .string()
                                            .describe(
                                              'The last 4 digits of the card number.'
                                            )
                                            .max(4)
                                            .optional()
                                            .nullable(),
                                          brand: z
                                            .string()
                                            .describe("Card issuer's brand.")
                                            .max(100)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Credit card details.'),
                                    }),
                                  ])
                                )
                                .describe('Regular payment.'),
                            }),
                          ])
                        )
                        .describe('Details of a pending payment.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentCanceled: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({ regular: z.never().optional() }),
                            z.object({
                              regular: z
                                .intersection(
                                  z.object({
                                    amount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Payment amount.')
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      creditCardDetails: z.never().optional(),
                                    }),
                                    z.object({
                                      creditCardDetails: z
                                        .object({
                                          lastFourDigits: z
                                            .string()
                                            .describe(
                                              'The last 4 digits of the card number.'
                                            )
                                            .max(4)
                                            .optional()
                                            .nullable(),
                                          brand: z
                                            .string()
                                            .describe("Card issuer's brand.")
                                            .max(100)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Credit card details.'),
                                    }),
                                  ])
                                )
                                .describe('Regular payment.'),
                            }),
                          ])
                        )
                        .describe('Details of a canceled payment.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      paymentDeclined: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({ regular: z.never().optional() }),
                            z.object({
                              regular: z
                                .intersection(
                                  z.object({
                                    amount: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Payment amount.')
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      creditCardDetails: z.never().optional(),
                                    }),
                                    z.object({
                                      creditCardDetails: z
                                        .object({
                                          lastFourDigits: z
                                            .string()
                                            .describe(
                                              'The last 4 digits of the card number.'
                                            )
                                            .max(4)
                                            .optional()
                                            .nullable(),
                                          brand: z
                                            .string()
                                            .describe("Card issuer's brand.")
                                            .max(100)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Credit card details.'),
                                    }),
                                  ])
                                )
                                .describe('Regular payment.'),
                            }),
                          ])
                        )
                        .describe('Details of a declined payment.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      receiptCreated: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              wixReceipt: z.never().optional(),
                              externalReceipt: z.never().optional(),
                            }),
                            z.object({
                              externalReceipt: z.never().optional(),
                              wixReceipt: z
                                .object({
                                  receiptId: z
                                    .string()
                                    .describe('Receipt ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  displayNumber: z
                                    .string()
                                    .describe('Display number of receipt.')
                                    .min(1)
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Receipt created by Wix.'),
                            }),
                            z.object({
                              wixReceipt: z.never().optional(),
                              externalReceipt: z
                                .object({
                                  receiptId: z
                                    .string()
                                    .describe('Receipt ID.')
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  displayNumber: z
                                    .string()
                                    .describe('Display number of receipt.')
                                    .min(1)
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Receipt created by an external system.'
                                ),
                            }),
                          ])
                        )
                        .describe('Receipt was added for associated payment.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      receiptSent: z
                        .intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe(
                                'Payment ID of payment associated with this activity.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              wixReceipt: z.never().optional(),
                              externalReceipt: z.never().optional(),
                            }),
                            z.object({
                              externalReceipt: z.never().optional(),
                              wixReceipt: z
                                .object({
                                  receiptId: z
                                    .string()
                                    .describe('Receipt ID.')
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                  displayNumber: z
                                    .string()
                                    .describe('Display number of receipt.')
                                    .min(1)
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Receipt created by Wix.'),
                            }),
                            z.object({
                              wixReceipt: z.never().optional(),
                              externalReceipt: z
                                .object({
                                  receiptId: z
                                    .string()
                                    .describe('Receipt ID.')
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  displayNumber: z
                                    .string()
                                    .describe('Display number of receipt.')
                                    .min(1)
                                    .max(40)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Receipt created by an external system.'
                                ),
                            }),
                          ])
                        )
                        .describe('Receipt sent to customer.'),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackReversed: z.never().optional(),
                      chargebackCreated: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'ID of the payment that received a chargeback.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          chargebackId: z
                            .string()
                            .describe('Chargeback ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Chargeback amount.')
                            .optional(),
                          paymentDetails: z
                            .intersection(
                              z.object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Payment amount.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  creditCardDetails: z.never().optional(),
                                }),
                                z.object({
                                  creditCardDetails: z
                                    .object({
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Credit card details.'),
                                }),
                              ])
                            )
                            .describe('Payment details.')
                            .optional(),
                        })
                        .describe(
                          'Order received a chargeback for one of its payments.'
                        ),
                    }),
                    z.object({
                      merchantComment: z.never().optional(),
                      orderRefunded: z.never().optional(),
                      draftOrderChangesApplied: z.never().optional(),
                      savedPaymentMethod: z.never().optional(),
                      authorizedPaymentCreated: z.never().optional(),
                      authorizedPaymentCaptured: z.never().optional(),
                      authorizedPaymentVoided: z.never().optional(),
                      refundInitiated: z.never().optional(),
                      paymentRefunded: z.never().optional(),
                      paymentRefundFailed: z.never().optional(),
                      refundedAsStoreCredit: z.never().optional(),
                      paymentPending: z.never().optional(),
                      paymentCanceled: z.never().optional(),
                      paymentDeclined: z.never().optional(),
                      receiptCreated: z.never().optional(),
                      receiptSent: z.never().optional(),
                      chargebackCreated: z.never().optional(),
                      chargebackReversed: z
                        .object({
                          paymentId: z
                            .string()
                            .describe(
                              'ID of the payment involved with the chargeback.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          chargebackId: z
                            .string()
                            .describe('Chargeback ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Chargeback amount.')
                            .optional(),
                          reversalAmount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Chargeback reversal amount.')
                            .optional(),
                          paymentDetails: z
                            .intersection(
                              z.object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Payment amount.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  creditCardDetails: z.never().optional(),
                                }),
                                z.object({
                                  creditCardDetails: z
                                    .object({
                                      lastFourDigits: z
                                        .string()
                                        .describe(
                                          'The last 4 digits of the card number.'
                                        )
                                        .max(4)
                                        .optional()
                                        .nullable(),
                                      brand: z
                                        .string()
                                        .describe("Card issuer's brand.")
                                        .max(100)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Credit card details.'),
                                }),
                              ])
                            )
                            .describe('Payment details.')
                            .optional(),
                        })
                        .describe(
                          "Chargeback reversed for one of the order's payments."
                        ),
                    }),
                  ])
                )
              )
              .max(1000)
              .optional(),
            attributionSource: z
              .enum(['UNSPECIFIED', 'FACEBOOK_ADS'])
              .describe('Order attribution source.')
              .optional(),
            createdBy: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    userId: z.never().optional(),
                    memberId: z.never().optional(),
                    visitorId: z.never().optional(),
                    appId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    visitorId: z.never().optional(),
                    appId: z.never().optional(),
                    userId: z
                      .string()
                      .describe(
                        'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    userId: z.never().optional(),
                    visitorId: z.never().optional(),
                    appId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe(
                        'Member ID - when the order was created by a **logged in** site visitor.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    userId: z.never().optional(),
                    memberId: z.never().optional(),
                    appId: z.never().optional(),
                    visitorId: z
                      .string()
                      .describe(
                        'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    userId: z.never().optional(),
                    memberId: z.never().optional(),
                    visitorId: z.never().optional(),
                    appId: z
                      .string()
                      .describe(
                        'App ID - when the order was created by an external application.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe("ID of the order's initiator.")
              .optional(),
            channelInfo: z
              .object({
                type: z
                  .enum([
                    'UNSPECIFIED',
                    'WEB',
                    'POS',
                    'EBAY',
                    'AMAZON',
                    'OTHER_PLATFORM',
                    'WIX_APP_STORE',
                    'WIX_INVOICES',
                    'BACKOFFICE_MERCHANT',
                    'WISH',
                    'CLASS_PASS',
                    'GLOBAL_E',
                    'FACEBOOK',
                    'ETSY',
                    'TIKTOK',
                    'FAIRE_COM',
                    'PAYPAL_AGENTIC_CHECKOUT',
                    'STRIPE_AGENTIC_CHECKOUT',
                  ])
                  .describe(
                    'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
                  )
                  .optional(),
                externalOrderId: z
                  .string()
                  .describe(
                    'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                externalOrderUrl: z
                  .string()
                  .describe(
                    'URL to the order in the external system. Relevant when recording orders from external platforms.'
                  )
                  .max(300)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Information about the sales channel that submitted this order.'
              )
              .optional(),
            seenByAHuman: z
              .boolean()
              .describe(
                'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
              )
              .optional()
              .nullable(),
            checkoutId: z
              .string()
              .describe('Checkout ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            customFields: z
              .array(
                z.object({
                  value: z
                    .any()
                    .describe('Custom field value.')
                    .optional()
                    .nullable(),
                  title: z
                    .string()
                    .describe('Custom field title.')
                    .min(1)
                    .max(500)
                    .optional(),
                  translatedTitle: z
                    .string()
                    .describe('Translated custom field title.')
                    .min(1)
                    .max(500)
                    .optional()
                    .nullable(),
                })
              )
              .optional(),
            balanceSummary: z
              .object({
                balance: z
                  .object({
                    amount: z
                      .string()
                      .describe(
                        'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
                      )
                      .optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Current amount left to pay.')
                  .optional(),
                paid: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
                  )
                  .optional(),
                refunded: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all successfully refunded payments.')
                  .optional(),
                authorized: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all authorized payments.')
                  .optional(),
                pendingRefund: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all pending refund transactions.')
                  .optional(),
                pending: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all pending transactions.')
                  .optional(),
                chargeback: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all transaction chargebacks.')
                  .optional(),
                chargebackReversal: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Sum of all chargeback reversals.')
                  .optional(),
                platformFees: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
                  )
                  .optional(),
                totalMinusPlatformFees: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
                  )
                  .optional(),
              })
              .describe('Balance summary.')
              .optional(),
            additionalFees: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe(
                      "Additional fee's unique code for future processing."
                    )
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Name of additional fee.')
                    .min(1)
                    .max(50)
                    .optional(),
                  price: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe("Additional fee's price.")
                    .optional(),
                  taxDetails: z
                    .object({
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of this line item.')
                        .optional(),
                      taxRate: z
                        .string()
                        .describe(
                          'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                        )
                        .optional(),
                      totalTax: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                        )
                        .optional(),
                    })
                    .describe('Tax details.')
                    .optional(),
                  taxInfo: z
                    .object({
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                        )
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Amount for which tax is calculated.')
                        .optional(),
                      taxRate: z
                        .string()
                        .describe('Tax rate %, as a decimal point.')
                        .optional()
                        .nullable(),
                      taxGroupId: z
                        .string()
                        .describe('Tax group ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      taxIncludedInPrice: z
                        .boolean()
                        .describe(
                          'Indicates whether the price already includes tax.'
                        )
                        .optional(),
                      taxBreakdown: z
                        .array(
                          z.object({
                            jurisdiction: z
                              .string()
                              .describe(
                                'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            rate: z
                              .string()
                              .describe(
                                'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                              )
                              .optional()
                              .nullable(),
                            taxAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Amount of tax calculated for this line item.'
                              )
                              .optional(),
                            taxType: z
                              .string()
                              .describe(
                                'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            taxName: z
                              .string()
                              .describe(
                                'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                              )
                              .max(200)
                              .optional()
                              .nullable(),
                            jurisdictionType: z
                              .enum([
                                'UNDEFINED',
                                'COUNTRY',
                                'STATE',
                                'COUNTY',
                                'CITY',
                                'SPECIAL',
                              ])
                              .describe(
                                'Type of jurisdiction that taxes were calculated for.'
                              )
                              .optional(),
                            nonTaxableAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Non-taxable amount of the line item price.'
                              )
                              .optional(),
                            taxableAmount: z
                              .object({
                                amount: z
                                  .string()
                                  .describe('Amount.')
                                  .optional(),
                                formattedAmount: z
                                  .string()
                                  .describe(
                                    'Amount formatted with currency symbol.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Taxable amount of the line item price.'
                              )
                              .optional(),
                          })
                        )
                        .max(7)
                        .optional(),
                    })
                    .describe(
                      'Represents all the relevant tax details for additional fee.'
                    )
                    .optional(),
                  providerAppId: z
                    .string()
                    .describe("SPI implementer's `appId`.")
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  priceBeforeTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe("Additional fee's price before tax.")
                    .optional(),
                  priceAfterTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe("Additional fee's price after tax.")
                    .optional(),
                  _id: z
                    .string()
                    .describe("Additional fee's id.")
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  lineItemIds: z.array(z.string()).optional(),
                  source: z
                    .enum([
                      'SERVICE_PLUGIN',
                      'ITEM',
                      'MANUAL',
                      'SHIPPING',
                      'PLATFORM',
                    ])
                    .describe(
                      'Specifies the entity that added the additional fee.'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            purchaseFlowId: z
              .string()
              .describe(
                'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            recipientInfo: z
              .object({
                address: z
                  .object({
                    streetAddress: z
                      .object({
                        name: z.string().optional(),
                        number: z.string().optional(),
                      })
                      .optional(),
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Address.')
                  .optional(),
                contactDetails: z
                  .object({
                    firstName: z
                      .string()
                      .describe('First name.')
                      .max(100)
                      .optional()
                      .nullable(),
                    lastName: z
                      .string()
                      .describe('Last name.')
                      .max(100)
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .optional()
                      .nullable(),
                    company: z
                      .string()
                      .describe('Company name.')
                      .max(1000)
                      .optional()
                      .nullable(),
                    vatId: z
                      .object({
                        _id: z
                          .string()
                          .describe("Customer's tax ID.")
                          .optional(),
                        type: z
                          .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                          .describe(
                            'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                          )
                          .optional(),
                      })
                      .describe(
                        'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                      )
                      .optional(),
                  })
                  .describe('Contact details.')
                  .optional(),
              })
              .describe(
                "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
              )
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
              )
              .optional(),
            purchasedDate: z
              .date()
              .describe(
                'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
              )
              .optional()
              .nullable(),
            businessLocation: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                name: z
                  .string()
                  .describe('Location name.')
                  .min(1)
                  .max(500)
                  .optional(),
              })
              .describe('Order location.')
              .optional(),
            payAfterFreeTrial: z
              .object({
                subtotal: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Subtotal of all line items, before discounts and before tax.'
                  )
                  .optional(),
                shipping: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total shipping price, before discounts and before tax.'
                  )
                  .optional(),
                tax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total tax applied to the order.')
                  .optional(),
                discount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total discount amount applied to the order.')
                  .optional(),
                total: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe("Order's total price after discounts and tax.")
                  .optional(),
                totalAdditionalFees: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total additional fees before tax.')
                  .optional(),
              })
              .describe(
                'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
              )
              .optional(),
            platformFeeSummary: z
              .object({
                total: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Total sum of all platform fees.')
                  .optional(),
                totalPassOn: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total amount of platform fees with `PASS_ON` charge type.'
                  )
                  .optional(),
                totalAbsorbed: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Total amount of platform fees with `ABSORBED` charge type.'
                  )
                  .optional(),
                fees: z
                  .array(
                    z.object({
                      name: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                            )
                            .min(1)
                            .max(200)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                            )
                            .min(1)
                            .max(400)
                            .optional()
                            .nullable(),
                        })
                        .describe('Platform fee name.')
                        .optional(),
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Platform fee amount.')
                        .optional(),
                      lineItemId: z
                        .string()
                        .describe(
                          'ID of the line item the platform fee applies to.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      chargeType: z
                        .enum(['PASS_ON', 'ABSORBED'])
                        .describe('Platform fee charge type.')
                        .optional(),
                      percentageRate: z
                        .string()
                        .describe(
                          'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
                        )
                        .optional(),
                    })
                  )
                  .max(300)
                  .optional(),
              })
              .describe(
                'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
              )
              .optional(),
          })
          .describe(
            'Updated order.\n\nReturned when `returnFullEntity = true`.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const AddActivitiesRequest = z.object({
  orderId: z
    .string()
    .describe('ID of the order to add activities to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      orderActivities: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Activity ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              authorEmail: z
                .string()
                .describe("Activity author's email.")
                .email()
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Activity creation date and time.')
                .optional()
                .nullable(),
              type: z
                .enum([
                  'ORDER_REFUNDED',
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'CUSTOM_ACTIVITY',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                ])
                .optional(),
              activityType: z.enum([
                'ORDER_PLACED',
                'ORDER_PAID',
                'ORDER_FULFILLED',
                'ORDER_NOT_FULFILLED',
                'ORDER_CANCELED',
                'DOWNLOAD_LINK_SENT',
                'TRACKING_NUMBER_ADDED',
                'TRACKING_NUMBER_EDITED',
                'TRACKING_LINK_ADDED',
                'SHIPPING_CONFIRMATION_EMAIL_SENT',
                'INVOICE_ADDED',
                'INVOICE_REMOVED',
                'INVOICE_SENT',
                'FULFILLER_EMAIL_SENT',
                'SHIPPING_ADDRESS_EDITED',
                'EMAIL_EDITED',
                'PICKUP_READY_EMAIL_SENT',
                'MERCHANT_COMMENT',
                'ORDER_PARTIALLY_PAID',
                'DRAFT_ORDER_CHANGES_APPLIED',
                'SAVED_PAYMENT_METHOD',
                'AUTHORIZED_PAYMENT_CREATED',
                'AUTHORIZED_PAYMENT_CAPTURED',
                'AUTHORIZED_PAYMENT_VOIDED',
                'REFUND_INITIATED',
                'PAYMENT_REFUNDED',
                'PAYMENT_REFUND_FAILED',
                'REFUNDED_AS_STORE_CREDIT',
                'PAYMENT_PENDING',
                'PAYMENT_CANCELED',
                'PAYMENT_DECLINED',
                'ORDER_PENDING',
                'ORDER_REJECTED',
                'RECEIPT_CREATED',
                'RECEIPT_SENT',
                'CHARGEBACK_CREATED',
                'CHARGEBACK_REVERSED',
                'ORDER_REFUNDED',
              ]),
            }),
            z.xor([
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
              }),
              z.object({
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                merchantComment: z
                  .object({
                    message: z
                      .string()
                      .describe('Merchant comment message.')
                      .min(1)
                      .max(5000)
                      .optional(),
                  })
                  .describe(
                    'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                orderRefunded: z
                  .object({
                    manual: z
                      .boolean()
                      .describe(
                        'Whether order was refunded manually. For example, via payment provider or using cash.'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                draftOrderChangesApplied: z
                  .object({
                    draftOrderId: z
                      .string()
                      .describe('Draft order id.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for edit, given by user (optional).')
                      .max(200)
                      .optional()
                      .nullable(),
                    changes: z
                      .array(
                        z.intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                            }),
                            z.object({
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemChanged: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name after change.')
                                  .optional(),
                                quantity: z
                                  .object({
                                    originalQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity before update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    newQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity after update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    diff: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Difference between original and new quantity. Absolute value.'
                                      )
                                      .min(1)
                                      .max(1000000)
                                      .optional(),
                                    deltaType: z
                                      .enum([
                                        'QUANTITY_INCREASED',
                                        'QUANTITY_DECREASED',
                                      ])
                                      .describe(
                                        'Type of quantity change: increase or decrease.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item quantity change.')
                                  .optional(),
                                price: z
                                  .object({
                                    originalPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price before update.')
                                      .optional(),
                                    newPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price after update.')
                                      .optional(),
                                  })
                                  .describe('Item price change.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              totalPriceChanged: z.object({
                                originalTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. Before update'
                                  )
                                  .optional(),
                                newTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.object({
                                originalShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. Before update'
                                  )
                                  .optional(),
                                newShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                          ])
                        )
                      )
                      .optional(),
                  })
                  .describe('Details of changes made by the Draft Orders API.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                savedPaymentMethod: z
                  .object({
                    name: z
                      .string()
                      .describe('Payment method name.')
                      .min(1)
                      .max(100)
                      .optional(),
                    description: z
                      .string()
                      .describe('Payment method description.')
                      .min(1)
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of the payment method saved for the order.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment created.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCaptured: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment captured.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentVoided: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment voided.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundInitiated: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    payments: z
                      .array(
                        z.intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe('Payment ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            externalRefund: z
                              .boolean()
                              .describe(
                                "Whether refund was made externally and manually on the payment provider's side."
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                            }),
                            z.object({
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                              regular: z
                                .object({
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Regular payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              membership: z.never().optional(),
                              giftCard: z
                                .object({
                                  giftCardPaymentId: z
                                    .string()
                                    .describe('Gift card payment ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                })
                                .describe('Gift card payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z
                                .object({
                                  membershipId: z
                                    .string()
                                    .describe('Membership ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Membership payment refund.'),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(50)
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefunded: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the refunded payment.')
                      .optional(),
                  })
                  .describe(
                    'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefundFailed: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the failed payment refund.')
                      .optional(),
                  })
                  .describe(
                    'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundedAsStoreCredit: z
                  .object({
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of refund to store credit.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentPending: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a pending payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentCanceled: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a canceled payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentDeclined: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a declined payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptCreated: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt was added for associated payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptSent: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt sent to customer.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackReversed: z.never().optional(),
                chargebackCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe('ID of the payment that received a chargeback.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    'Order received a chargeback for one of its payments.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'ID of the payment involved with the chargeback.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    reversalAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback reversal amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    "Chargeback reversed for one of the order's payments."
                  ),
              }),
            ])
          )
        )
        .max(300)
        .optional(),
    })
    .optional(),
});
export const AddActivitiesResponse = z.object({
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      number: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          "Order number displayed in the site owner's dashboard (auto-generated)."
        )
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      lineItems: z
        .array(
          z.object({
            _id: z.string().describe('Line item ID.').optional(),
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
              )
              .optional(),
            catalogReference: z
              .object({
                catalogItemId: z
                  .string()
                  .describe('ID of the item within the catalog it belongs to.')
                  .min(1)
                  .max(36)
                  .optional(),
                appId: z
                  .string()
                  .describe(
                    'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                  )
                  .min(1)
                  .optional(),
                options: z
                  .record(z.string(), z.any())
                  .describe(
                    'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "References to the line item's origin catalog. Empty for custom line items."
              )
              .optional(),
            quantity: z
              .number()
              .int()
              .describe('Line item quantity.')
              .min(1)
              .max(100000)
              .optional(),
            totalDiscount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Total discount for this line item's entire quantity.")
              .optional(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .min(0)
              .max(20)
              .optional(),
            image: z.string().describe('Line item image.').optional(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe(
                'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
              )
              .optional(),
            itemType: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    preset: z.never().optional(),
                    custom: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    preset: z
                      .enum([
                        'UNRECOGNISED',
                        'PHYSICAL',
                        'DIGITAL',
                        'GIFT_CARD',
                        'SERVICE',
                      ])
                      .describe('Preset item type.'),
                  }),
                  z.object({
                    preset: z.never().optional(),
                    custom: z
                      .string()
                      .describe(
                        'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                      ),
                  }),
                ])
              )
              .describe('Item type. Either a preset type or custom.')
              .optional(),
            fulfillerId: z
              .string()
              .describe(
                'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            refundQuantity: z
              .number()
              .int()
              .describe('Number of items that were refunded.')
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            restockQuantity: z
              .number()
              .int()
              .describe(
                'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
              )
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            price: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Line item price after line item discounts for display purposes.'
              )
              .optional(),
            priceBeforeDiscounts: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
              )
              .optional(),
            totalPriceBeforeTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total price after discounts, and before tax.')
              .optional(),
            totalPriceAfterTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total price after all discounts and tax.')
              .optional(),
            paymentOption: z
              .enum([
                'FULL_PAYMENT_ONLINE',
                'FULL_PAYMENT_OFFLINE',
                'MEMBERSHIP',
                'DEPOSIT_ONLINE',
                'MEMBERSHIP_OFFLINE',
                'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
              ])
              .describe(
                'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
              )
              .optional(),
            taxDetails: z
              .object({
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Taxable amount of this line item.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                  )
                  .optional(),
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                  )
                  .optional(),
              })
              .describe(
                'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
              )
              .optional(),
            taxInfo: z
              .object({
                taxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                  )
                  .optional(),
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Amount for which tax is calculated.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe('Tax rate %, as a decimal point.')
                  .optional()
                  .nullable(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                taxIncludedInPrice: z
                  .boolean()
                  .describe('Indicates whether the price already includes tax.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      jurisdiction: z
                        .string()
                        .describe(
                          'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                        )
                        .optional()
                        .nullable(),
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Amount of tax calculated for this line item.'
                        )
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .describe(
                          'Type of jurisdiction that taxes were calculated for.'
                        )
                        .optional(),
                      nonTaxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Non-taxable amount of the line item price.')
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of the line item price.')
                        .optional(),
                    })
                  )
                  .max(7)
                  .optional(),
              })
              .describe(
                'Represents all the relevant tax details for a specific line item.'
              )
              .optional(),
            digitalFile: z
              .object({
                fileId: z
                  .string()
                  .describe('ID of the secure file in media.')
                  .min(1)
                  .max(100)
                  .optional(),
                link: z
                  .string()
                  .describe(
                    'Link will exist after the digital links have been generated on the order.'
                  )
                  .url()
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                expirationDate: z
                  .date()
                  .describe('Link expiration time and date.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Digital file identifier, relevant only for items with type DIGITAL.'
              )
              .optional(),
            subscriptionInfo: z
              .object({
                _id: z
                  .string()
                  .describe('Subscription ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                cycleNumber: z
                  .number()
                  .int()
                  .describe(
                    'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
                  )
                  .min(0)
                  .optional(),
                subscriptionOptionTitle: z
                  .string()
                  .describe(
                    'Subscription option title. For example, `"Monthly coffee Subscription"`.'
                  )
                  .min(0)
                  .max(150)
                  .optional(),
                subscriptionOptionDescription: z
                  .string()
                  .describe(
                    'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                subscriptionSettings: z
                  .object({
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Frequency of recurring payment.')
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                      )
                      .min(1)
                      .max(3650)
                      .optional()
                      .nullable(),
                    autoRenewal: z
                      .boolean()
                      .describe(
                        'Whether subscription is renewed automatically at the end of each period.'
                      )
                      .optional(),
                    billingCycles: z
                      .number()
                      .int()
                      .describe(
                        'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    enableCustomerCancellation: z
                      .boolean()
                      .describe(
                        'Whether to allow the customer to cancel the subscription.'
                      )
                      .optional(),
                    freeTrialPeriod: z
                      .object({
                        frequency: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe(
                            'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                          )
                          .optional(),
                        interval: z
                          .number()
                          .int()
                          .describe(
                            'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                          )
                          .min(1)
                          .max(3650)
                          .optional(),
                      })
                      .describe(
                        'Period until first cycle starts. If None => no free trial'
                      )
                      .optional(),
                  })
                  .describe('Subscription detailed information.')
                  .optional(),
                chargesDescription: z
                  .string()
                  .describe(
                    'Description of the charges that will be applied for subscription.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
              .describe('Subscription info.')
              .optional(),
            priceDescription: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                  )
                  .min(1)
                  .max(100)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
              )
              .optional(),
            depositAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
              )
              .optional(),
            locations: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Location id in the associated owner app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'Location owner app, if not provided then the site business info locations will be used.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe('Quantity for specific location.')
                    .min(1)
                    .max(100000)
                    .optional(),
                  name: z
                    .string()
                    .describe('Location name.')
                    .max(500)
                    .optional()
                    .nullable(),
                })
              )
              .min(0)
              .max(5)
              .optional(),
            lineItemPrice: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total price **after** catalog discounts and line item discounts.'
              )
              .optional(),
            taxableAddress: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ addressType: z.never().optional() }),
                  z.object({
                    addressType: z
                      .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                      .describe(
                        'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                      ),
                  }),
                ])
              )
              .describe('Address used for tax calculation.')
              .optional(),
            priceUndetermined: z
              .boolean()
              .describe(
                'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            modifierGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Modifier group ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  name: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Modifier group name.')
                    .optional(),
                  modifiers: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Modifier ID.')
                          .min(1)
                          .max(36)
                          .optional(),
                        quantity: z
                          .number()
                          .int()
                          .describe('The quantity of this modifier.')
                          .min(1)
                          .max(100000)
                          .optional()
                          .nullable(),
                        label: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Primary display label for the modifier.')
                          .optional(),
                        details: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Additional details.')
                          .optional(),
                        price: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('The price of the modifier.')
                          .optional(),
                      })
                    )
                    .min(1)
                    .max(10)
                    .optional(),
                })
              )
              .max(10)
              .optional(),
          })
        )
        .min(1)
        .max(300)
        .optional(),
      buyerInfo: z
        .intersection(
          z.object({
            contactId: z
              .string()
              .describe(
                "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            email: z
              .string()
              .describe("Buyer's email address.")
              .email()
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              visitorId: z.never().optional(),
              memberId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              visitorId: z
                .string()
                .describe(
                  "Visitor ID. Returned when the buyer isn't a logged-in site member."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              visitorId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID. Returned when the buyer is a logged-in site member.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe('Buyer information.')
        .optional(),
      paymentStatus: z
        .enum([
          'UNSPECIFIED',
          'NOT_PAID',
          'PAID',
          'PARTIALLY_REFUNDED',
          'FULLY_REFUNDED',
          'PENDING',
          'PARTIALLY_PAID',
          'PENDING_MERCHANT',
          'CANCELED',
          'DECLINED',
        ])
        .describe('Order payment status.')
        .optional(),
      fulfillmentStatus: z
        .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
        .describe('Order fulfillment status.')
        .optional(),
      buyerLanguage: z
        .string()
        .describe(
          'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
        )
        .optional()
        .nullable(),
      weightUnit: z
        .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
        .describe("Weight measurement unit - defaults to site's weight unit.")
        .optional(),
      currency: z
        .string()
        .describe(
          'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
        )
        .optional()
        .nullable(),
      currencyConversionDetails: z
        .object({
          originalCurrency: z
            .string()
            .describe(
              "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
            )
            .optional()
            .nullable(),
          conversionRate: z
            .string()
            .describe(
              'The rate used for converting the original currency to the currency used for payment.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Currency conversion details. For use with multi-currency sites.'
        )
        .optional(),
      taxIncludedInPrices: z
        .boolean()
        .describe('Whether tax is included in line item prices.')
        .optional(),
      siteLanguage: z
        .string()
        .describe('Site language in which original values are shown.')
        .optional()
        .nullable(),
      priceSummary: z
        .object({
          subtotal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Subtotal of all line items, before discounts and before tax.'
            )
            .optional(),
          shipping: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total shipping price, before discounts and before tax.')
            .optional(),
          tax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax applied to the order.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total discount amount applied to the order.')
            .optional(),
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe("Order's total price after discounts and tax.")
            .optional(),
          totalAdditionalFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total additional fees before tax.')
            .optional(),
        })
        .describe('Order price summary.')
        .optional(),
      billingInfo: z
        .object({
          address: z
            .object({
              streetAddress: z
                .object({
                  name: z.string().optional(),
                  number: z.string().optional(),
                })
                .optional(),
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Address.')
            .optional(),
          contactDetails: z
            .object({
              firstName: z
                .string()
                .describe('First name.')
                .max(100)
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe('Last name.')
                .max(100)
                .optional()
                .nullable(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              company: z
                .string()
                .describe('Company name.')
                .max(1000)
                .optional()
                .nullable(),
              vatId: z
                .object({
                  _id: z.string().describe("Customer's tax ID.").optional(),
                  type: z
                    .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                    .describe(
                      'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                    )
                    .optional(),
                })
                .describe(
                  'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                )
                .optional(),
            })
            .describe('Contact details.')
            .optional(),
        })
        .describe('Billing address and contact details.')
        .optional(),
      shippingInfo: z
        .object({
          carrierId: z
            .string()
            .describe(
              'App Def Id of external provider which was a source of shipping info'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          code: z
            .string()
            .describe(
              'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          title: z
            .string()
            .describe(
              'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
            )
            .max(250)
            .optional(),
          logistics: z
            .intersection(
              z.object({
                deliveryTime: z
                  .string()
                  .describe(
                    'Expected delivery time in free text. For example, `"3-5 business days"`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                instructions: z
                  .string()
                  .describe(
                    'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                deliverByDate: z
                  .date()
                  .describe(
                    'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                  )
                  .optional()
                  .nullable(),
                deliveryTimeSlot: z
                  .object({
                    from: z
                      .date()
                      .describe('Delivery slot starting time.')
                      .optional()
                      .nullable(),
                    to: z
                      .date()
                      .describe('Delivery slot ending time.')
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Expected delivery time slot with start and end times.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  shippingDestination: z.never().optional(),
                  pickupDetails: z.never().optional(),
                }),
                z.object({
                  pickupDetails: z.never().optional(),
                  shippingDestination: z
                    .object({
                      address: z
                        .object({
                          streetAddress: z
                            .object({
                              name: z.string().optional(),
                              number: z.string().optional(),
                            })
                            .optional(),
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Address.')
                        .optional(),
                      contactDetails: z
                        .object({
                          firstName: z
                            .string()
                            .describe('First name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          lastName: z
                            .string()
                            .describe('Last name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          phone: z
                            .string()
                            .describe('Phone number.')
                            .optional()
                            .nullable(),
                          company: z
                            .string()
                            .describe('Company name.')
                            .max(1000)
                            .optional()
                            .nullable(),
                          vatId: z
                            .object({
                              _id: z
                                .string()
                                .describe("Customer's tax ID.")
                                .optional(),
                              type: z
                                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                                .describe(
                                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                                )
                                .optional(),
                            })
                            .describe(
                              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                            )
                            .optional(),
                        })
                        .describe('Contact details.')
                        .optional(),
                    })
                    .describe(
                      "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                    ),
                }),
                z.object({
                  shippingDestination: z.never().optional(),
                  pickupDetails: z
                    .object({
                      address: z
                        .object({
                          streetAddress: z
                            .object({
                              name: z.string().optional(),
                              number: z.string().optional(),
                            })
                            .optional(),
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Pickup address.')
                        .optional(),
                      pickupMethod: z
                        .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                        .describe('Pickup method')
                        .optional(),
                    })
                    .describe(
                      'Pickup details for store pickup or pickup point orders.'
                    ),
                }),
              ])
            )
            .describe('Shipping logistics.')
            .optional(),
          cost: z
            .object({
              price: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Shipping price for display purposes.')
                .optional(),
              totalPriceBeforeTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Total price of shipping after discounts (when relevant), and before tax.'
                )
                .optional(),
              totalPriceAfterTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Shipping price after all discounts (if any exist), and after tax.'
                )
                .optional(),
              taxDetails: z
                .object({
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of this line item.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                    )
                    .optional(),
                  totalTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                    )
                    .optional(),
                })
                .describe('Tax details.')
                .optional(),
              taxInfo: z
                .object({
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                    )
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount for which tax is calculated.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax rate %, as a decimal point.')
                    .optional()
                    .nullable(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  taxIncludedInPrice: z
                    .boolean()
                    .describe(
                      'Indicates whether the price already includes tax.'
                    )
                    .optional(),
                  taxBreakdown: z
                    .array(
                      z.object({
                        jurisdiction: z
                          .string()
                          .describe(
                            'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        rate: z
                          .string()
                          .describe(
                            'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                          )
                          .optional()
                          .nullable(),
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Amount of tax calculated for this line item.'
                          )
                          .optional(),
                        taxType: z
                          .string()
                          .describe(
                            'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        taxName: z
                          .string()
                          .describe(
                            'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        jurisdictionType: z
                          .enum([
                            'UNDEFINED',
                            'COUNTRY',
                            'STATE',
                            'COUNTY',
                            'CITY',
                            'SPECIAL',
                          ])
                          .describe(
                            'Type of jurisdiction that taxes were calculated for.'
                          )
                          .optional(),
                        nonTaxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Non-taxable amount of the line item price.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of the line item price.')
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe(
                  'Represents all the relevant tax details for a shipping.'
                )
                .optional(),
              discount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Shipping discount before tax.')
                .optional(),
            })
            .describe('Shipping costs.')
            .optional(),
          region: z
            .object({
              name: z
                .string()
                .describe(
                  'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Shipping region.')
            .optional(),
        })
        .describe('Shipping info and selected shipping option details.')
        .optional(),
      buyerNote: z
        .string()
        .describe(
          '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
        )
        .max(1000)
        .optional()
        .nullable(),
      status: z
        .enum(['INITIALIZED', 'APPROVED', 'CANCELED', 'PENDING', 'REJECTED'])
        .describe('Order status.')
        .optional(),
      archived: z
        .boolean()
        .describe('Whether order is archived.')
        .optional()
        .nullable(),
      taxSummary: z
        .object({
          totalTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax.')
            .optional(),
        })
        .describe(
          'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
        )
        .optional(),
      taxInfo: z
        .object({
          totalTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Calculated tax, added from line items.')
            .optional(),
          taxBreakdown: z
            .array(
              z.object({
                taxName: z
                  .string()
                  .describe(
                    'The name of the tax against which this tax amount was calculated.'
                  )
                  .max(200)
                  .optional(),
                taxType: z
                  .string()
                  .describe(
                    "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
                  )
                  .max(200)
                  .optional(),
                jurisdiction: z
                  .string()
                  .describe(
                    'The name of the jurisdiction in which this tax detail applies.'
                  )
                  .max(200)
                  .optional(),
                jurisdictionType: z
                  .enum([
                    'UNDEFINED',
                    'COUNTRY',
                    'STATE',
                    'COUNTY',
                    'CITY',
                    'SPECIAL',
                  ])
                  .describe(
                    'The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special).'
                  )
                  .optional(),
                rate: z
                  .string()
                  .describe('The rate at which this tax detail was calculated.')
                  .optional(),
                aggregatedTaxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The sum of all the tax from line items that calculated by the tax identifiers.'
                  )
                  .optional(),
              })
            )
            .min(0)
            .max(50)
            .optional(),
          taxExempt: z
            .boolean()
            .describe(
              'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
        })
        .describe('Tax information.')
        .optional(),
      appliedDiscounts: z
        .array(
          z.intersection(
            z.object({
              discountType: z
                .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
                .optional(),
              lineItemIds: z.array(z.string()).min(0).max(300).optional(),
              _id: z
                .string()
                .describe('Discount ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              lineItemDiscounts: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Line item ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    totalDiscount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Total discount amount for this line item.')
                      .optional(),
                  })
                )
                .max(300)
                .optional(),
            }),
            z.xor([
              z.object({
                coupon: z.never().optional(),
                merchantDiscount: z.never().optional(),
                discountRule: z.never().optional(),
              }),
              z.object({
                merchantDiscount: z.never().optional(),
                discountRule: z.never().optional(),
                coupon: z
                  .object({
                    _id: z.string().describe('Coupon ID.').optional(),
                    code: z.string().describe('Coupon code.').optional(),
                    name: z.string().describe('Coupon name.').optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Coupon value.')
                      .optional(),
                  })
                  .describe('Coupon applied by the customer.'),
              }),
              z.object({
                coupon: z.never().optional(),
                discountRule: z.never().optional(),
                merchantDiscount: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Discount amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        discountReason: z.never().optional(),
                        description: z.never().optional(),
                      }),
                      z.object({
                        description: z.never().optional(),
                        discountReason: z
                          .enum([
                            'UNSPECIFIED',
                            'EXCHANGED_ITEMS',
                            'BILLING_ADJUSTMENT',
                          ])
                          .describe('Predefined discount reason.'),
                      }),
                      z.object({
                        discountReason: z.never().optional(),
                        description: z
                          .string()
                          .describe('Custom discount description as free text.')
                          .max(200),
                      }),
                    ])
                  )
                  .describe('Discount applied manually by the merchant.'),
              }),
              z.object({
                coupon: z.never().optional(),
                merchantDiscount: z.never().optional(),
                discountRule: z
                  .object({
                    _id: z
                      .string()
                      .describe('Discount rule ID')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Original discount rule name (in site's default language)."
                          )
                          .min(1)
                          .max(256)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                          )
                          .min(1)
                          .max(500)
                          .optional()
                          .nullable(),
                      })
                      .describe('Discount rule name')
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Discount value.')
                      .optional(),
                  })
                  .describe(
                    'Automatic discount applied by the system based on configured discount rules.'
                  ),
              }),
            ])
          )
        )
        .max(320)
        .optional(),
      activities: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Activity ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              authorEmail: z
                .string()
                .describe("Activity author's email.")
                .email()
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Activity creation date and time.')
                .optional()
                .nullable(),
              type: z
                .enum([
                  'ORDER_REFUNDED',
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'CUSTOM_ACTIVITY',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                ])
                .describe('Activity type.')
                .optional(),
              activityType: z
                .enum([
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                  'ORDER_REFUNDED',
                ])
                .describe('Activity type.')
                .optional(),
            }),
            z.xor([
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
              }),
              z.object({
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                merchantComment: z
                  .object({
                    message: z
                      .string()
                      .describe('Merchant comment message.')
                      .min(1)
                      .max(5000)
                      .optional(),
                  })
                  .describe(
                    'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                orderRefunded: z
                  .object({
                    manual: z
                      .boolean()
                      .describe(
                        'Whether order was refunded manually. For example, via payment provider or using cash.'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                draftOrderChangesApplied: z
                  .object({
                    draftOrderId: z
                      .string()
                      .describe('Draft order id.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for edit, given by user (optional).')
                      .max(200)
                      .optional()
                      .nullable(),
                    changes: z
                      .array(
                        z.intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                            }),
                            z.object({
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemChanged: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name after change.')
                                  .optional(),
                                quantity: z
                                  .object({
                                    originalQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity before update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    newQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity after update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    diff: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Difference between original and new quantity. Absolute value.'
                                      )
                                      .min(1)
                                      .max(1000000)
                                      .optional(),
                                    deltaType: z
                                      .enum([
                                        'QUANTITY_INCREASED',
                                        'QUANTITY_DECREASED',
                                      ])
                                      .describe(
                                        'Type of quantity change: increase or decrease.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item quantity change.')
                                  .optional(),
                                price: z
                                  .object({
                                    originalPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price before update.')
                                      .optional(),
                                    newPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price after update.')
                                      .optional(),
                                  })
                                  .describe('Item price change.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              totalPriceChanged: z.object({
                                originalTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. Before update'
                                  )
                                  .optional(),
                                newTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.object({
                                originalShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. Before update'
                                  )
                                  .optional(),
                                newShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                          ])
                        )
                      )
                      .optional(),
                  })
                  .describe('Details of changes made by the Draft Orders API.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                savedPaymentMethod: z
                  .object({
                    name: z
                      .string()
                      .describe('Payment method name.')
                      .min(1)
                      .max(100)
                      .optional(),
                    description: z
                      .string()
                      .describe('Payment method description.')
                      .min(1)
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of the payment method saved for the order.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment created.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCaptured: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment captured.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentVoided: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment voided.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundInitiated: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    payments: z
                      .array(
                        z.intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe('Payment ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            externalRefund: z
                              .boolean()
                              .describe(
                                "Whether refund was made externally and manually on the payment provider's side."
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                            }),
                            z.object({
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                              regular: z
                                .object({
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Regular payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              membership: z.never().optional(),
                              giftCard: z
                                .object({
                                  giftCardPaymentId: z
                                    .string()
                                    .describe('Gift card payment ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                })
                                .describe('Gift card payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z
                                .object({
                                  membershipId: z
                                    .string()
                                    .describe('Membership ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Membership payment refund.'),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(50)
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefunded: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the refunded payment.')
                      .optional(),
                  })
                  .describe(
                    'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefundFailed: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the failed payment refund.')
                      .optional(),
                  })
                  .describe(
                    'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundedAsStoreCredit: z
                  .object({
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of refund to store credit.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentPending: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a pending payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentCanceled: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a canceled payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentDeclined: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a declined payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptCreated: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt was added for associated payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptSent: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt sent to customer.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackReversed: z.never().optional(),
                chargebackCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe('ID of the payment that received a chargeback.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    'Order received a chargeback for one of its payments.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'ID of the payment involved with the chargeback.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    reversalAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback reversal amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    "Chargeback reversed for one of the order's payments."
                  ),
              }),
            ])
          )
        )
        .max(1000)
        .optional(),
      attributionSource: z
        .enum(['UNSPECIFIED', 'FACEBOOK_ADS'])
        .describe('Order attribution source.')
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID - when the order was created by a **logged in** site visitor.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              appId: z.never().optional(),
              visitorId: z
                .string()
                .describe(
                  'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID - when the order was created by an external application.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe("ID of the order's initiator.")
        .optional(),
      channelInfo: z
        .object({
          type: z
            .enum([
              'UNSPECIFIED',
              'WEB',
              'POS',
              'EBAY',
              'AMAZON',
              'OTHER_PLATFORM',
              'WIX_APP_STORE',
              'WIX_INVOICES',
              'BACKOFFICE_MERCHANT',
              'WISH',
              'CLASS_PASS',
              'GLOBAL_E',
              'FACEBOOK',
              'ETSY',
              'TIKTOK',
              'FAIRE_COM',
              'PAYPAL_AGENTIC_CHECKOUT',
              'STRIPE_AGENTIC_CHECKOUT',
            ])
            .describe(
              'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
            )
            .optional(),
          externalOrderId: z
            .string()
            .describe(
              'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
            )
            .max(100)
            .optional()
            .nullable(),
          externalOrderUrl: z
            .string()
            .describe(
              'URL to the order in the external system. Relevant when recording orders from external platforms.'
            )
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Information about the sales channel that submitted this order.'
        )
        .optional(),
      seenByAHuman: z
        .boolean()
        .describe(
          'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
        )
        .optional()
        .nullable(),
      checkoutId: z
        .string()
        .describe('Checkout ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      customFields: z
        .array(
          z.object({
            value: z
              .any()
              .describe('Custom field value.')
              .optional()
              .nullable(),
            title: z
              .string()
              .describe('Custom field title.')
              .min(1)
              .max(500)
              .optional(),
            translatedTitle: z
              .string()
              .describe('Translated custom field title.')
              .min(1)
              .max(500)
              .optional()
              .nullable(),
          })
        )
        .optional(),
      balanceSummary: z
        .object({
          balance: z
            .object({
              amount: z
                .string()
                .describe(
                  'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
                )
                .optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Current amount left to pay.')
            .optional(),
          paid: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
            )
            .optional(),
          refunded: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all successfully refunded payments.')
            .optional(),
          authorized: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all authorized payments.')
            .optional(),
          pendingRefund: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all pending refund transactions.')
            .optional(),
          pending: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all pending transactions.')
            .optional(),
          chargeback: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all transaction chargebacks.')
            .optional(),
          chargebackReversal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all chargeback reversals.')
            .optional(),
          platformFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
            )
            .optional(),
          totalMinusPlatformFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
            )
            .optional(),
        })
        .describe('Balance summary.')
        .optional(),
      additionalFees: z
        .array(
          z.object({
            code: z
              .string()
              .describe("Additional fee's unique code for future processing.")
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of additional fee.')
              .min(1)
              .max(50)
              .optional(),
            price: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price.")
              .optional(),
            taxDetails: z
              .object({
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Taxable amount of this line item.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                  )
                  .optional(),
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                  )
                  .optional(),
              })
              .describe('Tax details.')
              .optional(),
            taxInfo: z
              .object({
                taxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                  )
                  .optional(),
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Amount for which tax is calculated.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe('Tax rate %, as a decimal point.')
                  .optional()
                  .nullable(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                taxIncludedInPrice: z
                  .boolean()
                  .describe('Indicates whether the price already includes tax.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      jurisdiction: z
                        .string()
                        .describe(
                          'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                        )
                        .optional()
                        .nullable(),
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Amount of tax calculated for this line item.'
                        )
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .describe(
                          'Type of jurisdiction that taxes were calculated for.'
                        )
                        .optional(),
                      nonTaxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Non-taxable amount of the line item price.')
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of the line item price.')
                        .optional(),
                    })
                  )
                  .max(7)
                  .optional(),
              })
              .describe(
                'Represents all the relevant tax details for additional fee.'
              )
              .optional(),
            providerAppId: z
              .string()
              .describe("SPI implementer's `appId`.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            priceBeforeTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price before tax.")
              .optional(),
            priceAfterTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price after tax.")
              .optional(),
            _id: z
              .string()
              .describe("Additional fee's id.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            lineItemIds: z.array(z.string()).optional(),
            source: z
              .enum([
                'SERVICE_PLUGIN',
                'ITEM',
                'MANUAL',
                'SHIPPING',
                'PLATFORM',
              ])
              .describe('Specifies the entity that added the additional fee.')
              .optional(),
          })
        )
        .max(100)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      purchaseFlowId: z
        .string()
        .describe(
          'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      recipientInfo: z
        .object({
          address: z
            .object({
              streetAddress: z
                .object({
                  name: z.string().optional(),
                  number: z.string().optional(),
                })
                .optional(),
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Address.')
            .optional(),
          contactDetails: z
            .object({
              firstName: z
                .string()
                .describe('First name.')
                .max(100)
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe('Last name.')
                .max(100)
                .optional()
                .nullable(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              company: z
                .string()
                .describe('Company name.')
                .max(1000)
                .optional()
                .nullable(),
              vatId: z
                .object({
                  _id: z.string().describe("Customer's tax ID.").optional(),
                  type: z
                    .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                    .describe(
                      'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                    )
                    .optional(),
                })
                .describe(
                  'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                )
                .optional(),
            })
            .describe('Contact details.')
            .optional(),
        })
        .describe(
          "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
        )
        .optional(),
      purchasedDate: z
        .date()
        .describe(
          'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
        )
        .optional()
        .nullable(),
      businessLocation: z
        .object({
          _id: z
            .string()
            .describe(
              'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Location name.')
            .min(1)
            .max(500)
            .optional(),
        })
        .describe('Order location.')
        .optional(),
      payAfterFreeTrial: z
        .object({
          subtotal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Subtotal of all line items, before discounts and before tax.'
            )
            .optional(),
          shipping: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total shipping price, before discounts and before tax.')
            .optional(),
          tax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax applied to the order.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total discount amount applied to the order.')
            .optional(),
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe("Order's total price after discounts and tax.")
            .optional(),
          totalAdditionalFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total additional fees before tax.')
            .optional(),
        })
        .describe(
          'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
        )
        .optional(),
      platformFeeSummary: z
        .object({
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total sum of all platform fees.')
            .optional(),
          totalPassOn: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total amount of platform fees with `PASS_ON` charge type.'
            )
            .optional(),
          totalAbsorbed: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total amount of platform fees with `ABSORBED` charge type.'
            )
            .optional(),
          fees: z
            .array(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                      )
                      .min(1)
                      .max(400)
                      .optional()
                      .nullable(),
                  })
                  .describe('Platform fee name.')
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Platform fee amount.')
                  .optional(),
                lineItemId: z
                  .string()
                  .describe('ID of the line item the platform fee applies to.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargeType: z
                  .enum(['PASS_ON', 'ABSORBED'])
                  .describe('Platform fee charge type.')
                  .optional(),
                percentageRate: z
                  .string()
                  .describe(
                    'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
                  )
                  .optional(),
              })
            )
            .max(300)
            .optional(),
        })
        .describe(
          'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
        )
        .optional(),
    })
    .describe('Updated order.')
    .optional(),
  activityIds: z.array(z.string()).min(1).max(300).optional(),
});
export const CancelOrderRequest = z.object({
  _id: z.string().describe('Order ID.').min(1).max(100),
  options: z
    .object({
      sendOrderCanceledEmail: z
        .boolean()
        .describe('Whether to send an order canceled email to the buyer.')
        .optional(),
      customMessage: z
        .string()
        .describe('Custom note to be added to the email (optional).')
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
      restockAllItems: z
        .boolean()
        .describe(
          'Whether to restock all items in the order. This will only apply to products in the Wix Stores inventory.'
        )
        .optional(),
    })
    .optional(),
});
export const CancelOrderResponse = z.object({
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      number: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          "Order number displayed in the site owner's dashboard (auto-generated)."
        )
        .optional(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the order was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the order was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      lineItems: z
        .array(
          z.object({
            _id: z.string().describe('Line item ID.').optional(),
            productName: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                  )
                  .min(1)
                  .max(200)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(400)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Item name.\n\n+ Stores - `product.name`\n+ Bookings - `service.info.name`\n+ Events - `ticket.name`'
              )
              .optional(),
            catalogReference: z
              .object({
                catalogItemId: z
                  .string()
                  .describe('ID of the item within the catalog it belongs to.')
                  .min(1)
                  .max(36)
                  .optional(),
                appId: z
                  .string()
                  .describe(
                    'ID of the app providing the catalog.\n\nYou can get your app\'s ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).\n\nFor items from Wix catalogs, the following values always apply:\n+ Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`\n+ Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n+ Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`'
                  )
                  .min(1)
                  .optional(),
                options: z
                  .record(z.string(), z.any())
                  .describe(
                    'Additional item details in `key:value` pairs.\n\nUse this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.\n\nFor Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)\nor [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                "References to the line item's origin catalog. Empty for custom line items."
              )
              .optional(),
            quantity: z
              .number()
              .int()
              .describe('Line item quantity.')
              .min(1)
              .max(100000)
              .optional(),
            totalDiscount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Total discount for this line item's entire quantity.")
              .optional(),
            descriptionLines: z
              .array(
                z.intersection(
                  z.object({
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                          )
                          .max(100)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            "Description line name translated into the buyer's language.\n\nDefault: Same as `original`."
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                      .describe('Description line name.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z.never().optional(),
                    }),
                    z.object({
                      colorInfo: z.never().optional(),
                      plainText: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(600)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line plain text value translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(600)
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line plain text value.'),
                    }),
                    z.object({
                      plainText: z.never().optional(),
                      colorInfo: z
                        .object({
                          original: z
                            .string()
                            .describe(
                              "Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                            )
                            .max(500)
                            .optional(),
                          translated: z
                            .string()
                            .describe(
                              "Description line color name translated into the buyer's language.\n\nDefault: Same as `original`."
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          code: z
                            .string()
                            .describe('HEX or RGB color code for display.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Description line color value.'),
                    }),
                  ])
                )
              )
              .min(0)
              .max(20)
              .optional(),
            image: z.string().describe('Line item image.').optional(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request.'
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe(
                    'Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).'
                  )
                  .max(40)
                  .optional()
                  .nullable(),
                shippable: z
                  .boolean()
                  .describe('Whether this line item is shippable.')
                  .optional(),
              })
              .describe(
                'Physical properties of the item. When relevant, contains information such as SKU and item weight.'
              )
              .optional(),
            itemType: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    preset: z.never().optional(),
                    custom: z.never().optional(),
                  }),
                  z.object({
                    custom: z.never().optional(),
                    preset: z
                      .enum([
                        'UNRECOGNISED',
                        'PHYSICAL',
                        'DIGITAL',
                        'GIFT_CARD',
                        'SERVICE',
                      ])
                      .describe('Preset item type.'),
                  }),
                  z.object({
                    preset: z.never().optional(),
                    custom: z
                      .string()
                      .describe(
                        'Custom item type. When none of the preset types are suitable, specifies the custom type.'
                      ),
                  }),
                ])
              )
              .describe('Item type. Either a preset type or custom.')
              .optional(),
            fulfillerId: z
              .string()
              .describe(
                'Fulfiller ID. Field is empty when the line item is self-fulfilled.\nTo get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            refundQuantity: z
              .number()
              .int()
              .describe('Number of items that were refunded.')
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            restockQuantity: z
              .number()
              .int()
              .describe(
                'Number of items restocked.\n\nFor a per-location breakdown, see `restockLocations`.'
              )
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            price: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Line item price after line item discounts for display purposes.'
              )
              .optional(),
            priceBeforeDiscounts: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Line item price before line item discounts for display purposes. Defaults to `price` when not provided.'
              )
              .optional(),
            totalPriceBeforeTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total price after discounts, and before tax.')
              .optional(),
            totalPriceAfterTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe('Total price after all discounts and tax.')
              .optional(),
            paymentOption: z
              .enum([
                'FULL_PAYMENT_ONLINE',
                'FULL_PAYMENT_OFFLINE',
                'MEMBERSHIP',
                'DEPOSIT_ONLINE',
                'MEMBERSHIP_OFFLINE',
                'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
              ])
              .describe(
                'Type of selected payment option for current item.\n\nDefault: `FULL_PAYMENT_ONLINE`'
              )
              .optional(),
            taxDetails: z
              .object({
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Taxable amount of this line item.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                  )
                  .optional(),
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                  )
                  .optional(),
              })
              .describe(
                'Deprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.\nTax details for this line item.'
              )
              .optional(),
            taxInfo: z
              .object({
                taxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                  )
                  .optional(),
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Amount for which tax is calculated.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe('Tax rate %, as a decimal point.')
                  .optional()
                  .nullable(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                taxIncludedInPrice: z
                  .boolean()
                  .describe('Indicates whether the price already includes tax.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      jurisdiction: z
                        .string()
                        .describe(
                          'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                        )
                        .optional()
                        .nullable(),
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Amount of tax calculated for this line item.'
                        )
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .describe(
                          'Type of jurisdiction that taxes were calculated for.'
                        )
                        .optional(),
                      nonTaxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Non-taxable amount of the line item price.')
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of the line item price.')
                        .optional(),
                    })
                  )
                  .max(7)
                  .optional(),
              })
              .describe(
                'Represents all the relevant tax details for a specific line item.'
              )
              .optional(),
            digitalFile: z
              .object({
                fileId: z
                  .string()
                  .describe('ID of the secure file in media.')
                  .min(1)
                  .max(100)
                  .optional(),
                link: z
                  .string()
                  .describe(
                    'Link will exist after the digital links have been generated on the order.'
                  )
                  .url()
                  .min(1)
                  .max(2000)
                  .optional()
                  .nullable(),
                expirationDate: z
                  .date()
                  .describe('Link expiration time and date.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Digital file identifier, relevant only for items with type DIGITAL.'
              )
              .optional(),
            subscriptionInfo: z
              .object({
                _id: z
                  .string()
                  .describe('Subscription ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                cycleNumber: z
                  .number()
                  .int()
                  .describe(
                    'Subscription cycle. For example, if this order is for the 3rd cycle of a subscription, value will be `3`.'
                  )
                  .min(0)
                  .optional(),
                subscriptionOptionTitle: z
                  .string()
                  .describe(
                    'Subscription option title. For example, `"Monthly coffee Subscription"`.'
                  )
                  .min(0)
                  .max(150)
                  .optional(),
                subscriptionOptionDescription: z
                  .string()
                  .describe(
                    'Subscription option description. For example, `"1kg of selected coffee, once a month"`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                subscriptionSettings: z
                  .object({
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Frequency of recurring payment.')
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Number of frequency units in a billing cycle. For example, frequency `MONTH` with interval `3` means billing every 3 months.'
                      )
                      .min(1)
                      .max(3650)
                      .optional()
                      .nullable(),
                    autoRenewal: z
                      .boolean()
                      .describe(
                        'Whether subscription is renewed automatically at the end of each period.'
                      )
                      .optional(),
                    billingCycles: z
                      .number()
                      .int()
                      .describe(
                        'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    enableCustomerCancellation: z
                      .boolean()
                      .describe(
                        'Whether to allow the customer to cancel the subscription.'
                      )
                      .optional(),
                    freeTrialPeriod: z
                      .object({
                        frequency: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe(
                            'Frequency of period. Values: DAY, WEEK, MONTH, YEAR'
                          )
                          .optional(),
                        interval: z
                          .number()
                          .int()
                          .describe(
                            'Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.'
                          )
                          .min(1)
                          .max(3650)
                          .optional(),
                      })
                      .describe(
                        'Period until first cycle starts. If None => no free trial'
                      )
                      .optional(),
                  })
                  .describe('Subscription detailed information.')
                  .optional(),
                chargesDescription: z
                  .string()
                  .describe(
                    'Description of the charges that will be applied for subscription.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
              .describe('Subscription info.')
              .optional(),
            priceDescription: z
              .object({
                original: z
                  .string()
                  .describe(
                    "__Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope)."
                  )
                  .min(1)
                  .max(100)
                  .optional(),
                translated: z
                  .string()
                  .describe(
                    "Price description translated into the buyer's language.\n\nDefault: Same as `original`."
                  )
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".'
              )
              .optional(),
            depositAmount: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Item\'s price amount to be charged during checkout. Relevant for items with a `paymentOption` value of `"DEPOSIT_ONLINE"`.'
              )
              .optional(),
            locations: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Location id in the associated owner app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  appId: z
                    .string()
                    .describe(
                      'Location owner app, if not provided then the site business info locations will be used.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe('Quantity for specific location.')
                    .min(1)
                    .max(100000)
                    .optional(),
                  name: z
                    .string()
                    .describe('Location name.')
                    .max(500)
                    .optional()
                    .nullable(),
                })
              )
              .min(0)
              .max(5)
              .optional(),
            lineItemPrice: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe(
                'Total price **after** catalog discounts and line item discounts.'
              )
              .optional(),
            taxableAddress: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({ addressType: z.never().optional() }),
                  z.object({
                    addressType: z
                      .enum(['BUSINESS', 'BILLING', 'SHIPPING'])
                      .describe(
                        'taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly.'
                      ),
                  }),
                ])
              )
              .describe('Address used for tax calculation.')
              .optional(),
            priceUndetermined: z
              .boolean()
              .describe(
                'Whether the price is not yet defined, and will be updated after the order is created.\n\nDefault: `false`'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom extended fields for the line item object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            modifierGroups: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Modifier group ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  name: z
                    .object({
                      original: z
                        .string()
                        .describe(
                          "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                        )
                        .min(1)
                        .max(200)
                        .optional(),
                      translated: z
                        .string()
                        .describe(
                          "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                        )
                        .min(1)
                        .max(400)
                        .optional()
                        .nullable(),
                    })
                    .describe('Modifier group name.')
                    .optional(),
                  modifiers: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Modifier ID.')
                          .min(1)
                          .max(36)
                          .optional(),
                        quantity: z
                          .number()
                          .int()
                          .describe('The quantity of this modifier.')
                          .min(1)
                          .max(100000)
                          .optional()
                          .nullable(),
                        label: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Primary display label for the modifier.')
                          .optional(),
                        details: z
                          .object({
                            original: z
                              .string()
                              .describe(
                                "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                              )
                              .min(1)
                              .max(200)
                              .optional(),
                            translated: z
                              .string()
                              .describe(
                                "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                              )
                              .min(1)
                              .max(400)
                              .optional()
                              .nullable(),
                          })
                          .describe('Additional details.')
                          .optional(),
                        price: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('The price of the modifier.')
                          .optional(),
                      })
                    )
                    .min(1)
                    .max(10)
                    .optional(),
                })
              )
              .max(10)
              .optional(),
          })
        )
        .min(1)
        .max(300)
        .optional(),
      buyerInfo: z
        .intersection(
          z.object({
            contactId: z
              .string()
              .describe(
                "Contact ID. Automatically created if one doesn't exist. For more information, see [Contacts API](https://dev.wix.com/docs/api-reference/crm/members-contacts/contacts/contacts/contact-v4/introduction)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            email: z
              .string()
              .describe("Buyer's email address.")
              .email()
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              visitorId: z.never().optional(),
              memberId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              visitorId: z
                .string()
                .describe(
                  "Visitor ID. Returned when the buyer isn't a logged-in site member."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              visitorId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID. Returned when the buyer is a logged-in site member.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe('Buyer information.')
        .optional(),
      paymentStatus: z
        .enum([
          'UNSPECIFIED',
          'NOT_PAID',
          'PAID',
          'PARTIALLY_REFUNDED',
          'FULLY_REFUNDED',
          'PENDING',
          'PARTIALLY_PAID',
          'PENDING_MERCHANT',
          'CANCELED',
          'DECLINED',
        ])
        .describe('Order payment status.')
        .optional(),
      fulfillmentStatus: z
        .enum(['NOT_FULFILLED', 'FULFILLED', 'PARTIALLY_FULFILLED'])
        .describe('Order fulfillment status.')
        .optional(),
      buyerLanguage: z
        .string()
        .describe(
          'Language for communication with the buyer. Defaults to the site language.\nFor a site that supports multiple languages, this is the language the buyer selected.'
        )
        .optional()
        .nullable(),
      weightUnit: z
        .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
        .describe("Weight measurement unit - defaults to site's weight unit.")
        .optional(),
      currency: z
        .string()
        .describe(
          'Currency used for the pricing of this order in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.'
        )
        .optional()
        .nullable(),
      currencyConversionDetails: z
        .object({
          originalCurrency: z
            .string()
            .describe(
              "Currency used for the pricing of this order, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nThis currency is used to calculate the pricing before conversion to the buyer's payment currency."
            )
            .optional()
            .nullable(),
          conversionRate: z
            .string()
            .describe(
              'The rate used for converting the original currency to the currency used for payment.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Currency conversion details. For use with multi-currency sites.'
        )
        .optional(),
      taxIncludedInPrices: z
        .boolean()
        .describe('Whether tax is included in line item prices.')
        .optional(),
      siteLanguage: z
        .string()
        .describe('Site language in which original values are shown.')
        .optional()
        .nullable(),
      priceSummary: z
        .object({
          subtotal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Subtotal of all line items, before discounts and before tax.'
            )
            .optional(),
          shipping: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total shipping price, before discounts and before tax.')
            .optional(),
          tax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax applied to the order.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total discount amount applied to the order.')
            .optional(),
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe("Order's total price after discounts and tax.")
            .optional(),
          totalAdditionalFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total additional fees before tax.')
            .optional(),
        })
        .describe('Order price summary.')
        .optional(),
      billingInfo: z
        .object({
          address: z
            .object({
              streetAddress: z
                .object({
                  name: z.string().optional(),
                  number: z.string().optional(),
                })
                .optional(),
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Address.')
            .optional(),
          contactDetails: z
            .object({
              firstName: z
                .string()
                .describe('First name.')
                .max(100)
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe('Last name.')
                .max(100)
                .optional()
                .nullable(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              company: z
                .string()
                .describe('Company name.')
                .max(1000)
                .optional()
                .nullable(),
              vatId: z
                .object({
                  _id: z.string().describe("Customer's tax ID.").optional(),
                  type: z
                    .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                    .describe(
                      'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                    )
                    .optional(),
                })
                .describe(
                  'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                )
                .optional(),
            })
            .describe('Contact details.')
            .optional(),
        })
        .describe('Billing address and contact details.')
        .optional(),
      shippingInfo: z
        .object({
          carrierId: z
            .string()
            .describe(
              'App Def Id of external provider which was a source of shipping info'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          code: z
            .string()
            .describe(
              'Unique code (or ID) of selected shipping option. For example, `"usps_std_overnight"`.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          title: z
            .string()
            .describe(
              'Shipping option title.\nFor example, `"USPS Standard Overnight Delivery"`, `"Standard"` or `"First-Class Package International"`.'
            )
            .max(250)
            .optional(),
          logistics: z
            .intersection(
              z.object({
                deliveryTime: z
                  .string()
                  .describe(
                    'Expected delivery time in free text. For example, `"3-5 business days"`.'
                  )
                  .max(500)
                  .optional()
                  .nullable(),
                instructions: z
                  .string()
                  .describe(
                    'Instructions for the carrier. For example, `"Please knock on the door. If unanswered, please call contact number."`.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                deliverByDate: z
                  .date()
                  .describe(
                    'Deprecated - Latest expected delivery date and time in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.'
                  )
                  .optional()
                  .nullable(),
                deliveryTimeSlot: z
                  .object({
                    from: z
                      .date()
                      .describe('Delivery slot starting time.')
                      .optional()
                      .nullable(),
                    to: z
                      .date()
                      .describe('Delivery slot ending time.')
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Expected delivery time slot with start and end times.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  shippingDestination: z.never().optional(),
                  pickupDetails: z.never().optional(),
                }),
                z.object({
                  pickupDetails: z.never().optional(),
                  shippingDestination: z
                    .object({
                      address: z
                        .object({
                          streetAddress: z
                            .object({
                              name: z.string().optional(),
                              number: z.string().optional(),
                            })
                            .optional(),
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Address.')
                        .optional(),
                      contactDetails: z
                        .object({
                          firstName: z
                            .string()
                            .describe('First name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          lastName: z
                            .string()
                            .describe('Last name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          phone: z
                            .string()
                            .describe('Phone number.')
                            .optional()
                            .nullable(),
                          company: z
                            .string()
                            .describe('Company name.')
                            .max(1000)
                            .optional()
                            .nullable(),
                          vatId: z
                            .object({
                              _id: z
                                .string()
                                .describe("Customer's tax ID.")
                                .optional(),
                              type: z
                                .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                                .describe(
                                  'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                                )
                                .optional(),
                            })
                            .describe(
                              'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                            )
                            .optional(),
                        })
                        .describe('Contact details.')
                        .optional(),
                    })
                    .describe(
                      "Shipping destination address and contact details. For pickup orders, this contains the pickup location address, not the recipient's address."
                    ),
                }),
                z.object({
                  shippingDestination: z.never().optional(),
                  pickupDetails: z
                    .object({
                      address: z
                        .object({
                          streetAddress: z
                            .object({
                              name: z.string().optional(),
                              number: z.string().optional(),
                            })
                            .optional(),
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Pickup address.')
                        .optional(),
                      pickupMethod: z
                        .enum(['STORE_PICKUP', 'PICKUP_POINT'])
                        .describe('Pickup method')
                        .optional(),
                    })
                    .describe(
                      'Pickup details for store pickup or pickup point orders.'
                    ),
                }),
              ])
            )
            .describe('Shipping logistics.')
            .optional(),
          cost: z
            .object({
              price: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Shipping price for display purposes.')
                .optional(),
              totalPriceBeforeTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Total price of shipping after discounts (when relevant), and before tax.'
                )
                .optional(),
              totalPriceAfterTax: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe(
                  'Shipping price after all discounts (if any exist), and after tax.'
                )
                .optional(),
              taxDetails: z
                .object({
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Taxable amount of this line item.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                    )
                    .optional(),
                  totalTax: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                    )
                    .optional(),
                })
                .describe('Tax details.')
                .optional(),
              taxInfo: z
                .object({
                  taxAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe(
                      'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                    )
                    .optional(),
                  taxableAmount: z
                    .object({
                      amount: z.string().describe('Amount.').optional(),
                      formattedAmount: z
                        .string()
                        .describe('Amount formatted with currency symbol.')
                        .optional(),
                    })
                    .describe('Amount for which tax is calculated.')
                    .optional(),
                  taxRate: z
                    .string()
                    .describe('Tax rate %, as a decimal point.')
                    .optional()
                    .nullable(),
                  taxGroupId: z
                    .string()
                    .describe('Tax group ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  taxIncludedInPrice: z
                    .boolean()
                    .describe(
                      'Indicates whether the price already includes tax.'
                    )
                    .optional(),
                  taxBreakdown: z
                    .array(
                      z.object({
                        jurisdiction: z
                          .string()
                          .describe(
                            'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        rate: z
                          .string()
                          .describe(
                            'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                          )
                          .optional()
                          .nullable(),
                        taxAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Amount of tax calculated for this line item.'
                          )
                          .optional(),
                        taxType: z
                          .string()
                          .describe(
                            'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        taxName: z
                          .string()
                          .describe(
                            'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                          )
                          .max(200)
                          .optional()
                          .nullable(),
                        jurisdictionType: z
                          .enum([
                            'UNDEFINED',
                            'COUNTRY',
                            'STATE',
                            'COUNTY',
                            'CITY',
                            'SPECIAL',
                          ])
                          .describe(
                            'Type of jurisdiction that taxes were calculated for.'
                          )
                          .optional(),
                        nonTaxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Non-taxable amount of the line item price.'
                          )
                          .optional(),
                        taxableAmount: z
                          .object({
                            amount: z.string().describe('Amount.').optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Amount formatted with currency symbol.'
                              )
                              .optional(),
                          })
                          .describe('Taxable amount of the line item price.')
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe(
                  'Represents all the relevant tax details for a shipping.'
                )
                .optional(),
              discount: z
                .object({
                  amount: z.string().describe('Amount.').optional(),
                  formattedAmount: z
                    .string()
                    .describe('Amount formatted with currency symbol.')
                    .optional(),
                })
                .describe('Shipping discount before tax.')
                .optional(),
            })
            .describe('Shipping costs.')
            .optional(),
          region: z
            .object({
              name: z
                .string()
                .describe(
                  'Name of shipping region. For example, `"Metropolitan London"`, or `"Outer Melbourne suburbs"`.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Shipping region.')
            .optional(),
        })
        .describe('Shipping info and selected shipping option details.')
        .optional(),
      buyerNote: z
        .string()
        .describe(
          '[Buyer note](https://support.wix.com/en/article/wix-stores-viewing-buyer-notes) left by the customer.'
        )
        .max(1000)
        .optional()
        .nullable(),
      status: z
        .enum(['INITIALIZED', 'APPROVED', 'CANCELED', 'PENDING', 'REJECTED'])
        .describe('Order status.')
        .optional(),
      archived: z
        .boolean()
        .describe('Whether order is archived.')
        .optional()
        .nullable(),
      taxSummary: z
        .object({
          totalTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax.')
            .optional(),
        })
        .describe(
          'Tax summary.\nDeprecated. Use `taxInfo` instead.\nThis field will be removed on September 30, 2024.'
        )
        .optional(),
      taxInfo: z
        .object({
          totalTax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Calculated tax, added from line items.')
            .optional(),
          taxBreakdown: z
            .array(
              z.object({
                taxName: z
                  .string()
                  .describe(
                    'The name of the tax against which this tax amount was calculated.'
                  )
                  .max(200)
                  .optional(),
                taxType: z
                  .string()
                  .describe(
                    "The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws."
                  )
                  .max(200)
                  .optional(),
                jurisdiction: z
                  .string()
                  .describe(
                    'The name of the jurisdiction in which this tax detail applies.'
                  )
                  .max(200)
                  .optional(),
                jurisdictionType: z
                  .enum([
                    'UNDEFINED',
                    'COUNTRY',
                    'STATE',
                    'COUNTY',
                    'CITY',
                    'SPECIAL',
                  ])
                  .describe(
                    'The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special).'
                  )
                  .optional(),
                rate: z
                  .string()
                  .describe('The rate at which this tax detail was calculated.')
                  .optional(),
                aggregatedTaxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The sum of all the tax from line items that calculated by the tax identifiers.'
                  )
                  .optional(),
              })
            )
            .min(0)
            .max(50)
            .optional(),
          taxExempt: z
            .boolean()
            .describe(
              'Whether the order is exempt from tax calculations.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
        })
        .describe('Tax information.')
        .optional(),
      appliedDiscounts: z
        .array(
          z.intersection(
            z.object({
              discountType: z
                .enum(['GLOBAL', 'SPECIFIC_ITEMS', 'SHIPPING'])
                .optional(),
              lineItemIds: z.array(z.string()).min(0).max(300).optional(),
              _id: z
                .string()
                .describe('Discount ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              lineItemDiscounts: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Line item ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    totalDiscount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Total discount amount for this line item.')
                      .optional(),
                  })
                )
                .max(300)
                .optional(),
            }),
            z.xor([
              z.object({
                coupon: z.never().optional(),
                merchantDiscount: z.never().optional(),
                discountRule: z.never().optional(),
              }),
              z.object({
                merchantDiscount: z.never().optional(),
                discountRule: z.never().optional(),
                coupon: z
                  .object({
                    _id: z.string().describe('Coupon ID.').optional(),
                    code: z.string().describe('Coupon code.').optional(),
                    name: z.string().describe('Coupon name.').optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Coupon value.')
                      .optional(),
                  })
                  .describe('Coupon applied by the customer.'),
              }),
              z.object({
                coupon: z.never().optional(),
                discountRule: z.never().optional(),
                merchantDiscount: z
                  .intersection(
                    z.object({
                      amount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Discount amount.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        discountReason: z.never().optional(),
                        description: z.never().optional(),
                      }),
                      z.object({
                        description: z.never().optional(),
                        discountReason: z
                          .enum([
                            'UNSPECIFIED',
                            'EXCHANGED_ITEMS',
                            'BILLING_ADJUSTMENT',
                          ])
                          .describe('Predefined discount reason.'),
                      }),
                      z.object({
                        discountReason: z.never().optional(),
                        description: z
                          .string()
                          .describe('Custom discount description as free text.')
                          .max(200),
                      }),
                    ])
                  )
                  .describe('Discount applied manually by the merchant.'),
              }),
              z.object({
                coupon: z.never().optional(),
                merchantDiscount: z.never().optional(),
                discountRule: z
                  .object({
                    _id: z
                      .string()
                      .describe('Discount rule ID')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    name: z
                      .object({
                        original: z
                          .string()
                          .describe(
                            "Original discount rule name (in site's default language)."
                          )
                          .min(1)
                          .max(256)
                          .optional(),
                        translated: z
                          .string()
                          .describe(
                            'Translated discount rule name according to buyer language. Defaults to `original` when not provided.'
                          )
                          .min(1)
                          .max(500)
                          .optional()
                          .nullable(),
                      })
                      .describe('Discount rule name')
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Discount value.')
                      .optional(),
                  })
                  .describe(
                    'Automatic discount applied by the system based on configured discount rules.'
                  ),
              }),
            ])
          )
        )
        .max(320)
        .optional(),
      activities: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('Activity ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              authorEmail: z
                .string()
                .describe("Activity author's email.")
                .email()
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Activity creation date and time.')
                .optional()
                .nullable(),
              type: z
                .enum([
                  'ORDER_REFUNDED',
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'CUSTOM_ACTIVITY',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                ])
                .describe('Activity type.')
                .optional(),
              activityType: z
                .enum([
                  'ORDER_PLACED',
                  'ORDER_PAID',
                  'ORDER_FULFILLED',
                  'ORDER_NOT_FULFILLED',
                  'ORDER_CANCELED',
                  'DOWNLOAD_LINK_SENT',
                  'TRACKING_NUMBER_ADDED',
                  'TRACKING_NUMBER_EDITED',
                  'TRACKING_LINK_ADDED',
                  'SHIPPING_CONFIRMATION_EMAIL_SENT',
                  'INVOICE_ADDED',
                  'INVOICE_REMOVED',
                  'INVOICE_SENT',
                  'FULFILLER_EMAIL_SENT',
                  'SHIPPING_ADDRESS_EDITED',
                  'EMAIL_EDITED',
                  'PICKUP_READY_EMAIL_SENT',
                  'MERCHANT_COMMENT',
                  'ORDER_PARTIALLY_PAID',
                  'DRAFT_ORDER_CHANGES_APPLIED',
                  'SAVED_PAYMENT_METHOD',
                  'AUTHORIZED_PAYMENT_CREATED',
                  'AUTHORIZED_PAYMENT_CAPTURED',
                  'AUTHORIZED_PAYMENT_VOIDED',
                  'REFUND_INITIATED',
                  'PAYMENT_REFUNDED',
                  'PAYMENT_REFUND_FAILED',
                  'REFUNDED_AS_STORE_CREDIT',
                  'PAYMENT_PENDING',
                  'PAYMENT_CANCELED',
                  'PAYMENT_DECLINED',
                  'ORDER_PENDING',
                  'ORDER_REJECTED',
                  'RECEIPT_CREATED',
                  'RECEIPT_SENT',
                  'CHARGEBACK_CREATED',
                  'CHARGEBACK_REVERSED',
                  'ORDER_REFUNDED',
                ])
                .describe('Activity type.')
                .optional(),
            }),
            z.xor([
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
              }),
              z.object({
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                merchantComment: z
                  .object({
                    message: z
                      .string()
                      .describe('Merchant comment message.')
                      .min(1)
                      .max(5000)
                      .optional(),
                  })
                  .describe(
                    'Merchant comment details (optional). `activity.type` must be `MERCHANT_COMMENT`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                orderRefunded: z
                  .object({
                    manual: z
                      .boolean()
                      .describe(
                        'Whether order was refunded manually. For example, via payment provider or using cash.'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Additional info about order refunded activity (optional). `activity.type` must be `ORDER_REFUNDED`.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                draftOrderChangesApplied: z
                  .object({
                    draftOrderId: z
                      .string()
                      .describe('Draft order id.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for edit, given by user (optional).')
                      .max(200)
                      .optional()
                      .nullable(),
                    changes: z
                      .array(
                        z.intersection(
                          z.object({}),
                          z.xor([
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                            }),
                            z.object({
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemChanged: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name after change.')
                                  .optional(),
                                quantity: z
                                  .object({
                                    originalQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity before update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    newQuantity: z
                                      .number()
                                      .int()
                                      .describe('Item quantity after update.')
                                      .min(0)
                                      .max(1000000)
                                      .optional(),
                                    diff: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Difference between original and new quantity. Absolute value.'
                                      )
                                      .min(1)
                                      .max(1000000)
                                      .optional(),
                                    deltaType: z
                                      .enum([
                                        'QUANTITY_INCREASED',
                                        'QUANTITY_DECREASED',
                                      ])
                                      .describe(
                                        'Type of quantity change: increase or decrease.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Item quantity change.')
                                  .optional(),
                                price: z
                                  .object({
                                    originalPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price before update.')
                                      .optional(),
                                    newPrice: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Item price after update.')
                                      .optional(),
                                  })
                                  .describe('Item price change.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              lineItemRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Line item ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                      )
                                      .min(1)
                                      .max(200)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe(
                                        "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                      )
                                      .min(1)
                                      .max(400)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item name.')
                                  .optional(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe('Added or removed item quantity.')
                                  .min(1)
                                  .max(1000000)
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              discountRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Discount id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Discount name: coupon name / discount rule name / merchant discount description.'
                                  )
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Discount amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeAdded: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              additionalFeeRemoved: z.object({
                                _id: z
                                  .string()
                                  .describe('Additional fee id.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                name: z
                                  .object({
                                    original: z
                                      .string()
                                      .describe(
                                        'Value in site default language.'
                                      )
                                      .min(1)
                                      .optional(),
                                    translated: z
                                      .string()
                                      .describe('Translated value.')
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Additional fee name.')
                                  .optional(),
                                affectedLineItems: z
                                  .array(
                                    z.object({
                                      _id: z
                                        .string()
                                        .describe('Order line item id')
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                      name: z
                                        .object({
                                          original: z
                                            .string()
                                            .describe(
                                              "__Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                                            )
                                            .min(1)
                                            .max(200)
                                            .optional(),
                                          translated: z
                                            .string()
                                            .describe(
                                              "Item name translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                                            )
                                            .min(1)
                                            .max(400)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe('Item name.')
                                        .optional(),
                                      amount: z
                                        .object({
                                          amount: z
                                            .string()
                                            .describe('Amount.')
                                            .optional(),
                                          formattedAmount: z
                                            .string()
                                            .describe(
                                              'Amount formatted with currency symbol.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'Amount associated with this item. (Discount amount for item / additional fee amount for item)'
                                        )
                                        .optional(),
                                    })
                                  )
                                  .min(0)
                                  .optional(),
                                totalAmount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Additional fee amount.')
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              shippingInformationChanged: z.never().optional(),
                              totalPriceChanged: z.object({
                                originalTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. Before update'
                                  )
                                  .optional(),
                                newTotal: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s total price after discounts and tax. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                            z.object({
                              lineItemChanged: z.never().optional(),
                              lineItemAdded: z.never().optional(),
                              lineItemRemoved: z.never().optional(),
                              discountAdded: z.never().optional(),
                              discountRemoved: z.never().optional(),
                              additionalFeeAdded: z.never().optional(),
                              additionalFeeRemoved: z.never().optional(),
                              totalPriceChanged: z.never().optional(),
                              shippingInformationChanged: z.object({
                                originalShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. Before update'
                                  )
                                  .optional(),
                                newShippingInfo: z
                                  .object({
                                    total: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe('Amount.')
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Amount formatted with currency symbol.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Order’s shipping price.')
                                      .optional(),
                                    shippingTitle: z
                                      .string()
                                      .describe('Order’s shipping title.')
                                      .max(250)
                                      .optional(),
                                  })
                                  .describe(
                                    'Order’s Shipping Information. After update'
                                  )
                                  .optional(),
                              }),
                            }),
                          ])
                        )
                      )
                      .optional(),
                  })
                  .describe('Details of changes made by the Draft Orders API.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                savedPaymentMethod: z
                  .object({
                    name: z
                      .string()
                      .describe('Payment method name.')
                      .min(1)
                      .max(100)
                      .optional(),
                    description: z
                      .string()
                      .describe('Payment method description.')
                      .min(1)
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of the payment method saved for the order.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment created.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentCaptured: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment captured.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                authorizedPaymentVoided: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'Payment ID of payment associated with this activity.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Payment amount.')
                      .optional(),
                    lastFourDigits: z
                      .string()
                      .describe('The last 4 digits of the card number.')
                      .max(4)
                      .optional()
                      .nullable(),
                    brand: z
                      .string()
                      .describe("Card issuer's brand.")
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of an authorized payment voided.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundInitiated: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    payments: z
                      .array(
                        z.intersection(
                          z.object({
                            paymentId: z
                              .string()
                              .describe('Payment ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            externalRefund: z
                              .boolean()
                              .describe(
                                "Whether refund was made externally and manually on the payment provider's side."
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                            }),
                            z.object({
                              giftCard: z.never().optional(),
                              membership: z.never().optional(),
                              regular: z
                                .object({
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                  lastFourDigits: z
                                    .string()
                                    .describe(
                                      'The last 4 digits of the card number.'
                                    )
                                    .max(4)
                                    .optional()
                                    .nullable(),
                                  brand: z
                                    .string()
                                    .describe("Card issuer's brand.")
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Regular payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              membership: z.never().optional(),
                              giftCard: z
                                .object({
                                  giftCardPaymentId: z
                                    .string()
                                    .describe('Gift card payment ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                  amount: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe('Amount.')
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Amount formatted with currency symbol.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Refund amount.')
                                    .optional(),
                                })
                                .describe('Gift card payment refund.'),
                            }),
                            z.object({
                              regular: z.never().optional(),
                              giftCard: z.never().optional(),
                              membership: z
                                .object({
                                  membershipId: z
                                    .string()
                                    .describe('Membership ID.')
                                    .min(1)
                                    .max(100)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Membership payment refund.'),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(50)
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Details of an initiated refund process.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefunded: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the refunded payment.')
                      .optional(),
                  })
                  .describe(
                    'Details of a refunded payment.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentRefundFailed: z
                  .object({
                    refundId: z
                      .string()
                      .describe('Refund ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    payment: z
                      .intersection(
                        z.object({
                          paymentId: z
                            .string()
                            .describe('Payment ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          externalRefund: z
                            .boolean()
                            .describe(
                              "Whether refund was made externally and manually on the payment provider's side."
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                          }),
                          z.object({
                            giftCard: z.never().optional(),
                            membership: z.never().optional(),
                            regular: z
                              .object({
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Regular payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            membership: z.never().optional(),
                            giftCard: z
                              .object({
                                giftCardPaymentId: z
                                  .string()
                                  .describe('Gift card payment ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                                amount: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe('Amount.')
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Amount formatted with currency symbol.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Refund amount.')
                                  .optional(),
                              })
                              .describe('Gift card payment refund.'),
                          }),
                          z.object({
                            regular: z.never().optional(),
                            giftCard: z.never().optional(),
                            membership: z
                              .object({
                                membershipId: z
                                  .string()
                                  .describe('Membership ID.')
                                  .min(1)
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Membership payment refund.'),
                          }),
                        ])
                      )
                      .describe('Details about the failed payment refund.')
                      .optional(),
                  })
                  .describe(
                    'Details of a failed payment refund.\n\n> **Note:** A single `refundInitiated` activity can result in multiple `paymentRefunded` or `paymentRefundFailed` activities.\n> In these cases, the `refundId` will be identical across the activities.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                refundedAsStoreCredit: z
                  .object({
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Refund amount.')
                      .optional(),
                    reason: z
                      .string()
                      .describe('Reason for refund.')
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Details of refund to store credit.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentPending: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a pending payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentCanceled: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a canceled payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                paymentDeclined: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ regular: z.never().optional() }),
                      z.object({
                        regular: z
                          .intersection(
                            z.object({
                              amount: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe('Amount.')
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Amount formatted with currency symbol.'
                                    )
                                    .optional(),
                                })
                                .describe('Payment amount.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                creditCardDetails: z.never().optional(),
                              }),
                              z.object({
                                creditCardDetails: z
                                  .object({
                                    lastFourDigits: z
                                      .string()
                                      .describe(
                                        'The last 4 digits of the card number.'
                                      )
                                      .max(4)
                                      .optional()
                                      .nullable(),
                                    brand: z
                                      .string()
                                      .describe("Card issuer's brand.")
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Credit card details.'),
                              }),
                            ])
                          )
                          .describe('Regular payment.'),
                      }),
                    ])
                  )
                  .describe('Details of a declined payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptCreated: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt was added for associated payment.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z.never().optional(),
                receiptSent: z
                  .intersection(
                    z.object({
                      paymentId: z
                        .string()
                        .describe(
                          'Payment ID of payment associated with this activity.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z.never().optional(),
                      }),
                      z.object({
                        externalReceipt: z.never().optional(),
                        wixReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by Wix.'),
                      }),
                      z.object({
                        wixReceipt: z.never().optional(),
                        externalReceipt: z
                          .object({
                            receiptId: z
                              .string()
                              .describe('Receipt ID.')
                              .max(100)
                              .optional()
                              .nullable(),
                            displayNumber: z
                              .string()
                              .describe('Display number of receipt.')
                              .min(1)
                              .max(40)
                              .optional()
                              .nullable(),
                          })
                          .describe('Receipt created by an external system.'),
                      }),
                    ])
                  )
                  .describe('Receipt sent to customer.'),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackReversed: z.never().optional(),
                chargebackCreated: z
                  .object({
                    paymentId: z
                      .string()
                      .describe('ID of the payment that received a chargeback.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    'Order received a chargeback for one of its payments.'
                  ),
              }),
              z.object({
                merchantComment: z.never().optional(),
                orderRefunded: z.never().optional(),
                draftOrderChangesApplied: z.never().optional(),
                savedPaymentMethod: z.never().optional(),
                authorizedPaymentCreated: z.never().optional(),
                authorizedPaymentCaptured: z.never().optional(),
                authorizedPaymentVoided: z.never().optional(),
                refundInitiated: z.never().optional(),
                paymentRefunded: z.never().optional(),
                paymentRefundFailed: z.never().optional(),
                refundedAsStoreCredit: z.never().optional(),
                paymentPending: z.never().optional(),
                paymentCanceled: z.never().optional(),
                paymentDeclined: z.never().optional(),
                receiptCreated: z.never().optional(),
                receiptSent: z.never().optional(),
                chargebackCreated: z.never().optional(),
                chargebackReversed: z
                  .object({
                    paymentId: z
                      .string()
                      .describe(
                        'ID of the payment involved with the chargeback.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    chargebackId: z
                      .string()
                      .describe('Chargeback ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    amount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback amount.')
                      .optional(),
                    reversalAmount: z
                      .object({
                        amount: z.string().describe('Amount.').optional(),
                        formattedAmount: z
                          .string()
                          .describe('Amount formatted with currency symbol.')
                          .optional(),
                      })
                      .describe('Chargeback reversal amount.')
                      .optional(),
                    paymentDetails: z
                      .intersection(
                        z.object({
                          amount: z
                            .object({
                              amount: z.string().describe('Amount.').optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Amount formatted with currency symbol.'
                                )
                                .optional(),
                            })
                            .describe('Payment amount.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({ creditCardDetails: z.never().optional() }),
                          z.object({
                            creditCardDetails: z
                              .object({
                                lastFourDigits: z
                                  .string()
                                  .describe(
                                    'The last 4 digits of the card number.'
                                  )
                                  .max(4)
                                  .optional()
                                  .nullable(),
                                brand: z
                                  .string()
                                  .describe("Card issuer's brand.")
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Credit card details.'),
                          }),
                        ])
                      )
                      .describe('Payment details.')
                      .optional(),
                  })
                  .describe(
                    "Chargeback reversed for one of the order's payments."
                  ),
              }),
            ])
          )
        )
        .max(1000)
        .optional(),
      attributionSource: z
        .enum(['UNSPECIFIED', 'FACEBOOK_ADS'])
        .describe('Order attribution source.')
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'User ID - when the order was created by a Wix user on behalf of a buyer.\nFor example, via POS (point of service).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID - when the order was created by a **logged in** site visitor.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              appId: z.never().optional(),
              visitorId: z
                .string()
                .describe(
                  'Visitor ID - when the order was created by a site visitor that was **not** logged in.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              userId: z.never().optional(),
              memberId: z.never().optional(),
              visitorId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID - when the order was created by an external application.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe("ID of the order's initiator.")
        .optional(),
      channelInfo: z
        .object({
          type: z
            .enum([
              'UNSPECIFIED',
              'WEB',
              'POS',
              'EBAY',
              'AMAZON',
              'OTHER_PLATFORM',
              'WIX_APP_STORE',
              'WIX_INVOICES',
              'BACKOFFICE_MERCHANT',
              'WISH',
              'CLASS_PASS',
              'GLOBAL_E',
              'FACEBOOK',
              'ETSY',
              'TIKTOK',
              'FAIRE_COM',
              'PAYPAL_AGENTIC_CHECKOUT',
              'STRIPE_AGENTIC_CHECKOUT',
            ])
            .describe(
              'Sales channel that submitted the order.\n\nWhen creating an order via the API to record an external order, use the channel type that matches the original source. If no matching type exists, use `OTHER_PLATFORM`.'
            )
            .optional(),
          externalOrderId: z
            .string()
            .describe(
              'Reference to an order ID from an external system. Relevant when recording orders from external platforms.'
            )
            .max(100)
            .optional()
            .nullable(),
          externalOrderUrl: z
            .string()
            .describe(
              'URL to the order in the external system. Relevant when recording orders from external platforms.'
            )
            .max(300)
            .optional()
            .nullable(),
        })
        .describe(
          'Information about the sales channel that submitted this order.'
        )
        .optional(),
      seenByAHuman: z
        .boolean()
        .describe(
          'Whether a human has seen the order. Set when an order is clicked on in the dashboard.'
        )
        .optional()
        .nullable(),
      checkoutId: z
        .string()
        .describe('Checkout ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      customFields: z
        .array(
          z.object({
            value: z
              .any()
              .describe('Custom field value.')
              .optional()
              .nullable(),
            title: z
              .string()
              .describe('Custom field title.')
              .min(1)
              .max(500)
              .optional(),
            translatedTitle: z
              .string()
              .describe('Translated custom field title.')
              .min(1)
              .max(500)
              .optional()
              .nullable(),
          })
        )
        .optional(),
      balanceSummary: z
        .object({
          balance: z
            .object({
              amount: z
                .string()
                .describe(
                  'Balance amount.\n\nA negative `amount` represents the amount to be refunded. This can happen due to overcharging or the order being modified after a payment has been made.'
                )
                .optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Current amount left to pay.')
            .optional(),
          paid: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Sum of all approved and successful payments.\n\nThe value includes payments that have subsequently been fully or partially refunded.'
            )
            .optional(),
          refunded: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all successfully refunded payments.')
            .optional(),
          authorized: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all authorized payments.')
            .optional(),
          pendingRefund: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all pending refund transactions.')
            .optional(),
          pending: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all pending transactions.')
            .optional(),
          chargeback: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all transaction chargebacks.')
            .optional(),
          chargebackReversal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Sum of all chargeback reversals.')
            .optional(),
          platformFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total platform fees actually charged across all payments.\nThis reflects amounts collected, which may differ from the expected fees in `order.platformFeeSummary`.'
            )
            .optional(),
          totalMinusPlatformFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Order total after deducting platform fees. Represents the net amount payable to the merchant.'
            )
            .optional(),
        })
        .describe('Balance summary.')
        .optional(),
      additionalFees: z
        .array(
          z.object({
            code: z
              .string()
              .describe("Additional fee's unique code for future processing.")
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of additional fee.')
              .min(1)
              .max(50)
              .optional(),
            price: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price.")
              .optional(),
            taxDetails: z
              .object({
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Taxable amount of this line item.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a decimal numeral between 0 and 1. For example, `"0.13"`.'
                  )
                  .optional(),
                totalTax: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'The calculated tax, based on the `taxableAmount` and `taxRate`.'
                  )
                  .optional(),
              })
              .describe('Tax details.')
              .optional(),
            taxInfo: z
              .object({
                taxAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe(
                    'Calculated tax, based on `taxable_amount` and `tax_rate`.'
                  )
                  .optional(),
                taxableAmount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Amount for which tax is calculated.')
                  .optional(),
                taxRate: z
                  .string()
                  .describe('Tax rate %, as a decimal point.')
                  .optional()
                  .nullable(),
                taxGroupId: z
                  .string()
                  .describe('Tax group ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                taxIncludedInPrice: z
                  .boolean()
                  .describe('Indicates whether the price already includes tax.')
                  .optional(),
                taxBreakdown: z
                  .array(
                    z.object({
                      jurisdiction: z
                        .string()
                        .describe(
                          'Jurisdiction that taxes were calculated for. For example, "New York", or "Quebec".'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is 0.1000.'
                        )
                        .optional()
                        .nullable(),
                      taxAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe(
                          'Amount of tax calculated for this line item.'
                        )
                        .optional(),
                      taxType: z
                        .string()
                        .describe(
                          'The type of tax that was calculated. Depends on the jurisdiction\'s tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      taxName: z
                        .string()
                        .describe(
                          'The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.\nThis name should be explicit enough to allow the merchant to understand what tax was calculated.'
                        )
                        .max(200)
                        .optional()
                        .nullable(),
                      jurisdictionType: z
                        .enum([
                          'UNDEFINED',
                          'COUNTRY',
                          'STATE',
                          'COUNTY',
                          'CITY',
                          'SPECIAL',
                        ])
                        .describe(
                          'Type of jurisdiction that taxes were calculated for.'
                        )
                        .optional(),
                      nonTaxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Non-taxable amount of the line item price.')
                        .optional(),
                      taxableAmount: z
                        .object({
                          amount: z.string().describe('Amount.').optional(),
                          formattedAmount: z
                            .string()
                            .describe('Amount formatted with currency symbol.')
                            .optional(),
                        })
                        .describe('Taxable amount of the line item price.')
                        .optional(),
                    })
                  )
                  .max(7)
                  .optional(),
              })
              .describe(
                'Represents all the relevant tax details for additional fee.'
              )
              .optional(),
            providerAppId: z
              .string()
              .describe("SPI implementer's `appId`.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            priceBeforeTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price before tax.")
              .optional(),
            priceAfterTax: z
              .object({
                amount: z.string().describe('Amount.').optional(),
                formattedAmount: z
                  .string()
                  .describe('Amount formatted with currency symbol.')
                  .optional(),
              })
              .describe("Additional fee's price after tax.")
              .optional(),
            _id: z
              .string()
              .describe("Additional fee's id.")
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            lineItemIds: z.array(z.string()).optional(),
            source: z
              .enum([
                'SERVICE_PLUGIN',
                'ITEM',
                'MANUAL',
                'SHIPPING',
                'PLATFORM',
              ])
              .describe('Specifies the entity that added the additional fee.')
              .optional(),
          })
        )
        .max(100)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the order object.\n\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      purchaseFlowId: z
        .string()
        .describe(
          'Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      recipientInfo: z
        .object({
          address: z
            .object({
              streetAddress: z
                .object({
                  name: z.string().optional(),
                  number: z.string().optional(),
                })
                .optional(),
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Address.')
            .optional(),
          contactDetails: z
            .object({
              firstName: z
                .string()
                .describe('First name.')
                .max(100)
                .optional()
                .nullable(),
              lastName: z
                .string()
                .describe('Last name.')
                .max(100)
                .optional()
                .nullable(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              company: z
                .string()
                .describe('Company name.')
                .max(1000)
                .optional()
                .nullable(),
              vatId: z
                .object({
                  _id: z.string().describe("Customer's tax ID.").optional(),
                  type: z
                    .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                    .describe(
                      'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                    )
                    .optional(),
                })
                .describe(
                  'Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed.'
                )
                .optional(),
            })
            .describe('Contact details.')
            .optional(),
        })
        .describe(
          "Final recipient's address and contact details.\n\nThis field represents who will ultimately receive the order. It may differ from `shippingInfo.logistics.shippingDestination` when:\n+ The chosen shipping option is a pickup point or store pickup, where `shippingDestination` contains the pickup location.\n+ No shipping option is selected."
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Order tags.\n\n[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) are labels attached to entities, allowing for flexible categorization and data management.'
        )
        .optional(),
      purchasedDate: z
        .date()
        .describe(
          'Date and time the order was originally purchased in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.\nUsed for migration from external systems.'
        )
        .optional()
        .nullable(),
      businessLocation: z
        .object({
          _id: z
            .string()
            .describe(
              'Location ID.\nLearn more about the [Wix Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          name: z
            .string()
            .describe('Location name.')
            .min(1)
            .max(500)
            .optional(),
        })
        .describe('Order location.')
        .optional(),
      payAfterFreeTrial: z
        .object({
          subtotal: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Subtotal of all line items, before discounts and before tax.'
            )
            .optional(),
          shipping: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total shipping price, before discounts and before tax.')
            .optional(),
          tax: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total tax applied to the order.')
            .optional(),
          discount: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total discount amount applied to the order.')
            .optional(),
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe("Order's total price after discounts and tax.")
            .optional(),
          totalAdditionalFees: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total additional fees before tax.')
            .optional(),
        })
        .describe(
          'Pay after free trial price summary. Price summary that should be paid for subscriptions after free trial period.'
        )
        .optional(),
      platformFeeSummary: z
        .object({
          total: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe('Total sum of all platform fees.')
            .optional(),
          totalPassOn: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total amount of platform fees with `PASS_ON` charge type.'
            )
            .optional(),
          totalAbsorbed: z
            .object({
              amount: z.string().describe('Amount.').optional(),
              formattedAmount: z
                .string()
                .describe('Amount formatted with currency symbol.')
                .optional(),
            })
            .describe(
              'Total amount of platform fees with `ABSORBED` charge type.'
            )
            .optional(),
          fees: z
            .array(
              z.object({
                name: z
                  .object({
                    original: z
                      .string()
                      .describe(
                        "__Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).\n\nMin: 1 character.\nMax: 200 characters."
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                    translated: z
                      .string()
                      .describe(
                        "String translated into the buyer's language.\n\nMin: 1 character.\nMax: 400 characters.\nDefault: Same as `original`."
                      )
                      .min(1)
                      .max(400)
                      .optional()
                      .nullable(),
                  })
                  .describe('Platform fee name.')
                  .optional(),
                amount: z
                  .object({
                    amount: z.string().describe('Amount.').optional(),
                    formattedAmount: z
                      .string()
                      .describe('Amount formatted with currency symbol.')
                      .optional(),
                  })
                  .describe('Platform fee amount.')
                  .optional(),
                lineItemId: z
                  .string()
                  .describe('ID of the line item the platform fee applies to.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                chargeType: z
                  .enum(['PASS_ON', 'ABSORBED'])
                  .describe('Platform fee charge type.')
                  .optional(),
                percentageRate: z
                  .string()
                  .describe(
                    'Percentage rate charged as platform fee.\nThe fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.'
                  )
                  .optional(),
              })
            )
            .max(300)
            .optional(),
        })
        .describe(
          'Summary of platform fees for this order, including totals by charge type and a breakdown of individual fees.'
        )
        .optional(),
    })
    .describe('Canceled order.')
    .optional(),
});
export const BulkUpdateOrderTagsRequest = z.object({
  orderIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to be added to orders')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to be removed from orders')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateOrderTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .optional(),
});
